import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FaShoppingCart } from "react-icons/fa";
import { PageShell, SectionCard } from "../components/ui";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../utils/axiosInstance";
import "./css/BattleSimulationCommanderProfiles.css";
import {
  createCommanderProfile,
  deleteCommanderProfile,
  getCommanderById,
  listCommanderProfiles,
  searchCommandersByName,
  updateCommanderProfile,
} from "./battle-simulation/profileApi";
import {
  computeOfficerSkillPreview,
  decimalStarToStarStage,
  getOfficerCatalogEntryById,
  getOfficerCategoryLabel,
  getOfficerDisplayIconPath,
  getOfficerIconPathsById,
  getOfficerLevelExpToNext,
  getOfficerMasteryValue,
  getOfficerPassiveSkillPoolForOfficer,
  getOfficerEquipmentSlotsForOfficer,
  getOfficerEquipmentSlotUnlockStar,
  isOfficerEquipmentSlotUnlocked,
  getOfficerEquipmentEffectContributions,
  getOfficerEquipmentPowerContribution,
  normalizeOfficerEquipmentRowsForOfficer,
  getOfficerSkinPowerContribution,
  getOfficerSkinTierBounds,
  getOfficerSkillMaxLevelForStar,
  getOfficerSkillLevelExpToNext,
  getOfficerSkillOptionById,
  getOfficerUnworldlyOptionsByOfficer,
  getOfficerUnworldlySlotUnlockStar,
  isOfficerUnworldlySlotUnlocked,
  isOfficerSkillSlotUnlocked,
  OFFICER_ACTIVE_SKILL_OPTIONS,
  OFFICER_APPOINTMENT_LIMITS,
  OFFICER_BASE_ROWS,
  OFFICER_CATEGORY_ORDER,
  OFFICER_PASSIVE_SKILL_OPTIONS,
  OFFICER_RESOURCE_CATALOG,
  createOfficerSeedRows,
  resolveUnworldlyTierBySkill,
} from "./battle-simulation/data/officerCatalog";
import { MALE_TITAN_TABLE_CONFIG } from "./battle-simulation/data/maleTitanTableConfig";
import { FEMALE_TITAN_TABLE_CONFIG } from "./battle-simulation/data/femaleTitanTableConfig";
import { WARPLANE_STATS_CONFIG } from "./battle-simulation/data/warplaneStatsConfig";
import { COMMANDER_EFFECT_CATALOG, getCommanderSectionCatalog } from "./battle-simulation/data/commanderSectionCatalog";
import { ALLIANCE_BUFF_CATALOG } from "./battle-simulation/data/allianceBuffCatalog";
import { ITEM_BUFF_CATALOG } from "./battle-simulation/data/itemBuffCatalog";
import { NATION_BUFF_CATALOG } from "./battle-simulation/data/nationBuffCatalog";
import {
  CITY_DECORATION_PERMANENT_OPTIONS,
  CITY_DECORATION_TEMPORARY_BUFFS,
} from "./battle-simulation/data/cityDecorationCatalog";
import { TEMPORARY_BUFF_CATALOG } from "./battle-simulation/data/temporaryBuffCatalog";
import { VIP_LEVEL_OPTIONS, VIP_MAX_LEVEL, getVipLevelOption } from "./battle-simulation/data/vipCatalog";
import { PROSPERITY_LEVEL_OPTIONS, PROSPERITY_MAX_LEVEL, getProsperityLevelOption } from "./battle-simulation/data/prosperityCatalog";
import { MILITARY_RANK_MAX_LEVEL, MILITARY_RANK_OPTIONS, getMilitaryRankOption } from "./battle-simulation/data/militaryRankCatalog";
import { ALLIANCE_BENEFIT_BUILDING_OPTIONS } from "./battle-simulation/data/allianceBenefitBuildingCatalog";
import {
  applySectionDerivedParityFields,
  buildSectionContributionDetails,
} from "./battle-simulation/commanderParityCompiler";

const PARITY_ARRAY_FIELDS = [
  "ba_tech",
  "ba_h_tech",
  "tow_sps",
  "titan_b_i",
  "fighter_b_i",
  "sw_items",
  "cw_items",
  "ew_items",
  "sw_sp_eff",
  "cw_sp_eff",
  "s_n_bfs",
  "c_n_bfs",
  "royal_it",
  "tk_i_b",
  "gvg_s",
];

const POWER_SUMMARY_FIELDS = Object.freeze([
  ["battlePower", "Battle Power"],
  ["officerPower", "Officer Power"],
  ["titanPower", "Titan Power"],
  ["warplanePower", "Warplane Power"],
  ["antiquesPower", "Antiques Power"],
  ["equipmentPower", "Equipment Power"],
]);

const COMBAT_CONTEXT_OPTIONS = Object.freeze([
  { key: "internal", label: "Internal" },
  { key: "crossNation", label: "Cross Nation" },
]);

const COMBAT_BATTLE_OPTIONS = Object.freeze([
  { key: "field", label: "Field / Other" },
  { key: "citySiege", label: "City Siege" },
]);

const COMBAT_SIDE_OPTIONS = Object.freeze([
  { key: "attacking", label: "Attacking" },
  { key: "defending", label: "Defending" },
]);

const EXPECTED_REPORT_TECH_GIDS = Object.freeze({
  ATTACK_ROW: [50397213, 50397215, 50397217],
  HP_ROW: [50397214, 50397216, 50397218],
  DEFENSE_ROW: [50397275, 50397276, 50397277],
  ATTACK_ALL: 50397254,
  HP_ALL: 50397255,
  DEFENSE_ALL: 50397279,
  SIEGE_ATTACK_ATTACKING: 50397332,
  SIEGE_HP_ATTACKING: 50397333,
  SIEGE_DEFENSE_ATTACKING: 50397313,
  SIEGE_ATTACK_DEFENDING: 50397267,
  SIEGE_HP_DEFENDING: 50397262,
  SIEGE_DEFENSE_DEFENDING: 50397325,
  CROSS_NATION_ATTACK_ROW: [50397369, 50397372, 50397375],
  CROSS_NATION_HP_ROW: [50397371, 50397374, 50397377],
  CROSS_NATION_DEFENSE_ROW: [50397370, 50397373, 50397376],
  CROSS_NATION_BIOTECH_ATTACK_RATE: 50397381,
  CROSS_NATION_BIOTECH_DEFENSE_RATE: 50397382,
  CROSS_NATION_BIOTECH_HP_RATE: 50397383,
  CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP: 50397406,
  DAMAGE_TO_ROW: [50397247, 50397246, 50397245],
  DAMAGE_FROM_ROW: [50397337, 50397336, 50397335],
  ATTACK_TO_ROW: [50397422, 50397423, 50397424],
  ATTACK_TO_BIO: 50397425,
  SUB_ATTACK_TO_ROW: [50397426, 50397427, 50397428],
  SUB_ATTACK_TO_BIO: 50397429,
  SUB_ATTACK_TO_ALL: 50397430,
  SUB_DEFENSE_TO_ROW: [50397436, 50397437, 50397438],
  BIOTECH_ATTACK_RATE: 50397353,
  BIOTECH_HP_RATE: 50397354,
  BIOTECH_DEFENSE_RATE: 50397355,
  BLOCK_RATE_ALL: 50397507,
  BLOCK_ALL: 50462930,
  BAN_SKILL_RESIST_ALL: 50462937,
  MISFIRE_RESIST_ALL: 50462940,
  MISC_ATTACK_AMOUNT: 50462916,
  MISC_HP_AMOUNT: 50462920,
  DAMAGE_TO_TITAN: 50397459,
  DAMAGE_FROM_TITAN: 50397460,
});

const FLEET_SIZE_BASE_UNITS = 10000;
const FLEET_SIZE_TECH_GIDS = Object.freeze({
  UNIT_LIMIT: 50462802,
  BOOST_RATE: 50397286,
  BONUS_UNIT_LIMIT: 50462870,
  CROSS_NATION_UNIT_LIMIT: 50462871,
  CROSS_NATION_WAR_MOBILIZATION_UNIT_LIMIT: 50462874,
  BIOCHEMICAL_ZOMBIE_LIMIT: 50462851,
});
const FLEET_SIZE_CORE_UNIT_EFFECT_IDS = Object.freeze([
  FLEET_SIZE_TECH_GIDS.UNIT_LIMIT,
]);
const FLEET_SIZE_PERCENT_EFFECT_IDS = Object.freeze([
  FLEET_SIZE_TECH_GIDS.BOOST_RATE,
]);
const FLEET_SIZE_BONUS_UNIT_EFFECT_IDS = Object.freeze([
  FLEET_SIZE_TECH_GIDS.BONUS_UNIT_LIMIT,
  FLEET_SIZE_TECH_GIDS.CROSS_NATION_UNIT_LIMIT,
  FLEET_SIZE_TECH_GIDS.CROSS_NATION_WAR_MOBILIZATION_UNIT_LIMIT,
]);
const FLEET_SIZE_ZOMBIE_EFFECT_IDS = Object.freeze([
  FLEET_SIZE_TECH_GIDS.BIOCHEMICAL_ZOMBIE_LIMIT,
]);
const FLEET_SIZE_RELEVANT_EFFECT_IDS = Object.freeze([
  ...FLEET_SIZE_CORE_UNIT_EFFECT_IDS,
  ...FLEET_SIZE_PERCENT_EFFECT_IDS,
  ...FLEET_SIZE_BONUS_UNIT_EFFECT_IDS,
  ...FLEET_SIZE_ZOMBIE_EFFECT_IDS,
]);
const FLEET_SIZE_RELEVANT_EFFECT_ID_SET = new Set(FLEET_SIZE_RELEVANT_EFFECT_IDS);
const FLEET_SIZE_PERCENT_EFFECT_ID_SET = new Set(FLEET_SIZE_PERCENT_EFFECT_IDS);
const FLEET_SIZE_ZOMBIE_EFFECT_ID_SET = new Set(FLEET_SIZE_ZOMBIE_EFFECT_IDS);

const rateValueToPercent = (rawValue) => {
  const value = Math.max(0, Number(rawValue) || 0);
  if (value <= 0) return 0;
  return (value / (1000 + value)) * 100;
};

const getTechMapValue = (techMap, gid) => Number(techMap.get(gid) || 0);

const sumTechMapValues = (techMap, gids = []) => gids.reduce(
  (sum, gid) => sum + getTechMapValue(techMap, gid),
  0,
);

const EXPECTED_REPORT_BASELINE_PERCENT_VALUE = 3;

const EXPECTED_REPORT_BASELINE_STAT_KEYS = new Set([
  "meleeHp",
  "midRangeHp",
  "longRangeHp",
  "meleeAttack",
  "midRangeAttack",
  "longRangeAttack",
  "meleeDefense",
  "midRangeDefense",
  "longRangeDefense",
  "biochemicalZombiesAttackBoost",
  "biochemicalZombiesHpBoost",
  "biochemicalZombiesDefenseBoost",
]);

const getExpectedReportStatBaselineValue = (statKey = "") => (
  EXPECTED_REPORT_BASELINE_STAT_KEYS.has(String(statKey || ""))
    ? EXPECTED_REPORT_BASELINE_PERCENT_VALUE
    : 0
);

const getEnemyAttackReductionStatValue = (techMap, specificGid) => (
  sumTechMapValues(techMap, [specificGid, EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ALL])
);

const getCombatSideTechValue = (techMap, combatSide, attackingGid, defendingGid) => (
  combatSide === "defending"
    ? getTechMapValue(techMap, defendingGid)
    : getTechMapValue(techMap, attackingGid)
);

const getExpectedReportUnitStatValue = (
  techMap,
  baseRow,
  crossNationRow,
  index,
  {
    allGid,
    attackingGid,
    defendingGid,
    combatBattle = "field",
    combatSide = "attacking",
  } = {},
) => (
  getTechMapValue(techMap, baseRow[index])
  + getTechMapValue(techMap, crossNationRow[index])
  + getTechMapValue(techMap, allGid)
  + getSiegeSideTechValue(techMap, combatBattle, combatSide, attackingGid, defendingGid)
  + getTechMapValue(techMap, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP)
);

const EXPECTED_REPORT_STAT_METRICS = Object.freeze([
  ["meleeHp", "Melee HP", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.HP_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_HP_ROW, 0, { allGid: EXPECTED_REPORT_TECH_GIDS.HP_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["midRangeHp", "Mid-Range HP", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.HP_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_HP_ROW, 1, { allGid: EXPECTED_REPORT_TECH_GIDS.HP_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["longRangeHp", "Long-Range HP", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.HP_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_HP_ROW, 2, { allGid: EXPECTED_REPORT_TECH_GIDS.HP_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["meleeAttack", "Melee Attack", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.ATTACK_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ATTACK_ROW, 0, { allGid: EXPECTED_REPORT_TECH_GIDS.ATTACK_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["midRangeAttack", "Mid-Range Attack", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.ATTACK_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ATTACK_ROW, 1, { allGid: EXPECTED_REPORT_TECH_GIDS.ATTACK_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["longRangeAttack", "Long-Range Attack", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.ATTACK_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ATTACK_ROW, 2, { allGid: EXPECTED_REPORT_TECH_GIDS.ATTACK_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["meleeDefense", "Melee Defense", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.DEFENSE_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_DEFENSE_ROW, 0, { allGid: EXPECTED_REPORT_TECH_GIDS.DEFENSE_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["midRangeDefense", "Mid-Range Defense", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.DEFENSE_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_DEFENSE_ROW, 1, { allGid: EXPECTED_REPORT_TECH_GIDS.DEFENSE_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["longRangeDefense", "Long-Range Defense", "percent", (techMap, options) => getExpectedReportUnitStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.DEFENSE_ROW, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_DEFENSE_ROW, 2, { allGid: EXPECTED_REPORT_TECH_GIDS.DEFENSE_ALL, attackingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_ATTACKING, defendingGid: EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_DEFENDING, combatBattle: options?.combatBattle, combatSide: options?.combatSide }) / 10],
  ["increaseDamageToMeleeTroops", "Increases Damage to Melee Troops", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.DAMAGE_TO_ROW[0]) || 0) / 10],
  ["increaseDamageToMidRangeTroops", "Increases Damage to Mid-Range Troops", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.DAMAGE_TO_ROW[1]) || 0) / 10],
  ["increaseDamageToLongRangeTroops", "Increases Damage to Long-Range Troops", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.DAMAGE_TO_ROW[2]) || 0) / 10],
  ["reducesDamageTakenFromMeleeTroops", "Reduces Damage taken from Melee Troops", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.DAMAGE_FROM_ROW[0]) || 0) / 10],
  ["reducesDamageTakenFromMidRangeTroops", "Reduces Damage taken from Mid-Range Troops", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.DAMAGE_FROM_ROW[1]) || 0) / 10],
  ["reducesDamageTakenFromLongRangeTroops", "Reduces Damage taken from Long-Range Troops", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.DAMAGE_FROM_ROW[2]) || 0) / 10],
  ["biochemicalZombiesAttackBoost", "Biochemical Zombies Attack Boost", "percent", (techMap) => sumTechMapValues(techMap, [EXPECTED_REPORT_TECH_GIDS.BIOTECH_ATTACK_RATE, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_BIOTECH_ATTACK_RATE]) / 10],
  ["biochemicalZombiesHpBoost", "Biochemical Zombies HP Boost", "percent", (techMap) => sumTechMapValues(techMap, [EXPECTED_REPORT_TECH_GIDS.BIOTECH_HP_RATE, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_BIOTECH_HP_RATE]) / 10],
  ["biochemicalZombiesDefenseBoost", "Biochemical Zombies Defense Boost", "percent", (techMap) => sumTechMapValues(techMap, [EXPECTED_REPORT_TECH_GIDS.BIOTECH_DEFENSE_RATE, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_BIOTECH_DEFENSE_RATE]) / 10],
  ["allTroopsBlock", "All Troops Block", "raw_plus_percent", (techMap) => Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.BLOCK_ALL) || 0) + (Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.BLOCK_RATE_ALL) || 0) / 1000), (techMap) => rateValueToPercent(Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.BLOCK_ALL) || 0) + (Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.BLOCK_RATE_ALL) || 0) / 1000))],
  ["allTroopsMisfireResistance", "All Troops Misfire Resistance", "raw_plus_percent", (techMap) => Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.MISFIRE_RESIST_ALL) || 0), (techMap) => rateValueToPercent(Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.MISFIRE_RESIST_ALL) || 0))],
  ["allTroopsDisruptorResistance", "All Troops Disruptor Resistance", "raw_plus_percent", (techMap) => Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.BAN_SKILL_RESIST_ALL) || 0), (techMap) => rateValueToPercent(Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.BAN_SKILL_RESIST_ALL) || 0))],
  ["attackAgainstMeleeBoost", "Attack Against Melee Boost", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.ATTACK_TO_ROW[0]) || 0) / 10],
  ["attackAgainstMidRangeBoost", "Attack Against Mid-Range Boost", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.ATTACK_TO_ROW[1]) || 0) / 10],
  ["attackAgainstLongRangeBoost", "Attack Against Long-Range Boost", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.ATTACK_TO_ROW[2]) || 0) / 10],
  ["attackAgainstBiochemicalZombieBoost", "Attack Against Biochemical Zombies Boost", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.ATTACK_TO_BIO) || 0) / 10],
  ["enemyMeleeAttackReduction", "Enemy Melee Attack Reduction", "percent", (techMap) => getEnemyAttackReductionStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ROW[0]) / 10],
  ["enemyMidRangeAttackReduction", "Enemy Mid-Range Attack Reduction", "percent", (techMap) => getEnemyAttackReductionStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ROW[1]) / 10],
  ["enemyLongRangeAttackReduction", "Enemy Long-Range Attack Reduction", "percent", (techMap) => getEnemyAttackReductionStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ROW[2]) / 10],
  ["enemyBiochemicalZombieAttackReduction", "Enemy Biochemical Zombie Attack Reduction", "percent", (techMap) => getEnemyAttackReductionStatValue(techMap, EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_BIO) / 10],
  ["enemyMeleeDefenseReduction", "Enemy Melee Defense Reduction", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.SUB_DEFENSE_TO_ROW[0]) || 0) / 10],
  ["troopsAttackBonusBoost", "Troops Attack Bonus Boost", "raw", (techMap) => Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.MISC_ATTACK_AMOUNT) || 0)],
  ["troopsHpBonusBoost", "Troops HP Bonus Boost", "raw", (techMap) => Number(techMap.get(EXPECTED_REPORT_TECH_GIDS.MISC_HP_AMOUNT) || 0)],
  ["damageAgainstTitansBoost", "Damage Against Titans Boost", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.DAMAGE_TO_TITAN) || 0) / 10],
  ["titanDamageTakenReduction", "Titan Damage Taken Reduction", "percent", (techMap) => (techMap.get(EXPECTED_REPORT_TECH_GIDS.DAMAGE_FROM_TITAN) || 0) / 10],
]);

const EXPECTED_REPORT_STAT_METRIC_BY_KEY = new Map(
  EXPECTED_REPORT_STAT_METRICS.map((row) => [row[0], row]),
);

/** @typedef {"officers"|"titans"|"warplane"|"equipment"|"antiques"|"badges"|"commanderBoosts"|"island"|"cityDecorations"|"temporaryBuffs"|"inventory"|"city"|"biochemicalWorkshop"|"alliance"|"academy"|"commanderSkills"} SectionKey */
/** @typedef {{ skillId: number|null, level: number }} OfficerSkillSlot */
/** @typedef {{ groupId: number|null, skillId: number|null, tierId: number|null, tierRank: number|null, level: number }} OfficerUnworldlySlot */
/** @typedef {{ id: number, level: number }} OfficerEquipmentMasteryLevel */
/** @typedef {{ equipId: number|null, level: number, masteryLevels: OfficerEquipmentMasteryLevel[] }} OfficerEquipmentSlot */
/** @typedef {{ officerId: number|null, officerLabel: string, category: string, appointed: boolean, skinLevel: number|null, equipment: OfficerEquipmentSlot[], level: number, stars: number, star: number, stage: number, mastery: number, skills: OfficerSkillSlot[], breakthroughLevel: number, unworldly: OfficerUnworldlySlot[] }} OfficerRow */
/** @typedef {{ skillId: number, label: string, source: string }} OfficerSkillOption */

const SECTION_ITEMS = [
  { key: "powerSummary", label: "Power Summary", description: "Documented and calculated power totals for this profile." },
  { key: "officers", label: "Officers", description: "Officer roster, appointments, and skills." },
  { key: "titans", label: "Titans", description: "Titan levels, spells, equipment, and skin progression." },
  { key: "warplane", label: "Warplane", description: "Warplane level, advancement, skills, equipment, and skins." },
  { key: "equipment", label: "Equipment", description: "Commander equipment items and synth progression." },
  { key: "antiques", label: "Antiques", description: "Collection antiques (rare/epic/legendary) with stars and set effects." },
  { key: "badges", label: "Badges", description: "Badge levels, inventory pieces, and collection stat effects." },
  { key: "commanderBoosts", label: "VIP, Prosperity & Military Rank", description: "VIP activation, prosperity, and military rank bonuses." },
  { key: "island", label: "Island", description: "City island decoration progression." },
  { key: "cityDecorations", label: "City Decorations", description: "Permanent city skins, avatar frames, chat bubbles, guards, and marching fleet cosmetics." },
  { key: "temporaryBuffs", label: "Temporary Buffs", description: "Active item, alliance, designation, nation reward, and temporary decoration buffs that can affect calculated stats." },
  { key: "inventory", label: "Inventory", description: "Point-in-time items, currencies, materials, and owned equipment imported from captures." },
  { key: "city", label: "City", description: "Commander identity, resources, buildings, troops, buffs, and power breakdown." },
  { key: "biochemicalWorkshop", label: "Biochemical workshop", description: "Zombie pets and biochemical skill rows imported from captures." },
  { key: "alliance", label: "Alliance", description: "Alliance technology and captured benefit building buffs." },
  { key: "academy", label: "Academy", description: "Academy, vehicle, drill, and commander talent tech rows imported from captures." },
  { key: "commanderSkills", label: "Commander skills", description: "Commander skill and battle-skill loadout setup." },
];

const SECTION_KEYS = SECTION_ITEMS.map((item) => item.key).filter((key) => key !== "powerSummary");
const STAT_BREAKDOWN_SECTION_ITEMS = SECTION_ITEMS.filter((item) => item.key !== "powerSummary" && item.key !== "inventory");
const OFFICER_DETAIL_GROUPS = Object.freeze([
  { key: "skin", label: "Skin" },
  { key: "equipment", label: "Equipment" },
  { key: "skills", label: "Skills" },
  { key: "unworldly", label: "Unworldly" },
]);
const OFFICER_QUALITY_FILTER_OPTIONS = Object.freeze([
  { key: "orangeGold", label: "Orange / Gold" },
  { key: "purple", label: "Purple" },
  { key: "blue", label: "Blue" },
]);
const ANTIQUES_EDITOR_TABS = Object.freeze([
  { key: "collections", label: "By Category" },
  { key: "sets", label: "By Set" },
]);
const TITAN_SIDE_ORDER = Object.freeze(["sourceSpirit", "empress"]);
const TITAN_SIDE_META = Object.freeze({
  sourceSpirit: { key: "sourceSpirit", label: "Source Spirit", subtitle: "Male Titan", typeId: 21 },
  empress: { key: "empress", label: "Empress", subtitle: "Female Titan", typeId: 20 },
});
const TITAN_DETAIL_GROUPS = Object.freeze([
  { key: "evolution", label: "Evolution" },
  { key: "talents", label: "Talents" },
  { key: "skins", label: "Skins" },
  { key: "equipment", label: "Equipment" },
]);
const TITAN_PHASE_COLOR_BY_KEY = Object.freeze({
  green: "#4aaf61",
  blue: "#4e7de1",
  purple: "#8c5fbe",
  gold: "#cda349",
  golden: "#cda349",
});
const TITAN_EFFECT_LABEL_OVERRIDES = Object.freeze({
  100925444: "Bloodfeast",
  100925448: "Source Energy Guard",
  50397441: "Source Spirit Damage To Mid-Range",
  50397442: "Empress Damage To Long-Range",
  50462864: "Source Spirit HP",
  50462865: "Source Spirit Attack",
  50462866: "Source Spirit Defence",
  50462867: "Empress HP",
  50462868: "Empress Attack",
  50462869: "Empress Defence",
  50462851: "Biochemical Zombie Army Limit Boost",
});
const TITAN_SKIN_STAR_LABEL_BY_EFFECT_ID = Object.freeze({
  50397388: "Source Spirit Attack Boost",
  50397389: "Source Spirit Defense Boost",
  50397390: "Source Spirit HP Boost",
  50397391: "Empress Attack Boost",
  50397392: "Empress Defense Boost",
  50397393: "Empress HP Boost",
});
const TITAN_SKIN_STAR_PERCENT_LIKE_EFFECT_IDS = Object.freeze(new Set([
  50397388,
  50397389,
  50397390,
  50397391,
  50397392,
  50397393,
  100925443,
  100925445,
  100925447,
  100925449,
]));
const WARPLANE_EFFECT_LABEL_OVERRIDES = Object.freeze({
  50462917: "Warplane Attack",
  50462918: "Warplane HP",
  50462919: "Warplane Defense",
  50397462: "Warplane Attack Boost",
  50397463: "Warplane HP Boost",
  50397464: "Warplane Defense Boost",
});
const WARPLANE_SKILL_GROUP_KEYS = Object.freeze({
  skills: "skills",
  equips: "equips",
  equipSkills: "equipSkills",
  skins: "skins",
});
const WARPLANE_SKILL_GROUP_LABELS = Object.freeze({
  [WARPLANE_SKILL_GROUP_KEYS.skills]: "Skills",
  [WARPLANE_SKILL_GROUP_KEYS.equips]: "Equipment",
  [WARPLANE_SKILL_GROUP_KEYS.equipSkills]: "Equipment Skills",
  [WARPLANE_SKILL_GROUP_KEYS.skins]: "Skins",
});
const WARPLANE_DETAIL_GROUPS = Object.freeze([
  { key: "evolution", label: "Evolution" },
  { key: "labSkills", label: "Lab Skills" },
  { key: "skins", label: "Skins" },
  { key: "equipmentSkills", label: "Equipment Skills" },
]);
const ACADEMY_DETAIL_GROUPS = Object.freeze([
  { key: "resources", label: "Resources", category: 1 },
  { key: "development", label: "Development", category: 2 },
  { key: "cityDefence", label: "City Defence", category: 4 },
  { key: "military", label: "Military", category: 3 },
]);
const COMMANDER_SKILL_DETAIL_GROUPS = Object.freeze([
  { key: "developmentSkills", label: "Development", category: 1 },
  { key: "warSkills", label: "War", category: 2 },
]);
const toWarplaneLevelDisplay = (internalLevel = 0) => (
  Math.max(1, Math.floor(Number(internalLevel || 0)) + 1)
);
const fromWarplaneLevelDisplay = (displayLevel = 1) => (
  Math.max(0, Math.floor(Number(displayLevel || 1)) - 1)
);
const toTitanLevelDisplay = (internalLevel = 0) => (
  Math.max(1, Math.floor(Number(internalLevel || 0)) + 1)
);
const fromTitanLevelDisplay = (displayLevel = 1) => (
  Math.max(0, Math.floor(Number(displayLevel || 1)) - 1)
);
const getOfficerInternalMaxLevel = (catalogEntry = null) => (
  Math.max(0, Math.floor(Number(catalogEntry?.maxLevel || 55)) - 1)
);
const toOfficerLevelDisplay = (internalLevel = 0) => (
  Math.max(1, Math.floor(Number(internalLevel || 0)) + 1)
);
const fromOfficerLevelDisplay = (displayLevel = 1) => (
  Math.max(0, Math.floor(Number(displayLevel || 1)) - 1)
);

const toResearchLevelDisplay = (internalLevel = 0, isKnown = true) => (
  isKnown ? Math.max(1, Math.floor(Number(internalLevel || 0)) + 1) : 0
);

const fromResearchLevelDisplay = (displayLevel = 0) => (
  Math.max(0, Math.floor(Number(displayLevel || 0)) - 1)
);

const toCityBuildingLevelDisplay = (internalLevel = 0, isKnown = true) => (
  isKnown ? Math.max(1, Math.floor(Number(internalLevel || 0)) + 1) : 0
);

const fromCityBuildingLevelDisplay = (displayLevel = 0) => (
  Math.max(0, Math.floor(Number(displayLevel || 0)) - 1)
);

const toIslandLevelDisplay = (internalLevel = 0, isKnown = true) => (
  isKnown ? Math.max(1, Math.floor(Number(internalLevel || 0)) + 1) : 0
);

const fromIslandLevelDisplay = (displayLevel = 0) => (
  Math.max(0, Math.floor(Number(displayLevel || 0)) - 1)
);

const getIslandLevelDisplayLabel = (displayLevel = 0, maxLevel = 0) => {
  const parsedDisplay = Math.max(0, Math.floor(Number(displayLevel || 0)));
  const parsedMax = Math.max(0, Math.floor(Number(maxLevel || 0)));
  if (parsedDisplay <= 0) return "Inactive";
  return parsedMax > 0 ? `Level ${parsedDisplay}` : "Activated";
};

const toOfficerSkillLevelDisplay = (internalLevel = 0, isActive = false) => (
  isActive ? Math.max(1, Math.floor(Number(internalLevel || 0)) + 1) : 0
);

const fromOfficerSkillLevelDisplay = (displayLevel = 1) => (
  Math.max(0, Math.floor(Number(displayLevel || 1)) - 1)
);

const OFFICER_SKILL_SLOT_UNLOCK_STARS = [2, 3, 4];
const OFFICER_BREAKTHROUGH_UNLOCK_STAR = 5;

const getOfficerSkillSlotUnlockStar = (slotIndex = 0) => (
  OFFICER_SKILL_SLOT_UNLOCK_STARS[Math.max(0, Math.floor(Number(slotIndex || 0)))]
  ?? Number.MAX_SAFE_INTEGER
);

const formatOfficerStarRequirement = (stars = 0) => {
  const value = Number(stars || 0);
  if (!Number.isFinite(value)) return "required stars";
  return Number.isInteger(value) ? `${value} stars` : `${value.toFixed(1)} stars`;
};

const findOfficerEquipmentMasteryUnlockRow = (slotMeta = null, masteryId = 0) => {
  const normalizedMasteryId = Number(masteryId || 0);
  if (!normalizedMasteryId) return null;
  const levelRows = Array.isArray(slotMeta?.levelRows) ? slotMeta.levelRows : [];
  return levelRows.find((entry) => (
    Array.isArray(entry?.masteryIds)
    && entry.masteryIds.some((value) => Number(value || 0) === normalizedMasteryId)
  )) || null;
};

const getOfficerEquipmentMasteryTracksInUnlockOrder = (slotMeta = null) => {
  const tracks = Array.isArray(slotMeta?.masteryTracks) ? slotMeta.masteryTracks : [];
  return tracks
    .map((track, index) => {
      const unlockRow = findOfficerEquipmentMasteryUnlockRow(slotMeta, track?.masteryId);
      const unlockLevel = Math.max(0, Math.floor(Number(unlockRow?.level || 0)));
      return { track, index, unlockLevel: unlockLevel > 0 ? unlockLevel : Number.MAX_SAFE_INTEGER };
    })
    .sort((left, right) => {
      if (left.unlockLevel !== right.unlockLevel) return left.unlockLevel - right.unlockLevel;
      return left.index - right.index;
    })
    .map((entry) => entry.track);
};

const toOfficerBreakthroughLevelDisplay = (internalLevel = -1) => (
  Math.max(0, Math.floor(Number(internalLevel ?? -1)) + 1)
);

const fromOfficerBreakthroughLevelDisplay = (displayLevel = 0) => (
  Math.max(-1, Math.floor(Number(displayLevel || 0)) - 1)
);

const toWarplaneOptionLevelDisplay = (internalLevel = -1) => (
  Math.max(0, Math.floor(Number(internalLevel ?? -1)) + 1)
);

const fromWarplaneOptionLevelDisplay = (displayLevel = 0) => (
  Math.max(-1, Math.floor(Number(displayLevel || 0)) - 1)
);

const buildIntegerRange = (minRaw = 0, maxRaw = 0) => {
  const min = Math.max(0, Math.floor(Number(minRaw || 0)));
  const max = Math.max(min, Math.floor(Number(maxRaw || min)));
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
};

const getLevelOptionsFromRows = (levelRows = [], maxFallback = 0) => {
  const options = (Array.isArray(levelRows) ? levelRows : [])
    .map((entry) => Math.max(0, Math.floor(Number(entry?.level || 0))))
    .filter((value, index, all) => all.indexOf(value) === index)
    .sort((left, right) => left - right);
  if (options.length > 0) return options;
  return buildIntegerRange(0, maxFallback);
};

const getWarplaneOptionLevelOptions = (option = null, maxLevelRaw = 0) => {
  const maxLevel = Math.max(0, Math.floor(Number(maxLevelRaw || 0)));
  const options = getLevelOptionsFromRows(option?.levelRows || [], maxLevel)
    .filter((level) => level <= maxLevel);
  return options.length > 0 ? options : buildIntegerRange(0, maxLevel);
};

const getOfficerStarSelectOptions = (officerId = null) => {
  const catalogEntry = getOfficerCatalogEntryById(officerId);
  const starStages = Array.isArray(catalogEntry?.starStages) ? catalogEntry.starStages : [];
  const options = [
    0,
    ...starStages.flatMap((row) => {
      const star = Math.max(0, Math.floor(Number(row?.star || 0)));
      const maxStage = Math.max(0, Math.floor(Number(row?.maxStage || 0)));
      if (star <= 0) return [];
      return Array.from({ length: maxStage + 1 }, (_, stage) => (
        Math.round((star + (stage / 10)) * 10) / 10
      ));
    }),
  ].filter((value, index, all) => all.indexOf(value) === index)
    .sort((left, right) => left - right);
  return options.length > 1
    ? options
    : buildIntegerRange(0, 6).flatMap((star) => (
      star === 0 ? [0] : Array.from({ length: 5 }, (_, stage) => Math.round((star + (stage / 10)) * 10) / 10)
    ));
};
const TITAN_PHASE_FALLBACK_ROWS = Object.freeze([
  { phase: "Green", evolutionLevels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] },
  { phase: "Blue", evolutionLevels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] },
  { phase: "Purple", evolutionLevels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] },
  { phase: "Gold", evolutionLevels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
]);
const WARPLANE_STAGE_META_FROM_CONFIG = Object.freeze(
  (Array.isArray(WARPLANE_STATS_CONFIG?.evolution?.phases) ? WARPLANE_STATS_CONFIG.evolution.phases : [])
    .map((row, index) => {
      const stage = Math.max(0, Math.floor(Number(index || 0)));
      const stageLabel = String(row?.phase || `Tier ${stage + 1}`).trim();
      const colorKey = stageLabel.split("-")[0].trim().toLowerCase();
      return {
        stage,
        stageLabel: stageLabel || `Tier ${stage + 1}`,
        stageColor: TITAN_PHASE_COLOR_BY_KEY[colorKey] || "#6b7280",
      };
    }),
);

const getWarplaneStageMeta = (stageRaw = 0) => {
  const stage = Math.max(0, Math.floor(Number(stageRaw || 0)));
  return WARPLANE_STAGE_META_FROM_CONFIG.find((row) => row.stage === stage)
    || {
      stage,
      stageLabel: `Tier ${stage + 1}`,
      stageColor: "#6b7280",
    };
};

const getTitanPhaseRowsBySide = (sideKey = "sourceSpirit") => {
  const config = sideKey === "sourceSpirit"
    ? MALE_TITAN_TABLE_CONFIG
    : FEMALE_TITAN_TABLE_CONFIG;
  const rows = Array.isArray(config?.levelProgression?.phases)
    ? config.levelProgression.phases
    : TITAN_PHASE_FALLBACK_ROWS;
  return rows.map((row, index) => {
    const phase = String(row?.phase || TITAN_PHASE_FALLBACK_ROWS[index]?.phase || `Tier ${index + 1}`).trim();
    const normalizedPhaseKey = phase.toLowerCase();
    const evolutionLevels = Array.isArray(row?.evolutionLevels)
      ? row.evolutionLevels
        .map((value) => Math.max(0, Math.floor(Number(value || 0))))
        .filter((value, i, arr) => arr.indexOf(value) === i)
        .sort((left, right) => left - right)
      : (Array.isArray(TITAN_PHASE_FALLBACK_ROWS[index]?.evolutionLevels)
        ? TITAN_PHASE_FALLBACK_ROWS[index].evolutionLevels
        : [0]);
    return {
      stage: index,
      phase,
      phaseColor: TITAN_PHASE_COLOR_BY_KEY[normalizedPhaseKey] || "#6b7280",
      evolutionLevels: evolutionLevels.length ? evolutionLevels : [0],
    };
  });
};

const getOfficerQualityFamily = (catalogEntry = null) => {
  const tier = String(catalogEntry?.tier || "").toLowerCase();
  const quality = Math.max(0, Math.floor(Number(catalogEntry?.quality || 0)));
  if (tier.includes("orange") || tier.includes("gold") || quality >= 5) return "orangeGold";
  if (tier.includes("purple") || quality === 4) return "purple";
  return "blue";
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const normalizeBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const normalized = String(value ?? "").trim().toLowerCase();
  if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "off"].includes(normalized)) return false;
  return Boolean(fallback);
};

const normalizeSkillId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const normalizeOfficerStarsInputValue = (rawValue, previousStars = 0, officerId = null) => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return Number(previousStars || 0);
  const prev = Number(previousStars || 0);
  const bounded = Math.max(0, Math.round(parsed * 10) / 10);
  const whole = Math.floor(bounded);
  const stage = Math.round((bounded - whole) * 10);
  if (stage <= 4) {
    const normalized = decimalStarToStarStage(bounded, officerId).stars;
    if (Math.abs(normalized - prev) <= 1e-9 && bounded > prev) {
      // Spinner +0.1 can land on invalid decimal sub-stages (for example 0.1 when star 0 has stage 0 only).
      // In that case advance to the next valid full-star step instead of getting stuck.
      return decimalStarToStarStage(Math.floor(prev) + 1, officerId).stars;
    }
    return normalized;
  }

  const isLikelySpinnerStep = Math.abs(bounded - prev) <= 0.11;
  if (isLikelySpinnerStep && bounded < prev) {
    // Spinner decrement from x.0 generates (x-1).9, which should map to (x-1).4.
    return decimalStarToStarStage(whole + 0.4, officerId).stars;
  }

  // Spinner increment from x.4 generates x.5, which should map to (x+1).0.
  return decimalStarToStarStage(whole + 1, officerId).stars;
};

const buildOfficerStarsPatchFromInput = (rawValue, previousRow = {}) => {
  const officerId = previousRow?.officerId || null;
  const stars = normalizeOfficerStarsInputValue(
    rawValue,
    Number(previousRow?.stars || 0),
    officerId,
  );
  const starsInfo = decimalStarToStarStage(stars, officerId);
  return {
    stars: starsInfo.stars,
    star: starsInfo.star,
    stage: starsInfo.stage,
  };
};

const normalizeOfficerSkillSlot = (slot = {}) => ({
  skillId: normalizeSkillId(slot?.skillId),
  level: Math.max(0, Math.floor(Number(slot?.level || 0))),
});

const normalizeOfficerStarsFromRaw = (raw = {}, officerId = null) => {
  const rawStar = Number(raw?.star);
  const rawStage = Number(raw?.stage);
  if (Number.isFinite(rawStar) || Number.isFinite(rawStage)) {
    const star = Math.max(0, Math.floor(Number.isFinite(rawStar) ? rawStar : Number(raw?.stars || 0)));
    const stage = Math.max(0, Math.floor(Number.isFinite(rawStage) ? rawStage : 0));
    return decimalStarToStarStage(star + (stage / 10), officerId);
  }
  return decimalStarToStarStage(raw?.stars, officerId);
};

const isGoldTierOfficer = (catalogEntry = null) => {
  const quality = Math.max(0, Math.floor(Number(catalogEntry?.quality || 0)));
  const tier = String(catalogEntry?.tier || "").trim().toLowerCase();
  return quality >= 3 || tier === "orange" || tier === "gold";
};

const normalizeOfficerUnworldlySlot = (slot = {}, officerId = null) => {
  const skillId = normalizeSkillId(slot?.skillId);
  const tierId = normalizeSkillId(slot?.tierId);
  if (!skillId) {
    return {
      groupId: null,
      skillId: null,
      tierId: null,
      tierRank: null,
      level: 0,
    };
  }
  const resolved = resolveUnworldlyTierBySkill(officerId, skillId, tierId);
  if (!resolved?.tier) {
    return {
      groupId: null,
      skillId: null,
      tierId: null,
      tierRank: null,
      level: 0,
    };
  }
  const maxLevel = Math.max(1, Number(resolved?.tier?.maxLevel || 1));
  return {
    groupId: Number(resolved?.option?.groupId || null),
    skillId,
    tierId: Number(resolved?.tier?.tierId || null),
    tierRank: Number(resolved?.tier?.tierRank || null),
    level: clamp(Math.max(0, Math.floor(Number(slot?.level || 0))), 1, maxLevel),
  };
};

const normalizeOfficerRow = (raw = {}) => {
  const normalizedOfficerId = normalizeSkillId(raw?.officerId);
  const catalogEntry = normalizedOfficerId ? getOfficerCatalogEntryById(normalizedOfficerId) : null;
  const supportsAdvancedOfficerSkills = isGoldTierOfficer(catalogEntry);
  const hasFixedPassiveSkillSlots = String(catalogEntry?.passiveSkillSelectionMode || "").toLowerCase() === "fixed";
  const fixedPassiveSkillSlotIds = Array.isArray(catalogEntry?.fixedPassiveSkillSlotIds)
    ? catalogEntry.fixedPassiveSkillSlotIds
    : [];
  const skinBounds = normalizedOfficerId ? getOfficerSkinTierBounds(normalizedOfficerId) : null;
  const starsInfo = normalizeOfficerStarsFromRaw(raw, normalizedOfficerId);
  const skillsRaw = Array.isArray(raw?.skills) ? raw.skills : [];
  const skills = [0, 1, 2].map((idx) => {
    const fixedSkillId = hasFixedPassiveSkillSlots ? normalizeSkillId(fixedPassiveSkillSlotIds[idx]) : null;
    const rawSlot = skillsRaw[idx];
    const baseSlot = rawSlot && typeof rawSlot === "object" && !Array.isArray(rawSlot)
      ? normalizeOfficerSkillSlot(rawSlot)
      : { skillId: normalizeSkillId(rawSlot), level: 0 };
    if (!isOfficerSkillSlotUnlocked(starsInfo.star, idx)) return { skillId: null, level: 0 };
    if (fixedSkillId) {
      const maxSkillLevel = getOfficerSkillMaxLevelForStar(starsInfo.star);
      const maxInternalSkillLevel = Math.max(0, maxSkillLevel);
      return {
        skillId: fixedSkillId,
        level: clamp(Math.max(0, Math.floor(Number(baseSlot.level || 0))), 0, maxInternalSkillLevel),
      };
    }
    if (!baseSlot.skillId) return { skillId: null, level: 0 };
    const pool = getOfficerPassiveSkillPoolForOfficer(normalizedOfficerId);
    const isAllowed = pool.some((option) => Number(option?.skillId || 0) === Number(baseSlot.skillId));
    if (!isAllowed) return { skillId: null, level: 0 };
    const maxSkillLevel = getOfficerSkillMaxLevelForStar(starsInfo.star);
    const maxInternalSkillLevel = Math.max(0, maxSkillLevel);
    return {
      skillId: baseSlot.skillId,
      level: clamp(Math.max(0, Math.floor(Number(baseSlot.level || 0))), 0, maxInternalSkillLevel),
    };
  });
  const unworldlyRaw = Array.isArray(raw?.unworldly) ? raw.unworldly : [];
  const unworldly = supportsAdvancedOfficerSkills
    ? [0, 1, 2, 3, 4].map((idx) => {
      if (!isOfficerUnworldlySlotUnlocked(starsInfo.stars, idx, normalizedOfficerId)) {
        return {
          groupId: null,
          skillId: null,
          tierId: null,
          tierRank: null,
          level: 0,
        };
      }
      return normalizeOfficerUnworldlySlot(unworldlyRaw[idx] || {}, normalizedOfficerId);
    })
    : Array.from({ length: 5 }, () => ({
      groupId: null,
      skillId: null,
      tierId: null,
      tierRank: null,
      level: 0,
    }));
  const rawCategory = String(raw?.category || "");
  const category = catalogEntry?.category || (
    OFFICER_CATEGORY_ORDER.includes(rawCategory) ? rawCategory : "warfare"
  );
  const fallbackLabel = normalizedOfficerId ? "Unknown Officer" : "Officer";
  const rawLabel = String(raw?.officerLabel || "").trim();
  const officerLabel = catalogEntry?.label || rawLabel || fallbackLabel;
  const level = clamp(
    Math.floor(Number(raw?.level || 0)),
    0,
    getOfficerInternalMaxLevel(catalogEntry),
  );
  const maxBreakthroughLevel = Math.max(
    0,
    Math.floor(Number(getOfficerSkillOptionById(catalogEntry?.breakthroughSkillId)?.maxLevel || 20)),
  );
  const maxBreakthroughInternalLevel = Math.max(0, maxBreakthroughLevel - 1);
  const equipment = normalizedOfficerId
    ? normalizeOfficerEquipmentRowsForOfficer(normalizedOfficerId, raw?.equipment, starsInfo.stars)
    : [];
  const skinLevel = (() => {
    if (!skinBounds?.hasSkin) return null;
    const rawSkinLevel = raw?.skinLevel == null || raw.skinLevel === "" ? NaN : Number(raw.skinLevel);
    const normalizedSkinLevel = Number.isFinite(rawSkinLevel) ? Math.trunc(rawSkinLevel) : -1;
    if (normalizedSkinLevel < -1) return -1;
    return Math.min(skinBounds.maxTier, normalizedSkinLevel);
  })();
  const mastery = getOfficerMasteryValue(normalizedOfficerId, level, starsInfo.stars);
  const rawActiveSkill = raw?.activeSkill && typeof raw.activeSkill === "object" ? raw.activeSkill : {};
  const breakthroughLevelRaw = Object.prototype.hasOwnProperty.call(raw || {}, "breakthroughLevel")
    ? raw?.breakthroughLevel
    : rawActiveSkill?.level;
  const breakthroughStage = Math.max(0, Math.floor(Number(raw?.breakthroughStage ?? rawActiveSkill?.stage ?? 0)));
  const breakthroughUnlocked = starsInfo.star >= OFFICER_BREAKTHROUGH_UNLOCK_STAR;
  const breakthroughLevel = supportsAdvancedOfficerSkills && breakthroughUnlocked
    ? clamp(Math.floor(Number(breakthroughLevelRaw ?? -1)), -1, maxBreakthroughInternalLevel)
    : -1;
  return {
    officerId: normalizedOfficerId,
    officerLabel,
    category,
    appointed: Boolean(raw?.appointed),
    skinLevel,
    equipment,
    level,
    stars: starsInfo.stars,
    star: starsInfo.star,
    stage: starsInfo.stage,
    mastery,
    skills,
    breakthroughLevel,
    breakthroughStage,
    activeSkill: {
      skillId: normalizeSkillId(rawActiveSkill?.skillId || catalogEntry?.breakthroughSkillId),
      level: breakthroughLevel,
      stage: breakthroughStage,
    },
    unworldly,
  };
};

const applyOfficerAppointmentLimits = (rowsRaw = []) => {
  if (!Array.isArray(rowsRaw)) return [];
  const counts = {};
  return rowsRaw.map((row) => {
    const category = OFFICER_CATEGORY_ORDER.includes(String(row?.category || ""))
      ? String(row.category)
      : "warfare";
    const next = normalizeOfficerRow(row);
    counts[category] = Number(counts[category] || 0);
    const limit = Math.max(0, Number(OFFICER_APPOINTMENT_LIMITS?.[category] || 0));
    if (next.appointed && counts[category] >= limit) {
      return { ...next, appointed: false };
    }
    if (next.appointed) {
      counts[category] += 1;
    }
    return next;
  });
};

const reconcileOfficerRowsWithCatalog = (rowsRaw) => {
  const catalogSeedRows = createOfficerSeedRows();
  const catalogIdSet = new Set(
    catalogSeedRows
      .map((row) => Number(row?.officerId || 0))
      .filter((officerId) => Number.isFinite(officerId) && officerId > 0),
  );

  const normalizedExisting = Array.isArray(rowsRaw)
    ? rowsRaw.map((row) => normalizeOfficerRow(row))
    : [];

  const existingByOfficerId = new Map();
  normalizedExisting.forEach((row) => {
    const officerId = Number(row?.officerId || 0);
    if (!Number.isFinite(officerId) || officerId <= 0) return;
    existingByOfficerId.set(officerId, row);
  });

  const reconciled = catalogSeedRows.map((seedRow) => {
    const officerId = Number(seedRow?.officerId || 0);
    const existing = existingByOfficerId.get(officerId);
    if (!existing) return normalizeOfficerRow(seedRow);
    return normalizeOfficerRow({
      ...existing,
      officerId: seedRow.officerId,
      officerLabel: seedRow.officerLabel,
      category: seedRow.category,
    });
  });

  const extras = normalizedExisting.filter((row) => {
    const officerId = Number(row?.officerId || 0);
    if (!Number.isFinite(officerId) || officerId <= 0) return true;
    return !catalogIdSet.has(officerId);
  });

  return applyOfficerAppointmentLimits([...reconciled, ...extras]);
};

const createEmptySectionsDraft = () => {
  const sections = SECTION_KEYS.reduce((acc, key) => {
    acc[key] = { status: "draft", notes: "" };
    return acc;
  }, {});
  sections.officers = {
    ...sections.officers,
    data: {
      rows: createOfficerSeedRows(),
    },
  };
  sections.titans = {
    ...sections.titans,
    data: normalizeTitansData({}),
  };
  sections.warplane = {
    ...sections.warplane,
    data: normalizeWarplaneData({}),
  };
  sections.equipment = {
    ...sections.equipment,
    data: normalizeEquipmentData({}),
  };
  sections.antiques = {
    ...sections.antiques,
    data: normalizeAntiquesData({}),
  };
  sections.badges = {
    ...sections.badges,
    data: normalizeBadgesData({}),
  };
  sections.commanderBoosts = {
    ...sections.commanderBoosts,
    data: normalizeCommanderBoostsData({}),
  };
  sections.island = {
    ...sections.island,
    data: normalizeIslandData({}),
  };
  sections.cityDecorations = {
    ...sections.cityDecorations,
    data: normalizeCityDecorationsData({}),
  };
  sections.temporaryBuffs = {
    ...sections.temporaryBuffs,
    data: normalizeTemporaryBuffsData({}),
  };
  sections.inventory = {
    ...sections.inventory,
    data: normalizeInventoryData({}),
  };
  sections.city = {
    ...sections.city,
    data: normalizeCityData({}),
  };
  sections.biochemicalWorkshop = {
    ...sections.biochemicalWorkshop,
    data: normalizeBiochemicalWorkshopData({}),
  };
  sections.alliance = {
    ...sections.alliance,
    data: normalizeAllianceData({}),
  };
  sections.academy = {
    ...sections.academy,
    data: normalizeAcademyData({}),
  };
  sections.commanderSkills = {
    ...sections.commanderSkills,
    data: normalizeCommanderSkillsData({}),
  };
  return sections;
};

const EMPTY_DRAFT = {
  _id: "",
  name: "",
  linkedCommanderId: "",
  linkedGameCommanderId: "",
  linkedCommanderName: "",
  powerSummary: {
    battlePower: 0,
    officerPower: 0,
    titanPower: 0,
    warplanePower: 0,
    antiquesPower: 0,
    equipmentPower: 0,
  },
  parityProfile: {
    lvl: 0,
    t_b_r: 1,
    ba_tech: "[]",
    ba_h_tech: "[]",
    tow_sps: "[]",
    titan_b_i: "[]",
    fighter_b_i: "[]",
    sw_items: "[]",
    cw_items: "[]",
    ew_items: "[]",
    sw_sp_eff: "[]",
    cw_sp_eff: "[]",
    s_n_bfs: "[]",
    c_n_bfs: "[]",
    royal_it: "[]",
    tk_i_b: "[]",
    gvg_s: "[]",
  },
  sections: createEmptySectionsDraft(),
};

const parseArrayField = (raw, fieldName) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) {
      throw new Error(`${fieldName} must be a JSON array.`);
    }
    return parsed
      .map((entry) => Number(entry))
      .filter((entry) => Number.isFinite(entry))
      .map((entry) => Math.trunc(entry));
  } catch (_error) {
    throw new Error(`${fieldName} must be a valid JSON array.`);
  }
};

const parsePairArrayToMap = (rawArray, sink = new Map()) => {
  if (!Array.isArray(rawArray)) return sink;
  for (let i = 0; i < rawArray.length - 1; i += 2) {
    const effectId = Math.trunc(Number(rawArray[i]));
    const value = Number(rawArray[i + 1]);
    if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value)) continue;
    sink.set(effectId, Number(sink.get(effectId) || 0) + value);
  }
  return sink;
};

const buildExpectedReportTechMap = (parityProfile = {}) => {
  const source = parityProfile && typeof parityProfile === "object" ? parityProfile : {};
  const techMap = new Map();
  ["ba_tech", "ba_h_tech", "sw_sp_eff", "cw_sp_eff"].forEach((fieldKey) => {
    parsePairArrayToMap(source[fieldKey], techMap);
  });
  return techMap;
};

const roundExpectedReportStatValue = (value, fractionDigits = 1) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  const multiplier = 10 ** Math.max(0, Math.trunc(Number(fractionDigits) || 0));
  return Math.round(numeric * multiplier) / multiplier;
};

const formatExpectedReportNumber = (value, maximumFractionDigits = 1) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return "0";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.max(0, Math.trunc(Number(maximumFractionDigits) || 0)),
  });
};

const hasExpectedReportStatValue = (value, percentValue) => (
  Math.abs(Number(value) || 0) >= 0.05 || Math.abs(Number(percentValue) || 0) >= 0.05
);

const buildExpectedReportStatsFromParityProfile = (parityProfile = {}, options = {}) => {
  const techMap = buildExpectedReportTechMap(parityProfile);
  return EXPECTED_REPORT_STAT_METRICS.map(([key, label, format, compute, computePercent]) => {
    const value = roundExpectedReportStatValue(
      compute(techMap, options) + getExpectedReportStatBaselineValue(key),
    );
    const percent = typeof computePercent === "function"
      ? roundExpectedReportStatValue(computePercent(techMap, options))
      : null;
    if (!hasExpectedReportStatValue(value, percent)) return null;
    return {
      key,
      label,
      format,
      value,
      percent,
    };
  }).filter(Boolean);
};

const buildExpectedReportStatRowFromTechMap = (metric = [], techMap = new Map(), options = {}) => {
  const [key, label, format, compute, computePercent] = metric;
  if (!key || typeof compute !== "function") return null;
  const baselineValue = options?.includeBaseline
    ? getExpectedReportStatBaselineValue(key)
    : 0;
  const precision = Number.isFinite(Number(options?.precision))
    ? Math.max(0, Math.trunc(Number(options.precision)))
    : 1;
  const value = roundExpectedReportStatValue(compute(techMap, options) + baselineValue, precision);
  const percent = typeof computePercent === "function"
    ? roundExpectedReportStatValue(computePercent(techMap, options), precision)
    : null;
  return {
    key,
    label,
    format,
    value,
    percent,
  };
};

const formatExpectedReportStatValue = (row = {}, options = {}) => {
  const value = Number(row?.value || 0);
  const percent = row?.percent === null || row?.percent === undefined ? null : Number(row.percent || 0);
  const maximumFractionDigits = Number.isFinite(Number(options?.maximumFractionDigits))
    ? Math.max(0, Math.trunc(Number(options.maximumFractionDigits)))
    : 1;
  if (row?.format === "raw_plus_percent") {
    const valueText = formatExpectedReportNumber(value, maximumFractionDigits);
    const percentText = Number.isFinite(percent) ? `${formatExpectedReportNumber(percent, maximumFractionDigits)}%` : "";
    return percentText ? `${valueText} (${percentText})` : valueText;
  }
  if (row?.format === "raw") {
    return formatExpectedReportNumber(value, maximumFractionDigits);
  }
  return `${formatExpectedReportNumber(value, maximumFractionDigits)}%`;
};

const compactUniqueNumbers = (values = []) => {
  const seen = new Set();
  return values
    .flat(Infinity)
    .map((value) => Math.trunc(Number(value || 0)))
    .filter((value) => {
      if (!Number.isFinite(value) || value <= 0 || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const getSideDependencyGid = (combatSide, attackingGid, defendingGid) => (
  combatSide === "defending" ? defendingGid : attackingGid
);

const isCitySiegeCombat = (value) => String(value || "") === "citySiege";

const getSiegeSideTechValue = (techMap, combatBattle, combatSide, attackingGid, defendingGid) => (
  isCitySiegeCombat(combatBattle)
    ? getCombatSideTechValue(techMap, combatSide, attackingGid, defendingGid)
    : 0
);

const getSiegeSideDependencyGid = (combatBattle, combatSide, attackingGid, defendingGid) => (
  isCitySiegeCombat(combatBattle)
    ? getSideDependencyGid(combatSide, attackingGid, defendingGid)
    : null
);

const getExpectedReportStatDependencyIds = (statKey, options = {}) => {
  const combatSide = String(options?.combatSide || "attacking");
  const combatBattle = String(options?.combatBattle || "field");
  const unitRows = {
    meleeHp: [EXPECTED_REPORT_TECH_GIDS.HP_ROW[0], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_HP_ROW[0], EXPECTED_REPORT_TECH_GIDS.HP_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
    midRangeHp: [EXPECTED_REPORT_TECH_GIDS.HP_ROW[1], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_HP_ROW[1], EXPECTED_REPORT_TECH_GIDS.HP_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
    longRangeHp: [EXPECTED_REPORT_TECH_GIDS.HP_ROW[2], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_HP_ROW[2], EXPECTED_REPORT_TECH_GIDS.HP_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_HP_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
    meleeAttack: [EXPECTED_REPORT_TECH_GIDS.ATTACK_ROW[0], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ATTACK_ROW[0], EXPECTED_REPORT_TECH_GIDS.ATTACK_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
    midRangeAttack: [EXPECTED_REPORT_TECH_GIDS.ATTACK_ROW[1], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ATTACK_ROW[1], EXPECTED_REPORT_TECH_GIDS.ATTACK_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
    longRangeAttack: [EXPECTED_REPORT_TECH_GIDS.ATTACK_ROW[2], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ATTACK_ROW[2], EXPECTED_REPORT_TECH_GIDS.ATTACK_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_ATTACK_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
    meleeDefense: [EXPECTED_REPORT_TECH_GIDS.DEFENSE_ROW[0], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_DEFENSE_ROW[0], EXPECTED_REPORT_TECH_GIDS.DEFENSE_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
    midRangeDefense: [EXPECTED_REPORT_TECH_GIDS.DEFENSE_ROW[1], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_DEFENSE_ROW[1], EXPECTED_REPORT_TECH_GIDS.DEFENSE_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
    longRangeDefense: [EXPECTED_REPORT_TECH_GIDS.DEFENSE_ROW[2], EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_DEFENSE_ROW[2], EXPECTED_REPORT_TECH_GIDS.DEFENSE_ALL, getSiegeSideDependencyGid(combatBattle, combatSide, EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_ATTACKING, EXPECTED_REPORT_TECH_GIDS.SIEGE_DEFENSE_DEFENDING), EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_ALL_TROOPS_ATTACK_DEFENSE_HP],
  };
  if (unitRows[statKey]) return compactUniqueNumbers(unitRows[statKey]);
  const directRows = {
    increaseDamageToMeleeTroops: [EXPECTED_REPORT_TECH_GIDS.DAMAGE_TO_ROW[0]],
    increaseDamageToMidRangeTroops: [EXPECTED_REPORT_TECH_GIDS.DAMAGE_TO_ROW[1]],
    increaseDamageToLongRangeTroops: [EXPECTED_REPORT_TECH_GIDS.DAMAGE_TO_ROW[2]],
    reducesDamageTakenFromMeleeTroops: [EXPECTED_REPORT_TECH_GIDS.DAMAGE_FROM_ROW[0]],
    reducesDamageTakenFromMidRangeTroops: [EXPECTED_REPORT_TECH_GIDS.DAMAGE_FROM_ROW[1]],
    reducesDamageTakenFromLongRangeTroops: [EXPECTED_REPORT_TECH_GIDS.DAMAGE_FROM_ROW[2]],
    biochemicalZombiesAttackBoost: [EXPECTED_REPORT_TECH_GIDS.BIOTECH_ATTACK_RATE, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_BIOTECH_ATTACK_RATE],
    biochemicalZombiesHpBoost: [EXPECTED_REPORT_TECH_GIDS.BIOTECH_HP_RATE, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_BIOTECH_HP_RATE],
    biochemicalZombiesDefenseBoost: [EXPECTED_REPORT_TECH_GIDS.BIOTECH_DEFENSE_RATE, EXPECTED_REPORT_TECH_GIDS.CROSS_NATION_BIOTECH_DEFENSE_RATE],
    allTroopsBlock: [EXPECTED_REPORT_TECH_GIDS.BLOCK_ALL, EXPECTED_REPORT_TECH_GIDS.BLOCK_RATE_ALL],
    allTroopsMisfireResistance: [EXPECTED_REPORT_TECH_GIDS.MISFIRE_RESIST_ALL],
    allTroopsDisruptorResistance: [EXPECTED_REPORT_TECH_GIDS.BAN_SKILL_RESIST_ALL],
    attackAgainstMeleeBoost: [EXPECTED_REPORT_TECH_GIDS.ATTACK_TO_ROW[0]],
    attackAgainstMidRangeBoost: [EXPECTED_REPORT_TECH_GIDS.ATTACK_TO_ROW[1]],
    attackAgainstLongRangeBoost: [EXPECTED_REPORT_TECH_GIDS.ATTACK_TO_ROW[2]],
    attackAgainstBiochemicalZombieBoost: [EXPECTED_REPORT_TECH_GIDS.ATTACK_TO_BIO],
    enemyMeleeAttackReduction: [EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ROW[0], EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ALL],
    enemyMidRangeAttackReduction: [EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ROW[1], EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ALL],
    enemyLongRangeAttackReduction: [EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ROW[2], EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ALL],
    enemyBiochemicalZombieAttackReduction: [EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_BIO, EXPECTED_REPORT_TECH_GIDS.SUB_ATTACK_TO_ALL],
    enemyMeleeDefenseReduction: [EXPECTED_REPORT_TECH_GIDS.SUB_DEFENSE_TO_ROW[0]],
    troopsAttackBonusBoost: [EXPECTED_REPORT_TECH_GIDS.MISC_ATTACK_AMOUNT],
    troopsHpBonusBoost: [EXPECTED_REPORT_TECH_GIDS.MISC_HP_AMOUNT],
    damageAgainstTitansBoost: [EXPECTED_REPORT_TECH_GIDS.DAMAGE_TO_TITAN],
    titanDamageTakenReduction: [EXPECTED_REPORT_TECH_GIDS.DAMAGE_FROM_TITAN],
  };
  return compactUniqueNumbers(directRows[statKey] || []);
};

const createBaseCalculatedParityProfile = (draft = {}) => {
  const baseParityProfile = {
    lvl: Math.max(0, Math.floor(Number(draft?.parityProfile?.lvl || 0))),
    t_b_r: Math.max(0.1, Number(draft?.parityProfile?.t_b_r || 1)),
  };
  PARITY_ARRAY_FIELDS.forEach((field) => {
    baseParityProfile[field] = [];
  });
  return baseParityProfile;
};

const getEffectLabelsForIds = (effectIds = [], effectLabelById = new Map()) => (
  compactUniqueNumbers(effectIds)
    .map((effectId) => normalizeEffectDescriptionText(String(effectLabelById.get(effectId) || "").trim()))
    .filter((label, index, labels) => label && !label.includes("$") && labels.indexOf(label) === index)
);

const getEffectLabelsForEffectMap = (effectMap = new Map(), effectLabelById = new Map()) => {
  if (!(effectMap instanceof Map) || !effectMap.size) return [];
  const labels = Array.from(effectMap.entries())
    .map(([effectId, value]) => formatCommanderEffectLine(effectId, value, effectLabelById))
    .map((label) => String(label || "").trim())
    .filter(Boolean);
  return labels.filter((label, index) => labels.indexOf(label) === index);
};

const catalogNodeMatchesEffectIds = (node, effectIds) => {
  if (!node) return false;
  if (Array.isArray(node)) {
    return node.some((entry) => catalogNodeMatchesEffectIds(entry, effectIds));
  }
  if (typeof node !== "object") return false;
  if (Object.prototype.hasOwnProperty.call(node, "effectId")) {
    const effectId = Math.trunc(Number(node.effectId || 0));
    if (effectIds.has(effectId)) return true;
  }
  if (Object.prototype.hasOwnProperty.call(node, "effectIds")) {
    const ids = Array.isArray(node.effectIds) ? node.effectIds : [node.effectIds];
    if (ids.some((rawId) => effectIds.has(Math.trunc(Number(rawId || 0))))) return true;
  }
  return Object.values(node).some((value) => catalogNodeMatchesEffectIds(value, effectIds));
};

const normalizePotentialSourceText = (value = "") => (
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
);

const getActiveContributionKeys = (activeRows = []) => {
  const keys = new Set();
  (Array.isArray(activeRows) ? activeRows : []).forEach((row) => {
    const sectionKey = String(row?.sectionKey || row?.key || "").trim();
    if (!sectionKey) return;
    if (Array.isArray(row?.detailRows)) {
      row.detailRows.forEach((detail) => {
        const label = normalizePotentialSourceText(detail?.label);
        if (label) keys.add(`${sectionKey}:${label}`);
      });
    }
  });
  return keys;
};

const addActiveProfileIdsFromNode = (keys, sectionKey, node) => {
  if (!(keys instanceof Set) || !sectionKey || !node) return;
  if (Array.isArray(node)) {
    node.forEach((entry) => addActiveProfileIdsFromNode(keys, sectionKey, entry));
    return;
  }
  if (typeof node !== "object") return;
  ["id", "gid", "typeId"].forEach((idKey) => {
    const id = Math.trunc(Number(node?.[idKey] || 0));
    if (Number.isFinite(id) && id > 0) keys.add(`${sectionKey}:${id}`);
  });
  Object.values(node).forEach((value) => addActiveProfileIdsFromNode(keys, sectionKey, value));
};

const getActiveProfileCatalogKeys = (sectionPayload = {}) => {
  const keys = new Set();
  STAT_BREAKDOWN_SECTION_ITEMS.forEach((sectionItem) => {
    const sectionData = sectionPayload?.[sectionItem.key]?.data;
    addActiveProfileIdsFromNode(keys, sectionItem.key, sectionData);
  });
  return keys;
};

const NON_STANDALONE_COMMANDER_SKILL_GROUP_KEYS = new Set([
  "heroActiveSkillOptions",
  "heroSuperAttributeOptions",
  "heroSuperExpTable",
  "heroSkillLevelTable",
]);

const getOfficerRowsFromSectionPayload = (sectionPayload = {}) => (
  Array.isArray(sectionPayload?.officers?.data?.rows) ? sectionPayload.officers.data.rows : []
);

const getOfficerProfileRowById = (officerRows = [], officerId = 0) => (
  (Array.isArray(officerRows) ? officerRows : [])
    .find((row) => Math.trunc(Number(row?.officerId || 0)) === officerId) || null
);

const isOfficerUnlockedInProfile = (officerRow = null) => {
  if (!officerRow || typeof officerRow !== "object") return false;
  if (officerRow?.appointed) return true;
  if (Math.floor(Number(officerRow?.level || 0)) > 0) return true;
  if (Number(officerRow?.stars || 0) > 0) return true;
  if (Math.floor(Number(officerRow?.breakthroughLevel || -1)) >= 0) return true;
  if (officerRow?.skinLevel != null && officerRow.skinLevel !== "" && Math.floor(Number(officerRow.skinLevel)) >= 0) return true;
  return (Array.isArray(officerRow?.skills) ? officerRow.skills : [])
    .some((slot) => Math.trunc(Number(slot?.skillId || 0)) > 0 || Math.floor(Number(slot?.level || 0)) > 0);
};

const getOfficerCatalogQualityRank = (officerRow = {}, catalogRow = {}) => {
  const quality = Math.max(
    0,
    Math.floor(Number(officerRow?.quality ?? catalogRow?.quality ?? 0)),
  );
  const tier = String(officerRow?.tier || catalogRow?.tier || "").trim().toLowerCase();
  if (tier.includes("orange") || tier.includes("gold") || quality >= 3) return 3;
  if (tier.includes("purple") || quality === 2) return 2;
  if (tier.includes("blue") || quality === 1) return 1;
  return quality;
};

const getOfficerAppointmentContext = (officerRows = [], category = "warfare") => {
  const normalizedCategory = String(category || "warfare").trim().toLowerCase();
  const appointedRows = (Array.isArray(officerRows) ? officerRows : [])
    .filter((row) => row?.appointed && String(row?.category || "warfare").trim().toLowerCase() === normalizedCategory);
  const limit = Math.max(0, Math.floor(Number(OFFICER_APPOINTMENT_LIMITS?.[normalizedCategory] || 0)));
  const appointedCount = appointedRows.length;
  const qualityRanks = appointedRows.map((row) => {
    const catalogRow = getOfficerCatalogEntryById(Math.trunc(Number(row?.officerId || 0)));
    return getOfficerCatalogQualityRank(row, catalogRow);
  });
  const minAppointedQualityRank = qualityRanks.length ? Math.min(...qualityRanks) : 0;
  return {
    limit,
    appointedCount,
    hasAppointmentSpace: limit <= 0 || appointedCount < limit,
    minAppointedQualityRank,
    isFull: limit > 0 && appointedCount >= limit,
  };
};

const getOfficerProfileLabel = (profileRow = {}, catalogRow = {}, officerId = 0) => (
  String(
    profileRow?.officerLabel
      || profileRow?.label
      || profileRow?.name
      || catalogRow?.label
      || catalogRow?.name
      || `Officer ${officerId}`,
  ).trim()
);

const OFFICER_BASE_SKILL_IDS = new Set(
  (Array.isArray(OFFICER_BASE_ROWS) ? OFFICER_BASE_ROWS : [])
    .map((officer) => Math.trunc(Number(officer?.baseSkillId || 0)))
    .filter((skillId) => Number.isFinite(skillId) && skillId > 0),
);

const isOfficerBaseSkillId = (skillId = 0) => (
  OFFICER_BASE_SKILL_IDS.has(Math.trunc(Number(skillId || 0)))
);

const officerHasPassiveSkill = (officerRow = {}, skillId = 0) => (
  (Array.isArray(officerRow?.skills) ? officerRow.skills : [])
    .some((slot) => Math.trunc(Number(slot?.skillId || 0)) === skillId)
);

const officerHasUnlockedPassiveSkillSlot = (officerRow = {}) => (
  (Array.isArray(officerRow?.skills) ? officerRow.skills : [])
    .some((_, slotIndex) => isOfficerSkillSlotUnlocked(Math.floor(Number(officerRow?.stars || 0)), slotIndex))
);

const officerHasOpenUnlockedPassiveSkillSlot = (officerRow = {}) => {
  const slots = Array.isArray(officerRow?.skills) ? officerRow.skills : [];
  const star = Math.floor(Number(officerRow?.star ?? officerRow?.stars ?? 0));
  return [0, 1, 2].some((slotIndex) => {
    if (!isOfficerSkillSlotUnlocked(star, slotIndex)) return false;
    const slot = slots[slotIndex] || {};
    return Math.trunc(Number(slot?.skillId || 0)) <= 0;
  });
};

const getOfficerPassiveSkillSelectionMode = (officerId = 0) => {
  const officer = getOfficerCatalogEntryById(officerId);
  return String(officer?.passiveSkillSelectionMode || "pool").trim().toLowerCase();
};

const getOfficerFixedPassiveSkillSlotIndex = (officerId = 0, skillId = 0) => {
  const officer = getOfficerCatalogEntryById(officerId);
  if (String(officer?.passiveSkillSelectionMode || "").trim().toLowerCase() !== "fixed") return -1;
  const normalizedSkillId = Math.trunc(Number(skillId || 0));
  if (!normalizedSkillId) return -1;
  return (Array.isArray(officer?.fixedPassiveSkillSlotIds) ? officer.fixedPassiveSkillSlotIds : [])
    .findIndex((fixedSkillId) => Math.trunc(Number(fixedSkillId || 0)) === normalizedSkillId);
};

const officerCanUsePassiveSkill = (officerRow = {}, skillId = 0) => {
  const officerId = Math.trunc(Number(officerRow?.officerId || 0));
  if (!officerId || !skillId) return false;
  if (getOfficerPassiveSkillSelectionMode(officerId) === "fixed") {
    return getOfficerFixedPassiveSkillSlotIndex(officerId, skillId) >= 0;
  }
  return getOfficerPassiveSkillPoolForOfficer(officerId)
    .some((option) => Math.trunc(Number(option?.skillId || option?.id || 0)) === skillId);
};

const getOfficerPassiveSkillCandidateRows = (skillRow = {}, sectionPayload = {}) => {
  const skillId = Math.trunc(Number(skillRow?.id || skillRow?.skillId || 0));
  if (!Number.isFinite(skillId) || skillId <= 0) return [];
  if (isOfficerBaseSkillId(skillId)) return [];
  const officerRows = getOfficerRowsFromSectionPayload(sectionPayload);
  if (!officerRows.length) return [];
  const skillType = Math.max(0, Math.floor(Number(skillRow?.type || 0)));
  const category = skillType === 1 ? "strategy" : skillType === 2 ? "director" : "warfare";
  const appointmentContext = getOfficerAppointmentContext(officerRows, category);
  const appointedRowsWithSkill = officerRows.filter((officerRow) => {
    const officerId = Math.trunc(Number(officerRow?.officerId || 0));
    if (getOfficerPassiveSkillSelectionMode(officerId) === "fixed") return false;
    return (
      officerRow?.appointed
      && officerCanUsePassiveSkill(officerRow, skillId)
      && officerHasUnlockedPassiveSkillSlot(officerRow)
      && officerHasPassiveSkill(officerRow, skillId)
    );
  });
  if (appointmentContext.limit > 0 && appointedRowsWithSkill.length >= appointmentContext.limit) return [];
  return officerRows.map((officerRow) => {
    const officerId = Math.trunc(Number(officerRow?.officerId || 0));
    const selectionMode = getOfficerPassiveSkillSelectionMode(officerId);
    const fixedSlotIndex = getOfficerFixedPassiveSkillSlotIndex(officerId, skillId);
    if (selectionMode === "fixed") {
      if (fixedSlotIndex < 0) return null;
      if (!isOfficerSkillSlotUnlocked(Math.floor(Number(officerRow?.stars || 0)), fixedSlotIndex)) return null;
      const slot = Array.isArray(officerRow?.skills) ? officerRow.skills[fixedSlotIndex] : null;
      const currentDisplayLevel = toOfficerSkillLevelDisplay(slot?.level || 0, true);
      const maxDisplayLevel = toOfficerSkillLevelDisplay(
        getOfficerSkillMaxLevelForStar(officerRow?.star ?? officerRow?.stars),
        true,
      );
      if (maxDisplayLevel <= 0 || currentDisplayLevel >= maxDisplayLevel) return null;
      const officerCategory = String(officerRow?.category || "warfare").trim().toLowerCase();
      if (officerCategory !== category) return null;
      if (!officerRow?.appointed && !appointmentContext.hasAppointmentSpace) return null;
      return {
        officerRow,
        mode: "fixedUpgrade",
        fixedSlotIndex,
        currentDisplayLevel,
        maxDisplayLevel,
      };
    }
    if (!officerCanUsePassiveSkill(officerRow, skillId)) return false;
    if (!officerHasOpenUnlockedPassiveSkillSlot(officerRow)) return false;
    if (officerHasPassiveSkill(officerRow, skillId)) return false;
    if (officerRow?.appointed) return { officerRow, mode: "poolAssign" };
    const officerCategory = String(officerRow?.category || "warfare").trim().toLowerCase();
    if (officerCategory !== category) return null;
    if (!appointmentContext.hasAppointmentSpace || !isOfficerUnlockedInProfile(officerRow)) return null;
    return { officerRow, mode: "poolAssign" };
  }).filter(Boolean);
};

const isOfficerPassiveSkillPotentialSource = (skillRow = {}, sectionPayload = {}) => (
  getOfficerPassiveSkillCandidateRows(skillRow, sectionPayload).length > 0
);

const getCatalogRowEffects = (row = {}) => {
  if (Array.isArray(row?.effects)) {
    return row.effects.map((effect) => ({
      effectId: Math.trunc(Number(effect?.effectId || 0)),
      value: Number(effect?.value || 0),
    })).filter((effect) => effect.effectId > 0 && Number.isFinite(effect.value));
  }
  const effectIds = Array.isArray(row?.effectIds) ? row.effectIds : [];
  const effectValues = Array.isArray(row?.effectValues) ? row.effectValues : [];
  return effectIds.map((rawEffectId, index) => ({
    effectId: Math.trunc(Number(rawEffectId || 0)),
    value: Number(effectValues[index] || 0),
  })).filter((effect) => effect.effectId > 0 && Number.isFinite(effect.value));
};

const catalogEffectsMatchBuff = (row = {}, buff = {}) => {
  const rowEffects = getCatalogRowEffects(row);
  const buffEffects = Array.isArray(buff?.effects) ? buff.effects : [];
  if (!rowEffects.length || rowEffects.length !== buffEffects.length) return false;
  return rowEffects.every((rowEffect) => buffEffects.some((buffEffect) => (
    Math.trunc(Number(buffEffect?.effectId || 0)) === rowEffect.effectId
    && Number(buffEffect?.value || 0) === rowEffect.value
  )));
};

const isCatalogRowShadowedByKnownBuff = (row = {}) => {
  const normalizedLabel = normalizePotentialSourceText(formatSectionRowLabel(row));
  if (String(row?.source || "").trim() !== "p_spe" || !normalizedLabel) return false;
  const buffCatalogs = [
    ...(Array.isArray(ITEM_BUFF_CATALOG) ? ITEM_BUFF_CATALOG : []),
    ...(Array.isArray(NATION_BUFF_CATALOG) ? NATION_BUFF_CATALOG : []),
  ];
  return buffCatalogs.some((buff) => {
    const aliases = Array.isArray(buff?.aliases) ? buff.aliases : [];
    return aliases.some((alias) => normalizePotentialSourceText(alias) === normalizedLabel)
      && catalogEffectsMatchBuff(row, buff);
  });
};

const formatCatalogSourceLabel = (source = "") => {
  const normalized = String(source || "").trim();
  if (normalized === "p_spe") return "Battle passive config (activation source unknown)";
  if (normalized === "hero_skills") return "Officer skill config";
  if (!normalized) return "";
  return normalized;
};

const formatCatalogRowDescription = (row = {}, effectLabelById = new Map()) => {
  const template = normalizeTroopRangeLabel(String(row?.description || "").trim());
  const effects = getCatalogRowEffects(row);
  const formattedTemplate = formatCommanderEffectDescriptionTemplate(template, effects, effectLabelById);
  if (formattedTemplate) return formattedTemplate;
  return effects
    .map((effect, index) => formatCommanderEffectLine(effect.effectId, effect.value, effectLabelById, {
      label: resolveCommanderEffectLabel(effectLabelById, effect.effectId),
      placeholderIndex: index,
    }))
    .join(", ");
};

const getCatalogEffectDisplayLevel = (node = {}, fallback = {}) => {
  const parseLevel = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const star = parseLevel(node?.star);
  if (star !== null) return { level: star, displayLevel: `${star}*` };
  const level = parseLevel(node?.level);
  if (level !== null) return { level, displayLevel: `Level ${level}` };
  const rank = parseLevel(node?.rank);
  if (rank !== null) return { level: rank, displayLevel: `Rank ${rank}` };
  return {
    level: Number.isFinite(Number(fallback?.level)) ? Number(fallback.level) : 0,
    displayLevel: String(fallback?.displayLevel || "Base"),
  };
};

const collectCatalogEffectRangeEntries = (node = null, dependencySet = new Set(), inheritedLevel = { level: 0, displayLevel: "Base" }) => {
  if (!node) return [];
  if (Array.isArray(node)) {
    return node.flatMap((entry) => collectCatalogEffectRangeEntries(entry, dependencySet, inheritedLevel));
  }
  if (typeof node !== "object") return [];
  const levelMeta = getCatalogEffectDisplayLevel(node, inheritedLevel);
  const rows = [];
  (Array.isArray(node?.effects) ? node.effects : []).forEach((effect) => {
    const effectId = Math.trunc(Number(effect?.effectId || 0));
    const value = Number(effect?.value || 0);
    if (!Number.isFinite(effectId) || effectId <= 0 || !dependencySet.has(effectId) || !Number.isFinite(value) || value === 0) return;
    rows.push({
      effectId,
      value,
      level: levelMeta.level,
      displayLevel: levelMeta.displayLevel,
    });
  });
  Object.entries(node).forEach(([key, value]) => {
    if (key === "effects" || key === "effectIds" || key === "effectValues") return;
    rows.push(...collectCatalogEffectRangeEntries(value, dependencySet, levelMeta));
  });
  return rows;
};

const formatPotentialCatalogRowDescription = (row = {}, effectLabelById = new Map(), dependencyIds = []) => {
  const dependencySet = new Set(compactUniqueNumbers(dependencyIds));
  const directEffects = getCatalogRowEffects(row);
  const matchedDirectEffects = dependencySet.size
    ? directEffects.filter((effect) => dependencySet.has(Math.trunc(Number(effect?.effectId || 0))))
    : directEffects;
  const matchedDirectWithValues = matchedDirectEffects.filter((effect) => Number(effect?.value || 0) !== 0);
  if (matchedDirectWithValues.length) {
    if (matchedDirectWithValues.length === directEffects.length) {
      const formattedTemplate = formatCommanderEffectDescriptionTemplate(row?.description, matchedDirectWithValues, effectLabelById, {
        label: row?.label,
      });
      if (formattedTemplate) return formattedTemplate;
    }
    return formatCommanderEffectLinesForEffects(matchedDirectWithValues, effectLabelById, {
      label: row?.label,
      description: matchedDirectWithValues.length === 1 ? row?.description : "",
    }).join(", ");
  }
  const nestedEntries = dependencySet.size ? collectCatalogEffectRangeEntries(row, dependencySet) : [];
  if (nestedEntries.length) {
    return buildEffectRangeLinesFromEntries(nestedEntries, effectLabelById, {
      label: row?.label,
    }).join(", ");
  }
  if (matchedDirectEffects.length) {
    return getEffectLabelsForIds(matchedDirectEffects.map((effect) => effect.effectId), effectLabelById).join(", ");
  }
  return formatCatalogRowDescription(row, effectLabelById);
};

const buildOfficerPassiveSkillPotentialRows = ({
  skillRow = {},
  sectionPayload = {},
  activeProfileCatalogKeys = new Set(),
  sectionItem = {},
  group = {},
  rowIndex = 0,
  effectLabelById = new Map(),
  dependencyIds = [],
} = {}) => {
  const skillId = Math.trunc(Number(skillRow?.id || skillRow?.skillId || 0));
  if (!Number.isFinite(skillId) || skillId <= 0) return [];
  const officerRows = getOfficerRowsFromSectionPayload(sectionPayload);
  const groupLabel = String(group?.label || group?.key || "Catalog group");
  const skillLabel = formatSectionRowLabel(skillRow);
  const skillOption = getOfficerSkillOptionById(skillId) || skillRow;
  const skillDescription = formatPotentialCatalogRowDescription(skillOption, effectLabelById, dependencyIds);
  const normalizedGroup = normalizePotentialSourceText(groupLabel);
  const rows = [];
  const seenOfficerKeys = new Set();

  (Array.isArray(OFFICER_BASE_ROWS) ? OFFICER_BASE_ROWS : [])
    .filter((officer) => Math.trunc(Number(officer?.baseSkillId || 0)) === skillId)
    .forEach((officer) => {
      const officerId = Math.trunc(Number(officer?.officerId || 0));
      if (!officerId) return;
      const profileRow = getOfficerProfileRowById(officerRows, officerId);
      if (profileRow?.appointed) return;
      const category = String(officer?.category || profileRow?.category || "warfare").trim().toLowerCase();
      const appointmentContext = getOfficerAppointmentContext(officerRows, category);
      const candidateQualityRank = getOfficerCatalogQualityRank(profileRow, officer);
      const canImproveFullAppointmentGroup = appointmentContext.isFull
        && candidateQualityRank > appointmentContext.minAppointedQualityRank;
      if (!appointmentContext.hasAppointmentSpace && !canImproveFullAppointmentGroup) return;
      const isUnlocked = isOfficerUnlockedInProfile(profileRow);
      const officerLabel = getOfficerProfileLabel(profileRow, officer, officerId);
      const key = `${sectionItem.key}:${normalizedGroup}:base:${skillId}:${officerId}:${rowIndex}`;
      if (activeProfileCatalogKeys.has(`${sectionItem.key}:${skillId}`)) return;
      const actionText = (() => {
        if (appointmentContext.hasAppointmentSpace) {
          return isUnlocked
            ? `Appoint ${officerLabel} (${getOfficerCategoryLabel(category)})`
            : `Unlock/fuse and appoint ${officerLabel} (${getOfficerCategoryLabel(category)})`;
        }
        return isUnlocked
          ? `Replace a lower-tier appointed ${getOfficerCategoryLabel(category)} officer with ${officerLabel}`
          : `Unlock/fuse ${officerLabel}, then replace a lower-tier appointed ${getOfficerCategoryLabel(category)} officer`;
      })();
      rows.push({
        key,
        sectionKey: "officers",
        sectionLabel: "Officers",
        groupLabel: isUnlocked ? "Officer Appointment" : "Officer Unlock",
        label: `${officerLabel} - ${skillLabel}`,
        meta: [
          `${skillLabel} is ${officerLabel}'s base skill`,
          actionText,
        ].join(" | "),
        description: skillDescription,
        isActiveSection: true,
      });
      seenOfficerKeys.add(`base:${officerId}`);
    });

  getOfficerPassiveSkillCandidateRows(skillRow, sectionPayload).forEach((candidate) => {
    const profileRow = candidate?.officerRow || candidate;
    const officerId = Math.trunc(Number(profileRow?.officerId || 0));
    if (!officerId || seenOfficerKeys.has(`base:${officerId}`)) return;
    const officerCatalog = getOfficerCatalogEntryById(officerId);
    const category = String(profileRow?.category || officerCatalog?.category || "warfare").trim().toLowerCase();
    const officerLabel = getOfficerProfileLabel(profileRow, officerCatalog, officerId);
    const mode = String(candidate?.mode || "poolAssign");
    const key = `${sectionItem.key}:${normalizedGroup}:passive:${mode}:${skillId}:${officerId}:${rowIndex}`;
    const meta = mode === "fixedUpgrade"
      ? [
        profileRow?.appointed
          ? `Level ${officerLabel}'s fixed slot ${Number(candidate?.fixedSlotIndex || 0) + 1} skill`
          : `Appoint ${officerLabel}, then level fixed slot ${Number(candidate?.fixedSlotIndex || 0) + 1} skill`,
        Number(candidate?.currentDisplayLevel || 0) > 0 && Number(candidate?.maxDisplayLevel || 0) > 0
          ? `Current ${Number(candidate.currentDisplayLevel).toLocaleString()} / max ${Number(candidate.maxDisplayLevel).toLocaleString()}`
          : "",
      ].filter(Boolean).join(" | ")
      : profileRow?.appointed
        ? `Can be assigned to ${officerLabel}'s unlocked officer skill slot`
        : `Can be assigned after ${officerLabel} is appointed (${getOfficerCategoryLabel(category)})`;
    rows.push({
      key,
      sectionKey: "officers",
      sectionLabel: "Officers",
      groupLabel: mode === "fixedUpgrade" ? "Officer Fixed Skill" : "Officer Passive Skill",
      label: `${officerLabel} - ${skillLabel}`,
      meta,
      description: skillDescription,
      isActiveSection: true,
      statusLabel: mode === "fixedUpgrade" ? "Upgrade" : "Apply",
    });
  });

  return rows;
};

const shouldIncludePotentialCatalogRow = ({
  sectionItem = {},
  group = {},
  row = {},
  sectionPayload = {},
  activeProfileCatalogKeys = new Set(),
} = {}) => {
  const sectionKey = String(sectionItem?.key || "").trim();
  const groupKey = String(group?.key || "").trim();
  if (sectionKey === "commanderSkills") {
    if (groupKey === "heroPassiveSkillOptions") {
      return isOfficerPassiveSkillPotentialSource(row, sectionPayload);
    }
    if (NON_STANDALONE_COMMANDER_SKILL_GROUP_KEYS.has(groupKey)) return false;
  }
  if (isCatalogRowShadowedByKnownBuff(row)) return false;
  const catalogIds = compactUniqueNumbers([row?.id, row?.gid, row?.typeId]);
  if (catalogIds.some((id) => activeProfileCatalogKeys.has(`${sectionKey}:${id}`))) return false;
  return true;
};

const buildPotentialCatalogRowVariants = ({
  sectionItem = {},
  group = {},
  row = {},
  rowIndex = 0,
  sectionPayload = {},
  activeProfileCatalogKeys = new Set(),
  activeSectionKeys = new Set(),
  effectLabelById = new Map(),
  dependencyIds = [],
} = {}) => {
  const sectionKey = String(sectionItem?.key || "").trim();
  const groupKey = String(group?.key || "").trim();
  if (sectionKey === "commanderSkills" && groupKey === "heroPassiveSkillOptions") {
    return buildOfficerPassiveSkillPotentialRows({
      skillRow: row,
      sectionPayload,
      activeProfileCatalogKeys,
      sectionItem,
      group,
      rowIndex,
      effectLabelById,
      dependencyIds,
    });
  }
  if (!shouldIncludePotentialCatalogRow({
    sectionItem,
    group,
    row,
    sectionPayload,
    activeProfileCatalogKeys,
  })) return [];
  const label = formatSectionRowLabel(row);
  const groupLabel = String(group?.label || group?.key || "Catalog group");
  const sourceLabel = formatCatalogSourceLabel(row?.source);
  const description = formatPotentialCatalogRowDescription(row, effectLabelById, dependencyIds);
  const normalizedGroup = normalizePotentialSourceText(groupLabel);
  const normalizedLabel = normalizePotentialSourceText(label);
  return [{
    key: `${sectionItem.key}:${normalizedGroup}:${normalizedLabel || row?.id || row?.gid || rowIndex}`,
    sectionKey: sectionItem.key,
    sectionLabel: sectionItem.label,
    groupLabel,
    label,
    meta: sourceLabel,
    description,
    isActiveSection: activeSectionKeys.has(sectionItem.key),
  }];
};

const buildAllianceBuffPotentialRows = ({
  dependencyIds = [],
  effectLabelById = new Map(),
} = {}) => {
  const effectIds = new Set(compactUniqueNumbers(dependencyIds));
  if (!effectIds.size) return [];
  return (Array.isArray(ALLIANCE_BUFF_CATALOG) ? ALLIANCE_BUFF_CATALOG : [])
    .map((buff) => {
      const matchingEffects = (Array.isArray(buff?.effects) ? buff.effects : [])
        .filter((effect) => effectIds.has(Math.trunc(Number(effect?.effectId || 0))));
      if (!matchingEffects.length) return null;
      const effectText = matchingEffects
        .map((effect, index) => formatCommanderEffectLine(effect.effectId, effect.value, effectLabelById, {
          label: resolveCommanderEffectLabel(effectLabelById, effect.effectId),
          placeholderIndex: index,
        }))
        .join(", ");
      return {
        key: `allianceBuff:${buff.itemGid}:${matchingEffects.map((effect) => effect.effectId).join("-")}`,
        sectionKey: "temporaryBuffs",
        sectionLabel: "Temporary Buffs",
        groupLabel: buff.categoryLabel || "Alliance Appearance",
        label: `${buff.categoryLabel || "Alliance Appearance"} - ${buff.name}`,
        meta: [
          "Temporary alliance-wide buff",
          buff.durationLabel ? `Duration ${buff.durationLabel}` : "",
        ].filter(Boolean).join(" | "),
        description: effectText,
        isActiveSection: false,
      };
    })
    .filter(Boolean);
};

const buildItemBuffPotentialRows = ({
  dependencyIds = [],
  effectLabelById = new Map(),
} = {}) => {
  const effectIds = new Set(compactUniqueNumbers(dependencyIds));
  if (!effectIds.size) return [];
  return (Array.isArray(ITEM_BUFF_CATALOG) ? ITEM_BUFF_CATALOG : [])
    .map((buff) => {
      const matchingEffects = (Array.isArray(buff?.effects) ? buff.effects : [])
        .filter((effect) => effectIds.has(Math.trunc(Number(effect?.effectId || 0))));
      if (!matchingEffects.length) return null;
      const effectText = matchingEffects
        .map((effect, index) => formatCommanderEffectLine(effect.effectId, effect.value, effectLabelById, {
          label: resolveCommanderEffectLabel(effectLabelById, effect.effectId),
          placeholderIndex: index,
        }))
        .join(", ");
      const durationLabels = (Array.isArray(buff?.durationOptions) ? buff.durationOptions : [])
        .map((option) => String(option?.durationLabel || "").trim())
        .filter(Boolean);
      return {
        key: `itemBuff:${buff.itemGid}:${matchingEffects.map((effect) => effect.effectId).join("-")}`,
        sectionKey: "temporaryBuffs",
        sectionLabel: "Temporary Buffs",
        groupLabel: buff.categoryLabel || "Temporary Consumables",
        label: buff.name,
        meta: [
          "Temporary consumable buff",
          durationLabels.length ? `Durations ${durationLabels.join(", ")}` : "",
        ].filter(Boolean).join(" | "),
        description: effectText,
        isActiveSection: false,
      };
    })
    .filter(Boolean);
};

const buildCityDecorationBuffPotentialRows = ({
  dependencyIds = [],
  effectLabelById = new Map(),
} = {}) => {
  const effectIds = new Set(compactUniqueNumbers(dependencyIds));
  if (!effectIds.size) return [];
  return (Array.isArray(CITY_DECORATION_TEMPORARY_BUFFS) ? CITY_DECORATION_TEMPORARY_BUFFS : [])
    .map((buff) => {
      const matchingEffects = (Array.isArray(buff?.effects) ? buff.effects : [])
        .filter((effect) => effectIds.has(Math.trunc(Number(effect?.effectId || 0))));
      if (!matchingEffects.length) return null;
      const effectText = matchingEffects
        .map((effect, index) => formatCommanderEffectLine(effect.effectId, effect.value, effectLabelById, {
          label: resolveCommanderEffectLabel(effectLabelById, effect.effectId),
          placeholderIndex: index,
        }))
        .join(", ");
      const durationLabels = (Array.isArray(buff?.durationOptions) ? buff.durationOptions : [])
        .map((option) => String(option?.durationLabel || "").trim())
        .filter(Boolean);
      return {
        key: `cityDecorationBuff:${buff.itemGid}:${matchingEffects.map((effect) => effect.effectId).join("-")}`,
        sectionKey: "temporaryBuffs",
        sectionLabel: "Temporary Buffs",
        groupLabel: buff.categoryLabel || "City Decorations",
        label: `${buff.categoryLabel || "City Decoration"} - ${buff.name}`,
        meta: [
          "Temporary city decoration buff",
          durationLabels.length ? `Durations ${durationLabels.join(", ")}` : "",
        ].filter(Boolean).join(" | "),
        description: effectText,
        isActiveSection: false,
      };
    })
    .filter(Boolean);
};

const buildNationBuffPotentialRows = ({
  dependencyIds = [],
  effectLabelById = new Map(),
} = {}) => {
  const effectIds = new Set(compactUniqueNumbers(dependencyIds));
  if (!effectIds.size) return [];
  return (Array.isArray(NATION_BUFF_CATALOG) ? NATION_BUFF_CATALOG : [])
    .map((buff) => {
      const matchingEffects = (Array.isArray(buff?.effects) ? buff.effects : [])
        .filter((effect) => effectIds.has(Math.trunc(Number(effect?.effectId || 0))));
      if (!matchingEffects.length) return null;
      const effectText = matchingEffects
        .map((effect, index) => formatCommanderEffectLine(effect.effectId, effect.value, effectLabelById, {
          label: resolveCommanderEffectLabel(effectLabelById, effect.effectId),
          placeholderIndex: index,
        }))
        .join(", ");
      const durationLabels = (Array.isArray(buff?.durationOptions) ? buff.durationOptions : [])
        .map((option) => String(option?.durationLabel || "").trim())
        .filter(Boolean);
      return {
        key: `nationBuff:${buff.itemGid}:${matchingEffects.map((effect) => effect.effectId).join("-")}`,
        sectionKey: "temporaryBuffs",
        sectionLabel: "Temporary Buffs",
        groupLabel: buff.categoryLabel || "Nation",
        label: `${buff.categoryLabel || "Nation"} - ${buff.name}`,
        meta: [
          "Temporary nation-wide buff",
          durationLabels.length ? `Durations ${durationLabels.join(", ")}` : "",
        ].filter(Boolean).join(" | "),
        description: effectText,
        isActiveSection: false,
      };
    })
    .filter(Boolean);
};

const buildOfficerSkinPotentialRows = ({
  dependencyIds = [],
  sectionPayload = {},
  effectLabelById = new Map(),
} = {}) => {
  const effectIds = new Set(compactUniqueNumbers(dependencyIds));
  if (!effectIds.size) return [];
  const officerRows = getOfficerRowsFromSectionPayload(sectionPayload);
  return (Array.isArray(OFFICER_BASE_ROWS) ? OFFICER_BASE_ROWS : [])
    .flatMap((officer) => {
      const officerId = Math.trunc(Number(officer?.officerId || 0));
      const skinOption = Array.isArray(officer?.skins) ? officer.skins[0] : null;
      const rankRows = Array.isArray(skinOption?.rankRows) ? skinOption.rankRows : [];
      if (!officerId || !skinOption || !rankRows.length) return [];
      const profileRow = getOfficerProfileRowById(officerRows, officerId);
      const rawSkinLevel = profileRow?.skinLevel == null || profileRow.skinLevel === "" ? NaN : Number(profileRow.skinLevel);
      const currentSkinLevel = Number.isFinite(rawSkinLevel) ? Math.floor(rawSkinLevel) : -1;
      const officerUnlocked = isOfficerUnlockedInProfile(profileRow);
      const officerLabel = getOfficerProfileLabel(profileRow, officer, officerId);
      const matchingRankRows = rankRows
        .filter((rankRow) => Math.floor(Number(rankRow?.rank || 0)) > currentSkinLevel)
        .map((rankRow) => {
          const rank = Math.max(0, Math.floor(Number(rankRow?.rank || 0)));
          const matchingEffects = [
            ...(Array.isArray(rankRow?.primaryEffects) ? rankRow.primaryEffects : []),
            ...(Array.isArray(rankRow?.milestoneEffects) ? rankRow.milestoneEffects : []),
          ].filter((effect) => effectIds.has(Math.trunc(Number(effect?.effectId || 0))));
          return matchingEffects.length ? { rank, matchingEffects } : null;
        })
        .filter(Boolean);
      if (!matchingRankRows.length) return [];
      const ranks = matchingRankRows.map((entry) => entry.rank).sort((left, right) => left - right);
      const minRank = ranks[0];
      const maxRank = ranks[ranks.length - 1];
      const rankText = minRank === maxRank
        ? `Rank ${minRank}`
        : `Ranks ${minRank}-${maxRank}`;
      const strongestEffectById = new Map();
      matchingRankRows.forEach((entry) => {
        entry.matchingEffects.forEach((effect) => {
          const effectId = Math.trunc(Number(effect?.effectId || 0));
          const current = strongestEffectById.get(effectId);
          if (!current || Math.abs(Number(effect?.value || 0)) > Math.abs(Number(current?.value || 0))) {
            strongestEffectById.set(effectId, effect);
          }
        });
      });
      const effectText = Array.from(strongestEffectById.values())
        .map((effect, index) => formatCommanderEffectLine(effect.effectId, effect.value, effectLabelById, {
          label: resolveCommanderEffectLabel(effectLabelById, effect.effectId),
          placeholderIndex: index,
        }))
        .join(", ");
      const actionText = !profileRow
        ? `Unlock/fuse ${officerLabel}, then unlock or upgrade skin`
        : currentSkinLevel < 0
          ? `Unlock ${officerLabel}'s skin`
          : `Upgrade ${officerLabel}'s skin beyond rank ${currentSkinLevel}`;
      return [{
        key: `officerSkin:${officerId}:${Array.from(strongestEffectById.keys()).join("-")}:${minRank}-${maxRank}`,
        sectionKey: "officers",
        sectionLabel: "Officers",
        groupLabel: "Officer Skin",
        label: `${officerLabel} - Skin`,
        meta: [
          actionText,
          rankText,
          officerUnlocked ? "Officer exists in profile" : "Officer locked/not in profile",
        ].filter(Boolean).join(" | "),
        description: effectText ? `Best matching bonus: ${effectText}` : "",
        isActiveSection: Boolean(profileRow),
        statusLabel: currentSkinLevel < 0 ? "Locked" : "Upgrade",
      }];
    });
};

const buildPotentialStatContributorGroups = ({
  dependencyIds = [],
  activeRows = [],
  sectionPayload = {},
  effectLabelById = new Map(),
} = {}) => {
  const effectIds = new Set(compactUniqueNumbers(dependencyIds));
  if (!effectIds.size) return [];
  const potentialRows = [];
  const seenKeys = new Set();
  const activeKeys = getActiveContributionKeys(activeRows);
  const activeProfileCatalogKeys = getActiveProfileCatalogKeys(sectionPayload);
  const activeSectionKeys = new Set((Array.isArray(activeRows) ? activeRows : []).map((row) => row?.sectionKey).filter(Boolean));
  buildAllianceBuffPotentialRows({ dependencyIds, effectLabelById }).forEach((row) => {
    if (!row?.key || seenKeys.has(row.key)) return;
    seenKeys.add(row.key);
    potentialRows.push(row);
  });
  buildItemBuffPotentialRows({ dependencyIds, effectLabelById }).forEach((row) => {
    if (!row?.key || seenKeys.has(row.key)) return;
    seenKeys.add(row.key);
    potentialRows.push(row);
  });
  buildCityDecorationBuffPotentialRows({ dependencyIds, effectLabelById }).forEach((row) => {
    if (!row?.key || seenKeys.has(row.key)) return;
    seenKeys.add(row.key);
    potentialRows.push(row);
  });
  buildNationBuffPotentialRows({ dependencyIds, effectLabelById }).forEach((row) => {
    if (!row?.key || seenKeys.has(row.key)) return;
    seenKeys.add(row.key);
    potentialRows.push(row);
  });
  buildOfficerSkinPotentialRows({ dependencyIds, sectionPayload, effectLabelById }).forEach((row) => {
    if (!row?.key || seenKeys.has(row.key)) return;
    seenKeys.add(row.key);
    potentialRows.push(row);
  });
  STAT_BREAKDOWN_SECTION_ITEMS.forEach((sectionItem) => {
    const catalog = getCommanderSectionCatalog(sectionItem.key);
    const groups = Array.isArray(catalog?.groups) ? catalog.groups : [];
    groups.forEach((group) => {
      const rows = Array.isArray(group?.rows) ? group.rows : [];
      rows.forEach((row, rowIndex) => {
        if (!catalogNodeMatchesEffectIds(row, effectIds)) return;
        const label = formatSectionRowLabel(row);
        const normalizedLabel = normalizePotentialSourceText(label);
        if (normalizedLabel && activeKeys.has(`${sectionItem.key}:${normalizedLabel}`)) return;
        const variants = buildPotentialCatalogRowVariants({
          sectionItem,
          group,
          row,
          rowIndex,
          sectionPayload,
          activeProfileCatalogKeys,
          activeSectionKeys,
          effectLabelById,
          dependencyIds,
        });
        variants.forEach((variant) => {
          if (!variant?.key || seenKeys.has(variant.key)) return;
          seenKeys.add(variant.key);
          potentialRows.push(variant);
        });
      });
    });
  });
  return potentialRows.sort((left, right) => (
    String(left.sectionLabel || "").localeCompare(String(right.sectionLabel || ""))
    || String(left.groupLabel || "").localeCompare(String(right.groupLabel || ""))
    || String(left.label || "").localeCompare(String(right.label || ""))
  ));
};

const formatOfficerEffectSource = (source = "") => {
  const normalized = String(source || "").trim();
  if (normalized === "active") return "Breakthrough";
  if (normalized === "base") return "Base Skill";
  if (normalized === "breakthrough") return "Breakthrough";
  if (normalized === "equipment") return "Officer Equipment";
  if (normalized === "equipment_mastery") return "Officer Equipment Expertise";
  if (normalized === "skin") return "Officer Skin";
  if (normalized === "skin_milestone") return "Officer Skin Milestone";
  if (normalized.startsWith("skill_")) return `Skill ${normalized.replace("skill_", "")}`;
  if (normalized.startsWith("unworldly_")) return `Unworldly ${normalized.replace("unworldly_", "")}`;
  return normalized || "Officer Effect";
};

const formatOfficerEffectMetaLabel = (label = "", displayValue = "") => {
  const text = String(label || "").trim();
  if (!text) return "";
  const valueText = String(displayValue || "").trim();
  if (!valueText || !text.includes("$")) return text;
  const numericText = valueText.replace(/%$/, "").trim();
  return text.replace(/\$(\d+)%?/g, (match) => (
    match.endsWith("%") ? `${numericText}%` : numericText
  ));
};

const buildOfficerStatDetailRows = ({
  officerEffects = [],
  metric = [],
  dependencyIds = [],
  combatBattle = "field",
  combatSide = "attacking",
  effectLabelById = new Map(),
} = {}) => {
  const dependencySet = new Set(compactUniqueNumbers(dependencyIds));
  if (!dependencySet.size || !Array.isArray(officerEffects)) return [];
  const rows = [];
  officerEffects.forEach((officerRow, officerIndex) => {
    const effects = Array.isArray(officerRow?.effects) ? officerRow.effects : [];
    effects.forEach((effect, effectIndex) => {
      const effectId = Math.trunc(Number(effect?.effectId || 0));
      if (!dependencySet.has(effectId)) return;
      const techValue = Number(effect?.value || 0);
      if (!Number.isFinite(techValue) || techValue === 0) return;
      const effectMap = new Map([[effectId, techValue]]);
      const statRow = buildExpectedReportStatRowFromTechMap(metric, effectMap, { combatBattle, combatSide, precision: 3 });
      if (!statRow || !hasExpectedReportStatValue(statRow.value, statRow.percent)) return;
      const effectLabel = formatOfficerEffectMetaLabel(resolveCommanderEffectLabel(effectLabelById, effectId), effect?.displayValue);
      const sourceLabel = formatOfficerEffectSource(effect?.source);
      const skillName = String(effect?.skillName || effect?.masteryLabel || "Officer Effect").trim();
      const officerName = String(officerRow?.officerName || "Officer").trim();
      const levelParts = [];
      if (String(effect?.source || "").startsWith("skill_") && Number(effect?.skillId || 0) > 0) {
        levelParts.push(sourceLabel);
      } else {
        levelParts.push(sourceLabel);
      }
      if (Number(effect?.slotIndex || 0) > 0) levelParts.push(`Slot ${Number(effect.slotIndex)}`);
      if (Number(effect?.masteryLevel || 0) > 0) levelParts.push(`Level ${Number(effect.masteryLevel)}`);
      rows.push({
        key: `${officerRow?.officerId || officerIndex}:${effectId}:${effect?.source || "effect"}:${effectIndex}`,
        label: `${officerName} - ${skillName}`,
        meta: [levelParts.join(" | "), effectLabel].filter(Boolean).join(" | "),
        value: statRow.value,
        percent: statRow.percent,
        format: statRow.format,
        displayValue: formatExpectedReportStatValue(statRow, { maximumFractionDigits: 3 }),
      });
    });
  });
  return rows.sort((left, right) => Math.abs(right.value || 0) - Math.abs(left.value || 0));
};

const buildGenericStatDetailRows = ({
  sectionKey = "",
  contributionDetails = [],
  metric = [],
  dependencyIds = [],
  combatBattle = "field",
  combatSide = "attacking",
  effectLabelById = new Map(),
} = {}) => {
  const dependencySet = new Set(compactUniqueNumbers(dependencyIds));
  if (!sectionKey || !dependencySet.size || !Array.isArray(contributionDetails)) return [];
  const rows = [];
  contributionDetails
    .filter((detail) => String(detail?.sectionKey || "") === sectionKey)
    .forEach((detail, detailIndex) => {
      const effects = Array.isArray(detail?.effects) ? detail.effects : [];
      const matchedEffects = effects.filter((effect) => dependencySet.has(Math.trunc(Number(effect?.effectId || 0))));
      if (!matchedEffects.length) return;
      const effectMap = new Map();
      matchedEffects.forEach((effect) => {
        const effectId = Math.trunc(Number(effect?.effectId || 0));
        const value = Number(effect?.value || 0);
        if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value) || value === 0) return;
        effectMap.set(effectId, (effectMap.get(effectId) || 0) + value);
      });
      if (!effectMap.size) return;
      const statRow = buildExpectedReportStatRowFromTechMap(metric, effectMap, { combatBattle, combatSide, precision: 3 });
      if (!statRow || !hasExpectedReportStatValue(statRow.value, statRow.percent)) return;
      const effectLabels = getEffectLabelsForEffectMap(effectMap, effectLabelById);
      rows.push({
        key: `${sectionKey}:${detail?.key || detailIndex}`,
        label: String(detail?.label || "Profile entry"),
        meta: [
          String(detail?.groupLabel || "").trim(),
          String(detail?.meta || "").trim(),
          effectLabels.slice(0, 3).join(", "),
        ].filter(Boolean).join(" | "),
        value: statRow.value,
        percent: statRow.percent,
        format: statRow.format,
        displayValue: formatExpectedReportStatValue(statRow, { maximumFractionDigits: 3 }),
      });
    });
  return rows.sort((left, right) => Math.abs(right.value || 0) - Math.abs(left.value || 0));
};

const buildExpectedReportStatBreakdown = ({
  statKey = "",
  sectionPayload = {},
  draft = {},
  combatContext = "internal",
  combatBattle = "field",
  combatSide = "attacking",
  effectLabelById = new Map(),
} = {}) => {
  const metric = EXPECTED_REPORT_STAT_METRIC_BY_KEY.get(statKey);
  if (!metric) return null;
  const dependencyIds = getExpectedReportStatDependencyIds(statKey, { combatBattle, combatSide });
  const totalParityProfile = applySectionDerivedParityFields(
    createBaseCalculatedParityProfile(draft),
    sectionPayload,
    { combatContext },
  );
  const officerDetailRows = buildOfficerStatDetailRows({
    officerEffects: totalParityProfile?.officerEffects,
    metric,
    dependencyIds,
    combatBattle,
    combatSide,
    effectLabelById,
  });
  const contributionDetails = buildSectionContributionDetails(sectionPayload, { combatContext });
  const activeRows = [];
  const baselineValue = getExpectedReportStatBaselineValue(statKey);
  if (baselineValue) {
    const baselineRow = {
      key: "baseline",
      sectionKey: "baseline",
      label: "Baseline",
      value: roundExpectedReportStatValue(baselineValue),
      percent: null,
      format: metric[2],
    };
    activeRows.push({
      ...baselineRow,
      displayValue: formatExpectedReportStatValue(baselineRow, { maximumFractionDigits: 3 }),
      effectLabels: ["Built-in report baseline"],
      detailRows: [],
    });
  }
  STAT_BREAKDOWN_SECTION_ITEMS.forEach((sectionItem) => {
    const sectionData = sectionPayload?.[sectionItem.key];
    if (!sectionData) return;
    const parityProfile = applySectionDerivedParityFields(
      createBaseCalculatedParityProfile(draft),
      { [sectionItem.key]: sectionData },
      { combatContext },
    );
    const techMap = buildExpectedReportTechMap(parityProfile);
    const row = buildExpectedReportStatRowFromTechMap(metric, techMap, { combatBattle, combatSide, precision: 3 });
    if (!row || !hasExpectedReportStatValue(row.value, row.percent)) return;
    const contributingEffectIds = dependencyIds.filter((effectId) => Math.abs(getTechMapValue(techMap, effectId)) >= 0.0001);
    const contributingEffectMap = new Map(
      contributingEffectIds.map((effectId) => [effectId, getTechMapValue(techMap, effectId)]),
    );
    activeRows.push({
      key: sectionItem.key,
      sectionKey: sectionItem.key,
      label: sectionItem.label,
      value: row.value,
      percent: row.percent,
      format: row.format,
      displayValue: formatExpectedReportStatValue(row, { maximumFractionDigits: 3 }),
      effectLabels: getEffectLabelsForEffectMap(contributingEffectMap, effectLabelById),
      detailRows: sectionItem.key === "officers"
        ? officerDetailRows
        : buildGenericStatDetailRows({
          sectionKey: sectionItem.key,
          contributionDetails,
          metric,
          dependencyIds,
          combatBattle,
          combatSide,
          effectLabelById,
        }),
    });
  });
  const potentialRows = buildPotentialStatContributorGroups({
    dependencyIds,
    activeRows,
    sectionPayload,
    effectLabelById,
  });
  const totalRow = buildExpectedReportStatRowFromTechMap(metric, buildExpectedReportTechMap(totalParityProfile), {
    combatBattle,
    combatSide,
    includeBaseline: true,
    precision: 3,
  });
  return {
    key: statKey,
    label: metric[1],
    totalRow,
    activeRows: activeRows.sort((left, right) => Math.abs(right.value || 0) - Math.abs(left.value || 0)),
    potentialRows,
  };
};

const formatSectionRowLabel = (row = {}) => {
  const label = String(row?.label || row?.name || "").trim();
  if (label) return normalizeTroopRangeLabel(label);
  const id = Number(row?.id || row?.gid || 0);
  if (Number.isFinite(id) && id > 0) return `ID #${Math.trunc(id)}`;
  const stage = Number(row?.stage ?? row?.level ?? NaN);
  if (Number.isFinite(stage)) return `Stage ${Math.trunc(stage)}`;
  return "Unnamed";
};

function normalizeIdLevelEntry(raw = {}) {
  const id = normalizeSkillId(raw?.id ?? raw?.gid ?? raw?.itemGid);
  if (!id) return null;
  const displayLevel = Number(raw?.displayLevel);
  return {
    id,
    level: Math.max(0, Math.floor(Number(raw?.level || 0))),
    ...(Number.isFinite(displayLevel) ? { displayLevel: Math.max(0, Math.floor(displayLevel)) } : {}),
  };
}

function normalizeIdLevelEntries(rawRows) {
  if (!Array.isArray(rawRows)) return [];
  const deduped = new Map();
  rawRows.forEach((row) => {
    const normalized = normalizeIdLevelEntry(row);
    if (!normalized) return;
    const existing = deduped.get(normalized.id);
    if (!existing || normalized.level > existing.level) {
      deduped.set(normalized.id, normalized);
    }
  });
  return Array.from(deduped.values());
}

function normalizeAntiqueCollectionEntry(raw = {}) {
  const id = normalizeSkillId(raw?.id);
  if (!id) return null;
  const rawStar = Number(raw?.star);
  const rawStage = Number(raw?.stage);
  if (Number.isFinite(rawStar) || Number.isFinite(rawStage)) {
    const star = Math.max(0, Math.floor(Number.isFinite(rawStar) ? rawStar : Number(raw?.level || 0)));
    const stage = Math.max(0, Math.floor(Number.isFinite(rawStage) ? rawStage : 0));
    return {
      id,
      level: Math.round((star + (stage / 10)) * 10) / 10,
      star,
      stage,
    };
  }
  const level = Math.max(0, Math.floor(Number(raw?.level || 0)));
  return {
    id,
    level,
    star: level,
    stage: 0,
  };
}

function normalizeAntiqueCollectionEntries(rawRows) {
  if (!Array.isArray(rawRows)) return [];
  const deduped = new Map();
  rawRows.forEach((row) => {
    const normalized = normalizeAntiqueCollectionEntry(row);
    if (!normalized) return;
    const existing = deduped.get(normalized.id);
    if (!existing || normalized.level > existing.level) {
      deduped.set(normalized.id, normalized);
    }
  });
  return Array.from(deduped.values());
}

function normalizeTitansData(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const hasTypedSides = (
    (source?.sourceSpirit && typeof source.sourceSpirit === "object")
      || (source?.empress && typeof source.empress === "object")
  );

  const normalizeTitanSide = (sideRaw = {}, fallback = {}) => {
    const merged = {
      ...fallback,
      ...(sideRaw && typeof sideRaw === "object" ? sideRaw : {}),
    };
    const fallbackSkinStarLevel = Math.max(0, Math.floor(Number(merged?.skinStarLevel || 0)));
    const skinGid = normalizeSkillId(merged?.skinGid);
    const skins = normalizeIdLevelEntries(merged?.skins);
    if (!skins.length && skinGid) {
      skins.push({ id: skinGid, level: fallbackSkinStarLevel });
    }
    const skinStarLevel = skins.reduce(
      (sum, row) => sum + Math.max(0, Math.floor(Number(row?.level || 0))),
      0,
    );
    return {
      level: Math.max(0, Math.floor(Number(merged?.level || 0))),
      evolutionStage: Math.max(0, Math.floor(Number(merged?.evolutionStage || 0))),
      evolutionLevel: Math.max(0, Math.floor(Number(merged?.evolutionLevel || 0))),
      power: Math.max(0, Math.floor(Number(merged?.power || 0))),
      skinGid,
      skinStarLevel,
      skins,
      spells: normalizeIdLevelEntries(merged?.spells),
      equipPieces: normalizeIdLevelEntries(
        Array.isArray(merged?.equipPieces) && merged.equipPieces.length ? merged.equipPieces : merged?.equipParts,
      ),
      equipSkills: normalizeIdLevelEntries(merged?.equipSkills),
    };
  };

  if (hasTypedSides) {
    return {
      sourceSpirit: normalizeTitanSide(source?.sourceSpirit || {}, {}),
      empress: normalizeTitanSide(source?.empress || {}, {}),
    };
  }

  // Legacy flat titan payload migration path.
  return {
    sourceSpirit: normalizeTitanSide({}, {
      level: Math.max(0, Math.floor(Number(source?.sourceSpiritLevel || 0))),
      evolutionStage: 0,
      evolutionLevel: 0,
      power: 0,
      skinGid: normalizeSkillId(Array.isArray(source?.skins) ? source.skins[0]?.id : null),
      skinStarLevel: Math.max(0, Math.floor(Number(Array.isArray(source?.skins) ? source.skins[0]?.level : 0))),
      skins: Array.isArray(source?.skins) && source.skins[0]
        ? [{ id: source.skins[0]?.id, level: source.skins[0]?.level }]
        : [],
      spells: normalizeIdLevelEntries(source?.spells),
      equipPieces: normalizeIdLevelEntries(source?.equipPieces),
      equipSkills: normalizeIdLevelEntries(source?.equipSkills),
    }),
    empress: normalizeTitanSide({}, {
      level: Math.max(0, Math.floor(Number(source?.empressLevel || 0))),
      evolutionStage: 0,
      evolutionLevel: 0,
      power: 0,
      skinGid: normalizeSkillId(Array.isArray(source?.skins) ? source.skins[1]?.id : null),
      skinStarLevel: Math.max(0, Math.floor(Number(Array.isArray(source?.skins) ? source.skins[1]?.level : 0))),
      skins: Array.isArray(source?.skins) && source.skins[1]
        ? [{ id: source.skins[1]?.id, level: source.skins[1]?.level }]
        : [],
      spells: [],
      equipPieces: [],
      equipSkills: [],
    }),
  };
}

function normalizeWarplaneOwnedRows(raw = {}) {
  const sourceRows = Array.isArray(raw?.ownedRows) ? raw.ownedRows : [];
  const seen = new Set();
  return sourceRows
    .map((row) => {
      const source = row && typeof row === "object" ? row : {};
      const gid = normalizeSkillId(source?.gid);
      if (!gid) return null;
      const instanceId = normalizeSkillId(source?.instanceId ?? source?.id);
      const slot = Math.max(0, Math.floor(Number(source?.slot ?? source?.num ?? 0))) || null;
      const key = `${instanceId || 0}:${slot || 0}:${gid}`;
      if (seen.has(key)) return null;
      seen.add(key);
      return {
        instanceId,
        slot,
        gid,
        level: Math.max(0, Math.floor(Number(source?.level || 0))),
        advancementStage: Math.max(0, Math.floor(Number(source?.advancementStage || 0))),
        advancementStep: Math.max(0, Math.floor(Number(source?.advancementStep ?? source?.advancementLevel ?? source?.progressStep ?? 0))),
        power: Math.max(0, Number(source?.power || 0)),
        name: String(source?.name || ""),
        status: Math.max(0, Math.floor(Number(source?.status ?? source?.st ?? 0))),
      };
    })
    .filter(Boolean)
    .sort((left, right) => (
      (Number(left?.slot || 999) - Number(right?.slot || 999))
      || (Number(right?.power || 0) - Number(left?.power || 0))
      || (Number(left?.gid || 0) - Number(right?.gid || 0))
    ))
    .slice(0, 5);
}

function getWarplaneRowKey(row = {}, index = 0) {
  const instanceId = normalizeSkillId(row?.instanceId);
  if (instanceId) return `instance:${instanceId}`;
  const slot = Math.max(0, Math.floor(Number(row?.slot || 0)));
  const gid = normalizeSkillId(row?.gid);
  return `slot:${slot || index + 1}:gid:${gid || 0}`;
}

function findWarplaneRowByKey(rows = [], key = "") {
  return (Array.isArray(rows) ? rows : []).find((row, index) => getWarplaneRowKey(row, index) === key) || null;
}

function pickSelectedWarplaneRow(data = {}) {
  const rows = Array.isArray(data?.ownedRows) ? data.ownedRows : [];
  if (rows.length === 0) return null;
  const selectedWarplaneId = normalizeSkillId(data?.selectedWarplaneId ?? data?.instanceId);
  const selectedGid = normalizeSkillId(data?.selectedGid);
  if (selectedWarplaneId) {
    const byInstance = rows.find((row) => Number(row?.instanceId || 0) === selectedWarplaneId);
    if (byInstance) return byInstance;
  }
  if (selectedGid) {
    const byGid = rows.find((row) => Number(row?.gid || 0) === selectedGid);
    if (byGid) return byGid;
  }
  const activeRows = rows.filter((row) => Number(row?.status || 0) > 0);
  if (activeRows.length > 0) {
    return [...activeRows].sort((left, right) => Number(right?.power || 0) - Number(left?.power || 0))[0];
  }
  return rows[0];
}

function applySelectedWarplaneRow(data = {}, row = null) {
  if (!row) return data;
  return {
    ...data,
    level: Math.max(0, Math.floor(Number(row?.level || 0))),
    advancementStage: Math.max(0, Math.floor(Number(row?.advancementStage || 0))),
    advancementStep: Math.max(0, Math.floor(Number(row?.advancementStep || 0))),
    selectedWarplaneId: normalizeSkillId(row?.instanceId),
    selectedGid: normalizeSkillId(row?.gid),
    name: String(row?.name || ""),
    power: Math.max(0, Number(row?.power || 0)),
  };
}

function normalizeWarplaneData(raw = {}) {
  const rawStep = raw?.advancementStep ?? raw?.advancementLevel ?? raw?.progressStep;
  const hasExplicitStep = rawStep !== undefined && rawStep !== null && String(rawStep).trim() !== "";
  const parsedStep = Number(rawStep);
  const ownedRows = normalizeWarplaneOwnedRows(raw);
  const selectedRow = pickSelectedWarplaneRow({
    ...raw,
    ownedRows,
  });
  return applySelectedWarplaneRow({
    level: Math.max(0, Math.floor(Number(raw?.level || 0))),
    advancementStage: Math.max(0, Math.floor(Number(raw?.advancementStage || 0))),
    advancementStep: hasExplicitStep && Number.isFinite(parsedStep)
      ? Math.max(0, Math.floor(parsedStep))
      : null,
    skills: normalizeIdLevelEntries(raw?.skills),
    equips: normalizeIdLevelEntries(raw?.equips),
    equipSkills: normalizeIdLevelEntries(raw?.equipSkills),
    skins: normalizeIdLevelEntries(raw?.skins),
    selectedWarplaneId: normalizeSkillId(raw?.selectedWarplaneId ?? raw?.instanceId),
    selectedGid: normalizeSkillId(raw?.selectedGid),
    name: String(raw?.name || ""),
    power: Math.max(0, Number(raw?.power || 0)),
    ownedRows,
  }, selectedRow);
}

function updateSelectedWarplaneProgress(data = {}, patch = {}, selectedRowKey = "", advancementRows = []) {
  const current = normalizeWarplaneData(data);
  const rows = Array.isArray(current?.ownedRows) ? current.ownedRows : [];
  const shouldApplyStageSkillUnlocks = (
    Object.prototype.hasOwnProperty.call(patch, "advancementStage")
    || Object.prototype.hasOwnProperty.call(patch, "advancementStep")
    || Object.prototype.hasOwnProperty.call(patch, "advancementLevel")
    || Object.prototype.hasOwnProperty.call(patch, "progressStep")
  );
  if (rows.length === 0) {
    const nextData = {
      ...current,
      ...patch,
    };
    return shouldApplyStageSkillUnlocks
      ? applyWarplaneStageSkillUnlocks(nextData, advancementRows)
      : nextData;
  }
  const selectedRow = findWarplaneRowByKey(rows, selectedRowKey) || pickSelectedWarplaneRow(current) || rows[0];
  const selectedKey = getWarplaneRowKey(selectedRow, rows.indexOf(selectedRow));
  const nextRows = rows.map((row, index) => (
    getWarplaneRowKey(row, index) === selectedKey
      ? {
        ...row,
        ...patch,
      }
      : row
  ));
  const nextSelectedRow = findWarplaneRowByKey(nextRows, selectedKey) || nextRows[0];
  const nextData = applySelectedWarplaneRow({
    ...current,
    ownedRows: nextRows,
  }, nextSelectedRow);
  return shouldApplyStageSkillUnlocks
    ? applyWarplaneStageSkillUnlocks(nextData, advancementRows)
    : nextData;
}

function normalizeEquipmentData(raw = {}) {
  const extensionSlotsRaw = Array.isArray(raw?.extensionSlots) ? raw.extensionSlots : [];
  const normalizedExtensionSlots = [];
  const seen = new Set();
  extensionSlotsRaw.forEach((entry) => {
    const slot = Math.max(1, Math.floor(Number(entry?.slot || 0)));
    const index = Math.max(1, Math.floor(Number(entry?.index || 0)));
    if (slot <= 0 || index <= 0) return;
    const key = `${slot}:${index}`;
    if (seen.has(key)) return;
    seen.add(key);
    normalizedExtensionSlots.push({
      slot,
      index,
      quality: Math.max(0, Math.min(4, Math.floor(Number(entry?.quality || 0)))),
      level: Math.max(0, Math.min(5, Math.floor(Number(entry?.level || 0)))),
    });
  });
  normalizedExtensionSlots.sort((left, right) => (
    left.slot - right.slot || left.index - right.index
  ));
  return {
    equipOptions: normalizeIdLevelEntries(raw?.equipOptions),
    synthRecipes: normalizeIdLevelEntries(raw?.synthRecipes),
    extensionSlots: normalizedExtensionSlots,
    ownedEquipment: normalizeMaterialRows(raw?.ownedEquipment),
    materials: normalizeMaterialRows(raw?.materials),
  };
}

function normalizeAntiquesData(raw = {}) {
  const legacyRows = [
    ...normalizeAntiqueCollectionEntries(raw?.badgeOptions),
    ...normalizeAntiqueCollectionEntries(raw?.diamondOptions),
  ];
  const collectionRows = normalizeAntiqueCollectionEntries(
    Array.isArray(raw?.collectionOptions) && raw.collectionOptions.length
      ? raw.collectionOptions
      : legacyRows,
  );
  return {
    collectionOptions: collectionRows,
  };
}

function normalizeBadgeEquippedEntries(rawRows = []) {
  if (!Array.isArray(rawRows)) return [];
  return rawRows.map((row) => {
    const source = row && typeof row === "object" ? row : {};
    const gid = normalizeSkillId(source?.gid || source?.id);
    if (!gid) return null;
    return {
      id: Math.max(0, Math.floor(Number(source?.id || 0))),
      gid,
      level: Math.max(0, Math.floor(Number(source?.level || 0))),
      position: Math.max(0, Math.floor(Number(source?.position ?? source?.pos ?? 0))),
    };
  }).filter(Boolean);
}

function normalizeBadgeItemEntries(rawRows = []) {
  if (!Array.isArray(rawRows)) return [];
  return rawRows.map((row) => {
    const source = row && typeof row === "object" ? row : {};
    const gid = normalizeSkillId(source?.gid || source?.id);
    if (!gid) return null;
    return {
      id: Math.max(0, Math.floor(Number(source?.id || 0))),
      gid,
      level: Math.max(0, Math.floor(Number(source?.level || 0))),
      count: Math.max(0, Math.floor(Number(source?.count || 0))),
      position: Math.max(0, Math.floor(Number(source?.position ?? source?.pos ?? 0))),
    };
  }).filter(Boolean);
}

function normalizeBadgesData(raw = {}) {
  return {
    equipped: normalizeBadgeEquippedEntries(raw?.equipped || raw?.badgeBuffs),
    items: normalizeBadgeItemEntries(raw?.items || raw?.badgeItems),
  };
}

function normalizeIslandData(raw = {}) {
  return {
    cityDecorationOptions: normalizeIdLevelEntries(raw?.cityDecorationOptions),
    specialDecorationOptions: normalizeIdLevelEntries(raw?.specialDecorationOptions),
    dockDecorationOptions: normalizeIdLevelEntries(raw?.dockDecorationOptions),
    placedDecorations: normalizeIslandPlacedDecorations(raw?.placedDecorations),
  };
}

function normalizeCityDecorationsData(raw = {}) {
  return {
    permanent: normalizeIdLevelEntries(raw?.permanent || raw?.rows || raw?.cityDecorations),
  };
}

function normalizeTemporaryBuffEntries(rawRows = []) {
  if (!Array.isArray(rawRows)) return [];
  const deduped = new Map();
  rawRows.forEach((row) => {
    const source = row && typeof row === "object" ? row : {};
    const id = normalizeSkillId(source?.id || source?.itemGid);
    if (!id) return;
    const sourceType = String(source?.sourceType || "item").trim() || "item";
    const buffId = String(source?.buffId || `${sourceType}:${id}`).trim();
    const normalized = {
      buffId,
      sourceType,
      id,
      itemGid: id,
      level: Math.max(0, Math.floor(Number(source?.level || 0))),
    };
    deduped.set(buffId, normalized);
  });
  return Array.from(deduped.values());
}

function normalizeTemporaryBuffsData(raw = {}) {
  return {
    active: normalizeTemporaryBuffEntries(raw?.active || raw?.rows || raw?.temporaryBuffs),
  };
}

function normalizeIslandPlacedDecorations(rawRows = []) {
  if (!Array.isArray(rawRows)) return [];
  return rawRows.map((row) => {
    const source = row && typeof row === "object" ? row : {};
    const gid = normalizeSkillId(source?.gid || source?.id);
    if (!gid) return null;
    const normalized = {
      id: Math.max(0, Math.floor(Number(source?.id || 0))),
      gid,
      level: Math.max(0, Math.floor(Number(source?.level || 0))),
      x: Math.max(0, Math.floor(Number(source?.x || 0))),
      y: Math.max(0, Math.floor(Number(source?.y || 0))),
    };
    const parentId = Math.max(0, Math.floor(Number(source?.parentId || 0)));
    if (parentId) normalized.parentId = parentId;
    if (Array.isArray(source?.unitIds)) {
      normalized.unitIds = source.unitIds
        .map((value) => Math.max(0, Math.floor(Number(value || 0))))
        .filter((value) => value > 0);
    }
    return normalized;
  }).filter(Boolean);
}

function normalizeCommanderSkillsData(raw = {}) {
  return {
    developmentSkills: normalizeIdLevelEntries(raw?.developmentSkills),
    warSkills: normalizeIdLevelEntries(raw?.warSkills),
    heroPassiveSkillOptions: normalizeIdLevelEntries(raw?.heroPassiveSkillOptions),
    heroActiveSkillOptions: normalizeIdLevelEntries(raw?.heroActiveSkillOptions),
    heroSuperAttributeOptions: normalizeIdLevelEntries(raw?.heroSuperAttributeOptions),
    battlePassiveSkillOptions: normalizeIdLevelEntries(raw?.battlePassiveSkillOptions),
    battleActiveSkillOptions: normalizeIdLevelEntries(raw?.battleActiveSkillOptions),
    battleUpdateSkillOptions: normalizeIdLevelEntries(raw?.battleUpdateSkillOptions),
    battleBuffOptions: normalizeIdLevelEntries(raw?.battleBuffOptions),
    heroSuperLevel: Math.max(0, Math.floor(Number(raw?.heroSuperLevel || 0))),
    heroSkillLevel: Math.max(0, Math.floor(Number(raw?.heroSkillLevel || 0))),
  };
}

function normalizeCommanderBoostsData(raw = {}) {
  const rawVip = raw?.vip && typeof raw.vip === "object" ? raw.vip : {};
  const rawProsperity = raw?.prosperity && typeof raw.prosperity === "object" ? raw.prosperity : {};
  const rawMilitaryRank = raw?.militaryRank && typeof raw.militaryRank === "object" ? raw.militaryRank : {};
  const vipLevel = Math.floor(Number(rawVip?.level ?? 0));
  const prosperityLevel = Math.floor(Number(rawProsperity?.level ?? 0));
  const prosperityCount = Math.floor(Number(rawProsperity?.count ?? 0));
  const militaryRankLevel = Math.floor(Number(rawMilitaryRank?.level ?? rawMilitaryRank?.id ?? 0));
  return {
    vip: {
      level: Number.isFinite(vipLevel) ? Math.max(0, Math.min(VIP_MAX_LEVEL, vipLevel)) : 0,
      active: normalizeBoolean(rawVip?.active, false),
    },
    prosperity: {
      level: Number.isFinite(prosperityLevel) ? Math.max(0, Math.min(PROSPERITY_MAX_LEVEL, prosperityLevel)) : 0,
      active: normalizeBoolean(rawProsperity?.active, false),
      count: Number.isFinite(prosperityCount) ? Math.max(0, prosperityCount) : 0,
    },
    militaryRank: {
      level: Number.isFinite(militaryRankLevel) ? Math.max(0, Math.min(MILITARY_RANK_MAX_LEVEL, militaryRankLevel)) : 0,
    },
  };
}

function normalizeNumberMap(raw = {}) {
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  return Object.entries(source).reduce((acc, [key, value]) => {
    const cleanKey = String(key || "").trim();
    if (!cleanKey) return acc;
    const parsed = Number(value || 0);
    acc[cleanKey] = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    return acc;
  }, {});
}

function normalizeInventoryRows(rawRows = []) {
  if (!Array.isArray(rawRows)) return [];
  return rawRows.map((row) => {
    const source = row && typeof row === "object" ? row : {};
    const gid = normalizeSkillId(source?.gid || source?.id);
    if (!gid) return null;
    return {
      id: Math.max(0, Math.floor(Number(source?.id || 0))),
      gid,
      level: Math.max(0, Math.floor(Number(source?.level || 0))),
      count: Math.max(0, Math.floor(Number(source?.count || 0))),
      type: Math.max(0, Math.floor(Number(source?.type || 0))),
      name: String(source?.name || ""),
      description: String(source?.description || ""),
      iconKey: String(source?.iconKey || ""),
      itemType: source?.itemType === null || source?.itemType === undefined ? null : Math.max(0, Math.floor(Number(source.itemType || 0))),
      quality: source?.quality === null || source?.quality === undefined ? null : Math.max(0, Math.floor(Number(source.quality || 0))),
    };
  }).filter(Boolean);
}

function normalizeMaterialRows(rawRows = []) {
  if (!Array.isArray(rawRows)) return [];
  return rawRows.map((row) => {
    const source = row && typeof row === "object" ? row : {};
    const gid = normalizeSkillId(source?.gid || source?.id);
    if (!gid) return null;
    return {
      id: Math.max(0, Math.floor(Number(source?.id || 0))),
      gid,
      rarity: Math.max(0, Math.floor(Number(source?.rarity || 0))),
      count: Math.max(0, Math.floor(Number(source?.count || 0))),
      diamonds: Array.isArray(source?.diamonds)
        ? source.diamonds.map((value) => normalizeSkillId(value)).filter(Boolean)
        : [],
      name: String(source?.name || ""),
      description: String(source?.description || ""),
      iconKey: String(source?.iconKey || ""),
      itemType: source?.itemType === null || source?.itemType === undefined ? null : Math.max(0, Math.floor(Number(source.itemType || 0))),
      quality: source?.quality === null || source?.quality === undefined ? null : Math.max(0, Math.floor(Number(source.quality || 0))),
    };
  }).filter(Boolean);
}

function normalizeInventoryData(raw = {}) {
  return {
    resources: normalizeNumberMap(raw?.resources),
    items: normalizeInventoryRows(raw?.items),
    materials: normalizeMaterialRows(raw?.materials),
    equipment: normalizeMaterialRows(raw?.equipment),
    badges: normalizeInventoryRows(raw?.badges),
    titanItems: normalizeInventoryRows(raw?.titanItems),
    valleyItems: normalizeInventoryRows(raw?.valleyItems),
  };
}

function normalizeCityData(raw = {}) {
  const commander = raw?.commander && typeof raw.commander === "object" ? raw.commander : {};
  return {
    commander: {
      commanderId: normalizeSkillId(commander?.commanderId),
      name: String(commander?.name || ""),
      nationId: normalizeSkillId(commander?.nationId),
      allianceId: normalizeSkillId(commander?.allianceId),
      allianceAcronym: String(commander?.allianceAcronym || ""),
      allianceName: String(commander?.allianceName || ""),
      cityLevel: Math.max(0, Math.floor(Number(commander?.cityLevel || 0))),
      commanderLevel: Math.max(0, Math.floor(Number(commander?.commanderLevel || 0))),
      battlePower: Math.max(0, Number(commander?.battlePower || 0)),
    },
    resources: normalizeNumberMap(raw?.resources),
    buildings: Array.isArray(raw?.buildings)
      ? raw.buildings.map((row) => {
        const source = row && typeof row === "object" ? row : {};
        const gid = normalizeSkillId(source?.gid);
        if (!gid) return null;
        return {
          id: Math.max(0, Math.floor(Number(source?.id || 0))),
          gid,
          level: Math.max(0, Math.floor(Number(source?.level || 0))),
          position: Math.max(0, Math.floor(Number(source?.position || 0))),
          state: Math.max(0, Math.floor(Number(source?.state || 0))),
        };
      }).filter(Boolean)
      : [],
    cityBuffs: Array.isArray(raw?.cityBuffs) ? raw.cityBuffs : [],
    army: raw?.army && typeof raw.army === "object" && !Array.isArray(raw.army) ? raw.army : {},
    powerBreakdown: normalizeNumberMap(raw?.powerBreakdown),
  };
}

function normalizeBiochemicalWorkshopData(raw = {}) {
  const normalizeGrowthRewards = (source) => {
    const rawRows = Array.isArray(source?.growthRewards)
      ? source.growthRewards
      : Array.isArray(source?.groRewards)
        ? source.groRewards
        : [];
    return rawRows.map((row) => {
      const reward = row && typeof row === "object" ? row : {};
      const id = normalizeSkillId(reward?.id ?? reward?.gid);
      if (!id) return null;
      return {
        id,
        status: Math.floor(Number(reward?.status ?? reward?.level ?? 0)),
      };
    }).filter(Boolean);
  };
  const normalizePet = (row) => {
    const source = row && typeof row === "object" ? row : {};
    const gid = normalizeSkillId(source?.gid);
    if (!gid) return null;
    const intimacyLevel = Math.max(0, Math.floor(Number(source?.intimacyLevel ?? source?.incubationLevel ?? 0)));
    const healLevel = Math.max(0, Math.floor(Number(source?.healLevel ?? source?.hatcheryLevel ?? 0)));
    const intimacyExp = Math.max(0, Math.floor(Number(source?.intimacyExp ?? source?.incubationExp ?? 0)));
    const healExp = Math.max(0, Math.floor(Number(source?.healExp ?? source?.hatcheryExp ?? 0)));
    return {
      gid,
      name: String(source?.name || ""),
      intimacyLevel,
      healLevel,
      intimacyExp,
      healExp,
      incubationLevel: intimacyLevel,
      hatcheryLevel: healLevel,
      incubationExp: intimacyExp,
      hatcheryExp: healExp,
      collectedGids: Array.isArray(source?.collectedGids)
        ? source.collectedGids.map((value) => normalizeSkillId(value)).filter(Boolean)
        : [],
      growthRewards: normalizeGrowthRewards(source),
    };
  };
  const normalizeTechRows = (rows) => (Array.isArray(rows)
    ? rows.map((row) => {
      const source = row && typeof row === "object" ? row : {};
      const gid = normalizeSkillId(source?.gid);
      if (!gid) return null;
      return {
        gid,
        petGid: normalizeSkillId(source?.petGid),
        level: Math.max(0, Math.floor(Number(source?.level || 0))),
        status: Math.max(0, Math.floor(Number(source?.status || 0))),
      };
    }).filter(Boolean)
    : []);
  const techRows = normalizeTechRows(raw?.biochemicalTech || raw?.zombieSkills);
  return {
    geneTechLevel: Math.max(0, Math.floor(Number(raw?.geneTechLevel ?? raw?.zombieGene ?? 0))),
    geneTechPoints: Math.max(0, Math.floor(Number(raw?.geneTechPoints ?? raw?.zombiePetPoints ?? 0))),
    pets: Array.isArray(raw?.pets) ? raw.pets.map(normalizePet).filter(Boolean) : [],
    biochemicalTech: techRows,
    zombieSkills: normalizeTechRows(raw?.zombieSkills || raw?.biochemicalTech),
  };
}

function normalizeAllianceData(raw = {}) {
  const benefitBuildings = new Map();
  (Array.isArray(raw?.benefitBuildings) ? raw.benefitBuildings : []).forEach((row) => {
    const id = normalizeSkillId(row?.id ?? row?.gid ?? row?.type);
    if (!id) return;
    const count = Math.max(0, Math.floor(Number(row?.count ?? row?.level ?? 0)));
    const existing = benefitBuildings.get(id);
    if (!existing || count > existing.count) {
      benefitBuildings.set(id, { id, count });
    }
  });
  return {
    allianceTech: normalizeIdLevelEntries(raw?.allianceTech),
    benefitBuildings: Array.from(benefitBuildings.values()).filter((row) => row.count > 0),
  };
}

function normalizeAcademyData(raw = {}) {
  return {
    academyTech: normalizeIdLevelEntries(raw?.academyTech),
    userTech: normalizeIdLevelEntries(raw?.userTech),
    vehicleTech: normalizeIdLevelEntries(raw?.vehicleTech),
    allianceTech: normalizeIdLevelEntries(raw?.allianceTech),
    drillTech: normalizeIdLevelEntries(raw?.drillTech),
    userTalents: normalizeIdLevelEntries(raw?.userTalents),
  };
}

const normalizeTroopRangeLabel = (value = "") => String(value || "")
  .replace(/\bClose(?:-Range)?\b/g, "Melee")
  .replace(/\bNear\b/g, "Melee")
  .replace(/\bMiddle\b/g, "Mid-Range")
  .replace(/\bMid Range\b/g, "Mid-Range")
  .replace(/\bFar\b/g, "Long-Range")
  .replace(/\bLong Range\b/g, "Long-Range");

const stripGameColorTags = (value = "") => String(value || "")
  .replace(/\[[0-9a-fA-F]{6}\]/g, "")
  .replace(/\[-\]/g, "");

const normalizeEffectDescriptionText = (value = "") => normalizeTroopRangeLabel(stripGameColorTags(value));

const COMMANDER_EFFECT_LABEL_OVERRIDES = new Map([
  [50397302, "Recruitment Speed Up"],
  [50397343, "Gas Bomb Damage Reduction"],
  [50397344, "Rampart Smasher Damage Taken Increase"],
  [50397345, "Rescue Killed-to-Wounded Conversion"],
  [50397346, "Exterminate Wounded Enemy Kill"],
  [50397348, "Magnetic Shield Absorption"],
  [FLEET_SIZE_TECH_GIDS.CROSS_NATION_WAR_MOBILIZATION_UNIT_LIMIT, "Cross-Nation War Mobilization Fleet Units Bonus Limit"],
]);

const resolveCommanderEffectLabel = (effectLabelById = new Map(), effectId = 0) => {
  const normalizedEffectId = Number(effectId || 0);
  return normalizeTroopRangeLabel(
    effectLabelById.get(normalizedEffectId)
    || COMMANDER_EFFECT_LABEL_OVERRIDES.get(normalizedEffectId)
    || `Effect #${normalizedEffectId}`,
  );
};

const COMMANDER_EFFECT_META_BY_ID = new Map(
  (Array.isArray(COMMANDER_EFFECT_CATALOG) ? COMMANDER_EFFECT_CATALOG : [])
    .map((entry) => [Number(entry?.id || 0), entry])
    .filter(([id]) => Number.isFinite(id) && id > 0),
);

const formatOfficerSkillTemplateValue = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return "0.000";
  const rounded = Math.round(numeric * 1000) / 1000;
  return rounded.toFixed(3);
};

const EFFECT_TYPE_CATEGORY = 3;
const EFFECT_TYPE_PERCENT_SUBCATEGORY = 1;

const getGidCategory = (gid) => (Math.trunc(Number(gid || 0)) >>> 24);
const getGidSubCategory = (gid) => ((Math.trunc(Number(gid || 0)) >>> 16) & 0xff);
const isPercentEffectGid = (gid) => (
  getGidCategory(gid) === EFFECT_TYPE_CATEGORY
  && getGidSubCategory(gid) === EFFECT_TYPE_PERCENT_SUBCATEGORY
);

const FORCE_PERCENT_EFFECT_IDS = new Set([
  2,
  3,
  4,
  10,
  14,
  420,
  50462850,
  50462866,
  50462869,
  50462916,
  50462919,
  50462920,
  50462930,
  50462937,
  50462940,
  50462950,
  50462951,
  100925443,
  100925444,
  100925445,
  100925447,
  100925448,
  100925449,
  302317570,
  302317571,
  302317572,
  302317573,
  302317574,
]);

const FORCE_RAW_EFFECT_IDS = new Set([
  1,
  5,
  448,
  470,
  512,
  530,
  576,
  600,
  640,
  670,
  704,
  730,
  768,
  790,
  800,
  804,
  830,
  832,
  860,
  870,
  896,
  900,
  905,
  920,
  930,
  940,
  960,
  980,
  1000,
  1005,
  1010,
  1020,
  1024,
  1040,
  1050,
  1088,
  1090,
  1106,
  1152,
  1206,
  1216,
  1280,
  1307,
  1344,
  1408,
  1472,
  1508,
  1536,
  1600,
  1609,
  1664,
  1709,
  1810,
  1910,
  2011,
  2112,
  2213,
  2314,
  234881049,
  234881064,
  234881135,
  2415,
  2516,
  2617,
]);

const RAW_EFFECT_TEXT_PATTERN = /\b(capacity|limit|units?|queue|fleet size|max|storage|depot|resource|food|oil|steel|mineral|gold|power|shield|wounded units|recruit(?:ment)?|hospital|army limit|supplies)\b/i;
const PERCENT_EFFECT_TEXT_PATTERN = /\b(percent|chance|rate|speed|attack|defen[cs]e|hp|damage|boost|reduction|resistance|dodge|hit|crit|block|armor|firepower|medic|scavenger|looter|surmount|healing)\b/i;

const shouldFormatEffectAsPercent = (effectId, context = {}) => {
  const normalizedId = Number(effectId || 0);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return false;
  if (FORCE_PERCENT_EFFECT_IDS.has(normalizedId)) return true;
  if (FORCE_RAW_EFFECT_IDS.has(normalizedId)) return false;
  if (isPercentEffectGid(normalizedId)) return true;

  const meta = COMMANDER_EFFECT_META_BY_ID.get(normalizedId) || {};
  const contextText = [
    meta.label,
    meta.description,
    context?.label,
    context?.description,
    context?.sourceLabel,
  ].map((entry) => String(entry || "").trim()).filter(Boolean).join(" ");
  if (!contextText) return false;
  if (RAW_EFFECT_TEXT_PATTERN.test(contextText) && !/%|\bchance\b|\brate\b/i.test(contextText)) return false;
  return PERCENT_EFFECT_TEXT_PATTERN.test(contextText);
};

const formatCommanderNumericValue = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value || "");
  const rounded = Math.round(parsed * 1000) / 1000;
  if (Number.isInteger(rounded)) return rounded.toLocaleString();
  return rounded.toFixed(3).replace(/\.?0+$/, "");
};

const formatCommanderEffectValue = (value, effectId = null, context = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value || "");
  if (shouldFormatEffectAsPercent(effectId, context)) {
    return `${formatOfficerSkillTemplateValue(parsed / 10)}%`;
  }
  return formatCommanderNumericValue(parsed);
};

const EFFECT_DESCRIPTION_PLACEHOLDER_PATTERN = /\$(\d+)/g;

const normalizeCommanderEffectEntry = (entry = null) => {
  if (!entry) return null;
  const effectId = Number(entry?.effectId || 0);
  const value = Number(entry?.value ?? entry?.scaledValue ?? 0);
  if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value)) return null;
  return { ...entry, effectId, value };
};

const getEffectDescriptionPlaceholderIndexes = (template = "") => (
  Array.from(String(template || "").matchAll(EFFECT_DESCRIPTION_PLACEHOLDER_PATTERN))
    .map((match) => Math.max(0, Math.floor(Number(match[1] || 0))))
);

const formatCommanderEffectDescriptionTemplate = (
  template = "",
  effects = [],
  effectLabelById = new Map(),
  context = {},
) => {
  const normalizedTemplate = normalizeEffectDescriptionText(String(template || "").trim());
  if (!normalizedTemplate || !normalizedTemplate.includes("$")) return "";
  const placeholderIndexes = getEffectDescriptionPlaceholderIndexes(normalizedTemplate);
  if (!placeholderIndexes.length) return "";
  const normalizedEffects = Array.isArray(effects)
    ? effects.map((entry) => normalizeCommanderEffectEntry(entry))
    : [];
  const validEffects = normalizedEffects.filter(Boolean);
  const hasMissingPlaceholder = placeholderIndexes.some((index) => (
    !normalizedEffects[index] && !(index === 0 && validEffects.length === 1)
  ));
  if (hasMissingPlaceholder) return "";
  return normalizedTemplate.replace(EFFECT_DESCRIPTION_PLACEHOLDER_PATTERN, (match, rawIndex, offset) => {
    const index = Math.max(0, Math.floor(Number(rawIndex || 0)));
    const effect = normalizedEffects[index] || (index === 0 && validEffects.length === 1 ? validEffects[0] : null);
    if (!effect) return "";
    const effectLabel = resolveCommanderEffectLabel(effectLabelById, effect.effectId);
    const customFormatted = typeof context?.formatTemplateValue === "function"
      ? context.formatTemplateValue(effect, {
        match,
        placeholderIndex: index,
        offset,
        template: normalizedTemplate,
      })
      : null;
    const formatted = customFormatted == null
      ? formatCommanderEffectValue(effect.value, effect.effectId, {
        ...context,
        description: normalizedTemplate,
        label: effectLabel,
      })
      : String(customFormatted);
    return normalizedTemplate[offset + match.length] === "%" ? formatted.replace(/%$/, "") : formatted;
  });
};

const formatCommanderEffectLine = (
  effectId,
  value,
  effectLabelById = new Map(),
  context = {},
) => {
  const label = resolveCommanderEffectLabel(effectLabelById, effectId);
  const description = String(context?.description || "").trim();
  const template = description.includes("$") ? description : (label.includes("$") ? label : "");
  if (template) {
    const placeholderIndexes = getEffectDescriptionPlaceholderIndexes(template);
    const canUseSingleEffectTemplate = placeholderIndexes.length === 1;
    if (canUseSingleEffectTemplate) {
      const targetIndex = placeholderIndexes[0];
      const placeholderEffects = Array.from({ length: targetIndex + 1 }, (_entry, index) => (
        index === targetIndex ? { effectId, value } : null
      ));
      const formattedTemplate = formatCommanderEffectDescriptionTemplate(
        template,
        placeholderEffects,
        effectLabelById,
        context,
      );
      if (formattedTemplate) return formattedTemplate;
    }
  }
  return `${label}: ${formatCommanderEffectValue(value, effectId, { ...context, label })}`;
};

const formatCommanderEffectLinesForEffects = (
  effects = [],
  effectLabelById = new Map(),
  context = {},
) => {
  const normalizedEffects = Array.isArray(effects)
    ? effects.map((entry) => normalizeCommanderEffectEntry(entry)).filter(Boolean)
    : [];
  if (!normalizedEffects.length) return [];
  const description = String(context?.description || "").trim();
  const formattedTemplate = formatCommanderEffectDescriptionTemplate(description, normalizedEffects, effectLabelById, context);
  if (formattedTemplate) return [formattedTemplate];
  return normalizedEffects.map((effect, index) => formatCommanderEffectLine(effect.effectId, effect.value, effectLabelById, {
    ...context,
    placeholderIndex: index,
  }));
};

const roundFleetSizeUnits = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric);
};

const formatFleetSizeUnits = (value) => formatExpectedReportNumber(roundFleetSizeUnits(value), 0);

const formatSignedFleetSizeUnits = (value) => {
  const numeric = roundFleetSizeUnits(value);
  const prefix = numeric < 0 ? "-" : "+";
  return `${prefix}${formatFleetSizeUnits(Math.abs(numeric))} units`;
};

const formatFleetSizePercent = (value) => `${formatExpectedReportNumber(Number(value || 0), 3)}%`;

const formatFleetSizeContributionValue = (effectId, value) => {
  const normalizedEffectId = Math.trunc(Number(effectId || 0));
  const numericValue = Number(value || 0);
  if (FLEET_SIZE_PERCENT_EFFECT_ID_SET.has(normalizedEffectId)) {
    const percentValue = numericValue / 10;
    const prefix = percentValue < 0 ? "-" : "+";
    return `${prefix}${formatFleetSizePercent(Math.abs(percentValue))}`;
  }
  return formatSignedFleetSizeUnits(numericValue);
};

const normalizeFleetSizeContributionEffects = (effects = []) => (
  (Array.isArray(effects) ? effects : [])
    .map((effect) => {
      const effectId = Math.trunc(Number(effect?.effectId || effect?.id || 0));
      const value = Number(effect?.value ?? effect?.scaledValue ?? 0);
      if (!FLEET_SIZE_RELEVANT_EFFECT_ID_SET.has(effectId) || !Number.isFinite(value) || value === 0) {
        return null;
      }
      return { effectId, value };
    })
    .filter(Boolean)
);

const buildFleetSizeContributionLines = (effects = [], effectLabelById = new Map()) => (
  normalizeFleetSizeContributionEffects(effects).map((effect) => ({
    key: `${effect.effectId}:${effect.value}`,
    effectId: effect.effectId,
    isZombieLimit: FLEET_SIZE_ZOMBIE_EFFECT_ID_SET.has(effect.effectId),
    text: `${resolveCommanderEffectLabel(effectLabelById, effect.effectId)}: ${formatFleetSizeContributionValue(effect.effectId, effect.value)}`,
  }))
);

const isFleetSizeBoostableCoreContribution = (detail = {}) => {
  const sectionKey = String(detail?.sectionKey || "").trim();
  const text = [
    detail?.groupLabel,
    detail?.label,
    detail?.meta,
  ].map((entry) => String(entry || "")).join(" ");
  if (sectionKey === "academy") return true;
  if (sectionKey === "equipment") return true;
  if (sectionKey === "city" && /\bgarage\b|\bbuilding\b/i.test(text)) return true;
  if (sectionKey === "profile") return true;
  return false;
};

const getFleetSizeEffectBucket = (effect = {}, detail = {}) => {
  const effectId = Math.trunc(Number(effect?.effectId || 0));
  if (FLEET_SIZE_ZOMBIE_EFFECT_ID_SET.has(effectId)) return "zombie";
  if (FLEET_SIZE_PERCENT_EFFECT_ID_SET.has(effectId)) return "boostRate";
  if (
    effectId === FLEET_SIZE_TECH_GIDS.UNIT_LIMIT
    && isFleetSizeBoostableCoreContribution(detail)
  ) {
    return "boostableCore";
  }
  return "additional";
};

const summarizeFleetSizeEffects = (effects = [], detail = {}) => (
  normalizeFleetSizeContributionEffects(effects).reduce((summary, effect) => {
    const bucket = getFleetSizeEffectBucket(effect, detail);
    if (bucket === "boostRate") summary.percentRaw += Number(effect.value || 0);
    else if (bucket === "zombie") summary.zombieUnits += Number(effect.value || 0);
    else if (bucket === "boostableCore") summary.boostableCoreUnits += Number(effect.value || 0);
    else summary.additionalUnits += Number(effect.value || 0);
    return summary;
  }, {
    boostableCoreUnits: 0,
    additionalUnits: 0,
    percentRaw: 0,
    zombieUnits: 0,
  })
);

const formatFleetSizeRowDisplayValue = (summary = {}) => {
  const pieces = [];
  const boostableCoreUnits = roundFleetSizeUnits(summary.boostableCoreUnits);
  const additionalUnits = roundFleetSizeUnits(summary.additionalUnits);
  const zombieUnits = roundFleetSizeUnits(summary.zombieUnits);
  const percentBoost = Number(summary.percentRaw || 0) / 10;
  if (boostableCoreUnits) pieces.push(formatSignedFleetSizeUnits(boostableCoreUnits));
  if (additionalUnits) pieces.push(formatSignedFleetSizeUnits(additionalUnits));
  if (Math.abs(percentBoost) >= 0.0001) {
    pieces.push(`${percentBoost < 0 ? "-" : "+"}${formatFleetSizePercent(Math.abs(percentBoost))}`);
  }
  if (zombieUnits) pieces.push(`${formatSignedFleetSizeUnits(zombieUnits)} zombie`);
  return pieces.join(" | ") || "--";
};

const getFleetSizeContributionMagnitude = (row = {}) => (
  (Array.isArray(row.effects) ? row.effects : []).reduce((sum, effect) => {
    const value = Number(effect?.value || 0);
    if (!Number.isFinite(value)) return sum;
    return sum + Math.abs(FLEET_SIZE_PERCENT_EFFECT_ID_SET.has(effect.effectId) ? value / 10 : value);
  }, 0)
);

const buildFleetSizeContributionRows = ({
  sectionPayload = {},
  combatContext = "internal",
  effectLabelById = new Map(),
} = {}) => {
  const rows = buildSectionContributionDetails(sectionPayload, { combatContext })
    .map((detail, detailIndex) => {
      const effects = normalizeFleetSizeContributionEffects(detail?.effects);
      if (!effects.length) return null;
      const lines = buildFleetSizeContributionLines(effects, effectLabelById);
      const normalLines = lines.filter((line) => !line.isZombieLimit);
      const zombieLines = lines.filter((line) => line.isZombieLimit);
      const summary = summarizeFleetSizeEffects(effects, detail);
      return {
        key: `section:${detail?.sectionKey || "section"}:${detail?.key || detailIndex}`,
        sectionKey: String(detail?.sectionKey || ""),
        groupLabel: String(detail?.groupLabel || "").trim(),
        label: String(detail?.label || "Profile entry").trim() || "Profile entry",
        meta: String(detail?.meta || "").trim(),
        effects,
        lines,
        normalLines,
        zombieLines,
        summary,
        displayValue: formatFleetSizeRowDisplayValue(summary),
        isZombieOnly: normalLines.length === 0 && zombieLines.length > 0,
      };
    })
    .filter(Boolean);

  return rows.sort((left, right) => {
    if (left.isZombieOnly !== right.isZombieOnly) return left.isZombieOnly ? 1 : -1;
    return getFleetSizeContributionMagnitude(right) - getFleetSizeContributionMagnitude(left);
  });
};

const buildFleetSizeSummaryFromParityProfile = (parityProfile = {}, options = {}) => {
  void parityProfile;
  const combatContext = options?.combatContext || "internal";
  const contributionRows = buildFleetSizeContributionRows({
    sectionPayload: options?.sectionPayload,
    combatContext,
    effectLabelById: options?.effectLabelById,
  });
  const contributionSummary = contributionRows.reduce((summary, row) => {
    const rowSummary = row?.summary || {};
    summary.boostableCoreUnits += Number(rowSummary.boostableCoreUnits || 0);
    summary.additionalUnits += Number(rowSummary.additionalUnits || 0);
    summary.percentRaw += Number(rowSummary.percentRaw || 0);
    summary.zombieUnits += Number(rowSummary.zombieUnits || 0);
    return summary;
  }, {
    boostableCoreUnits: 0,
    additionalUnits: 0,
    percentRaw: 0,
    zombieUnits: 0,
  });
  const coreUnitLimit = roundFleetSizeUnits(contributionSummary.boostableCoreUnits);
  const percentBoostRaw = contributionSummary.percentRaw;
  const percentBoost = roundExpectedReportStatValue(percentBoostRaw / 10, 3);
  const scalableUnitLimit = FLEET_SIZE_BASE_UNITS + coreUnitLimit;
  const percentBonusUnits = roundFleetSizeUnits(scalableUnitLimit * (percentBoost / 100));
  const additionalUnitLimit = roundFleetSizeUnits(contributionSummary.additionalUnits);
  const totalUnits = Math.max(0, roundFleetSizeUnits(scalableUnitLimit + percentBonusUnits + additionalUnitLimit));
  const biochemicalZombieLimit = Math.max(0, roundFleetSizeUnits(contributionSummary.zombieUnits));

  return {
    baseUnits: FLEET_SIZE_BASE_UNITS,
    coreUnitLimit,
    boostableUnitLimit: scalableUnitLimit,
    scalableUnitLimit,
    percentBoost,
    percentBonusUnits,
    additionalUnitLimit,
    totalUnits,
    biochemicalZombieLimit,
    totalWithBiochemicalZombies: totalUnits + biochemicalZombieLimit,
    contributionRows,
    normalContributionRows: contributionRows.filter((row) => Array.isArray(row?.normalLines) && row.normalLines.length > 0),
    zombieContributionRows: contributionRows.filter((row) => Array.isArray(row?.zombieLines) && row.zombieLines.length > 0),
  };
};

const normalizeEffectEntriesForDisplay = (effects = []) => (
  (Array.isArray(effects) ? effects : [])
    .map((entry) => {
      const effectId = Number(entry?.effectId || 0);
      const value = Number(entry?.value ?? entry?.scaledValue ?? 0);
      if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value) || value === 0) return null;
      return { effectId, value };
    })
    .filter(Boolean)
);

const buildEffectRangeLinesFromEntries = (
  entries = [],
  effectLabelById = new Map(),
  context = {},
) => {
  const byEffectId = new Map();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const effectId = Number(entry?.effectId || 0);
    const value = Number(entry?.value || 0);
    if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value) || value === 0) return;
    const level = Number(entry?.level || 0);
    const displayLevel = String(entry?.displayLevel || (Number.isFinite(level) && level > 0 ? `Level ${level}` : "Base"));
    if (!byEffectId.has(effectId)) byEffectId.set(effectId, []);
    byEffectId.get(effectId).push({ value, level: Number.isFinite(level) ? level : 0, displayLevel });
  });
  return Array.from(byEffectId.entries())
    .sort((left, right) => left[0] - right[0])
    .map(([effectId, rows]) => {
      const sorted = [...rows].sort((left, right) => left.level - right.level);
      const first = sorted[0];
      const last = sorted[sorted.length - 1] || first;
      const label = resolveCommanderEffectLabel(effectLabelById, effectId);
      const firstValue = formatCommanderEffectValue(first.value, effectId, context);
      const lastValue = formatCommanderEffectValue(last.value, effectId, context);
      if (!last || last.level === first.level || firstValue === lastValue) {
        return `${label}: ${first.displayLevel} ${firstValue}`;
      }
      return `${label}: ${first.displayLevel} ${firstValue} - ${last.displayLevel} ${lastValue}`;
    });
};

const buildEffectRangeLinesFromLevelRows = (
  levelRows = [],
  effectLabelById = new Map(),
  context = {},
  options = {},
) => {
  const getLevel = typeof options.getLevel === "function"
    ? options.getLevel
    : (row) => Number(row?.level || 0);
  const getDisplayLevel = typeof options.getDisplayLevel === "function"
    ? options.getDisplayLevel
    : (row) => `Level ${Number(getLevel(row) || 0).toLocaleString()}`;
  const rows = (Array.isArray(levelRows) ? levelRows : [])
    .map((row) => {
      const level = Number(getLevel(row));
      return {
        row,
        level: Number.isFinite(level) ? level : 0,
        displayLevel: String(getDisplayLevel(row) || "").trim(),
      };
    })
    .filter((entry) => entry.displayLevel)
    .sort((left, right) => left.level - right.level);
  const entries = [];
  rows.forEach((entry) => {
    normalizeEffectEntriesForDisplay(entry.row?.effects).forEach((effect) => {
      entries.push({
        ...effect,
        level: entry.level,
        displayLevel: entry.displayLevel,
      });
    });
  });
  return buildEffectRangeLinesFromEntries(entries, effectLabelById, context);
};

const buildEffectRangeLinesForOption = (option = {}, effectLabelById = new Map(), context = {}) => {
  const levelRows = Array.isArray(option?.levelRows) ? option.levelRows : [];
  if (levelRows.length) {
    return buildEffectRangeLinesFromLevelRows(levelRows, effectLabelById, context);
  }
  const directEffects = normalizeEffectEntriesForDisplay(option?.effects);
  if (directEffects.length) {
    return buildEffectRangeLinesFromEntries(
      directEffects.map((effect) => ({ ...effect, level: 1, displayLevel: "Base" })),
      effectLabelById,
      context,
    );
  }
  const firstLevelEffects = normalizeEffectEntriesForDisplay(option?.firstLevelEffects);
  if (firstLevelEffects.length) {
    const maxLevel = Math.max(1, Number(option?.maxLevel || 1));
    return buildEffectRangeLinesFromEntries(
      firstLevelEffects.flatMap((effect) => [
        { ...effect, level: 1, displayLevel: "Level 1" },
        { ...effect, value: effect.value * maxLevel, level: maxLevel, displayLevel: `Level ${maxLevel}` },
      ]),
      effectLabelById,
      context,
    );
  }
  return [];
};

const renderEffectLineList = (lines = [], keyPrefix = "effect-line") => (
  (Array.isArray(lines) ? lines : []).map((line, index) => (
    <div key={`${keyPrefix}-${index}`}>{line}</div>
  ))
);

const formatOfficerSkillDescriptionValue = (row = {}, skillOption = null) => {
  const scaledValue = Number(row?.scaledValue);
  if (Number.isFinite(scaledValue)) return formatOfficerSkillTemplateValue(scaledValue);
  const rawValue = Number(row?.rawValue || 0);
  const effectFactor = Number(skillOption?.effectFactor || 1);
  return formatOfficerSkillTemplateValue(rawValue * effectFactor);
};

const renderOfficerSkillDescription = (preview = null, skillOption = null, effectLabelById = new Map()) => {
  const template = String(preview?.description || skillOption?.description || "").trim();
  if (!template || !template.includes("$")) return "";
  const rows = Array.isArray(preview?.rows) ? preview.rows : [];
  const effects = rows.map((row) => ({
    ...row,
    effectId: Number(row?.effectId || 0),
    value: Number(row?.techValue ?? row?.scaledValue ?? row?.rawValue ?? 0),
  }));
  return formatCommanderEffectDescriptionTemplate(template, effects, effectLabelById, {
    label: preview?.label || skillOption?.label,
    description: template,
    formatTemplateValue: (effect) => formatOfficerSkillDescriptionValue(effect, skillOption),
  });
};

const formatOfficerEffectValueWithDelta = (effectId, currentValue, nextValue = null, effectLabelById = new Map(), context = {}) => {
  const current = Number(currentValue || 0);
  const currentLine = formatCommanderEffectLine(effectId, current, effectLabelById, context);
  const parsedNext = Number(nextValue);
  if (!Number.isFinite(parsedNext)) return currentLine;
  const delta = parsedNext - current;
  if (!Number.isFinite(delta) || delta <= 0) return currentLine;
  return `${currentLine} (+${formatCommanderEffectValue(delta, effectId, context)})`;
};

const sumEffectValuesById = (effects = []) => {
  const totals = new Map();
  (Array.isArray(effects) ? effects : []).forEach((entry) => {
    const effectId = Number(entry?.effectId || 0);
    const value = Number(entry?.value ?? entry?.scaledValue ?? 0);
    if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value) || value === 0) return;
    totals.set(effectId, (totals.get(effectId) || 0) + value);
  });
  return totals;
};

const collectOfficerEquipmentLevelEffects = (slotMeta = null, level = 0) => {
  const parsedLevel = Math.max(0, Math.floor(Number(level || 0)));
  if (!slotMeta || parsedLevel <= 0) return [];
  const levelRows = Array.isArray(slotMeta?.levelRows) ? slotMeta.levelRows : [];
  const levelRow = levelRows.find((entry) => Number(entry?.level || 0) === parsedLevel)
    || [...levelRows].reverse().find((entry) => Number(entry?.level || 0) <= parsedLevel)
    || null;
  return (Array.isArray(levelRow?.effectBuckets) ? levelRow.effectBuckets : [])
    .flatMap((bucket) => (Array.isArray(bucket?.effects) ? bucket.effects : []));
};

const collectOfficerEquipmentMasteryEffects = (track = null, level = 0) => {
  const parsedLevel = Math.max(0, Math.floor(Number(level || 0)));
  if (!track || parsedLevel <= 0) return [];
  const levelRows = Array.isArray(track?.levelRows) ? track.levelRows : [];
  const levelRow = levelRows.find((entry) => Number(entry?.level || 0) === parsedLevel)
    || [...levelRows].reverse().find((entry) => Number(entry?.level || 0) <= parsedLevel)
    || null;
  return Array.isArray(levelRow?.effects) ? levelRow.effects : [];
};

const buildOfficerSkillCalculatedRows = ({
  officerId = 0,
  skillId = null,
  skillLevel = 0,
  officerLevel = 0,
  decimalStars = 0,
  effectLabelById = new Map(),
  applyMastery = true,
  includeNextDelta = false,
  previewWhenInactive = false,
}) => {
  const parsedSkillId = Number(skillId || 0);
  const parsedSkillLevel = Math.max(0, Math.floor(Number(skillLevel || 0)));
  const effectiveSkillLevel = parsedSkillLevel > 0 ? parsedSkillLevel : (previewWhenInactive ? 1 : 0);
  if (!Number.isFinite(parsedSkillId) || parsedSkillId <= 0 || effectiveSkillLevel <= 0) return [];
  const skillOption = getOfficerSkillOptionById(parsedSkillId);
  const preview = computeOfficerSkillPreview({
    officerId,
    skillId: parsedSkillId,
    skillLevel: effectiveSkillLevel,
    officerLevel,
    decimalStars,
    applyMastery,
  });
  if (!preview?.rows?.length) return [];
  if (includeNextDelta || previewWhenInactive) {
    const nextPreview = includeNextDelta
      ? computeOfficerSkillPreview({
        officerId,
        skillId: parsedSkillId,
        skillLevel: Math.min(Number(skillOption?.maxLevel || effectiveSkillLevel), effectiveSkillLevel + 1),
        officerLevel,
        decimalStars,
        applyMastery,
      })
      : null;
    return preview.rows.map((entry, index) => formatOfficerEffectValueWithDelta(
      entry?.effectId,
      entry?.techValue ?? entry?.scaledValue,
      nextPreview?.rows?.[index]?.techValue ?? nextPreview?.rows?.[index]?.scaledValue,
      effectLabelById,
      { label: skillOption?.label, description: skillOption?.description },
    ));
  }
  const renderedDescription = renderOfficerSkillDescription(preview, skillOption, effectLabelById);
  if (renderedDescription) return [renderedDescription];
  return preview.rows.map((entry, index) => formatCommanderEffectLine(
    entry?.effectId,
    entry?.techValue ?? entry?.scaledValue,
    effectLabelById,
    {
      label: skillOption?.label,
      description: skillOption?.description,
      placeholderIndex: index,
    },
  ));
};

const buildOfficerSkillRangeRows = ({
  officerId = 0,
  skillId = null,
  officerLevel = 0,
  decimalStars = 0,
  effectLabelById = new Map(),
  applyMastery = true,
}) => {
  const parsedSkillId = Number(skillId || 0);
  if (!Number.isFinite(parsedSkillId) || parsedSkillId <= 0) return [];
  const skillOption = getOfficerSkillOptionById(parsedSkillId);
  const maxLevel = Math.max(1, Math.floor(Number(skillOption?.maxLevel || 1)));
  const levelEntries = [1, maxLevel]
    .filter((level, index, rows) => rows.indexOf(level) === index)
    .flatMap((skillLevel) => {
      const preview = computeOfficerSkillPreview({
        officerId,
        skillId: parsedSkillId,
        skillLevel,
        officerLevel,
        decimalStars,
        applyMastery,
      });
      return (Array.isArray(preview?.rows) ? preview.rows : []).map((entry) => ({
        effectId: entry?.effectId,
        value: entry?.techValue ?? entry?.scaledValue,
        level: skillLevel,
        displayLevel: `Level ${skillLevel}`,
      }));
    });
  return buildEffectRangeLinesFromEntries(levelEntries, effectLabelById, {
    label: skillOption?.label,
    description: skillOption?.description,
  });
};

const buildOfficerEquipmentPreviewRows = ({
  slotMeta = null,
  selectedSlot = {},
  selectedLevel = 0,
  effectLabelById = new Map(),
}) => {
  if (!slotMeta) return [];
  const rows = [];
  const maxLevel = Math.max(0, Number(slotMeta?.maxLevel || 0));
  const currentLevel = Math.max(0, Math.floor(Number(selectedLevel || 0)));
  const previewLevel = currentLevel > 0 ? currentLevel : (maxLevel > 0 ? 1 : 0);
  if (previewLevel > 0) {
    const currentEffects = collectOfficerEquipmentLevelEffects(slotMeta, previewLevel);
    const nextEffects = previewLevel < maxLevel ? collectOfficerEquipmentLevelEffects(slotMeta, previewLevel + 1) : [];
    const nextById = sumEffectValuesById(nextEffects);
    const prefix = currentLevel > 0 ? `Level ${previewLevel}` : `Level ${previewLevel} preview`;
    currentEffects.forEach((effect) => {
      const effectId = Number(effect?.effectId || 0);
      rows.push(`${prefix} ${formatOfficerEffectValueWithDelta(
        effectId,
        effect?.value,
        nextById.get(effectId),
        effectLabelById,
        { label: slotMeta?.label },
      )}`);
    });
  }
  if (currentLevel <= 0) {
    buildEffectRangeLinesFromLevelRows(slotMeta?.levelRows || [], effectLabelById, {
      label: slotMeta?.label,
      description: slotMeta?.description,
    }).forEach((line) => rows.push(`Base range ${line}`));
  }
  const masteryById = new Map(
    (Array.isArray(selectedSlot?.masteryLevels) ? selectedSlot.masteryLevels : [])
      .map((entry) => [Number(entry?.id || 0), Math.max(0, Math.floor(Number(entry?.level || 0)))]),
  );
  getOfficerEquipmentMasteryTracksInUnlockOrder(slotMeta).forEach((track) => {
    const masteryId = Number(track?.masteryId || 0);
    const maxMasteryLevel = Math.max(0, Number(track?.maxLevel || 0));
    if (!masteryId || maxMasteryLevel <= 0) return;
    const currentMasteryLevel = masteryById.get(masteryId) || 0;
    const previewMasteryLevel = currentMasteryLevel > 0 ? currentMasteryLevel : 1;
    const currentEffects = collectOfficerEquipmentMasteryEffects(track, previewMasteryLevel);
    const nextEffects = previewMasteryLevel < maxMasteryLevel
      ? collectOfficerEquipmentMasteryEffects(track, previewMasteryLevel + 1)
      : [];
    const nextById = sumEffectValuesById(nextEffects);
    const prefix = currentMasteryLevel > 0
      ? `${track?.label || `Expertise #${masteryId}`} ${previewMasteryLevel}`
      : `${track?.label || `Expertise #${masteryId}`} preview`;
    currentEffects.forEach((effect) => {
      const effectId = Number(effect?.effectId || 0);
      rows.push(`${prefix} ${formatOfficerEffectValueWithDelta(
        effectId,
        effect?.value,
        nextById.get(effectId),
        effectLabelById,
        { label: track?.label, description: track?.description },
      )}`);
    });
    if (currentMasteryLevel <= 0) {
      buildEffectRangeLinesFromLevelRows(track?.levelRows || [], effectLabelById, {
        label: track?.label,
        description: track?.description,
      }).forEach((line) => rows.push(`${track?.label || `Expertise #${masteryId}`} range ${line}`));
    }
  });
  return rows;
};

const buildOfficerUnworldlyCalculatedRows = ({
  option = null,
  tier = null,
  level = 0,
}) => {
  const parsedLevel = Math.max(0, Math.floor(Number(level || 0)));
  if (!option || !tier || parsedLevel <= 0) return [];
  const values = Array.isArray(tier?.effectValues) ? tier.effectValues : [];
  if (!values.length) return [];
  const idx = Math.max(0, Math.min(values.length - 1, parsedLevel - 1));
  const value = Number(values[idx] || 0);
  if (!Number.isFinite(value)) return [];
  const effectIds = Array.isArray(option?.effectIds) ? option.effectIds : [];
  const effectId = Number(effectIds[0] || option?.effectId || option?.skillId || 0);
  return [`${String(option?.label || "Unworldly")}: ${formatCommanderEffectValue(value, effectId, {
    label: option?.label,
    description: option?.description,
  })}`];
};

const buildOfficerUnworldlyRangeRows = ({
  option = null,
  tier = null,
}) => {
  if (!option || !tier) return [];
  const values = Array.isArray(tier?.effectValues) ? tier.effectValues : [];
  if (!values.length) return [];
  const effectIds = Array.isArray(option?.effectIds) ? option.effectIds : [];
  const effectId = Number(effectIds[0] || option?.effectId || option?.skillId || 0);
  if (!Number.isFinite(effectId) || effectId <= 0) return [];
  return buildEffectRangeLinesFromEntries(
    [
      { effectId, value: Number(values[0] || 0), level: 1, displayLevel: "Level 1" },
      {
        effectId,
        value: Number(values[values.length - 1] || 0),
        level: values.length,
        displayLevel: `Level ${values.length}`,
      },
    ],
    new Map([[effectId, String(option?.label || "Unworldly")]]),
    { label: option?.label, description: option?.description },
  );
};

const formatOfficerPreviewSkillLine = ({
  prefix = "",
  preview = null,
  skillOption = null,
  effectLabelById = new Map(),
} = {}) => {
  if (!preview?.rows?.length) return "";
  const skillLabel = String(preview?.label || skillOption?.label || "Skill").trim();
  const renderedDescription = renderOfficerSkillDescription(preview, skillOption, effectLabelById);
  const labelPrefix = [prefix, skillLabel].map((entry) => String(entry || "").trim()).filter(Boolean).join(" ");
  if (renderedDescription) {
    return labelPrefix ? `${labelPrefix}: ${renderedDescription}` : renderedDescription;
  }
  const valueText = preview.rows.map((entry) => entry.displayValue).join(", ");
  return labelPrefix ? `${labelPrefix}: ${valueText}` : valueText;
};

const buildOfficerPreviewRows = (row = {}, equipmentEffectsInput = null, effectLabelById = new Map()) => {
  const officerId = Number(row?.officerId || 0);
  if (!Number.isFinite(officerId) || officerId <= 0) return [];
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer) return [];
  const rows = [];

  if (officer?.baseSkillId) {
    const skillOption = getOfficerSkillOptionById(officer.baseSkillId);
    const preview = computeOfficerSkillPreview({
      officerId,
      skillId: officer.baseSkillId,
      skillLevel: 1,
      officerLevel: Number(row?.level || 0),
      decimalStars: Number(row?.stars || 0),
    });
    if (preview?.rows?.length) {
      rows.push(formatOfficerPreviewSkillLine({
        preview,
        skillOption,
        effectLabelById,
      }));
    }
  }

  const skillSlots = Array.isArray(row?.skills) ? row.skills : [];
  skillSlots.forEach((slot, idx) => {
    const skillId = Number(slot?.skillId || 0);
    if (!Number.isFinite(skillId) || skillId <= 0) return;
    const skillLevel = toOfficerSkillLevelDisplay(slot?.level || 0, true);
    const skillOption = getOfficerSkillOptionById(skillId);
    const preview = computeOfficerSkillPreview({
      officerId,
      skillId,
      skillLevel,
      officerLevel: Number(row?.level || 0),
      decimalStars: Number(row?.stars || 0),
    });
    if (preview?.rows?.length) {
      rows.push(formatOfficerPreviewSkillLine({
        prefix: `Skill ${idx + 1}`,
        preview,
        skillOption,
        effectLabelById,
      }));
    }
  });

  const unworldlySlots = Array.isArray(row?.unworldly) ? row.unworldly : [];
  unworldlySlots.forEach((slot, idx) => {
    const skillId = Number(slot?.skillId || 0);
    const level = Number(slot?.level || 0);
    if (!Number.isFinite(skillId) || skillId <= 0 || level <= 0) return;
    const resolved = resolveUnworldlyTierBySkill(officerId, skillId, slot?.tierId);
    if (!resolved?.tier) return;
    const tierValues = Array.isArray(resolved?.tier?.effectValues) ? resolved.tier.effectValues : [];
    const levelIndex = Math.max(0, Math.min(tierValues.length - 1, level - 1));
    const value = Number(tierValues[levelIndex] || 0);
    const effectIds = Array.isArray(resolved?.option?.effectIds) ? resolved.option.effectIds : [];
    const effectId = Number(effectIds[0] || resolved?.option?.effectId || resolved?.option?.skillId || skillId);
    rows.push(`Unworldly ${idx + 1} ${resolved.option?.label || `Skill ${skillId}`}: ${formatCommanderEffectValue(value, effectId, {
      label: resolved?.option?.label,
      description: resolved?.option?.description,
    })}`);
  });

  const equipmentEffects = Array.isArray(equipmentEffectsInput)
    ? equipmentEffectsInput
    : getOfficerEquipmentEffectContributions(officerId, row?.equipment, Number(row?.stars || 0));
  if (Array.isArray(equipmentEffects) && equipmentEffects.length > 0) {
    const groupedBySlot = new Map();
    equipmentEffects.forEach((entry) => {
      const slotIndex = Number(entry?.slotIndex || 0);
      if (!groupedBySlot.has(slotIndex)) groupedBySlot.set(slotIndex, []);
      groupedBySlot.get(slotIndex).push(entry);
    });
    Array.from(groupedBySlot.entries())
      .sort((left, right) => left[0] - right[0])
      .forEach(([slotIndex, effects]) => {
        const first = effects[0];
        const label = String(first?.equipLabel || `Equip ${slotIndex}`);
        rows.push(`Equip ${slotIndex} ${label}: ${effects.length} buffs active`);
      });
  }

  return rows.slice(0, 4);
};

const getGroupRowsByKey = (sectionCatalog, groupKey) => {
  const groups = Array.isArray(sectionCatalog?.groups) ? sectionCatalog.groups : [];
  const match = groups.find((group) => String(group?.key || "") === String(groupKey));
  return Array.isArray(match?.rows) ? match.rows : [];
};

const getOptionMaxLevel = (option, fallback = 20) => {
  const maxLevel = Number(option?.maxLevel);
  if (Number.isFinite(maxLevel) && maxLevel > 0) return Math.trunc(maxLevel);
  const maxStarLevel = Number(option?.maxStarLevel);
  if (Number.isFinite(maxStarLevel) && maxStarLevel > 0) return Math.trunc(maxStarLevel);
  return fallback;
};

const getSelectedEntryById = (rows, id) => {
  const parsedId = Number(id);
  if (!Array.isArray(rows) || !Number.isFinite(parsedId)) return null;
  return rows.find((row) => Number(row?.id || 0) === Math.trunc(parsedId)) || null;
};

const getOptionId = (option = {}) => {
  const parsed = normalizeSkillId(option?.id ?? option?.gid);
  return parsed || null;
};

const EQUIPMENT_QUALITY_LABELS = Object.freeze({
  0: "Grey",
  1: "Green",
  2: "Blue",
  3: "Purple",
  4: "Gold",
});
const EQUIPMENT_QUALITY_NOT_ENABLED = -1;

const formatEquipmentQualityLabel = (qualityRank) => {
  const parsedRaw = Math.floor(Number(qualityRank));
  if (!Number.isFinite(parsedRaw) || parsedRaw < 0) return "Not enabled";
  const parsed = Math.max(0, Math.min(4, parsedRaw));
  return EQUIPMENT_QUALITY_LABELS[parsed] || `Rank ${parsed}`;
};

const buildEffectLinesFromRows = (effects = [], effectLabelById = new Map(), context = {}) => {
  if (String(context?.description || "").includes("$")) {
    const templateLines = formatCommanderEffectLinesForEffects(effects, effectLabelById, context);
    if (templateLines.length) return templateLines;
  }
  const totalsByEffectId = new Map();
  (Array.isArray(effects) ? effects : []).forEach((entry) => {
    const effectId = Number(entry?.effectId || 0);
    const value = Number(entry?.value || 0);
    if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value) || value === 0) return;
    totalsByEffectId.set(effectId, (totalsByEffectId.get(effectId) || 0) + value);
  });
  return Array.from(totalsByEffectId.entries())
    .sort((left, right) => left[0] - right[0])
    .map(([effectId, value], index) => formatCommanderEffectLine(effectId, value, effectLabelById, {
      ...context,
      placeholderIndex: index,
    }));
};

const CITY_BUILDING_CATEGORY_ORDER = Object.freeze(["core", "military", "resources", "support", "other"]);
const CITY_BUILDING_CATEGORY_LABELS = Object.freeze({
  core: "Core",
  military: "Military",
  resources: "Resources",
  support: "Support",
  other: "Other",
});

const CITY_BUILDING_STATUS_LABELS = Object.freeze({
  0: "Idle",
  1: "Building",
  2: "Working",
  3: "Ready",
  4: "Upgrading",
});

const getCityBuildingCategory = (building = {}) => {
  const label = String(building?.label || "").toLowerCase();
  if (/(main hall|city wall|garage|hospital|dispatch|academy|depot|city hall|embassy|radar)/.test(label)) return "core";
  if (/(camp|factory|workshop|training|barrack|garage)/.test(label)) return "military";
  if (/(farm|oil|iron|rare earth|field|mine|refinery|resource)/.test(label)) return "resources";
  if (/(warehouse|depot|biochemical|treatment|institute|tower|exchange|market)/.test(label)) return "support";
  return "other";
};

const getCityBuildingStatusLabel = (status) => {
  const normalized = Math.max(0, Math.floor(Number(status || 0)));
  return CITY_BUILDING_STATUS_LABELS[normalized] || `Status ${normalized}`;
};

const getCityBuildingLevelRow = (buildingOption = {}, level = 0) => {
  const rows = Array.isArray(buildingOption?.levelRows) ? buildingOption.levelRows : [];
  const normalizedLevel = Math.max(0, Math.floor(Number(level || 0)));
  return rows.find((row) => Number(row?.level || 0) === normalizedLevel)
    || [...rows].reverse().find((row) => Number(row?.level || 0) <= normalizedLevel)
    || null;
};

const buildCityBuildingEditorRows = ({
  cityData = {},
  buildingOptions = [],
  effectLabelById = new Map(),
}) => {
  const buildingById = new Map(
    (Array.isArray(buildingOptions) ? buildingOptions : [])
      .map((row) => [normalizeSkillId(row?.id), row])
      .filter(([id]) => Boolean(id)),
  );
  const buildings = Array.isArray(cityData?.buildings) ? cityData.buildings : [];
  return buildings
    .map((building, sourceIndex) => {
      const gid = normalizeSkillId(building?.gid);
      if (!gid) return null;
      const option = buildingById.get(gid) || {};
      const level = Math.max(0, Math.floor(Number(building?.level || 0)));
      const hasKnownLevel = building?.level !== null && building?.level !== undefined;
      const selectedLevelRow = getCityBuildingLevelRow(option, level);
      const label = String(option?.label || `Building #${gid}`);
      const maxInternalLevel = Math.max(level, Math.floor(Number(option?.maxLevel || level || 0)));
      return {
        ...building,
        gid,
        sourceIndex,
        rowKey: `${Number(building?.id || 0) || gid}:${Number(building?.position || 0)}:${sourceIndex}`,
        label,
        description: String(option?.description || ""),
        category: getCityBuildingCategory({ ...option, label }),
        level,
        displayLevel: toCityBuildingLevelDisplay(level, hasKnownLevel),
        maxLevel: maxInternalLevel,
        maxDisplayLevel: toCityBuildingLevelDisplay(maxInternalLevel, true),
        selectedLevelRow,
        effectLines: buildEffectLinesFromRows(selectedLevelRow?.effects || [], effectLabelById, {
          label,
          description: option?.description,
        }),
        power: Math.max(0, Math.floor(Number(selectedLevelRow?.power || 0))),
      };
    })
    .filter(Boolean)
    .sort((left, right) => (
      CITY_BUILDING_CATEGORY_ORDER.indexOf(left.category) - CITY_BUILDING_CATEGORY_ORDER.indexOf(right.category)
      || String(left.label).localeCompare(String(right.label))
      || Number(left.position || 0) - Number(right.position || 0)
      || Number(left.sourceIndex || 0) - Number(right.sourceIndex || 0)
    ));
};

const getSectionCatalogGroupRows = (sectionCatalog = {}, groupKey = "") => {
  const groups = Array.isArray(sectionCatalog?.groups) ? sectionCatalog.groups : [];
  const group = groups.find((entry) => String(entry?.key || "") === String(groupKey || ""));
  return Array.isArray(group?.rows) ? group.rows : [];
};

const getBiochemicalPetLabel = (petId) => {
  const normalized = normalizeSkillId(petId);
  if (normalized === 201326762) return "Zombz";
  if (normalized === 201326792) return "Infected";
  return normalized ? `Pet #${normalized}` : "Unassigned";
};

const getBiochemicalGeneGroupLabel = (groupRows = [], fallbackGroup = 0) => {
  const showName = String((groupRows.find((row) => String(row?.showName || "").trim()) || {})?.showName || "").trim();
  if (showName) return `Gene ${showName}`;
  const parsedGroup = Math.max(0, Math.floor(Number(fallbackGroup || 0)));
  if (!parsedGroup) return "Gene Group";
  return `Gene Group ${parsedGroup}`;
};

const resolveBiochemicalPetLevelRow = (rows = [], petId, level) => {
  const normalizedPetId = normalizeSkillId(petId);
  const normalizedLevel = Math.max(0, Math.floor(Number(level || 0)));
  if (!normalizedPetId) return null;
  const petRows = (Array.isArray(rows) ? rows : [])
    .filter((row) => normalizeSkillId(row?.petId) === normalizedPetId)
    .sort((left, right) => Number(left?.level || 0) - Number(right?.level || 0));
  return [...petRows].reverse().find((row) => Number(row?.level || 0) <= normalizedLevel) || null;
};

const toBiochemicalPetLevelDisplay = (level) => Math.max(1, Math.floor(Number(level || 0)) + 1);

const fromBiochemicalPetLevelDisplay = (level) => Math.max(0, Math.floor(Number(level || 1)) - 1);

const resolveBiochemicalGeneTechLevelFromPoints = (geneRows = [], earnedPoints = 0) => {
  const points = Math.max(0, Math.floor(Number(earnedPoints || 0)));
  const sortedRows = (Array.isArray(geneRows) ? geneRows : [])
    .filter((row) => normalizeSkillId(row?.id))
    .sort((left, right) => Number(left?.sortIndex || 0) - Number(right?.sortIndex || 0));
  if (points > 0 && sortedRows.length > 0) {
    const row = sortedRows.find((entry) => Number(entry?.sortIndex || 0) === points)
      || [...sortedRows].reverse().find((entry) => Number(entry?.sortIndex || 0) <= points)
      || null;
    return normalizeSkillId(row?.id) || 0;
  }
  return 0;
};

const BIOCHEMICAL_GROWTH_REWARD_TECH_POINTS = 3;

const getBiochemicalPetEarnedTechPoints = (selectedIntimacyRow = {}, pet = {}) => {
  const basePoints = Math.max(0, Math.floor(Number(selectedIntimacyRow?.totalTechPoints || 0)));
  const unclaimedGrowthRewardCount = Array.isArray(pet?.growthRewards)
    ? pet.growthRewards.filter((reward) => Math.floor(Number(reward?.status ?? 0)) < 1).length
    : 0;
  return Math.max(0, basePoints - (unclaimedGrowthRewardCount * BIOCHEMICAL_GROWTH_REWARD_TECH_POINTS));
};

const formatAcademyTechEffectLine = (row = {}, selectedLevel = 0, effectLabelById = new Map()) => {
  const effectId = Number(row?.effectId || 0);
  const perLevel = Number(row?.effectValue || 0);
  const displayLevel = Math.max(0, Math.floor(Number(selectedLevel || 0)));
  if (displayLevel <= 0 || !Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(perLevel) || perLevel === 0) {
    return "";
  }
  const total = perLevel * displayLevel;
  return formatCommanderEffectLine(effectId, total, effectLabelById, {
    label: row?.label,
    description: row?.description,
  });
};

const COMMANDER_DEVELOPMENT_SKILL_ORDER = Object.freeze([
  83886172,
  83886173,
  83886348,
  83886349,
  83886186,
  83886354,
  83886350,
  83886351,
  83886344,
  83886345,
  83886360,
  83886361,
  83886362,
]);

const getCommanderSkillDisplaySort = (row = {}) => {
  const id = normalizeSkillId(row?.id);
  const category = Number(row?.category || 0);
  if (category === 1) {
    const index = COMMANDER_DEVELOPMENT_SKILL_ORDER.indexOf(id);
    if (index >= 0) return index;
    return 1000 + id;
  }
  return id;
};

const buildEquipmentEditorRows = (
  equipOptionRows = [],
  extensionTypeRows = [],
  equipmentData = {},
  effectLabelById = new Map(),
) => {
  const equipRows = Array.isArray(equipOptionRows) ? equipOptionRows : [];
  const equipBySlot = new Map();
  const equipById = new Map();
  const extensionTypeById = new Map(
    (Array.isArray(extensionTypeRows) ? extensionTypeRows : [])
      .map((row) => [Math.max(0, Math.floor(Number(row?.typeId || 0))), row])
      .filter(([typeId]) => typeId > 0),
  );

  equipRows.forEach((row) => {
    const id = getOptionId(row);
    const slot = Math.max(0, Math.floor(Number(row?.slot || 0)));
    if (!id || slot <= 0) return;
    const levelIndex = Math.max(0, Math.floor(Number(
      row?.levelIndex ?? row?.displayLevel ?? 0,
    )));
    const next = { ...row, id, slot, levelIndex };
    equipById.set(id, next);
    if (!equipBySlot.has(slot)) equipBySlot.set(slot, []);
    equipBySlot.get(slot).push(next);
  });

  const selectedBaseRows = normalizeIdLevelEntries(equipmentData?.equipOptions);
  const selectedBaseBySlot = new Map();
  selectedBaseRows.forEach((entry) => {
    const id = Number(entry?.id || 0);
    const qualityRank = Math.max(0, Math.min(4, Math.floor(Number(entry?.level || 0))));
    const base = equipById.get(id);
    const slot = Math.max(0, Math.floor(Number(base?.slot || 0)));
    if (!base || slot <= 0) return;
    selectedBaseBySlot.set(slot, { id, qualityRank });
  });

  const selectedExtensions = Array.isArray(equipmentData?.extensionSlots)
    ? equipmentData.extensionSlots
    : [];
  const extensionByKey = new Map();
  selectedExtensions.forEach((entry) => {
    const slot = Math.max(0, Math.floor(Number(entry?.slot || 0)));
    const index = Math.max(0, Math.floor(Number(entry?.index || 0)));
    if (!slot || !index) return;
    extensionByKey.set(`${slot}:${index}`, {
      quality: Math.max(0, Math.min(4, Math.floor(Number(entry?.quality || 0)))),
      level: Math.max(0, Math.min(5, Math.floor(Number(entry?.level || 0)))),
    });
  });

  const slotKeys = [...new Set([...equipBySlot.keys()])]
    .filter((slot) => Number.isFinite(Number(slot)) && Number(slot) > 0)
    .map((slot) => Math.trunc(Number(slot)))
    .sort((left, right) => left - right);

  const rows = slotKeys.map((slot) => {
    const baseOptionsRaw = Array.isArray(equipBySlot.get(slot)) ? equipBySlot.get(slot) : [];
    const baseOptionsSorted = [...baseOptionsRaw].sort((left, right) => (
      Number(left?.levelIndex || 0) - Number(right?.levelIndex || 0)
      || Number(left?.id || 0) - Number(right?.id || 0)
    ));
    const baseOptions = baseOptionsSorted.map((row) => ({ ...row }));
    const selectedBase = selectedBaseBySlot.get(slot) || null;
    const selectedLevelOption = (
      baseOptions.find((option) => Number(option?.id || 0) === Number(selectedBase?.id || 0))
      || baseOptions[0]
      || null
    );
    const selectedBaseId = Number(selectedLevelOption?.id || 0) || null;
    const selectedLevelIndex = Math.max(0, Math.floor(Number(selectedLevelOption?.levelIndex || 0)));
    const selectedQualityRank = selectedBase
      ? Math.max(0, Math.min(4, Math.floor(Number(selectedBase?.qualityRank || 0))))
      : EQUIPMENT_QUALITY_NOT_ENABLED;
    const selectedBaseQualityRow = selectedQualityRank < 0
      ? null
      : (Array.isArray(selectedLevelOption?.qualityRows)
        ? selectedLevelOption.qualityRows.find((entry) => Math.max(0, Math.floor(Number(entry?.rank || 0))) === selectedQualityRank)
          || [...selectedLevelOption.qualityRows].reverse().find((entry) => (
            Math.max(0, Math.floor(Number(entry?.rank || 0))) <= selectedQualityRank
          ))
          || selectedLevelOption.qualityRows[0]
        : null);
    const baseBuffLines = buildEffectLinesFromRows(selectedBaseQualityRow?.effects, effectLabelById, {
      label: formatSectionRowLabel(selectedLevelOption || { slot }),
      description: selectedLevelOption?.description,
    });
    const baseEffectRangeLines = buildEffectRangeLinesFromEntries(
      baseOptions.flatMap((option) => (
        (Array.isArray(option?.qualityRows) ? option.qualityRows : []).flatMap((qualityRow) => (
          normalizeEffectEntriesForDisplay(qualityRow?.effects).map((effect) => ({
            ...effect,
            level: Number(option?.levelIndex || option?.displayLevel || 0) * 10 + Number(qualityRow?.rank || 0),
            displayLevel: `Level ${Math.max(1, Math.floor(Number(option?.displayLevel || option?.levelIndex || 1)))} ${formatEquipmentQualityLabel(qualityRow?.rank)}`,
          }))
        ))
      )),
      effectLabelById,
      {
        label: formatSectionRowLabel(selectedLevelOption || { slot }),
        description: selectedLevelOption?.description,
      },
    );
    const peripheralTypes = Array.isArray(selectedLevelOption?.peripheralTypes)
      ? selectedLevelOption.peripheralTypes
      : [0, 0, 0];
    const unlockLevelByIndex = new Map();
    Array.from({ length: 3 }).forEach((_, idx) => {
      const index = idx + 1;
      const unlockedAt = baseOptions.find((option) => (
        Math.max(0, Math.floor(Number(option?.peripheralTypes?.[idx] || 0))) > 0
      ));
      unlockLevelByIndex.set(index, Math.max(1, Math.floor(Number(unlockedAt?.displayLevel || 1))));
    });
    const extensionSlots = Array.from({ length: 3 }).map((_, idx) => {
      const index = idx + 1;
      const typeId = Math.max(0, Math.floor(Number(peripheralTypes[idx] || 0)));
      const typeRow = extensionTypeById.get(typeId) || null;
      const persisted = extensionByKey.get(`${slot}:${index}`) || null;
      const rankOptionsRaw = Array.isArray(typeRow?.ranks) ? typeRow.ranks : [];
      const rankOptions = rankOptionsRaw
        .map((entry) => Math.max(0, Math.min(4, Math.floor(Number(entry || 0)))))
        .filter((entry, rankIndex, arr) => arr.indexOf(entry) === rankIndex)
        .sort((left, right) => left - right);
      const minRank = Number.isFinite(Number(typeRow?.minRank))
        ? Math.max(0, Math.min(4, Math.floor(Number(typeRow?.minRank || 0))))
        : (rankOptions[0] ?? 0);
      const maxRank = Number.isFinite(Number(typeRow?.maxRank))
        ? Math.max(minRank, Math.min(4, Math.floor(Number(typeRow?.maxRank || 4))))
        : (rankOptions[rankOptions.length - 1] ?? 4);
      const maxLevel = Number.isFinite(Number(typeRow?.maxLevel))
        ? Math.max(0, Math.min(5, Math.floor(Number(typeRow?.maxLevel || 5))))
        : 5;
      const selectedQuality = persisted
        ? Math.max(0, Math.min(4, Math.floor(Number(persisted?.quality || 0))))
        : EQUIPMENT_QUALITY_NOT_ENABLED;
      const selectedRank = selectedQuality >= 0
        ? (rankOptions.includes(selectedQuality) ? selectedQuality : (rankOptions[0] ?? 0))
        : null;
      const rankRows = Array.isArray(typeRow?.rankRows) ? typeRow.rankRows : [];
      const selectedRankRow = selectedRank === null
        ? null
        : rankRows.find((row) => Math.max(0, Math.floor(Number(row?.rank || 0))) === selectedRank)
          || [...rankRows].reverse().find((row) => Math.max(0, Math.floor(Number(row?.rank || 0))) <= selectedRank)
          || rankRows[0]
          || null;
      const rankLabel = String(selectedRankRow?.label || typeRow?.label || (typeId > 0 ? `Extension Type ${typeId}` : ""));
      const selectedLevel = persisted
        ? Math.max(0, Math.min(maxLevel, Math.floor(Number(persisted?.level || 0))))
        : 0;
      const levelRows = Array.isArray(selectedRankRow?.levelRows) ? selectedRankRow.levelRows : [];
      const selectedLevelRow = levelRows.find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === selectedLevel)
        || [...levelRows].reverse().find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) <= selectedLevel)
        || levelRows[0]
        || null;
      const buffLines = buildEffectLinesFromRows(selectedLevelRow?.effects, effectLabelById, {
        label: rankLabel,
        description: typeRow?.description,
      });
      const effectRangeLines = buildEffectRangeLinesFromLevelRows(levelRows, effectLabelById, {
        label: rankLabel,
        description: typeRow?.description,
      });
      const qualityOptions = [
        EQUIPMENT_QUALITY_NOT_ENABLED,
        ...(rankOptions.length ? rankOptions : [0, 1, 2, 3, 4]),
      ];
      return {
        index,
        typeId,
        typeLabel: typeId > 0 ? (String(typeRow?.label || `Extension Type ${typeId}`)) : "",
        selectedRankLabel: rankLabel,
        unlocked: typeId > 0,
        enabled: typeId > 0 && selectedQuality >= 0,
        unlockDisplayLevel: Math.max(1, Math.floor(Number(unlockLevelByIndex.get(index) || 1))),
        rankOptions,
        qualityOptions,
        minRank,
        maxRank,
        maxLevel,
        quality: selectedQuality,
        level: selectedLevel,
        buffLines,
        effectRangeLines,
      };
    });
    const activeBuffLines = [
      ...baseBuffLines,
      ...extensionSlots.flatMap((entry) => (
        entry?.enabled && Array.isArray(entry?.buffLines)
          ? entry.buffLines.map((line) => `${entry.selectedRankLabel || entry.typeLabel}: ${line}`)
          : []
      )),
    ];
    const qualityOptions = [EQUIPMENT_QUALITY_NOT_ENABLED, ...Array.from({ length: 5 }).map((_, rank) => rank)].map((rank) => ({
      rank,
      label: formatEquipmentQualityLabel(rank),
    }));
    return {
      slot,
      label: formatSectionRowLabel(baseOptions[0] || { slot }),
      baseOptions,
      selectedLevelOption,
      selectedBaseId,
      selectedLevelIndex,
      selectedQualityRank,
      baseBuffLines,
      baseEffectRangeLines,
      activeBuffLines,
      qualityOptions,
      extensionSlots,
    };
  });

  return { rows, extensionColumnCount: 3 };
};

const resolveWarplaneOptionMaxLevel = (option = null, fallback = 20, skillLevelCap = null) => {
  const rawMax = getOptionMaxLevel(option || {}, fallback);
  const normalizedMax = Math.max(0, Math.floor(Number(rawMax || 0)));
  if (!Number.isFinite(skillLevelCap)) return normalizedMax;
  const normalizedSkillCap = Math.max(0, Math.floor(Number(skillLevelCap || 0)));
  if (normalizedMax <= 0) return 0;
  return Math.max(0, Math.min(normalizedMax, normalizedSkillCap));
};

const resolveWarplaneOptionEffectsForLevel = (option = null, selectedLevel = 0) => {
  if (!option || typeof option !== "object") return [];
  const levelRows = Array.isArray(option?.levelRows) ? option.levelRows : [];
  const normalizedLevel = Math.max(0, Math.floor(Number(selectedLevel || 0)));
  if (levelRows.length) {
    const row = levelRows.find((entry) => Math.max(0, Math.floor(Number(entry?.level || 0))) === normalizedLevel)
      || [...levelRows].reverse().find((entry) => Math.max(0, Math.floor(Number(entry?.level || 0))) <= normalizedLevel)
      || levelRows[0];
    return Array.isArray(row?.effects) ? row.effects : [];
  }
  const baseEffects = Array.isArray(option?.firstLevelEffects) ? option.firstLevelEffects : [];
  if (!baseEffects.length || normalizedLevel <= 0) return [];
  const multiplier = Math.max(1, normalizedLevel);
  return baseEffects.map((entry) => ({
    effectId: Number(entry?.effectId || 0),
    value: Number(entry?.value || 0) * multiplier,
  }));
};

const resolveWarplaneOptionDescriptionValuesForLevel = (option = null, selectedLevel = 0) => {
  if (!option || typeof option !== "object") return [];
  const normalizedLevel = Math.max(0, Math.floor(Number(selectedLevel || 0)));
  const levelRows = Array.isArray(option?.levelRows) ? option.levelRows : [];
  if (levelRows.length) {
    const row = levelRows.find((entry) => Math.max(0, Math.floor(Number(entry?.level || 0))) === normalizedLevel)
      || [...levelRows].reverse().find((entry) => Math.max(0, Math.floor(Number(entry?.level || 0))) <= normalizedLevel)
      || levelRows[0];
    const rowValues = Array.isArray(row?.descriptionValues) ? row.descriptionValues : [];
    if (rowValues.length > 0) return rowValues;
  }
  const firstValues = Array.isArray(option?.firstDescriptionValues) ? option.firstDescriptionValues : [];
  if (firstValues.length > 0) return firstValues;
  const effects = resolveWarplaneOptionEffectsForLevel(option, normalizedLevel);
  return effects.map((entry) => Number(entry?.value || 0));
};

const formatWarplaneDescriptionTemplateValue = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  return `${formatOfficerSkillTemplateValue(parsed / 10)}%`;
};

const renderWarplaneDescriptionForLevel = (option = null, selectedLevel = 0) => {
  const template = String(option?.description || "").trim();
  if (!template) return "";
  const descriptionValues = resolveWarplaneOptionDescriptionValuesForLevel(option, selectedLevel);
  const formattedDescriptionValues = descriptionValues.map((entry) => formatWarplaneDescriptionTemplateValue(entry));
  const effects = resolveWarplaneOptionEffectsForLevel(option, selectedLevel);
  const formattedValues = effects.map((entry) => (
    formatCommanderEffectValue(Number(entry?.value || 0), Number(entry?.effectId || 0), {
      label: option?.label,
      description: option?.description,
    })
  ));
  if (!template.includes("$")) return template;
  return template.replace(/\$(\d+)/g, (_match, rawIndex) => {
    const idx = Math.max(0, Math.floor(Number(rawIndex || 0)));
    if (formattedDescriptionValues.length > 0) {
      return formattedDescriptionValues[idx] || formattedDescriptionValues[0] || "0";
    }
    return formattedValues[idx] || formattedValues[0] || "0";
  });
};

const parseGameColorTaggedText = (text = "") => {
  const raw = String(text || "");
  if (!raw) return [];
  const rows = [];
  const colorTagRegex = /\[([0-9A-Fa-f]{6})\]([\s\S]*?)\[-\]/g;
  let lastIndex = 0;
  let match = colorTagRegex.exec(raw);
  while (match) {
    if (match.index > lastIndex) {
      rows.push({ text: raw.slice(lastIndex, match.index), color: null });
    }
    rows.push({ text: match[2], color: `#${String(match[1] || "").toLowerCase()}` });
    lastIndex = colorTagRegex.lastIndex;
    match = colorTagRegex.exec(raw);
  }
  if (lastIndex < raw.length) {
    rows.push({ text: raw.slice(lastIndex), color: null });
  }
  return rows.length > 0 ? rows : [{ text: raw, color: null }];
};

const renderGameColorTaggedText = (text = "", keyPrefix = "desc") => (
  parseGameColorTaggedText(text).map((entry, index) => (
    entry?.color ? (
      <span key={`${keyPrefix}-${index}`} style={{ color: entry.color }}>
        {entry.text}
      </span>
    ) : (
      <React.Fragment key={`${keyPrefix}-${index}`}>{entry?.text || ""}</React.Fragment>
    )
  ))
);

const buildWarplaneOptionRows = ({
  fieldKey = WARPLANE_SKILL_GROUP_KEYS.skills,
  options = [],
  selectedRows = [],
  effectLabelById = new Map(),
  fallbackMaxLevel = 20,
  skillLevelCap = null,
}) => {
  const normalizedRows = normalizeIdLevelEntries(selectedRows);
  const selectedById = new Map(
    normalizedRows.map((entry) => [Number(entry?.id || 0), Math.max(0, Math.floor(Number(entry?.level || 0)))]),
  );
  return (Array.isArray(options) ? options : [])
    .map((option, index) => {
      const id = getOptionId(option);
      if (!id) return null;
      const hasSelectedLevel = selectedById.has(id);
      const selectedRawLevel = hasSelectedLevel
        ? Math.max(0, Math.floor(Number(selectedById.get(id) || 0)))
        : -1;
      const configuredMaxLevel = resolveWarplaneOptionMaxLevel(option, fallbackMaxLevel, skillLevelCap);
      const maxLevel = hasSelectedLevel
        ? Math.max(configuredMaxLevel, selectedRawLevel)
        : configuredMaxLevel;
      const selectedLevel = selectedRawLevel;
      const enabled = selectedLevel >= 0;
      const activeEffects = enabled ? resolveWarplaneOptionEffectsForLevel(option, selectedLevel) : [];
      const label = formatSectionRowLabel(option);
      const effectLines = buildEffectLinesFromRows(activeEffects, effectLabelById, {
        label,
        description: option?.description,
      });
      const effectRangeLines = buildEffectRangeLinesForOption(option, effectLabelById, {
        label,
        description: option?.description,
      });
      const description = enabled ? renderWarplaneDescriptionForLevel(option, selectedLevel) : "";
      return {
        id,
        fieldKey,
        order: index,
        label,
        maxLevel,
        selectedLevel,
        enabled,
        option,
        description,
        effectLines,
        effectRangeLines,
      };
    })
    .filter(Boolean)
    .sort((left, right) => (
      Math.floor(Number(left?.option?.displayOrder ?? left.order ?? 0))
      - Math.floor(Number(right?.option?.displayOrder ?? right.order ?? 0))
      || left.label.localeCompare(right.label)
      || left.id - right.id
    ));
};

const buildWarplaneEquipmentSkillGroups = ({ equipRows = [], equipSkillRows = [] } = {}) => {
  const equipById = new Map(
    (Array.isArray(equipRows) ? equipRows : [])
      .map((row) => [normalizeSkillId(row?.id), row])
      .filter(([id]) => id > 0),
  );
  const skillById = new Map(
    (Array.isArray(equipSkillRows) ? equipSkillRows : [])
      .map((row) => [normalizeSkillId(row?.id), row])
      .filter(([id]) => id > 0),
  );
  const orderedEquipmentIds = [
    ...(Array.isArray(equipRows) ? equipRows : [])
      .map((row) => normalizeSkillId(row?.id))
      .filter((id) => id > 0),
  ].filter((id, index, rows) => rows.indexOf(id) === index);

  const groups = orderedEquipmentIds
    .map((equipmentId, index) => {
      const equipmentRow = equipById.get(equipmentId);
      if (!equipmentRow) return null;
      const skillIds = Array.isArray(equipmentRow?.option?.unlockedSkillIds)
        ? equipmentRow.option.unlockedSkillIds.map((skillId) => normalizeSkillId(skillId)).filter((skillId) => skillId > 0)
        : [];
      const skillRows = skillIds
        .map((skillId) => skillById.get(skillId))
        .filter(Boolean);
      return {
        id: equipmentId,
        order: index,
        label: equipmentRow.label,
        equipmentRow,
        skillRows,
      };
    })
    .filter(Boolean);

  const groupedSkillIds = new Set(groups.flatMap((group) => group.skillRows.map((row) => normalizeSkillId(row?.id))));
  const ungroupedSkillRows = (Array.isArray(equipSkillRows) ? equipSkillRows : [])
    .filter((row) => {
      const id = normalizeSkillId(row?.id);
      return id > 0 && !groupedSkillIds.has(id);
    })
    .sort((left, right) => (
      String(left?.label || "").localeCompare(String(right?.label || ""))
      || normalizeSkillId(left?.id) - normalizeSkillId(right?.id)
    ));

  if (ungroupedSkillRows.length > 0) {
    groups.push({
      id: "other",
      order: groups.length,
      label: "Other Equipment Skills",
      equipmentRow: null,
      skillRows: ungroupedSkillRows,
    });
  }

  return groups;
};

const getWarplaneStageRowsWithMeta = (advancementRows = []) => (
  [...(Array.isArray(advancementRows) ? advancementRows : [])]
    .sort((left, right) => (
      Math.max(0, Math.floor(Number(left?.stage || 0)))
      - Math.max(0, Math.floor(Number(right?.stage || 0)))
    ))
    .map((row) => {
      const stage = Math.max(0, Math.floor(Number(row?.stage || 0)));
      const stageMeta = getWarplaneStageMeta(stage);
      const progressionLength = Array.isArray(row?.powerProgression) ? row.powerProgression.length : 0;
      return {
        ...row,
        stage,
        stageLabel: String(row?.stageLabel || stageMeta.stageLabel),
        stageColor: String(row?.stageColor || stageMeta.stageColor),
        maxProgressStep: Math.max(0, progressionLength > 0 ? progressionLength - 1 : 0),
      };
    })
);

const getUnlockedWarplaneStageSkillIds = (advancementRows = [], state = {}) => {
  const selectedStage = Math.max(0, Math.floor(Number(state?.advancementStage || 0)));
  const selectedStep = Math.max(0, Math.floor(Number(
    state?.advancementStep ?? state?.advancementLevel ?? state?.progressStep ?? 0,
  )));
  const skillIds = [];
  getWarplaneStageRowsWithMeta(advancementRows).forEach((stageRow) => {
    const rowStage = Math.max(0, Math.floor(Number(stageRow?.stage || 0)));
    const stageUnlocked = (unlockStep = 0) => (
      selectedStage > rowStage
      || (selectedStage === rowStage && selectedStep >= Math.max(0, Math.floor(Number(unlockStep || 0))))
    );
    const activeUnlockStep = Math.max(0, Math.floor(Number(
      stageRow?.activeUnlockStep ?? stageRow?.activeUnlockStage ?? 0,
    )));
    const activeSkillId = normalizeSkillId(stageRow?.activeSkillId);
    if (activeSkillId && stageUnlocked(activeUnlockStep)) {
      skillIds.push(activeSkillId);
    }
    const passiveSkillIds = Array.isArray(stageRow?.passiveSkillIds) ? stageRow.passiveSkillIds : [];
    const passiveUnlockStages = Array.isArray(stageRow?.passiveUnlockStages) ? stageRow.passiveUnlockStages : [];
    passiveSkillIds.forEach((rawSkillId, index) => {
      const passiveSkillId = normalizeSkillId(rawSkillId);
      const passiveUnlockStep = Math.max(0, Math.floor(Number(passiveUnlockStages[index] || 0)));
      if (passiveSkillId && stageUnlocked(passiveUnlockStep)) {
        skillIds.push(passiveSkillId);
      }
    });
  });
  return skillIds.filter((skillId, index, rows) => skillId && rows.indexOf(skillId) === index);
};

const applyWarplaneStageSkillUnlocks = (warplaneData = {}, advancementRows = []) => {
  const unlockedSkillIds = getUnlockedWarplaneStageSkillIds(advancementRows, warplaneData);
  if (!unlockedSkillIds.length) return warplaneData;
  const currentSkills = normalizeIdLevelEntries(warplaneData?.skills);
  const selectedSkillIds = new Set(currentSkills.map((row) => normalizeSkillId(row?.id)).filter(Boolean));
  const addedSkills = unlockedSkillIds
    .filter((skillId) => !selectedSkillIds.has(skillId))
    .map((skillId) => ({ id: skillId, level: 0 }));
  if (!addedSkills.length) return warplaneData;
  return {
    ...warplaneData,
    skills: normalizeIdLevelEntries([...currentSkills, ...addedSkills]),
  };
};

const getWarplaneStateOrdinal = (state = {}, stageRows = []) => {
  const stage = Math.max(0, Math.floor(Number(state?.advancementStage || 0)));
  const targetRow = stageRows.find((row) => Number(row?.stage || 0) === stage) || stageRows[0] || null;
  const maxStep = Math.max(0, Math.floor(Number(targetRow?.maxProgressStep || 0)));
  const step = Math.max(0, Math.min(maxStep, Math.floor(Number(state?.advancementStep || 0))));
  let ordinal = step;
  for (const row of stageRows) {
    const rowStage = Math.max(0, Math.floor(Number(row?.stage || 0)));
    if (rowStage >= stage) break;
    ordinal += Math.max(1, Math.floor(Number(row?.maxProgressStep || 0)) + 1);
  }
  return ordinal;
};

const resolveWarplaneStateFromOrdinal = (ordinalRaw = 0, stageRows = []) => {
  let remaining = Math.max(0, Math.floor(Number(ordinalRaw || 0)));
  for (const row of stageRows) {
    const stateCount = Math.max(1, Math.floor(Number(row?.maxProgressStep || 0)) + 1);
    if (remaining < stateCount) {
      return {
        stage: Math.max(0, Math.floor(Number(row?.stage || 0))),
        step: Math.max(0, remaining),
        row,
      };
    }
    remaining -= stateCount;
  }
  const last = stageRows[stageRows.length - 1] || null;
  return {
    stage: Math.max(0, Math.floor(Number(last?.stage || 0))),
    step: Math.max(0, Math.floor(Number(last?.maxProgressStep || 0))),
    row: last,
  };
};

const addMaterialCosts = (accumulator, costs = [], multiplier = 1) => {
  (Array.isArray(costs) ? costs : []).forEach((cost) => {
    const itemGid = normalizeSkillId(cost?.itemGid);
    const resourceKey = String(cost?.resourceKey || "").trim();
    const count = Math.max(0, Number(cost?.count || 0) * Math.max(0, Number(multiplier || 0)));
    if ((!itemGid && !resourceKey) || count <= 0) return;
    const key = `${String(cost?.resourceType || "item")}:${itemGid || resourceKey}:${Number(cost?.level || 0)}`;
    const existing = accumulator.get(key) || {
      resourceType: String(cost?.resourceType || "item"),
      itemGid: itemGid || null,
      resourceKey,
      level: Math.max(0, Math.floor(Number(cost?.level || 0))),
      label: String(cost?.label || cost?.name || (itemGid ? `Item #${itemGid}` : resourceKey)),
      count: 0,
    };
    existing.count += count;
    accumulator.set(key, existing);
  });
};

const summarizeWarplaneEvolutionCosts = (fromState = {}, toState = {}, advancementRows = []) => {
  const stageRows = getWarplaneStageRowsWithMeta(advancementRows);
  if (!stageRows.length) return { stepCount: 0, materials: [] };
  const fromOrdinal = getWarplaneStateOrdinal(fromState, stageRows);
  const toOrdinal = getWarplaneStateOrdinal(toState, stageRows);
  if (toOrdinal <= fromOrdinal) return { stepCount: 0, materials: [] };

  const materialMap = new Map();
  for (let ordinal = fromOrdinal; ordinal < toOrdinal; ordinal += 1) {
    const sourceState = resolveWarplaneStateFromOrdinal(ordinal, stageRows);
    addMaterialCosts(materialMap, sourceState?.row?.costs || [], 1);
  }

  return {
    stepCount: toOrdinal - fromOrdinal,
    materials: Array.from(materialMap.values()).sort((left, right) => (
      String(left.label || "").localeCompare(String(right.label || ""))
      || String(left.itemGid || left.resourceKey || "").localeCompare(String(right.itemGid || right.resourceKey || ""))
    )),
  };
};

const formatWarplaneEvolutionState = (state = {}) => {
  const stage = Math.max(0, Math.floor(Number(state?.advancementStage || 0)));
  const step = Math.max(0, Math.floor(Number(state?.advancementStep || 0)));
  const stageMeta = getWarplaneStageMeta(stage);
  return `${stageMeta.stageLabel} Step ${step}`;
};

const formatMaterialList = (materials = []) => (
  (Array.isArray(materials) ? materials : [])
    .filter((row) => Number(row?.count || 0) > 0)
    .map((row) => `${Number(row.count || 0).toLocaleString()} ${row.label || (row.itemGid ? `Item #${row.itemGid}` : row.resourceKey || "materials")}`)
    .join(", ")
);

const summarizeChangeRowMaterials = (rows = []) => {
  const materialMap = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => addMaterialCosts(materialMap, row?.materials || [], 1));
  return sortMaterialRows(materialMap);
};

const formatCartUsd = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "Unpriced";
  return number.toLocaleString(undefined, { style: "currency", currency: "USD" });
};

const formatCartTimeline = (daysRaw) => {
  const days = Number(daysRaw);
  if (!Number.isFinite(days)) return "Unknown";
  if (days <= 0) return "Today";
  if (days === 1) return "1 day";
  return `${Math.ceil(days).toLocaleString()} days`;
};

const CART_WEEKDAY_LABELS = Object.freeze([
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]);

const getCartWeekdayIndex = (weekday) => (
  CART_WEEKDAY_LABELS.findIndex((label) => label.toLowerCase() === String(weekday || "").trim().toLowerCase())
);

const getPackInitialAvailableDay = (pack = {}, now = new Date()) => {
  const weekdayIndex = getCartWeekdayIndex(pack?.weekday);
  if (weekdayIndex < 0) return 0;
  const currentWeekdayIndex = Number.isNaN(now.getTime()) ? new Date().getUTCDay() : now.getUTCDay();
  return (weekdayIndex - currentWeekdayIndex + 7) % 7;
};

const getPackResetDays = (pack = {}) => {
  const categoryKey = String(pack?.categoryKey || "").toLowerCase();
  const categoryLabel = String(pack?.categoryLabel || "").toLowerCase();
  if (categoryKey === "daily_picks" && getCartWeekdayIndex(pack?.weekday) >= 0) return 7;
  if (categoryKey.includes("monthly") || categoryLabel.includes("monthly")) return 30;
  if (categoryKey.includes("weekly") || categoryLabel.includes("weekly")) return 7;
  if (categoryKey.includes("daily") || categoryLabel.includes("daily")) return 1;
  if (categoryKey === "normal_shop") return 4;
  return 7;
};

const isPackEligibleForUpgradeCartPricing = (pack = {}) => {
  const categoryKey = String(pack?.categoryKey || "").toLowerCase();
  return categoryKey !== "event_special";
};

const getCartCandidateScore = (pack = {}, strategyKey = "balanced") => {
  const priceUsd = Number(pack?.priceUsd);
  const rewardCount = Number(pack?.matchingItemCount || 0);
  if (!Number.isFinite(priceUsd) || priceUsd < 0 || rewardCount <= 0) return Number.POSITIVE_INFINITY;
  const unitUsd = priceUsd / rewardCount;
  const resetDays = getPackResetDays(pack);
  if (strategyKey === "lowest") return unitUsd - (Math.min(resetDays, 30) * 0.000001);
  if (strategyKey === "fastest") return resetDays + (unitUsd / 100000);
  return unitUsd * (1 + (resetDays / 21));
};

const getCartPurchaseOccurrenceScore = (occurrence = {}, strategyKey = "balanced") => {
  const priceUsd = Number(occurrence?.priceUsd);
  const rewardCount = Number(occurrence?.rewardCount || 0);
  const availableDay = Number(occurrence?.availableDay || 0);
  if (!Number.isFinite(priceUsd) || priceUsd < 0 || rewardCount <= 0) return Number.POSITIVE_INFINITY;
  const unitUsd = priceUsd / rewardCount;
  if (strategyKey === "lowest") return unitUsd + (availableDay * 0.000001);
  if (strategyKey === "fastest") return availableDay + (unitUsd / 100000);
  return (unitUsd * (1 + (availableDay / 21))) + (availableDay * 0.01);
};

const getCartPackIdentity = (pack = {}) => (
  [pack?.packGid, pack?.productId, pack?.title].filter((part) => part !== undefined && part !== null && String(part).trim()).join("|")
);

const buildCartPurchaseOccurrences = (candidates = [], requiredCount = 0, strategyKey = "balanced") => {
  const normalizedCandidates = (Array.isArray(candidates) ? candidates : [])
    .map((pack) => {
      const rewardCount = Number(pack?.matchingItemCount || 0);
      const priceUsd = Number(pack?.priceUsd);
      if (rewardCount <= 0 || !Number.isFinite(priceUsd) || priceUsd < 0) return null;
      return {
        pack,
        rewardCount,
        priceUsd,
        resetDays: getPackResetDays(pack),
      };
    })
    .filter(Boolean);
  const maxPerCycle = normalizedCandidates.reduce((sum, row) => sum + row.rewardCount, 0);
  if (!normalizedCandidates.length || maxPerCycle <= 0) return [];
  const primaryCandidate = [...normalizedCandidates]
    .sort((left, right) => (
      getCartCandidateScore(left.pack, strategyKey) - getCartCandidateScore(right.pack, strategyKey)
      || Number(left.priceUsd || 0) - Number(right.priceUsd || 0)
      || String(left.pack?.title || "").localeCompare(String(right.pack?.title || ""))
    ))[0];
  const primaryRewardCount = Math.max(1, Number(primaryCandidate?.rewardCount || maxPerCycle));
  const cyclesNeeded = Math.max(
    Math.ceil(Number(requiredCount || 0) / maxPerCycle) + 2,
    Math.ceil(Number(requiredCount || 0) / primaryRewardCount) + 2,
    1
  );
  const maxCycles = Math.min(cyclesNeeded, 5000);
  const occurrences = [];
  for (let cycleIndex = 0; cycleIndex < maxCycles; cycleIndex += 1) {
    normalizedCandidates.forEach((row) => {
      const initialAvailableDay = getPackInitialAvailableDay(row.pack);
      occurrences.push({
        ...row,
        availableDay: initialAvailableDay + (cycleIndex * row.resetDays),
      });
    });
  }
  return occurrences;
};

const summarizeCartPurchaseGroups = (occurrences = []) => {
  const groupMap = new Map();
  (Array.isArray(occurrences) ? occurrences : []).forEach((occurrence) => {
    const pack = occurrence?.pack || {};
    const key = getCartPackIdentity(pack) || `${pack?.title || "Pack"}-${occurrence?.priceUsd || 0}-${occurrence?.rewardCount || 0}-${occurrence?.resetDays || 0}`;
    const current = groupMap.get(key) || {
      pack,
      title: pack?.title || "Pack",
      weekday: pack?.weekday || "",
      quantity: 0,
      rewardEach: Number(occurrence?.rewardCount || 0),
      rewardCountTotal: 0,
      subtotalUsd: 0,
      firstDay: Number.POSITIVE_INFINITY,
      lastDay: 0,
      resetDays: Number(occurrence?.resetDays || 0),
    };
    current.quantity += 1;
    current.rewardCountTotal += Number(occurrence?.rewardCount || 0);
    current.subtotalUsd += Number(occurrence?.priceUsd || 0);
    current.firstDay = Math.min(current.firstDay, Number(occurrence?.availableDay || 0));
    current.lastDay = Math.max(current.lastDay, Number(occurrence?.availableDay || 0));
    groupMap.set(key, current);
  });
  return Array.from(groupMap.values())
    .map((group) => ({
      ...group,
      firstDay: Number.isFinite(group.firstDay) ? group.firstDay : 0,
    }))
    .sort((left, right) => (
      Number(left.firstDay || 0) - Number(right.firstDay || 0)
      || Number(left.lastDay || 0) - Number(right.lastDay || 0)
      || String(left.title || "").localeCompare(String(right.title || ""))
    ));
};

const formatCartPurchaseWindow = (firstDayRaw, lastDayRaw) => {
  const firstDay = Number(firstDayRaw || 0);
  const lastDay = Number(lastDayRaw || 0);
  if (!Number.isFinite(firstDay) || !Number.isFinite(lastDay)) return "unknown timing";
  if (lastDay <= 0) return "available today";
  if (firstDay === lastDay) return `available after ${formatCartTimeline(lastDay).toLowerCase()}`;
  if (firstDay <= 0) return `today through ${formatCartTimeline(lastDay).toLowerCase()}`;
  return `${formatCartTimeline(firstDay).toLowerCase()} through ${formatCartTimeline(lastDay).toLowerCase()}`;
};

const UPGRADE_CART_PLAN_CONFIGS = Object.freeze([
  {
    key: "lowest",
    label: "Lowest Cost",
    description: "Prioritizes the best unit price, even when the reset cadence is slower.",
  },
  {
    key: "balanced",
    label: "Balanced",
    description: "Weights unit price and reset cadence together.",
  },
  {
    key: "fastest",
    label: "Fastest",
    description: "Prioritizes packs with the shortest repeat cadence.",
  },
]);

const buildUpgradeCartPlanOptions = (materials = [], candidatesByItem = new Map()) => {
  const itemMaterials = (Array.isArray(materials) ? materials : [])
    .filter((row) => row?.resourceType === "item" && normalizeSkillId(row?.itemGid) && Number(row?.count || 0) > 0);
  return UPGRADE_CART_PLAN_CONFIGS.map((config) => {
    let totalUsd = 0;
    let completionDays = 0;
    let packCount = 0;
    const detailRows = [];
    const missingRows = [];
    itemMaterials.forEach((material) => {
      const itemGid = normalizeSkillId(material?.itemGid);
      const requiredCount = Number(material?.count || 0);
      const candidates = (candidatesByItem.get(itemGid) || [])
        .filter((pack) => (
          isPackEligibleForUpgradeCartPricing(pack)
          && Number(pack?.matchingItemCount || 0) > 0
          && Number.isFinite(Number(pack?.priceUsd))
        ))
        .sort((left, right) => (
          getCartCandidateScore(left, config.key) - getCartCandidateScore(right, config.key)
          || Number(left?.priceUsd || 0) - Number(right?.priceUsd || 0)
          || String(left?.title || "").localeCompare(String(right?.title || ""))
        ));
      const occurrences = buildCartPurchaseOccurrences(candidates, requiredCount, config.key)
        .sort((left, right) => (
          getCartPurchaseOccurrenceScore(left, config.key) - getCartPurchaseOccurrenceScore(right, config.key)
          || Number(left?.availableDay || 0) - Number(right?.availableDay || 0)
          || Number(left?.priceUsd || 0) - Number(right?.priceUsd || 0)
          || String(left?.pack?.title || "").localeCompare(String(right?.pack?.title || ""))
        ));
      if (!occurrences.length) {
        missingRows.push(material);
        detailRows.push({
          material,
          status: "missing",
          requiredCount,
        });
        return;
      }
      const selectedOccurrences = [];
      let remainingCount = requiredCount;
      occurrences.some((occurrence) => {
        selectedOccurrences.push(occurrence);
        remainingCount -= Number(occurrence.rewardCount || 0);
        return remainingCount <= 0;
      });
      const totalRewardCount = selectedOccurrences.reduce((sum, occurrence) => sum + Number(occurrence.rewardCount || 0), 0);
      if (totalRewardCount < requiredCount) {
        missingRows.push(material);
        detailRows.push({
          material,
          status: "missing",
          requiredCount,
        });
        return;
      }
      const subtotalUsd = selectedOccurrences.reduce((sum, occurrence) => sum + Number(occurrence.priceUsd || 0), 0);
      const materialDays = selectedOccurrences.reduce((maxDay, occurrence) => Math.max(maxDay, Number(occurrence.availableDay || 0)), 0);
      const purchaseGroups = summarizeCartPurchaseGroups(selectedOccurrences);
      totalUsd += subtotalUsd;
      completionDays = Math.max(completionDays, materialDays);
      packCount += selectedOccurrences.length;
      detailRows.push({
        material,
        status: "priced",
        requiredCount,
        purchaseGroups,
        totalRewardCount,
        quantity: selectedOccurrences.length,
        subtotalUsd,
        completionDays: materialDays,
      });
    });
    return {
      ...config,
      totalUsd: missingRows.length ? null : totalUsd,
      completionDays: missingRows.length ? null : completionDays,
      packCount,
      detailRows,
      missingRows,
      pricedMaterialCount: detailRows.filter((row) => row.status === "priced").length,
      totalMaterialCount: itemMaterials.length,
    };
  });
};

const renderPendingMaterialRows = (rows = [], keyPrefix = "pending-materials") => {
  const pendingRows = Array.isArray(rows) ? rows : [];
  if (!pendingRows.length) return null;
  return (
    <div className="battle-sim-officer-materials">
      <strong>Pending materials</strong>
      {pendingRows.map((changeRow) => (
        <div key={`${keyPrefix}-${changeRow.key}`}>
          <span>{`${changeRow.subsection}: ${changeRow.fromLabel} -> ${changeRow.toLabel}`}</span>
          <small>
            {changeRow.materials.length > 0
              ? formatMaterialList(changeRow.materials)
              : "No mapped material requirement."}
          </small>
        </div>
      ))}
    </div>
  );
};

const findEquipmentBaseOptionForUnlock = (row = null, unlockDisplayLevel = 1) => {
  const targetLevel = Math.max(1, Math.floor(Number(unlockDisplayLevel || 1)));
  const options = Array.isArray(row?.baseOptions) ? row.baseOptions : [];
  return (
    options.find((option) => Math.max(1, Math.floor(Number(option?.displayLevel || 1))) >= targetLevel)
    || options[options.length - 1]
    || null
  );
};

const buildWarplaneEditorModel = ({
  warplaneData = {},
  advancementRows = [],
  levelRows = [],
  skillOptions = [],
  equipOptions = [],
  equipSkillOptions = [],
  skinOptions = [],
  effectLabelById = new Map(),
}) => {
  const sortedAdvancementRows = getWarplaneStageRowsWithMeta(advancementRows);
  const maxStage = sortedAdvancementRows.reduce((max, row) => {
    const next = Math.max(0, Math.floor(Number(row?.stage || 0)));
    return next > max ? next : max;
  }, 0);
  const selectedStage = Math.max(0, Math.min(maxStage, Math.floor(Number(warplaneData?.advancementStage || 0))));

  const sortedLevelRows = [...(Array.isArray(levelRows) ? levelRows : [])]
    .sort((left, right) => (
      Math.max(0, Math.floor(Number(left?.level || 0)))
      - Math.max(0, Math.floor(Number(right?.level || 0)))
    ));
  const maxLevel = sortedLevelRows.reduce((max, row) => {
    const next = Math.max(0, Math.floor(Number(row?.level || 0)));
    return next > max ? next : max;
  }, 0);
  const selectedLevel = Math.max(0, Math.min(maxLevel, Math.floor(Number(warplaneData?.level || 0))));
  const selectedLevelRow = sortedLevelRows.find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === selectedLevel)
    || [...sortedLevelRows].reverse().find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) <= selectedLevel)
    || sortedLevelRows[0]
    || null;
  const maxSkillDisplayLevel = Math.max(1, Math.floor(Number(selectedLevelRow?.maxSkillLevel || 20)));
  const maxSkillLevel = fromWarplaneOptionLevelDisplay(maxSkillDisplayLevel);
  const selectedStageRow = sortedAdvancementRows.find((row) => Math.max(0, Math.floor(Number(row?.stage || 0))) === selectedStage)
    || [...sortedAdvancementRows].reverse().find((row) => Math.max(0, Math.floor(Number(row?.stage || 0))) <= selectedStage)
    || sortedAdvancementRows[0]
    || null;
  const progressionLength = Array.isArray(selectedStageRow?.powerProgression) ? selectedStageRow.powerProgression.length : 0;
  const maxProgressStep = progressionLength > 0 ? progressionLength - 1 : 0;
  const rawRequestedStep = warplaneData?.advancementStep ?? warplaneData?.advancementLevel ?? warplaneData?.progressStep;
  const hasExplicitProgressStep = (
    rawRequestedStep !== undefined
    && rawRequestedStep !== null
    && String(rawRequestedStep).trim() !== ""
    && Number.isFinite(Number(rawRequestedStep))
  );
  const parsedRequestedStep = Number(rawRequestedStep);
  const derivedStepFromLevel = maxLevel > 0 && maxProgressStep > 0
    ? Math.max(0, Math.min(maxProgressStep, Math.round((selectedLevel / maxLevel) * maxProgressStep)))
    : 0;
  const selectedProgressStep = hasExplicitProgressStep
    ? Math.max(0, Math.min(maxProgressStep, Math.floor(parsedRequestedStep)))
    : derivedStepFromLevel;

  const skillRows = buildWarplaneOptionRows({
    fieldKey: WARPLANE_SKILL_GROUP_KEYS.skills,
    options: skillOptions,
    selectedRows: warplaneData?.skills,
    effectLabelById,
    fallbackMaxLevel: 29,
    skillLevelCap: maxSkillLevel,
  });
  const skillById = new Map(skillRows.map((row) => [row.id, row]));
  const stageSkillRows = [];
  sortedAdvancementRows.forEach((stageRow) => {
    const rowStage = Math.max(0, Math.floor(Number(stageRow?.stage || 0)));
    const stageLabel = String(stageRow?.stageLabel || getWarplaneStageMeta(rowStage).stageLabel);
    const stageColor = String(stageRow?.stageColor || getWarplaneStageMeta(rowStage).stageColor);
    const activeUnlockStep = Math.max(0, Math.floor(Number(
      stageRow?.activeUnlockStep ?? stageRow?.activeUnlockStage ?? 0,
    )));
    const activeSkillId = normalizeSkillId(stageRow?.activeSkillId);
    if (activeSkillId) {
      const row = skillById.get(activeSkillId);
      if (row) {
        const unlocked = (
          selectedStage > rowStage
          || (selectedStage === rowStage && selectedProgressStep >= activeUnlockStep)
        );
        stageSkillRows.push({
          ...row,
          stage: rowStage,
          stageLabel,
          stageColor,
          slotType: "active",
          slotOrder: 0,
          unlocked,
          unlockStep: activeUnlockStep,
          unlockText: `Unlocks at ${stageLabel} step ${activeUnlockStep}`,
        });
      }
    }
    const passiveSkillIds = Array.isArray(stageRow?.passiveSkillIds) ? stageRow.passiveSkillIds : [];
    const passiveUnlockStages = Array.isArray(stageRow?.passiveUnlockStages) ? stageRow.passiveUnlockStages : [];
    passiveSkillIds.forEach((rawSkillId, idx) => {
      const passiveSkillId = normalizeSkillId(rawSkillId);
      if (!passiveSkillId) return;
      const row = skillById.get(passiveSkillId);
      if (!row) return;
      const unlockStep = Math.max(0, Math.floor(Number(passiveUnlockStages[idx] || 0)));
      const unlocked = (
        selectedStage > rowStage
        || (selectedStage === rowStage && selectedProgressStep >= unlockStep)
      );
      stageSkillRows.push({
        ...row,
        stage: rowStage,
        stageLabel,
        stageColor,
        slotType: "passive",
        slotOrder: idx + 1,
        unlocked,
        unlockStep,
        unlockText: `Unlocks at ${stageLabel} step ${unlockStep}`,
      });
    });
  });
  const stageSkillRowsSorted = stageSkillRows.sort((left, right) => (
    left.stage - right.stage
    || left.slotOrder - right.slotOrder
    || left.label.localeCompare(right.label)
    || left.id - right.id
  ));

  const stageSkillIdSet = new Set(stageSkillRowsSorted.map((row) => row.id));
  const additionalSkillRows = skillRows
    .filter((row) => !stageSkillIdSet.has(row.id))
    .map((row) => ({
      ...row,
      stage: null,
      stageLabel: "Other",
      slotType: "other",
      slotOrder: 99,
      unlocked: true,
      unlockText: "",
    }));

  const equipRows = buildWarplaneOptionRows({
    fieldKey: WARPLANE_SKILL_GROUP_KEYS.equips,
    options: equipOptions,
    selectedRows: warplaneData?.equips,
    effectLabelById,
    fallbackMaxLevel: 54,
    skillLevelCap: null,
  });
  const equipSkillRows = buildWarplaneOptionRows({
    fieldKey: WARPLANE_SKILL_GROUP_KEYS.equipSkills,
    options: equipSkillOptions,
    selectedRows: warplaneData?.equipSkills,
    effectLabelById,
    fallbackMaxLevel: 29,
    skillLevelCap: maxSkillLevel,
  });
  const equipmentSkillGroups = buildWarplaneEquipmentSkillGroups({ equipRows, equipSkillRows });
  const skinRows = buildWarplaneOptionRows({
    fieldKey: WARPLANE_SKILL_GROUP_KEYS.skins,
    options: skinOptions,
    selectedRows: warplaneData?.skins,
    effectLabelById,
    fallbackMaxLevel: 1,
    skillLevelCap: null,
  });

  return {
    maxStage,
    selectedStage,
    maxLevel,
    selectedLevel,
    selectedLevelRow,
    maxSkillLevel,
    maxSkillDisplayLevel,
    maxProgressStep,
    selectedProgressStep,
    stageRows: sortedAdvancementRows,
    stageSkillRows: [...stageSkillRowsSorted, ...additionalSkillRows],
    equipRows,
    equipSkillRows,
    equipmentSkillGroups,
    skinRows,
  };
};

const ANTIQUE_QUALITY_LABELS = Object.freeze({
  5: "Legendary",
  4: "Epic",
  3: "Rare",
});

const formatAntiqueQualityLabel = (quality) => {
  const parsed = Math.max(0, Math.floor(Number(quality || 0)));
  if (parsed <= 0) return "Other";
  return ANTIQUE_QUALITY_LABELS[parsed] || `Quality ${parsed}`;
};

const resolveAntiqueStarRowAtSelection = (collectionRow = null, selectedStar = 0) => {
  const starRows = Array.isArray(collectionRow?.starRows) ? collectionRow.starRows : [];
  if (!starRows.length) return null;
  const targetStar = asNonNegativeInt(selectedStar, 0);
  const exactRows = starRows.filter((row) => asNonNegativeInt(row?.star, 0) === targetStar);
  if (exactRows.length) {
    return exactRows.reduce((best, row) => (
      asNonNegativeInt(row?.stage, 0) > asNonNegativeInt(best?.stage, 0) ? row : best
    ), exactRows[0]);
  }
  return (
    [...starRows]
      .reverse()
      .find((row) => asNonNegativeInt(row?.star, 0) <= targetStar)
    || starRows[0]
  );
};

const getAntiqueMaxStageForStar = (collectionRow = null, star = 0) => {
  const targetStar = asNonNegativeInt(star, 0);
  return (Array.isArray(collectionRow?.starRows) ? collectionRow.starRows : [])
    .filter((row) => asNonNegativeInt(row?.star, 0) === targetStar)
    .reduce((max, row) => Math.max(max, asNonNegativeInt(row?.stage, 0)), 0);
};

const getAntiqueProgressOptions = (collectionRow = null) => {
  if (Array.isArray(collectionRow?.progressOptions) && collectionRow.progressOptions.length) {
    return collectionRow.progressOptions
      .map((option) => ({
        star: asNonNegativeInt(option?.star, 0),
        stage: asNonNegativeInt(option?.stage, 0),
        value: Math.round(Number(option?.value || 0) * 10) / 10,
      }))
      .sort((left, right) => left.star - right.star || left.stage - right.stage);
  }
  const rows = Array.isArray(collectionRow?.starRows) ? collectionRow.starRows : [];
  const seen = new Set();
  return rows
    .map((row) => {
      const star = asNonNegativeInt(row?.star, 0);
      const stage = asNonNegativeInt(row?.stage, 0);
      const key = `${star}:${stage}`;
      if (seen.has(key)) return null;
      seen.add(key);
      return {
        star,
        stage,
        value: Math.round((star + (stage / 10)) * 10) / 10,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.star - right.star || left.stage - right.stage);
};

const resolveAntiqueProgressOption = (collectionRow = null, selected = {}) => {
  const options = getAntiqueProgressOptions(collectionRow);
  if (!options.length) {
    const star = asNonNegativeInt(selected?.star ?? selected?.level, 0);
    const stage = asNonNegativeInt(selected?.stage, 0);
    return { star, stage, value: Math.round((star + (stage / 10)) * 10) / 10 };
  }
  const selectedStar = asNonNegativeInt(selected?.star ?? selected?.level, 0);
  const selectedStage = asNonNegativeInt(selected?.stage, 0);
  return options.find((option) => option.star === selectedStar && option.stage === selectedStage)
    || options.find((option) => option.star === selectedStar && option.stage === 0)
    || [...options].reverse().find((option) => option.star <= selectedStar)
    || options[0];
};

const getAntiqueProgressOptionByDirection = (collectionRow = null, current = {}, direction = 0) => {
  const options = getAntiqueProgressOptions(collectionRow);
  if (!options.length) return resolveAntiqueProgressOption(collectionRow, current);
  const active = resolveAntiqueProgressOption(collectionRow, current);
  const currentIndex = Math.max(0, options.findIndex((option) => option.star === active.star && option.stage === active.stage));
  const nextIndex = clamp(currentIndex + Math.sign(direction || 0), 0, options.length - 1);
  return options[nextIndex] || active;
};

const getAntiqueProgressOptionKey = (option = {}) => (
  `${asNonNegativeInt(option?.star, 0)}:${asNonNegativeInt(option?.stage, 0)}`
);

const formatAntiqueProgressOptionLabel = (option = {}) => {
  const star = asNonNegativeInt(option?.star, 0);
  const stage = asNonNegativeInt(option?.stage, 0);
  return stage > 0 ? `${star}* +${stage}` : `${star}*`;
};

const getAntiqueProgressSelectOptions = (collectionRow = null, current = {}) => {
  const optionMap = new Map();
  getAntiqueProgressOptions(collectionRow).forEach((option) => {
    optionMap.set(getAntiqueProgressOptionKey(option), option);
  });
  const active = resolveAntiqueProgressOption(collectionRow, current);
  optionMap.set(getAntiqueProgressOptionKey(active), active);
  return Array.from(optionMap.values())
    .sort((left, right) => left.star - right.star || left.stage - right.stage);
};

const normalizeAntiqueProgressInputValue = (rawValue, previous = {}, collectionRow = null) => {
  const options = getAntiqueProgressOptions(collectionRow);
  if (!options.length) return resolveAntiqueProgressOption(collectionRow, previous);
  const parsed = Number(rawValue);
  const previousOption = resolveAntiqueProgressOption(collectionRow, previous);
  if (!Number.isFinite(parsed)) return previousOption;
  const bounded = clamp(Math.round(Math.max(0, parsed) * 10) / 10, options[0].value, options[options.length - 1].value);
  const exact = options.find((option) => Math.abs(option.value - bounded) <= 1e-9);
  if (exact) return exact;

  const isLikelySpinnerStep = Math.abs(bounded - previousOption.value) <= 0.11;
  if (isLikelySpinnerStep && bounded > previousOption.value) {
    return getAntiqueProgressOptionByDirection(collectionRow, previousOption, 1);
  }
  if (isLikelySpinnerStep && bounded < previousOption.value) {
    return getAntiqueProgressOptionByDirection(collectionRow, previousOption, -1);
  }

  return options.reduce((best, option) => (
    Math.abs(option.value - bounded) < Math.abs(best.value - bounded) ? option : best
  ), options[0]);
};

const normalizeAntiqueProgressSelectValue = (rawValue, previous = {}, collectionRow = null) => {
  const key = String(rawValue || "");
  const [rawStar, rawStage] = key.split(":");
  const star = Number(rawStar);
  const stage = Number(rawStage);
  if (Number.isFinite(star) && Number.isFinite(stage)) {
    return resolveAntiqueProgressOption(collectionRow, { star, stage });
  }
  return normalizeAntiqueProgressInputValue(rawValue, previous, collectionRow);
};

const resolveAntiqueStarStageRow = (collectionRow = null, selected = {}) => {
  const starRows = Array.isArray(collectionRow?.starRows) ? collectionRow.starRows : [];
  if (!starRows.length) return null;
  const targetStar = asNonNegativeInt(selected?.star ?? selected?.level, 0);
  const maxStage = getAntiqueMaxStageForStar(collectionRow, targetStar);
  const targetStage = Math.min(maxStage, asNonNegativeInt(selected?.stage, 0));
  return starRows.find((row) => (
    asNonNegativeInt(row?.star, 0) === targetStar
      && asNonNegativeInt(row?.stage, 0) === targetStage
  )) || resolveAntiqueStarRowAtSelection(collectionRow, targetStar);
};

const buildAntiqueCollectionEditorGroups = ({
  collectionRows = [],
  selectedRows = [],
  effectLabelById = new Map(),
}) => {
  const rows = Array.isArray(collectionRows) ? collectionRows : [];
  const selectedById = new Map(
    normalizeAntiqueCollectionEntries(selectedRows)
      .map((entry) => [Number(entry?.id || 0), entry])
      .filter(([id]) => Number.isFinite(id) && id > 0),
  );
  const groupsByQuality = new Map();
  rows.forEach((row) => {
    const id = getOptionId(row);
    if (!id) return;
    const quality = Math.max(0, Math.floor(Number(row?.quality || 0)));
    if (!groupsByQuality.has(quality)) groupsByQuality.set(quality, []);
    const maxStar = Math.max(0, Math.floor(Number(row?.maxStar || 0)));
    const activated = selectedById.has(id);
    const selected = selectedById.get(id) || { star: 0, stage: 0, level: 0 };
    const progressOptions = getAntiqueProgressOptions(row);
    const selectedProgress = activated ? resolveAntiqueProgressOption(row, selected) : { star: 0, stage: 0, value: 0 };
    const selectedStar = activated
      ? Math.max(0, Math.min(maxStar || 0, selectedProgress.star))
      : 0;
    const maxStage = getAntiqueMaxStageForStar(row, selectedStar);
    const selectedStage = activated ? Math.min(maxStage, selectedProgress.stage) : 0;
    const activeStarRow = activated ? resolveAntiqueStarStageRow(row, { star: selectedStar, stage: selectedStage }) : null;
    const activeEffects = Array.isArray(activeStarRow?.effects) ? activeStarRow.effects : [];
    const buffLines = formatCommanderEffectLinesForEffects(activeEffects, effectLabelById, {
      label: row?.label,
      description: row?.description,
    });
    const effectRangeLines = buildEffectRangeLinesFromLevelRows(row?.starRows || [], effectLabelById, {
      label: row?.label,
      description: row?.description,
    }, {
      getLevel: (levelRow) => asNonNegativeInt(levelRow?.star, 0) + (asNonNegativeInt(levelRow?.stage, 0) / 10),
      getDisplayLevel: (levelRow) => {
        const star = asNonNegativeInt(levelRow?.star, 0);
        const stage = asNonNegativeInt(levelRow?.stage, 0);
        return stage > 0 ? `${star}* +${stage}` : `${star}*`;
      },
    });

    groupsByQuality.get(quality).push({
      id,
      label: String(row?.label || `Collection #${id}`),
      description: String(row?.description || ""),
      quality,
      qualityLabel: formatAntiqueQualityLabel(quality),
      iconPath: String(row?.iconPath || ""),
      iconSprite: String(row?.iconSprite || ""),
      maxStar,
      activated,
      star: selectedStar,
      stage: selectedStage,
      maxStage,
      progressValue: Math.round((selectedStar + (selectedStage / 10)) * 10) / 10,
      progressMaxValue: progressOptions.reduce((max, option) => Math.max(max, Number(option?.value || 0)), 0),
      progressOptions,
      displayLevel: Math.round((selectedStar + (selectedStage / 10)) * 10) / 10,
      activePower: asNonNegativeInt(activeStarRow?.power, 0),
      activeStage: activeStarRow ? asNonNegativeInt(activeStarRow?.stage, 0) : 0,
      buffLines,
      effectRangeLines,
    });
  });
  return Array.from(groupsByQuality.entries())
    .sort((left, right) => right[0] - left[0])
    .map(([quality, groupRows]) => ({
      quality,
      label: formatAntiqueQualityLabel(quality),
      rows: [...groupRows].sort((left, right) => (
        left.label.localeCompare(right.label)
        || left.id - right.id
      )),
    }));
};

const buildAntiqueSuitRows = ({
  suitRows = [],
  selectedRows = [],
  collectionRows = [],
  effectLabelById = new Map(),
}) => {
  const selectedById = new Map(
    normalizeAntiqueCollectionEntries(selectedRows)
      .map((entry) => [Number(entry?.id || 0), entry])
      .filter(([id]) => Number.isFinite(id) && id > 0),
  );
  const collectionById = new Map(
    (Array.isArray(collectionRows) ? collectionRows : [])
      .map((row) => [Number(row?.id || 0), row])
      .filter(([id]) => Number.isFinite(id) && id > 0),
  );
  const rows = Array.isArray(suitRows) ? suitRows : [];
  return rows
    .map((row) => {
      const suitId = Number(row?.id || 0);
      if (!Number.isFinite(suitId) || suitId <= 0) return null;
      const collectionIds = Array.isArray(row?.collectionIds)
        ? row.collectionIds.map((value) => Number(value || 0)).filter((value) => Number.isFinite(value) && value > 0)
        : [];
      const levelRows = Array.isArray(row?.levelRows)
        ? [...row.levelRows].sort((left, right) => asNonNegativeInt(left?.star, 0) - asNonNegativeInt(right?.star, 0))
        : [];
      const matchedStar = levelRows.reduce((best, levelRow) => {
        const requiredStar = asNonNegativeInt(levelRow?.star, 0);
        if (requiredStar <= 0 || !collectionIds.length) return best;
        const allMatched = collectionIds.every((id) => asNonNegativeInt(selectedById.get(id)?.star ?? selectedById.get(id)?.level, 0) >= requiredStar);
        return allMatched && requiredStar > best ? requiredStar : best;
      }, 0);
      const activeLevelRow = levelRows.find((levelRow) => asNonNegativeInt(levelRow?.star, 0) === matchedStar) || null;
      const activeEffects = Array.isArray(activeLevelRow?.effects) ? activeLevelRow.effects : [];
      const activeBuffLines = formatCommanderEffectLinesForEffects(activeEffects, effectLabelById, {
        label: row?.label,
        description: row?.description,
      });
      const effectRangeLines = buildEffectRangeLinesFromLevelRows(levelRows, effectLabelById, {
        label: row?.label,
        description: row?.description,
      }, {
        getLevel: (levelRow) => asNonNegativeInt(levelRow?.star, 0),
        getDisplayLevel: (levelRow) => `${asNonNegativeInt(levelRow?.star, 0)}*`,
      });
      const requirements = collectionIds.map((id) => {
        const collection = collectionById.get(id);
        const selected = selectedById.get(id) || null;
        const progressOptions = getAntiqueProgressOptions(collection);
        const progress = selected ? resolveAntiqueProgressOption(collection, selected) : { star: 0, stage: 0, value: 0 };
        const star = progress.star;
        const stage = progress.stage;
        return {
          id,
          label: String(collection?.label || `Collection #${id}`),
          iconPath: String(collection?.iconPath || ""),
          iconSprite: String(collection?.iconSprite || ""),
          qualityLabel: formatAntiqueQualityLabel(collection?.quality),
          maxStar: Math.max(0, Math.floor(Number(collection?.maxStar || 0))),
          activated: selectedById.has(id),
          star,
          stage,
          progressValue: progress.value,
          progressMaxValue: progressOptions.reduce((max, option) => Math.max(max, Number(option?.value || 0)), 0),
          progressOptions,
          maxStage: getAntiqueMaxStageForStar(collection, star),
        };
      });
      return {
        id: suitId,
        label: String(row?.label || `Set #${suitId}`),
        description: String(row?.description || ""),
        requirements,
        levelRows,
        matchedStar,
        activeBuffLines,
        effectRangeLines,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.label.localeCompare(right.label) || left.id - right.id);
};

const ISLAND_DECORATION_QUALITY_LABELS = Object.freeze({
  5: "Mythic",
  4: "Legendary",
  3: "Epic",
  2: "Rare",
  1: "Common",
});

const formatIslandDecorationQualityLabel = (quality) => {
  const parsed = Math.max(0, Math.floor(Number(quality || 0)));
  if (parsed <= 0) return "Other";
  return ISLAND_DECORATION_QUALITY_LABELS[parsed] || `Quality ${parsed}`;
};

const resolveCumulativeIslandDecorationEffects = (levelRows = [], selectedLevel = 0) => {
  const targetLevel = Math.max(0, Math.floor(Number(selectedLevel || 0)));
  const sortedRows = [...(Array.isArray(levelRows) ? levelRows : [])].sort((left, right) => (
    Math.max(0, Math.floor(Number(left?.level || 0))) - Math.max(0, Math.floor(Number(right?.level || 0)))
  ));
  const activeRows = sortedRows.filter((entry) => Math.max(0, Math.floor(Number(entry?.level || 0))) <= targetLevel);
  const rowsToApply = activeRows.length ? activeRows : sortedRows.slice(0, 1);
  const effectsById = new Map();
  rowsToApply.forEach((entry) => {
    (Array.isArray(entry?.effects) ? entry.effects : []).forEach((effect) => {
      const effectId = Number(effect?.effectId || 0);
      if (!Number.isFinite(effectId) || effectId <= 0) return;
      effectsById.set(effectId, effect);
    });
  });
  return {
    row: rowsToApply[rowsToApply.length - 1] || null,
    effects: Array.from(effectsById.values()),
  };
};

const buildIslandDecorationDisplayRow = (row, selectedById, effectLabelById) => {
  const id = getOptionId(row);
  if (!id) return null;
  const quality = Math.max(0, Math.floor(Number(row?.quality || 0)));
  const maxLevel = Math.max(0, Math.floor(Number(getOptionMaxLevel(row, 0) || 0)));
  const hasSelection = selectedById.has(id);
  const rawSelectedLevel = selectedById.get(id);
  const selectedLevel = Number.isFinite(rawSelectedLevel)
    ? Math.max(0, Math.floor(rawSelectedLevel))
    : 0;
  const boundedLevel = maxLevel > 0 ? Math.min(selectedLevel, maxLevel) : 0;

  const levelRows = Array.isArray(row?.levelRows) ? row.levelRows : [];
  const setLevelRows = Array.isArray(row?.setLevelRows) ? row.setLevelRows : [];
  const hasLevelRows = levelRows.length > 0;
  const activateOnly = Boolean(row?.activateOnly);
  const activeResult = hasSelection
    ? resolveCumulativeIslandDecorationEffects(levelRows, boundedLevel)
    : { row: null, effects: [] };
  const activeSetResult = hasSelection
    ? resolveCumulativeIslandDecorationEffects(setLevelRows, boundedLevel)
    : { row: null, effects: [] };
  const activeLevelRow = activeResult.row;
  const activeEffects = activeResult.effects;
  const activeSetLevelRow = activeSetResult.row;
  const activeSetEffects = activeSetResult.effects;
  const maxDisplayLevel = activateOnly ? 1 : (maxLevel > 0 ? toIslandLevelDisplay(maxLevel, true) : (hasLevelRows ? 1 : 0));
  const displayLevel = hasSelection
    ? (activateOnly ? 1 : (maxLevel > 0 ? toIslandLevelDisplay(boundedLevel, true) : 1))
    : 0;
  const buffLines = formatCommanderEffectLinesForEffects(activeEffects, effectLabelById, {
    label: row?.label,
    description: row?.description,
  });
  const effectRangeLines = buildEffectRangeLinesFromLevelRows(levelRows, effectLabelById, {
    label: row?.label,
    description: row?.description,
  }, {
    getDisplayLevel: (levelRow) => getIslandLevelDisplayLabel(toIslandLevelDisplay(levelRow?.level, true), maxLevel),
  });
  const setBuffLines = formatCommanderEffectLinesForEffects(activeSetEffects, effectLabelById, {
    label: row?.label,
    description: row?.description,
  });
  const setEffectRangeLines = buildEffectRangeLinesFromLevelRows(setLevelRows, effectLabelById, {
    label: row?.label,
    description: row?.description,
  }, {
    getDisplayLevel: (levelRow) => getIslandLevelDisplayLabel(toIslandLevelDisplay(levelRow?.level, true), maxLevel),
  });

  return {
    id,
    label: String(row?.label || `Decoration #${id}`),
    description: String(row?.description || ""),
    quality,
    qualityLabel: formatIslandDecorationQualityLabel(quality),
    iconPath: String(row?.iconPath || ""),
    iconSprite: String(row?.iconSprite || ""),
    resourceKey: String(row?.resourceKey || ""),
    skinGid: Math.max(0, Math.floor(Number(row?.skinGid || 0))),
    skinBuffGid: Math.max(0, Math.floor(Number(row?.skinBuffGid || 0))),
    maxLevel,
    activateOnly,
    selected: hasSelection,
    level: boundedLevel,
    displayLevel,
    maxDisplayLevel,
    displayLevelOptions: activateOnly ? [0, 1] : buildIntegerRange(0, maxDisplayLevel),
    activeLevelLabel: activateOnly && hasSelection ? "Activated" : (activeLevelRow ? getIslandLevelDisplayLabel(toIslandLevelDisplay(activeLevelRow?.level, true), maxLevel) : ""),
    buffLines,
    effectRangeLines,
    setActiveLevelLabel: activeSetLevelRow ? getIslandLevelDisplayLabel(toIslandLevelDisplay(activeSetLevelRow?.level, true), maxLevel) : "",
    setBuffLines,
    setEffectRangeLines,
  };
};

const buildIslandDecorationEditorGroups = ({
  decorationRows = [],
  selectedRows = [],
  effectLabelById = new Map(),
}) => {
  const rows = Array.isArray(decorationRows) ? decorationRows : [];
  const selectedById = new Map(
    (Array.isArray(selectedRows) ? selectedRows : [])
      .map((entry) => [Number(entry?.id || 0), Math.max(0, Math.floor(Number(entry?.level || 0)))])
      .filter(([id]) => Number.isFinite(id) && id > 0),
  );

  const groupsByQuality = new Map();
  rows.forEach((row) => {
    const id = getOptionId(row);
    if (!id) return;
    const quality = Math.max(0, Math.floor(Number(row?.quality || 0)));
    if (!groupsByQuality.has(quality)) groupsByQuality.set(quality, []);

    const displayRow = buildIslandDecorationDisplayRow(row, selectedById, effectLabelById);
    if (displayRow) groupsByQuality.get(quality).push(displayRow);
  });

  return Array.from(groupsByQuality.entries())
    .sort((left, right) => right[0] - left[0])
    .map(([quality, groupRows]) => ({
      quality,
      label: formatIslandDecorationQualityLabel(quality),
      rows: [...groupRows].sort((left, right) => (
        left.label.localeCompare(right.label)
        || left.id - right.id
      )),
    }));
};

const findCityDecorationGateForIslandDecoration = (islandDecorationId = 0) => {
  const normalizedIslandId = normalizeSkillId(islandDecorationId);
  if (!normalizedIslandId) return null;
  for (const row of CITY_DECORATION_PERMANENT_OPTIONS) {
    const levelRows = Array.isArray(row?.levels) ? row.levels : [];
    const unlockLevel = levelRows.find((levelRow) => (
      normalizeSkillId(levelRow?.decorationUnlockGid) === normalizedIslandId
    ));
    if (unlockLevel) {
      const level = Math.max(1, Math.floor(Number(unlockLevel?.level || 1)));
      return {
        decorationId: normalizeSkillId(row?.decorationId || row?.id || row?.itemGid),
        decorationName: String(row?.name || "Required city skin"),
        level,
        levelLabel: String(unlockLevel?.levelLabel || `Level ${level}`),
      };
    }
  }
  return null;
};

const buildIslandDecorationSetRows = ({
  decorationRows = [],
  selectedRows = [],
  effectLabelById = new Map(),
}) => {
  const selectedById = new Map(
    (Array.isArray(selectedRows) ? selectedRows : [])
      .map((entry) => [Number(entry?.id || 0), Math.max(0, Math.floor(Number(entry?.level || 0)))])
      .filter(([id]) => Number.isFinite(id) && id > 0),
  );

  const rowsById = new Map();
  const groupsBySkinGid = new Map();
  (Array.isArray(decorationRows) ? decorationRows : []).forEach((row) => {
    const displayRow = buildIslandDecorationDisplayRow(row, selectedById, effectLabelById);
    if (!displayRow || displayRow.skinGid <= 0) return;
    rowsById.set(displayRow.id, displayRow);
    if (!groupsBySkinGid.has(displayRow.skinGid)) groupsBySkinGid.set(displayRow.skinGid, []);
    groupsBySkinGid.get(displayRow.skinGid).push(displayRow);
  });

  return Array.from(groupsBySkinGid.entries())
    .map(([skinGid, rows]) => {
      if (!Array.isArray(rows) || rows.length <= 1) return null;
      const linkedMainId = rows.find((row) => row.skinBuffGid > 0)?.skinBuffGid || 0;
      const main = rowsById.get(linkedMainId)
        || rows.find((row) => row.skinBuffGid <= 0)
        || rows[0];
      const requirements = [...rows].sort((left, right) => {
        if (left.id === main.id) return -1;
        if (right.id === main.id) return 1;
        return left.label.localeCompare(right.label) || left.id - right.id;
      }).map((row) => ({
        ...row,
        roleLabel: row.id === main.id ? "Main" : "Attached",
      }));
      const activeCount = requirements.filter((row) => row.selected).length;
      const setActive = activeCount === requirements.length;
      return {
        id: skinGid,
        label: main.label,
        description: main.description,
        activeCount,
        totalCount: requirements.length,
        setActive,
        setBuffLines: setActive ? main.setBuffLines : [],
        setEffectRangeLines: main.setEffectRangeLines || [],
        setLevelLabel: setActive ? main.setActiveLevelLabel : "",
        requirements,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.label.localeCompare(right.label) || left.id - right.id);
};

const groupRowsByCategory = (rows = []) => {
  const groups = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const category = String(row?.category || "other").trim() || "other";
    const label = String(row?.categoryLabel || category).trim() || "Other";
    if (!groups.has(category)) groups.set(category, { category, label, rows: [] });
    groups.get(category).rows.push(row);
  });
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      rows: [...group.rows].sort((left, right) => String(left?.name || "").localeCompare(String(right?.name || ""))),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

const CITY_DECORATION_CATEGORY_ORDER = ["avatarFrame", "chatBubble", "citySkin", "guard", "marchingFleet"];

const buildCityDecorationDisplayGroups = (rows = []) => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const childrenByParent = new Map();
  sourceRows
    .filter((row) => ["citySkinComponent", "guardVariant"].includes(String(row?.category || "")))
    .forEach((row) => {
      const parentId = Number(row?.parentDecorationId || 0);
      if (!Number.isFinite(parentId) || parentId <= 0) return;
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(row);
    });
  return CITY_DECORATION_CATEGORY_ORDER
    .map((category) => {
      const rowsForCategory = sourceRows
        .filter((row) => String(row?.category || "") === category)
        .map((row) => ({
          ...row,
          childRows: [...(childrenByParent.get(Number(row?.id || row?.decorationId || row?.itemGid || 0)) || [])]
            .sort((left, right) => (
              Number(left?.rank || 0) - Number(right?.rank || 0)
              || String(left?.name || "").localeCompare(String(right?.name || ""))
            )),
        }))
        .sort((left, right) => String(left?.name || "").localeCompare(String(right?.name || "")));
      if (!rowsForCategory.length) return null;
      return {
        category,
        label: String(rowsForCategory[0]?.categoryLabel || category).trim(),
        rows: rowsForCategory,
      };
    })
    .filter(Boolean);
};

const formatDurationOptionsText = (options = []) => (
  (Array.isArray(options) ? options : [])
    .map((option) => String(option?.durationLabel || "").trim())
    .filter(Boolean)
    .join(", ")
);

const formatBuffEffectLines = (effects = [], effectLabelById = new Map(), source = {}) => (
  formatCommanderEffectLinesForEffects(effects, effectLabelById, {
    label: source?.label || source?.name,
    description: source?.description,
  })
);

const asNonNegativeInt = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return Math.max(0, Math.floor(Number(fallback || 0)));
  return Math.max(0, Math.floor(parsed));
};

const formatPowerNumber = (value) => Number(asNonNegativeInt(value, 0)).toLocaleString();

const normalizePowerSummaryShape = (raw = {}) => ({
  battlePower: asNonNegativeInt(raw?.battlePower, 0),
  officerPower: asNonNegativeInt(raw?.officerPower, 0),
  titanPower: asNonNegativeInt(raw?.titanPower, 0),
  warplanePower: asNonNegativeInt(raw?.warplanePower, 0),
  antiquesPower: asNonNegativeInt(raw?.antiquesPower, 0),
  equipmentPower: asNonNegativeInt(raw?.equipmentPower, 0),
});

const buildDocumentedPowerSummary = (linkedCommander = null, fallbackRaw = {}) => {
  const fallback = normalizePowerSummaryShape(fallbackRaw);
  if (!linkedCommander || typeof linkedCommander !== "object") {
    return {
      source: "profile",
      updatedAt: null,
      summary: fallback,
    };
  }
  const rankingData = linkedCommander?.rankingData && typeof linkedCommander.rankingData === "object"
    ? linkedCommander.rankingData
    : {};
  return {
    source: "linkedCommander",
    updatedAt: linkedCommander?.updatedAt || null,
    summary: {
      battlePower: asNonNegativeInt(linkedCommander?.battlePower, fallback.battlePower),
      officerPower: asNonNegativeInt(rankingData?.officer?.power, fallback.officerPower),
      titanPower: asNonNegativeInt(rankingData?.titan?.power, fallback.titanPower),
      warplanePower: asNonNegativeInt(rankingData?.warplane?.power, fallback.warplanePower),
      antiquesPower: asNonNegativeInt(rankingData?.antiques?.score, fallback.antiquesPower),
      equipmentPower: fallback.equipmentPower,
    },
  };
};

const resolveLevelRowForPower = (option = null, selectedLevel = 0) => {
  const rows = Array.isArray(option?.levelRows) ? option.levelRows : [];
  if (!rows.length) return null;
  const targetLevel = asNonNegativeInt(selectedLevel, 0);
  return rows.find((row) => asNonNegativeInt(row?.level, 0) === targetLevel)
    || [...rows].reverse().find((row) => asNonNegativeInt(row?.level, 0) <= targetLevel)
    || rows[0];
};

const resolveOptionPowerAtLevel = (option = null, selectedLevel = 0) => {
  if (!option || typeof option !== "object") return 0;
  const selected = asNonNegativeInt(selectedLevel, 0);

  const qualityRows = Array.isArray(option?.qualityRows) ? option.qualityRows : [];
  if (qualityRows.length > 0) {
    const sortedQualityRows = [...qualityRows].sort(
      (left, right) => asNonNegativeInt(left?.rank, 0) - asNonNegativeInt(right?.rank, 0),
    );
    const selectedQuality = sortedQualityRows.find((row) => asNonNegativeInt(row?.rank, 0) === selected)
      || [...sortedQualityRows].reverse().find((row) => asNonNegativeInt(row?.rank, 0) <= selected)
      || sortedQualityRows[0];
    return asNonNegativeInt(selectedQuality?.power, 0);
  }

  const levelRow = resolveLevelRowForPower(option, selected);
  if (levelRow) {
    return asNonNegativeInt(levelRow?.power, 0);
  }

  const progression = Array.isArray(option?.powerProgression) ? option.powerProgression : [];
  if (progression.length > 0) {
    const index = Math.max(0, Math.min(progression.length - 1, selected));
    return asNonNegativeInt(progression[index], 0);
  }

  const directPower = Number(option?.power);
  if (Number.isFinite(directPower) && directPower > 0) {
    return asNonNegativeInt(directPower, 0);
  }

  const basePower = Number(option?.basePower);
  const maxPower = Number(option?.maxPower);
  const rankCount = asNonNegativeInt(option?.rankCount, 0);
  if (Number.isFinite(basePower) && Number.isFinite(maxPower) && rankCount > 0) {
    const boundedRank = Math.max(0, Math.min(rankCount, selected));
    if (boundedRank <= 0) return 0;
    const ratio = boundedRank / rankCount;
    return asNonNegativeInt(Math.round(basePower + ((maxPower - basePower) * ratio)), 0);
  }

  return 0;
};

const calculateTitanSideCatalogPower = (sideData = {}, sideCatalog = {}) => {
  const stageRows = Array.isArray(sideCatalog?.stageRows) ? sideCatalog.stageRows : [];
  const stage = asNonNegativeInt(sideData?.evolutionStage, 0);
  const evolutionLevel = asNonNegativeInt(sideData?.evolutionLevel, 0);
  const stageRow = stageRows.find((row) => asNonNegativeInt(row?.stage, 0) === stage)
    || [...stageRows].reverse().find((row) => asNonNegativeInt(row?.stage, 0) <= stage)
    || null;
  const stagePower = resolveOptionPowerAtLevel(stageRow, evolutionLevel);

  const spellRows = Array.isArray(sideData?.spells) ? sideData.spells : [];
  const spellPower = spellRows.reduce((innerSum, entry) => {
    const option = sideCatalog?.spellMap instanceof Map
      ? sideCatalog.spellMap.get(asNonNegativeInt(entry?.id, 0))
      : null;
    return innerSum + resolveOptionPowerAtLevel(option, entry?.level || 0);
  }, 0);

  const equipPieceRows = Array.isArray(sideData?.equipPieces) ? sideData.equipPieces : [];
  const equipPiecePower = equipPieceRows.reduce((innerSum, entry) => {
    const option = sideCatalog?.equipPieceMap instanceof Map
      ? sideCatalog.equipPieceMap.get(asNonNegativeInt(entry?.id, 0))
      : null;
    return innerSum + resolveOptionPowerAtLevel(option, entry?.level || 0);
  }, 0);

  const equipSkillRows = Array.isArray(sideData?.equipSkills) ? sideData.equipSkills : [];
  const equipSkillPower = equipSkillRows.reduce((innerSum, entry) => {
    const option = sideCatalog?.equipSkillMap instanceof Map
      ? sideCatalog.equipSkillMap.get(asNonNegativeInt(entry?.id, 0))
      : null;
    return innerSum + resolveOptionPowerAtLevel(option, entry?.level || 0);
  }, 0);

  const skinRows = Array.isArray(sideData?.skins) ? sideData.skins : [];
  const skinPower = skinRows.reduce((innerSum, entry) => {
    const option = sideCatalog?.skinMap instanceof Map
      ? sideCatalog.skinMap.get(asNonNegativeInt(entry?.id, 0))
      : null;
    return innerSum + resolveOptionPowerAtLevel(option, entry?.level || 0);
  }, 0);

  const skinStarLevel = asNonNegativeInt(sideData?.skinStarLevel, 0);
  const milestoneRows = Array.isArray(sideCatalog?.skinStarRow?.milestones) ? sideCatalog.skinStarRow.milestones : [];
  const milestonePower = milestoneRows
    .filter((row) => asNonNegativeInt(row?.starLevel, 0) <= skinStarLevel)
    .reduce((innerSum, row) => innerSum + asNonNegativeInt(row?.power, 0), 0);

  return stagePower + spellPower + equipPiecePower + equipSkillPower + skinPower + milestonePower;
};

const calculateTitanSidePower = (sideData = {}, sideCatalog = {}) => {
  const capturedPower = asNonNegativeInt(sideData?.power, 0);
  const catalogPower = calculateTitanSideCatalogPower(sideData, sideCatalog);
  return Math.max(capturedPower, catalogPower);
};

const calculateTitanPower = (titansData = {}, titanCatalogIndex = null) => {
  const perType = titanCatalogIndex?.perType && typeof titanCatalogIndex.perType === "object"
    ? titanCatalogIndex.perType
    : {};
  return TITAN_SIDE_ORDER.reduce((sum, sideKey) => {
    const sideData = titansData?.[sideKey] && typeof titansData[sideKey] === "object"
      ? titansData[sideKey]
      : {};
    const sideCatalog = perType?.[sideKey] && typeof perType[sideKey] === "object"
      ? perType[sideKey]
      : {};

    return sum + calculateTitanSidePower(sideData, sideCatalog);
  }, 0);
};

const calculateWarplaneAdvancementPower = (warplaneRow = {}, warplaneSectionCatalog = null) => {
  const advancementRows = getGroupRowsByKey(warplaneSectionCatalog, "advancementRows");
  const levelRows = getGroupRowsByKey(warplaneSectionCatalog, "levelRows");
  const stage = asNonNegativeInt(warplaneRow?.advancementStage, 0);
  const level = asNonNegativeInt(warplaneRow?.level, 0);
  const stageRow = advancementRows.find((row) => asNonNegativeInt(row?.stage, 0) === stage)
    || [...advancementRows].reverse().find((row) => asNonNegativeInt(row?.stage, 0) <= stage)
    || null;
  const progression = Array.isArray(stageRow?.powerProgression) ? stageRow.powerProgression : [];
  if (!progression.length) return 0;
  const maxLevel = levelRows.reduce((max, row) => {
    const next = asNonNegativeInt(row?.level, 0);
    return next > max ? next : max;
  }, 0);
  const maxProgressStep = Math.max(0, progression.length - 1);
  const rawRequestedStep = warplaneRow?.advancementStep ?? warplaneRow?.advancementLevel ?? warplaneRow?.progressStep;
  const hasExplicitProgressStep = (
    rawRequestedStep !== undefined
    && rawRequestedStep !== null
    && String(rawRequestedStep).trim() !== ""
    && Number.isFinite(Number(rawRequestedStep))
  );
  const parsedRequestedStep = Number(rawRequestedStep);
  const derivedStepFromLevel = maxLevel > 0 && maxProgressStep > 0
    ? Math.round((Math.max(0, Math.min(maxLevel, level)) / maxLevel) * maxProgressStep)
    : 0;
  const index = hasExplicitProgressStep
    ? Math.max(0, Math.min(maxProgressStep, Math.floor(parsedRequestedStep)))
    : derivedStepFromLevel;
  return asNonNegativeInt(progression[Math.max(0, Math.min(progression.length - 1, index))], 0);
};

const calculateWarplanePower = (warplaneData = {}, warplaneSectionCatalog = null) => {
  const calculateRowPower = (row = {}) => Math.max(
    asNonNegativeInt(row?.power, 0),
    calculateWarplaneAdvancementPower(row, warplaneSectionCatalog),
  );
  const ownedRows = Array.isArray(warplaneData?.ownedRows) ? warplaneData.ownedRows : [];
  if (ownedRows.length > 0) {
    return ownedRows.reduce((sum, row) => sum + calculateRowPower(row), 0);
  }
  return calculateRowPower(warplaneData);
};

const calculateEquipmentPower = (equipmentData = {}, equipmentSectionCatalog = null) => {
  const equipRows = getGroupRowsByKey(equipmentSectionCatalog, "equipOptions");
  const extensionTypeRows = getGroupRowsByKey(equipmentSectionCatalog, "extensionTypeOptions");
  const equipMap = new Map(
    equipRows
      .map((row) => [asNonNegativeInt(row?.id, 0), row])
      .filter(([id]) => id > 0),
  );
  const extensionTypeMap = new Map(
    extensionTypeRows
      .map((row) => [asNonNegativeInt(row?.typeId, 0), row])
      .filter(([id]) => id > 0),
  );
  const selectedRows = Array.isArray(equipmentData?.equipOptions) ? equipmentData.equipOptions : [];
  const selectedBaseBySlot = new Map();
  const basePower = selectedRows.reduce((sum, entry) => {
    const option = equipMap.get(asNonNegativeInt(entry?.id, 0));
    if (!option) return sum;
    const slot = asNonNegativeInt(option?.slot, 0);
    if (slot > 0) selectedBaseBySlot.set(slot, option);
    return sum + resolveOptionPowerAtLevel(option, entry?.level || 0);
  }, 0);
  const extensionPower = (Array.isArray(equipmentData?.extensionSlots) ? equipmentData.extensionSlots : []).reduce((sum, entry) => {
    const slot = asNonNegativeInt(entry?.slot, 0);
    const index = asNonNegativeInt(entry?.index, 0);
    if (slot <= 0 || index <= 0) return sum;
    const selectedBase = selectedBaseBySlot.get(slot);
    const peripheralTypes = Array.isArray(selectedBase?.peripheralTypes) ? selectedBase.peripheralTypes : [];
    const typeId = asNonNegativeInt(peripheralTypes[index - 1], 0);
    const typeRow = extensionTypeMap.get(typeId);
    if (!typeRow) return sum;
    const rank = Math.max(0, Math.min(4, asNonNegativeInt(entry?.quality, 0)));
    const rankRows = Array.isArray(typeRow?.rankRows) ? typeRow.rankRows : [];
    const rankRow = rankRows.find((row) => asNonNegativeInt(row?.rank, 0) === rank)
      || [...rankRows].reverse().find((row) => asNonNegativeInt(row?.rank, 0) <= rank)
      || rankRows[0]
      || null;
    return sum + resolveOptionPowerAtLevel(rankRow, entry?.level || 0);
  }, 0);
  return basePower + extensionPower;
};

const calculateAntiquesPower = (antiquesData = {}, antiquesSectionCatalog = null) => {
  const collectionRows = getGroupRowsByKey(antiquesSectionCatalog, "collectionOptions");
  const collectionMap = new Map(
    collectionRows
      .map((row) => [asNonNegativeInt(row?.id, 0), row])
      .filter(([id]) => id > 0),
  );
  const selectedCollections = normalizeAntiqueCollectionEntries(antiquesData?.collectionOptions);
  return selectedCollections.reduce((sum, entry) => {
    const row = collectionMap.get(asNonNegativeInt(entry?.id, 0));
    if (!row) return sum;
    const bestRow = resolveAntiqueStarStageRow(row, entry);
    return sum + asNonNegativeInt(bestRow?.power, 0);
  }, 0);
};

const calculateOfficerUnworldlyPower = (officerId = 0, unworldlySlots = []) => (
  (Array.isArray(unworldlySlots) ? unworldlySlots : []).reduce((innerSum, slot) => {
    const skillId = asNonNegativeInt(slot?.skillId, 0);
    const level = asNonNegativeInt(slot?.level, 0);
    if (skillId <= 0 || level <= 0) return innerSum;
    const resolved = resolveUnworldlyTierBySkill(officerId, skillId, slot?.tierId);
    const powerValues = Array.isArray(resolved?.tier?.powerValues) ? resolved.tier.powerValues : [];
    if (!powerValues.length) return innerSum;
    const index = Math.max(0, Math.min(powerValues.length - 1, level - 1));
    return innerSum + asNonNegativeInt(powerValues[index], 0);
  }, 0)
);

const calculateOfficerRowBattlePower = (row = {}) => {
  const officerId = asNonNegativeInt(row?.officerId, 0);
  if (officerId <= 0) return 0;
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer) return 0;

  const level = asNonNegativeInt(row?.level, 0);
  const stars = Number(row?.stars);
  const starsInfo = decimalStarToStarStage(Number.isFinite(stars) ? stars : 0, officerId);
  const rawStar = Number(row?.star);
  const star = Math.max(0, Math.floor(Number.isFinite(rawStar) ? rawStar : Number(starsInfo?.star || 0)));
  const mastery = getOfficerMasteryValue(officerId, level, starsInfo?.stars || 0);

  const skillSlots = Array.isArray(row?.skills) ? row.skills : [];
  const passiveSkillLevelTotal = skillSlots.reduce((sum, slot, idx) => {
    if (!isOfficerSkillSlotUnlocked(star, idx)) return sum;
    const skillId = asNonNegativeInt(slot?.skillId, 0);
    if (skillId <= 0) return sum;
    return sum + toOfficerSkillLevelDisplay(slot?.level || 0, true);
  }, 0);
  const breakthroughLevel = toOfficerBreakthroughLevelDisplay(row?.breakthroughLevel ?? -1);
  const corePower = Math.floor(
    Number(mastery || 0) * 4.5 * (20 + (passiveSkillLevelTotal * 1.5) + (breakthroughLevel * 2)),
  );
  const unworldlyPower = calculateOfficerUnworldlyPower(officerId, row?.unworldly);
  const skinPower = getOfficerSkinPowerContribution(officerId, row?.skinLevel);
  const equipmentPower = getOfficerEquipmentPowerContribution(officerId, row?.equipment, Number(row?.stars || 0));
  return asNonNegativeInt(corePower + unworldlyPower + skinPower + equipmentPower, 0);
};

const calculateOfficerPower = (officerRows = []) => {
  if (!Array.isArray(officerRows) || !officerRows.length) return 0;
  return officerRows.reduce(
    (sum, row) => (row?.appointed ? sum + calculateOfficerRowBattlePower(row) : sum),
    0,
  );
};

const calculateCommanderPowerSummary = ({
  officerRows = [],
  titansData = {},
  titanCatalogIndex = null,
  warplaneData = {},
  warplaneSectionCatalog = null,
  equipmentData = {},
  equipmentSectionCatalog = null,
  antiquesData = {},
  antiquesSectionCatalog = null,
} = {}) => {
  const officerPower = calculateOfficerPower(officerRows);
  const titanPower = calculateTitanPower(titansData, titanCatalogIndex);
  const warplanePower = calculateWarplanePower(warplaneData, warplaneSectionCatalog);
  const equipmentPower = calculateEquipmentPower(equipmentData, equipmentSectionCatalog);
  const antiquesPower = calculateAntiquesPower(antiquesData, antiquesSectionCatalog);
  const battlePower = officerPower + titanPower + warplanePower + equipmentPower + antiquesPower;
  return normalizePowerSummaryShape({
    battlePower,
    officerPower,
    titanPower,
    warplanePower,
    antiquesPower,
    equipmentPower,
  });
};

const POWER_CALCULATION_NOTES = Object.freeze([
  "Officer battle power uses the in-game hero-power formula: hero value + passive skills + breakthrough, then adds super-attribute and equipment power.",
  "Officer skin power is added from extracted hero skin rank rows when a skin evolution rank is selected.",
  "Titan and warplane power prefer captured per-unit power when available, with catalog-derived progression used as a fallback.",
  "Equipment power is interpolated between base/max power using selected equipment rank.",
]);

const buildTitanCatalogIndex = (titansSectionCatalog = null) => {
  const groups = Array.isArray(titansSectionCatalog?.groups) ? titansSectionCatalog.groups : [];
  const rowsByGroup = Object.fromEntries(groups.map((group) => [String(group?.key || ""), Array.isArray(group?.rows) ? group.rows : []]));

  const typeBySide = {
    sourceSpirit: Number(TITAN_SIDE_META.sourceSpirit.typeId),
    empress: Number(TITAN_SIDE_META.empress.typeId),
  };
  const typeRows = rowsByGroup.titanTypes || [];
  const spellRows = rowsByGroup.spellOptions || [];
  const equipPieceRows = rowsByGroup.equipPieceOptions || [];
  const equipSkillRows = rowsByGroup.equipSkillOptions || [];
  const skinRows = rowsByGroup.skinOptions || [];
  const skinStarRows = rowsByGroup.skinStarMilestones || [];
  const titanXpItems = rowsByGroup.titanXpItems || [];

  const toMap = (rows = []) => new Map(
    rows
      .map((row) => [Number(row?.id || 0), row])
      .filter(([id]) => Number.isFinite(id) && id > 0),
  );

  const typeRowsById = new Map(
    typeRows
      .map((row) => [Number(row?.id || 0), row])
      .filter(([id]) => Number.isFinite(id) && id > 0),
  );

  const perType = {};
  Object.entries(typeBySide).forEach(([sideKey, typeId]) => {
    const phaseRows = getTitanPhaseRowsBySide(sideKey);
    const skillRows = spellRows.filter((row) => {
      const rowTypes = Array.isArray(row?.titanTypes) ? row.titanTypes.map((value) => Number(value || 0)) : [];
      if (!rowTypes.length) return true;
      return rowTypes.includes(typeId);
    });
    const pieceRows = equipPieceRows.filter((row) => Number(row?.titanType || 0) === typeId);
    const skillEquipRows = equipSkillRows.filter((row) => Number(row?.titanType || 0) === typeId);
    const filteredSkinRows = skinRows.filter((row) => Number(row?.titanType || 0) === typeId);
    const skinStar = skinStarRows.find((row) => Number(row?.titanType || 0) === typeId) || null;
    const stageRows = Array.isArray(typeRowsById.get(typeId)?.advancementRows)
      ? typeRowsById.get(typeId).advancementRows
      : [];
    const maxCatalogStage = stageRows.reduce((max, row) => {
      const next = Number(row?.stage || 0);
      return Number.isFinite(next) && next > max ? Math.trunc(next) : max;
    }, 0);
    const maxPhaseStage = phaseRows.reduce((max, row) => {
      const next = Number(row?.stage || 0);
      return Number.isFinite(next) && next > max ? Math.trunc(next) : max;
    }, 0);
    const maxStage = Math.max(maxCatalogStage, maxPhaseStage);
    const maxLevel = Math.max(0, Number(typeRowsById.get(typeId)?.maxLevel || 0));
    perType[sideKey] = {
      typeId,
      typeRow: typeRowsById.get(typeId) || null,
      maxLevel,
      maxStage,
      titanXpItems,
      phaseRows,
      stageRows,
      spellRows: skillRows,
      equipPieceRows: pieceRows,
      equipSkillRows: skillEquipRows,
      skinRows: filteredSkinRows,
      skinStarRow: skinStar,
      spellMap: toMap(skillRows),
      equipPieceMap: toMap(pieceRows),
      equipSkillMap: toMap(skillEquipRows),
      skinMap: toMap(filteredSkinRows),
    };
  });

  return {
    typeBySide,
    rowsByGroup,
    perType,
  };
};

const isTitanSpellUnlockedAtSelection = (
  unlockStage = 0,
  unlockEvolutionLevel = 0,
  selectedStage = 0,
  selectedEvolutionLevel = 0,
) => (
  selectedStage > unlockStage
  || (
    selectedStage === unlockStage
    && selectedEvolutionLevel >= unlockEvolutionLevel
  )
);

const getUnlockedTitanSpellIdSet = (
  sideCatalog = null,
  selectedStageRaw = 0,
  selectedEvolutionLevelRaw = 0,
) => {
  const selectedStage = Math.max(0, Math.floor(Number(selectedStageRaw || 0)));
  const selectedEvolutionLevel = Math.max(0, Math.floor(Number(selectedEvolutionLevelRaw || 0)));
  const stageRows = Array.isArray(sideCatalog?.stageRows) ? sideCatalog.stageRows : [];
  const unlockedSpellIds = new Set();

  stageRows.forEach((stageRow) => {
    const unlockStage = Math.max(0, Math.floor(Number(stageRow?.stage || 0)));
    const activeSkillIds = Array.isArray(stageRow?.activeSkillIds) ? stageRow.activeSkillIds : [];
    const passiveSkillIds = Array.isArray(stageRow?.passiveSkillIds) ? stageRow.passiveSkillIds : [];
    const passiveUnlockLevels = Array.isArray(stageRow?.passiveUnlockLevels) ? stageRow.passiveUnlockLevels : [];

    activeSkillIds.forEach((skillId) => {
      const parsed = normalizeSkillId(skillId);
      if (!parsed) return;
      if (isTitanSpellUnlockedAtSelection(unlockStage, 0, selectedStage, selectedEvolutionLevel)) {
        unlockedSpellIds.add(parsed);
      }
    });

    passiveSkillIds.forEach((skillId, index) => {
      const parsed = normalizeSkillId(skillId);
      if (!parsed) return;
      const unlockEvolutionLevel = Math.max(0, Math.floor(Number(passiveUnlockLevels[index] || 0)));
      if (isTitanSpellUnlockedAtSelection(unlockStage, unlockEvolutionLevel, selectedStage, selectedEvolutionLevel)) {
        unlockedSpellIds.add(parsed);
      }
    });
  });

  return unlockedSpellIds;
};

const applyTitanSideSpellUnlocks = (sideData = {}, sideCatalog = null) => {
  const stageRows = Array.isArray(sideCatalog?.stageRows) ? sideCatalog.stageRows : [];
  if (!stageRows.length) return sideData;
  const unlockedSpellIds = getUnlockedTitanSpellIdSet(
    sideCatalog,
    sideData?.evolutionStage,
    sideData?.evolutionLevel,
  );
  const currentSpells = normalizeIdLevelEntries(sideData?.spells);
  const unlockedCurrentSpells = currentSpells.filter((row) => unlockedSpellIds.has(normalizeSkillId(row?.id)));
  const selectedSpellIds = new Set(unlockedCurrentSpells.map((row) => normalizeSkillId(row?.id)).filter(Boolean));
  const addedSpells = Array.from(unlockedSpellIds)
    .filter((spellId) => !selectedSpellIds.has(spellId))
    .map((spellId) => ({ id: spellId, level: 0 }));
  if (!addedSpells.length && unlockedCurrentSpells.length === currentSpells.length) return sideData;
  return {
    ...sideData,
    spells: normalizeIdLevelEntries([...unlockedCurrentSpells, ...addedSpells]),
  };
};

const normalizeTitanSideWithCatalog = (sideRaw = {}, sideCatalog = null) => {
  const rawSkinGid = normalizeSkillId(sideRaw?.skinGid);
  const rawSkinStarLevel = Math.max(0, Math.floor(Number(sideRaw?.skinStarLevel || 0)));
  const rawSkins = normalizeIdLevelEntries(sideRaw?.skins);
  if (!rawSkins.length && rawSkinGid) {
    rawSkins.push({ id: rawSkinGid, level: rawSkinStarLevel });
  }
  const normalized = {
    level: Math.max(0, Math.floor(Number(sideRaw?.level || 0))),
    evolutionStage: Math.max(0, Math.floor(Number(sideRaw?.evolutionStage || 0))),
    evolutionLevel: Math.max(0, Math.floor(Number(sideRaw?.evolutionLevel || 0))),
    power: Math.max(0, Math.floor(Number(sideRaw?.power || 0))),
    skinGid: rawSkinGid,
    skinStarLevel: rawSkinStarLevel,
    skins: rawSkins,
    spells: normalizeIdLevelEntries(sideRaw?.spells),
    equipPieces: normalizeIdLevelEntries(
      Array.isArray(sideRaw?.equipPieces) && sideRaw.equipPieces.length ? sideRaw.equipPieces : sideRaw?.equipParts,
    ),
    equipSkills: normalizeIdLevelEntries(sideRaw?.equipSkills),
  };
  if (!sideCatalog) return normalized;

  const clampRows = (rows, optionsMap, { allowZero = false } = {}) => rows
    .map((entry) => {
      const option = optionsMap.get(Number(entry?.id || 0));
      if (!option) return null;
      const maxLevel = Math.max(0, Number(option?.maxLevel || 0));
      const level = Math.max(0, Math.floor(Number(entry?.level || 0)));
      return level > 0 || allowZero
        ? { id: Number(entry.id), level: maxLevel > 0 ? Math.min(level, maxLevel) : level }
        : null;
    })
    .filter(Boolean);

  const maxLevel = Math.max(0, Number(sideCatalog.maxLevel || 0));
  const maxStage = Math.max(0, Number(sideCatalog.maxStage || 0));
  normalized.level = maxLevel > 0 ? Math.min(normalized.level, maxLevel) : normalized.level;
  normalized.evolutionStage = maxStage > 0 ? Math.min(normalized.evolutionStage, maxStage) : normalized.evolutionStage;
  const phaseRows = Array.isArray(sideCatalog?.phaseRows) ? sideCatalog.phaseRows : [];
  const phaseRow = phaseRows.find((row) => Number(row?.stage || 0) === Number(normalized.evolutionStage || 0))
    || phaseRows[0]
    || { evolutionLevels: [0] };
  const allowedEvolutionLevels = Array.isArray(phaseRow?.evolutionLevels) && phaseRow.evolutionLevels.length
    ? phaseRow.evolutionLevels
    : [0];
  normalized.evolutionLevel = allowedEvolutionLevels.includes(normalized.evolutionLevel)
    ? normalized.evolutionLevel
    : Number(allowedEvolutionLevels[0] || 0);
  normalized.spells = clampRows(normalized.spells, sideCatalog.spellMap, { allowZero: true });
  normalized.spells = applyTitanSideSpellUnlocks(normalized, sideCatalog).spells;
  normalized.equipPieces = clampRows(normalized.equipPieces, sideCatalog.equipPieceMap);
  normalized.equipSkills = clampRows(normalized.equipSkills, sideCatalog.equipSkillMap, { allowZero: true });
  normalized.skins = normalized.skins
    .map((entry) => {
      const option = sideCatalog?.skinMap?.get(Number(entry?.id || 0));
      if (!option) return null;
      const maxLevel = Math.max(0, Number(option?.maxStarLevel || 0));
      const level = Math.max(0, Math.floor(Number(entry?.level || 0)));
      const clampedLevel = maxLevel > 0 ? Math.min(level, maxLevel) : level;
      return { id: Number(entry.id), level: clampedLevel };
    })
    .filter(Boolean);

  normalized.skinGid = normalized.skins.length > 0
    ? Number(normalized.skins[0]?.id || null)
    : null;
  normalized.skinStarLevel = normalized.skins.reduce(
    (sum, row) => sum + Math.max(0, Math.floor(Number(row?.level || 0))),
    0,
  );
  return normalized;
};

const normalizeTitansDataWithCatalog = (raw = {}, titanCatalogIndex = null) => {
  const source = raw && typeof raw === "object" ? raw : {};
  const hasTypedSides = (
    (source?.sourceSpirit && typeof source.sourceSpirit === "object")
    || (source?.empress && typeof source.empress === "object")
  );
  const normalized = normalizeTitansData(source);

  if (!hasTypedSides && titanCatalogIndex?.perType) {
    const sourceCatalog = titanCatalogIndex.perType.sourceSpirit || null;
    const empressCatalog = titanCatalogIndex.perType.empress || null;
    const splitRowsBySide = (rows = [], sourceMap = new Map(), empressMap = new Map(), { allowZero = false } = {}) => {
      const out = { sourceSpirit: [], empress: [] };
      normalizeIdLevelEntries(rows).forEach((entry) => {
        const id = Number(entry?.id || 0);
        const level = Number(entry?.level || 0);
        if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(level) || (level <= 0 && !allowZero)) return;
        const sourceHas = sourceMap instanceof Map ? sourceMap.has(id) : false;
        const empressHas = empressMap instanceof Map ? empressMap.has(id) : false;
        if (sourceHas && !empressHas) {
          out.sourceSpirit.push({ id, level });
        } else if (empressHas && !sourceHas) {
          out.empress.push({ id, level });
        } else {
          out.sourceSpirit.push({ id, level });
        }
      });
      return out;
    };
    const splitSkinsBySide = (rows = []) => {
      const out = {
        sourceSpirit: { skinGid: null, skinStarLevel: 0, skins: [] },
        empress: { skinGid: null, skinStarLevel: 0, skins: [] },
      };
      normalizeIdLevelEntries(rows).forEach((entry, index) => {
        const id = Number(entry?.id || 0);
        const level = Number(entry?.level || 0);
        if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(level) || level < 0) return;
        const sourceHas = sourceCatalog?.skinMap instanceof Map ? sourceCatalog.skinMap.has(id) : false;
        const empressHas = empressCatalog?.skinMap instanceof Map ? empressCatalog.skinMap.has(id) : false;
        let sideKey = "sourceSpirit";
        if (sourceHas && !empressHas) {
          sideKey = "sourceSpirit";
        } else if (empressHas && !sourceHas) {
          sideKey = "empress";
        } else if (index === 1) {
          sideKey = "empress";
        }
        const sideSkinMap = sideKey === "sourceSpirit"
          ? sourceCatalog?.skinMap
          : empressCatalog?.skinMap;
        const option = sideSkinMap instanceof Map ? sideSkinMap.get(id) : null;
        const maxLevel = Math.max(0, Number(option?.maxStarLevel || 0));
        const clampedLevel = maxLevel > 0 ? Math.min(level, maxLevel) : level;
        out[sideKey].skins.push({ id, level: clampedLevel });
      });
      out.sourceSpirit.skins = normalizeIdLevelEntries(out.sourceSpirit.skins);
      out.empress.skins = normalizeIdLevelEntries(out.empress.skins);
      out.sourceSpirit.skinStarLevel = out.sourceSpirit.skins.reduce(
        (sum, row) => sum + Math.max(0, Math.floor(Number(row?.level || 0))),
        0,
      );
      out.empress.skinStarLevel = out.empress.skins.reduce(
        (sum, row) => sum + Math.max(0, Math.floor(Number(row?.level || 0))),
        0,
      );
      out.sourceSpirit.skinGid = out.sourceSpirit.skins.length ? out.sourceSpirit.skins[0].id : null;
      out.empress.skinGid = out.empress.skins.length ? out.empress.skins[0].id : null;
      return out;
    };

    const splitSpells = splitRowsBySide(
      source?.spells || [],
      sourceCatalog?.spellMap || new Map(),
      empressCatalog?.spellMap || new Map(),
      { allowZero: true },
    );
    const splitPieces = splitRowsBySide(
      source?.equipPieces || [],
      sourceCatalog?.equipPieceMap || new Map(),
      empressCatalog?.equipPieceMap || new Map(),
    );
    const splitSkills = splitRowsBySide(
      source?.equipSkills || [],
      sourceCatalog?.equipSkillMap || new Map(),
      empressCatalog?.equipSkillMap || new Map(),
      { allowZero: true },
    );
    const splitSkins = splitSkinsBySide(source?.skins || []);

    normalized.sourceSpirit = {
      ...normalized.sourceSpirit,
      level: Math.max(0, Math.floor(Number(source?.sourceSpiritLevel || normalized.sourceSpirit?.level || 0))),
      spells: splitSpells.sourceSpirit,
      equipPieces: splitPieces.sourceSpirit,
      equipSkills: splitSkills.sourceSpirit,
      skinGid: splitSkins.sourceSpirit.skinGid,
      skinStarLevel: splitSkins.sourceSpirit.skinStarLevel,
      skins: splitSkins.sourceSpirit.skins,
    };
    normalized.empress = {
      ...normalized.empress,
      level: Math.max(0, Math.floor(Number(source?.empressLevel || normalized.empress?.level || 0))),
      spells: splitSpells.empress,
      equipPieces: splitPieces.empress,
      equipSkills: splitSkills.empress,
      skinGid: splitSkins.empress.skinGid,
      skinStarLevel: splitSkins.empress.skinStarLevel,
      skins: splitSkins.empress.skins,
    };
  }

  return {
    sourceSpirit: normalizeTitanSideWithCatalog(
      normalized?.sourceSpirit || {},
      titanCatalogIndex?.perType?.sourceSpirit || null,
    ),
    empress: normalizeTitanSideWithCatalog(
      normalized?.empress || {},
      titanCatalogIndex?.perType?.empress || null,
    ),
  };
};

const resolveTitanLevelRow = (option = null, selectedLevel = 0) => {
  const rows = Array.isArray(option?.levelRows) ? option.levelRows : [];
  if (!rows.length) return null;
  const targetLevel = Math.max(0, Math.floor(Number(selectedLevel || 0)));
  return rows.find((row) => Number(row?.level || 0) === targetLevel)
    || [...rows].reverse().find((row) => Number(row?.level || 0) <= targetLevel)
    || rows[0];
};

const formatTitanEffectValue = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return "0";
  const rounded = Math.round(numeric * 1000) / 1000;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(3).replace(/\.?0+$/, "");
};

const resolveTitanSkillPlaceholderValue = (index, resolvedRow = null, option = null) => {
  const levelRows = Array.isArray(option?.levelRows) ? option.levelRows : [];
  const fallbackRow = levelRows.length ? levelRows[0] : null;
  const castData = Array.isArray(resolvedRow?.castData)
    ? resolvedRow.castData
    : (Array.isArray(fallbackRow?.castData) ? fallbackRow.castData : []);
  const effects = Array.isArray(resolvedRow?.effects)
    ? resolvedRow.effects
    : (Array.isArray(fallbackRow?.effects) ? fallbackRow.effects : []);

  const castValue = Number(castData?.[index]);
  if (Number.isFinite(castValue)) {
    return { value: castValue, source: "castData" };
  }

  const effectValue = Number(effects?.[index]?.value);
  if (Number.isFinite(effectValue)) {
    return { value: effectValue, source: "effects" };
  }

  if (index === 0 && effects.length === 1) {
    const single = Number(effects[0]?.value);
    if (Number.isFinite(single)) {
      return { value: single, source: "effects" };
    }
  }

  return null;
};

const renderTitanSkillDescription = (option = null, resolvedRow = null) => {
  const template = String(option?.description || "").trim();
  if (!template) return "";
  return template.replace(/\$(\d+)/g, (match, rawIndex, offset, fullText) => {
    const tokenIndex = Math.max(0, Math.floor(Number(rawIndex || 0)));
    const resolvedToken = resolveTitanSkillPlaceholderValue(tokenIndex, resolvedRow, option);
    if (!resolvedToken) return "?";
    const suffix = String(fullText || "").slice(offset + match.length).trimStart();
    const isPercentToken = suffix.startsWith("%");
    let displayValue = Number(resolvedToken.value || 0);
    if (isPercentToken && resolvedToken.source === "effects") {
      displayValue = displayValue / 10;
    }
    return formatTitanEffectValue(displayValue);
  });
};

const mapTitanEffectsForDisplay = (effects = [], effectLabelById = new Map(), context = {}) => (
  (Array.isArray(effects) ? effects : [])
    .map((entry) => {
      const effectId = Number(entry?.effectId || 0);
      const value = Number(entry?.value || 0);
      if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value)) return null;
      return {
        effectId,
        label: resolveCommanderEffectLabel(effectLabelById, effectId),
        value,
        displayValue: formatCommanderEffectValue(value, effectId, context),
      };
    })
    .filter(Boolean)
);

const renderTitanEffectsSummary = (effects = [], effectLabelById = new Map(), context = {}) => {
  const effectLines = formatCommanderEffectLinesForEffects(effects, effectLabelById, context);
  if (effectLines.length) return effectLines.slice(0, 3).join(" | ");
  const rows = mapTitanEffectsForDisplay(effects, effectLabelById, context);
  if (!rows.length) return "No effect at selected level";
  return rows
    .slice(0, 3)
    .map((row) => `${row.label}: ${row.displayValue}`)
    .join(" | ");
};

const getTitanEquipmentGroupLabel = (rows = [], fallback = "Equipment") => {
  const firstRow = Array.isArray(rows) ? rows.find(Boolean) : null;
  const label = String(firstRow?.label || "").trim();
  return label || fallback;
};

const getTitanEquipmentPieceLabel = (option = {}, effectLabelById = new Map()) => {
  const labels = mapTitanEffectsForDisplay(option?.firstLevelEffects || [], effectLabelById, {
    label: option?.label,
    description: option?.description,
  })
    .map((entry) => String(entry?.label || "").trim())
    .filter(Boolean);
  const uniqueLabels = Array.from(new Set(labels)).slice(0, 2);
  if (uniqueLabels.length > 1) {
    const titanPrefix = uniqueLabels[0].match(/^((?:Source Spirit|Empress) )(.+)$/);
    if (titanPrefix && uniqueLabels.every((label) => label.startsWith(titanPrefix[1]))) {
      return `${titanPrefix[1]}${uniqueLabels.map((label) => label.slice(titanPrefix[1].length)).join(" + ")}`;
    }
  }
  if (uniqueLabels.length) return uniqueLabels.join(" + ");
  return String(option?.label || "Equipment piece").trim();
};

const formatTitanSkinStarPercentFromPermille = (rawValue) => {
  const numeric = Number(rawValue || 0);
  if (!Number.isFinite(numeric)) return "+0%";
  const asPercent = Math.round((numeric / 10) * 1000) / 1000;
  const display = Number.isInteger(asPercent)
    ? String(asPercent)
    : asPercent.toFixed(3).replace(/\.?0+$/, "");
  return `${asPercent >= 0 ? "+" : ""}${display}%`;
};

const resolveTitanSkinStarEffectLabel = (effectId, sideCatalog = null, titanEffectLabelById = new Map()) => {
  const mapped = TITAN_SKIN_STAR_LABEL_BY_EFFECT_ID[effectId];
  if (mapped) return mapped;
  const spellLabel = String(sideCatalog?.spellMap?.get(effectId)?.label || "").trim();
  if (spellLabel) return spellLabel;
  return resolveCommanderEffectLabel(titanEffectLabelById, effectId);
};

const renderTitanSkinStarEffectsSummary = (effects = [], sideCatalog = null, titanEffectLabelById = new Map()) => {
  const rows = (Array.isArray(effects) ? effects : [])
    .map((entry) => {
      const effectId = Number(entry?.effectId || 0);
      const value = Number(entry?.value || 0);
      if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value)) return null;
      const label = resolveTitanSkinStarEffectLabel(effectId, sideCatalog, titanEffectLabelById);
      const displayValue = TITAN_SKIN_STAR_PERCENT_LIKE_EFFECT_IDS.has(effectId)
        ? formatTitanSkinStarPercentFromPermille(value)
        : formatCommanderEffectValue(value, effectId, {
          label,
          description: sideCatalog?.label,
        });
      return `${label}: ${displayValue}`;
    })
    .filter(Boolean);
  if (!rows.length) return "No effect";
  return rows.join(" | ");
};

const buildTitanSkillRowsForEvolution = (sideCatalog = null, sideData = {}) => {
  const spellRows = Array.isArray(sideCatalog?.spellRows) ? sideCatalog.spellRows : [];
  const spellMap = sideCatalog?.spellMap instanceof Map ? sideCatalog.spellMap : new Map();
  const phaseRows = Array.isArray(sideCatalog?.phaseRows) ? sideCatalog.phaseRows : [];
  const phaseLabelByStage = new Map(
    phaseRows.map((row) => [Number(row?.stage || 0), String(row?.phase || `Tier ${Number(row?.stage || 0)}`)]),
  );
  const stageRows = Array.isArray(sideCatalog?.stageRows) ? sideCatalog.stageRows : [];
  const selectedStage = Math.max(0, Math.floor(Number(sideData?.evolutionStage || 0)));
  const selectedEvolutionLevel = Math.max(0, Math.floor(Number(sideData?.evolutionLevel || 0)));
  const entries = [];
  stageRows.forEach((stageRow) => {
    const unlockStage = Math.max(0, Math.floor(Number(stageRow?.stage || 0)));
    const activeSkillIds = Array.isArray(stageRow?.activeSkillIds) ? stageRow.activeSkillIds : [];
    const passiveSkillIds = Array.isArray(stageRow?.passiveSkillIds) ? stageRow.passiveSkillIds : [];
    const passiveUnlockLevels = Array.isArray(stageRow?.passiveUnlockLevels) ? stageRow.passiveUnlockLevels : [];

    activeSkillIds.forEach((skillId, index) => {
      const id = normalizeSkillId(skillId);
      if (!id) return;
      entries.push({
        id,
        unlockStage,
        unlockEvolutionLevel: 0,
        source: "active",
        sourceOrder: index,
      });
    });
    passiveSkillIds.forEach((skillId, index) => {
      const id = normalizeSkillId(skillId);
      if (!id) return;
      entries.push({
        id,
        unlockStage,
        unlockEvolutionLevel: Math.max(0, Math.floor(Number(passiveUnlockLevels[index] || 0))),
        source: "passive",
        sourceOrder: index,
      });
    });
  });

  const deduped = new Map();
  entries.forEach((entry) => {
    const existing = deduped.get(entry.id);
    if (!existing) {
      deduped.set(entry.id, entry);
      return;
    }
    const shouldReplace = entry.unlockStage < existing.unlockStage
      || (
        entry.unlockStage === existing.unlockStage
        && entry.unlockEvolutionLevel < existing.unlockEvolutionLevel
      );
    if (shouldReplace) {
      deduped.set(entry.id, entry);
    }
  });

  return Array.from(deduped.values())
    .sort((left, right) => {
      if (left.unlockStage !== right.unlockStage) {
        return left.unlockStage - right.unlockStage;
      }
      if (left.unlockEvolutionLevel !== right.unlockEvolutionLevel) {
        return left.unlockEvolutionLevel - right.unlockEvolutionLevel;
      }
      if (left.source !== right.source) {
        return left.source === "active" ? -1 : 1;
      }
      if (left.sourceOrder !== right.sourceOrder) {
        return left.sourceOrder - right.sourceOrder;
      }
      return left.id - right.id;
    })
    .map((entry) => {
      const option = spellMap.get(entry.id)
        || spellRows.find((row) => Number(row?.id || 0) === Number(entry.id))
        || null;
      const unlocked = isTitanSpellUnlockedAtSelection(
        entry.unlockStage,
        entry.unlockEvolutionLevel,
        selectedStage,
        selectedEvolutionLevel,
      );
      const unlockPhase = phaseLabelByStage.get(Number(entry.unlockStage || 0))
        || `Tier ${Number(entry.unlockStage || 0)}`;
      return {
        ...entry,
        option,
        unlocked,
        unlockText: `Unlocks at ${unlockPhase} step ${Number(entry.unlockEvolutionLevel || 0)}`,
      };
    })
    .filter((entry) => entry.option);
};

const renderTitanIconCell = (row = null, label = "", placeholder = "?") => {
  if (row?.iconPath) {
    return <img src={row.iconPath} alt={label} loading="lazy" />;
  }
  return null;
};

const ANTIQUE_ICON_PREFIXES = Object.freeze([
  "/battle-simulation/antique-icons",
  "/battle-simulation/section-icons",
]);

const ISLAND_ICON_PREFIXES = Object.freeze([
  "/battle-simulation/island-decoration-icons",
  "/battle-simulation/section-icons",
]);

const SectionIconCell = ({
  iconPath = "",
  iconSprite = "",
  alt = "",
  candidatePrefixes = [],
  fallbackClassName = "battle-sim-missing-icon",
}) => {
  const [candidateIndex, setCandidateIndex] = useState(0);
  const candidatePrefixKey = useMemo(() => candidatePrefixes.join("|"), [candidatePrefixes]);
  const candidates = [];
  const seen = new Set();
  const addCandidate = (value) => {
    const normalized = String(value || "").trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    candidates.push(normalized);
  };
  addCandidate(iconPath);
  if (iconSprite) {
    candidatePrefixes.forEach((prefix) => {
      addCandidate(`${String(prefix || "").replace(/\/$/, "")}/${iconSprite}.png`);
    });
  }
  useEffect(() => {
    setCandidateIndex(0);
  }, [iconPath, iconSprite, candidatePrefixKey]);
  if (!candidates.length || candidateIndex >= candidates.length) {
    return <span className={fallbackClassName} aria-label={`${alt || "Item"} icon unavailable`}>--</span>;
  }
  return (
    <img
      src={candidates[candidateIndex]}
      alt={alt}
      loading="lazy"
      onError={() => setCandidateIndex((prev) => prev + 1)}
    />
  );
};

const selectInputText = (event) => {
  if (event?.target && typeof event.target.select === "function") {
    event.target.select();
  }
};

const IMPORTED_SECTION_SUMMARY_KEYS = Object.freeze({
  inventory: [
    ["resources", "Resources"],
    ["items", "Items"],
    ["materials", "Materials"],
    ["equipment", "Equipment"],
    ["badges", "Badges"],
    ["titanItems", "Titan Items"],
    ["valleyItems", "Valley Items"],
  ],
  city: [
    ["buildings", "Buildings"],
    ["cityBuffs", "City Buffs"],
    ["army", "Army Buckets"],
    ["powerBreakdown", "Power Rows"],
  ],
  biochemicalWorkshop: [
    ["pets", "Treatment Room"],
    ["biochemicalTech", "Biochemical Tech"],
  ],
  alliance: [
    ["allianceTech", "Alliance Tech"],
    ["benefitBuildings", "Benefit Buildings"],
  ],
  academy: [
    ["academyTech", "Academy Tech"],
    ["userTech", "User Tech"],
    ["vehicleTech", "Vehicle Tech"],
    ["drillTech", "Drill Tech"],
    ["userTalents", "Talents"],
  ],
});

const countImportedValue = (value) => {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === "object") return Object.keys(value).length;
  return value ? 1 : 0;
};

const ImportedProfileSectionEditor = ({
  sectionKey,
  title,
  description,
  data,
  onApply,
}) => {
  const normalizedDataJson = JSON.stringify(data || {}, null, 2);
  const [jsonText, setJsonText] = useState(() => normalizedDataJson);
  const [jsonError, setJsonError] = useState("");
  useEffect(() => {
    setJsonText(normalizedDataJson);
    setJsonError("");
  }, [sectionKey, normalizedDataJson]);
  const summaryRows = IMPORTED_SECTION_SUMMARY_KEYS[sectionKey] || [];
  const handleApply = () => {
    try {
      const parsed = JSON.parse(jsonText || "{}");
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("Section data must be a JSON object.");
      }
      onApply(parsed);
      setJsonError("");
    } catch (err) {
      setJsonError(err?.message || "Invalid JSON.");
    }
  };
  return (
    <div className="battle-sim-imported-section-editor">
      <div className="battle-sim-imported-section-head">
        <div>
          <strong>{title}</strong>
          <p>{description}</p>
        </div>
        <button type="button" onClick={handleApply}>Apply JSON</button>
      </div>
      {summaryRows.length > 0 ? (
        <div className="battle-sim-imported-section-summary">
          {summaryRows.map(([key, label]) => (
            <div key={`${sectionKey}-${key}`}>
              <span>{label}</span>
              <strong>{countImportedValue(data?.[key]).toLocaleString()}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {sectionKey === "city" && data?.commander ? (
        <div className="battle-sim-imported-section-commander">
          <span>Commander</span>
          <strong>
            {data.commander.name || "Unknown"} #{data.commander.commanderId || "?"}
            {data.commander.cityLevel ? ` | City ${data.commander.cityLevel}` : ""}
            {data.commander.allianceAcronym ? ` | ${data.commander.allianceAcronym}` : ""}
          </strong>
        </div>
      ) : null}
      <textarea
        value={jsonText}
        onChange={(event) => setJsonText(event.target.value)}
        rows={18}
        spellCheck={false}
      />
      {jsonError ? <div className="battle-sim-profile-error">{jsonError}</div> : null}
    </div>
  );
};

const createDraftFromProfile = (profile) => {
  if (!profile) return { ...EMPTY_DRAFT, sections: createEmptySectionsDraft() };
  const parity = profile?.parityProfile && typeof profile.parityProfile === "object"
    ? profile.parityProfile
    : {};
  const sectionsRaw = profile?.sections && typeof profile.sections === "object"
    ? profile.sections
    : {};
  const next = {
    _id: String(profile?._id || ""),
    name: String(profile?.name || ""),
    linkedCommanderId: String(profile?.linkedCommanderId || ""),
    linkedGameCommanderId: String(profile?.linkedGameCommanderId || ""),
    linkedCommanderName: String(profile?.linkedCommanderName || ""),
    powerSummary: {
      battlePower: Number(profile?.powerSummary?.battlePower || 0),
      officerPower: Number(profile?.powerSummary?.officerPower || 0),
      titanPower: Number(profile?.powerSummary?.titanPower || 0),
      warplanePower: Number(profile?.powerSummary?.warplanePower || 0),
      antiquesPower: Number(profile?.powerSummary?.antiquesPower || 0),
      equipmentPower: Number(profile?.powerSummary?.equipmentPower || 0),
    },
    parityProfile: {
      lvl: Number(parity?.lvl || 0),
      t_b_r: Number(parity?.t_b_r || 1),
    },
    externalContext: profile?.externalContext && typeof profile.externalContext === "object"
      ? profile.externalContext
      : {},
    capturedTruth: profile?.capturedTruth && typeof profile.capturedTruth === "object"
      ? profile.capturedTruth
      : null,
    sections: createEmptySectionsDraft(),
  };

  PARITY_ARRAY_FIELDS.forEach((field) => {
    next.parityProfile[field] = JSON.stringify(Array.isArray(parity?.[field]) ? parity[field] : []);
  });

  SECTION_KEYS.forEach((key) => {
    next.sections[key] = {
      ...next.sections[key],
      status: String(sectionsRaw?.[key]?.status || "draft"),
      notes: String(sectionsRaw?.[key]?.notes || ""),
    };
  });

  const rowsRaw = sectionsRaw?.officers?.data?.rows;
  const normalizedRows = reconcileOfficerRowsWithCatalog(rowsRaw);

  next.sections.officers = {
    ...next.sections.officers,
    data: {
      rows: normalizedRows,
    },
  };
  next.sections.titans = {
    ...next.sections.titans,
    data: normalizeTitansData(sectionsRaw?.titans?.data || {}),
  };
  next.sections.warplane = {
    ...next.sections.warplane,
    data: normalizeWarplaneData(sectionsRaw?.warplane?.data || {}),
  };
  next.sections.equipment = {
    ...next.sections.equipment,
    data: normalizeEquipmentData(sectionsRaw?.equipment?.data || {}),
  };
  next.sections.antiques = {
    ...next.sections.antiques,
    data: normalizeAntiquesData(sectionsRaw?.antiques?.data || {}),
  };
  next.sections.badges = {
    ...next.sections.badges,
    data: normalizeBadgesData(sectionsRaw?.badges?.data || {}),
  };
  next.sections.commanderBoosts = {
    ...next.sections.commanderBoosts,
    data: normalizeCommanderBoostsData(sectionsRaw?.commanderBoosts?.data || {}),
  };
  next.sections.island = {
    ...next.sections.island,
    data: normalizeIslandData(sectionsRaw?.island?.data || {}),
  };
  next.sections.cityDecorations = {
    ...next.sections.cityDecorations,
    data: normalizeCityDecorationsData(sectionsRaw?.cityDecorations?.data || {}),
  };
  next.sections.temporaryBuffs = {
    ...next.sections.temporaryBuffs,
    data: normalizeTemporaryBuffsData(sectionsRaw?.temporaryBuffs?.data || {}),
  };
  next.sections.inventory = {
    ...next.sections.inventory,
    data: normalizeInventoryData(sectionsRaw?.inventory?.data || {}),
  };
  next.sections.city = {
    ...next.sections.city,
    data: normalizeCityData(sectionsRaw?.city?.data || {}),
  };
  next.sections.biochemicalWorkshop = {
    ...next.sections.biochemicalWorkshop,
    data: normalizeBiochemicalWorkshopData(sectionsRaw?.biochemicalWorkshop?.data || {}),
  };
  next.sections.alliance = {
    ...next.sections.alliance,
    data: normalizeAllianceData(sectionsRaw?.alliance?.data || {}),
  };
  next.sections.academy = {
    ...next.sections.academy,
    data: normalizeAcademyData(sectionsRaw?.academy?.data || {}),
  };
  next.sections.commanderSkills = {
    ...next.sections.commanderSkills,
    data: normalizeCommanderSkillsData(sectionsRaw?.commanderSkills?.data || {}),
  };

  return next;
};

const getComparableProfileDraft = (draft = {}) => JSON.stringify({
  name: String(draft?.name || ""),
  linkedCommanderId: String(draft?.linkedCommanderId || ""),
  linkedGameCommanderId: String(draft?.linkedGameCommanderId || ""),
  linkedCommanderName: String(draft?.linkedCommanderName || ""),
  parityProfile: draft?.parityProfile || {},
  sections: Object.fromEntries(
    Object.entries(draft?.sections || {}).map(([key, section]) => [key, getComparableProfileSection(key, section)]),
  ),
});

const getComparableProfileSection = (sectionKey = "", section = {}) => {
  if (sectionKey !== "warplane") return section || {};
  const data = section?.data && typeof section.data === "object" ? section.data : {};
  const hasOwnedRows = Array.isArray(data?.ownedRows) && data.ownedRows.length > 0;
  if (!hasOwnedRows) return section || {};
  const derivedSelectionFields = new Set([
    "level",
    "advancementStage",
    "advancementStep",
    "advancementLevel",
    "progressStep",
    "selectedWarplaneId",
    "selectedGid",
    "instanceId",
    "gid",
    "name",
    "power",
  ]);
  const comparableData = Object.fromEntries(
    Object.entries(data).filter(([key]) => !derivedSelectionFields.has(key)),
  );
  return {
    ...(section || {}),
    data: comparableData,
  };
};

const getChangedSectionLabels = (baselineDraft = {}, currentDraft = {}) => (
  SECTION_ITEMS
    .filter((item) => item.key !== "powerSummary")
    .filter((item) => {
      const before = getComparableProfileSection(item.key, baselineDraft?.sections?.[item.key] || {});
      const after = getComparableProfileSection(item.key, currentDraft?.sections?.[item.key] || {});
      return JSON.stringify(before) !== JSON.stringify(after);
    })
    .map((item) => item.label)
);

const getWarplaneRowsByKey = (warplaneData = {}) => {
  const data = normalizeWarplaneData(warplaneData || {});
  const rows = Array.isArray(data?.ownedRows) && data.ownedRows.length > 0
    ? data.ownedRows
    : [{
      instanceId: normalizeSkillId(data?.selectedWarplaneId),
      gid: normalizeSkillId(data?.selectedGid),
      name: String(data?.name || "Warplane"),
      level: Math.max(0, Math.floor(Number(data?.level || 0))),
      advancementStage: Math.max(0, Math.floor(Number(data?.advancementStage || 0))),
      advancementStep: Math.max(0, Math.floor(Number(data?.advancementStep || 0))),
      power: Math.max(0, Number(data?.power || 0)),
    }];
  return new Map(rows.map((row, index) => [getWarplaneRowKey(row, index), row]));
};

const buildWarplaneEvolutionChangeRows = (baselineDraft = {}, currentDraft = {}, advancementRows = []) => {
  const beforeRows = getWarplaneRowsByKey(baselineDraft?.sections?.warplane?.data || {});
  const afterRows = getWarplaneRowsByKey(currentDraft?.sections?.warplane?.data || {});
  const rows = [];
  afterRows.forEach((afterRow, rowKey) => {
    const beforeRow = beforeRows.get(rowKey);
    if (!beforeRow) return;
    const beforeStage = Math.max(0, Math.floor(Number(beforeRow?.advancementStage || 0)));
    const beforeStep = Math.max(0, Math.floor(Number(beforeRow?.advancementStep || 0)));
    const afterStage = Math.max(0, Math.floor(Number(afterRow?.advancementStage || 0)));
    const afterStep = Math.max(0, Math.floor(Number(afterRow?.advancementStep || 0)));
    if (beforeStage === afterStage && beforeStep === afterStep) return;
    const costSummary = summarizeWarplaneEvolutionCosts(beforeRow, afterRow, advancementRows);
    rows.push({
      key: rowKey,
      label: String(afterRow?.name || beforeRow?.name || "Warplane"),
      fromLabel: formatWarplaneEvolutionState(beforeRow),
      toLabel: formatWarplaneEvolutionState(afterRow),
      stepCount: costSummary.stepCount,
      materials: costSummary.materials,
    });
  });
  return rows;
};

const getWarplaneLevelUpgradeCosts = (levelRows = [], xpItems = [], fromLevelRaw = 0, toLevelRaw = 0) => {
  const fromLevel = Math.max(0, Math.floor(Number(fromLevelRaw || 0)));
  const toLevel = Math.max(0, Math.floor(Number(toLevelRaw || 0)));
  if (toLevel <= fromLevel) return [];
  let totalXp = 0;
  for (let level = fromLevel; level < toLevel; level += 1) {
    const levelRow = (Array.isArray(levelRows) ? levelRows : [])
      .find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === level);
    totalXp += Math.max(0, Math.floor(Number(levelRow?.exp || 0)));
  }
  if (totalXp <= 0) return [];
  return [
    {
      resourceType: "xp",
      resourceKey: "warplane_xp",
      count: totalXp,
      label: "total Warplane XP",
    },
    ...buildXpItemBreakdown(totalXp, xpItems),
  ];
};

const formatWarplaneLevelLabel = (levelRaw = 0) => `Level ${toWarplaneLevelDisplay(levelRaw)}`;

const buildWarplaneOptionMaterialRows = ({
  beforeRows = [],
  afterRows = [],
  optionRows = [],
  fieldLabel = "Option",
  rowKey = "global",
  label = "Warplane",
  useDisplayLevel = true,
  lockedLabel = "Inactive",
  costMode = "source",
  fieldKey = "",
  includeCatalogOnly = false,
  freeActivationIds = new Set(),
  appendRow,
}) => {
  const beforeMap = toIdLevelMap(beforeRows);
  const afterMap = toIdLevelMap(afterRows);
  const optionMap = new Map(
    (Array.isArray(optionRows) ? optionRows : [])
      .map((option) => [normalizeSkillId(option?.id), option])
      .filter(([id]) => Boolean(id)),
  );
  const ids = new Set([
    ...(includeCatalogOnly ? Array.from(optionMap.keys()) : []),
    ...Array.from(beforeMap.keys()),
    ...Array.from(afterMap.keys()),
  ]);
  ids.forEach((id) => {
    if (!id) return;
    const beforeEntry = beforeMap.get(id) || null;
    const afterEntry = afterMap.get(id) || null;
    if (!beforeEntry && !afterEntry) return;
    const beforeLevel = beforeEntry ? Math.max(0, Math.floor(Number(beforeEntry?.level || 0))) : 0;
    const afterLevel = afterEntry ? Math.max(0, Math.floor(Number(afterEntry?.level || 0))) : 0;
    if (Boolean(beforeEntry) === Boolean(afterEntry) && beforeLevel === afterLevel) return;
    const option = optionMap.get(id) || { id, label: `${fieldLabel} #${id}`, levelRows: [] };
    let materials = [];
    if (afterEntry && (afterLevel > beforeLevel || !beforeEntry)) {
      const freeActivation = !beforeEntry && freeActivationIds instanceof Set && freeActivationIds.has(id);
      if (!freeActivation || afterLevel > 0) {
        materials = costMode === "target"
          ? summarizeCostRowsBetweenLevels(option?.levelRows || [], freeActivation ? 0 : beforeLevel, afterLevel, { fromMissing: !beforeEntry && !freeActivation })
          : summarizeTitanCostRowsForLevelIncrease(option?.levelRows || [], freeActivation ? 0 : beforeLevel, afterLevel, { fromMissing: !beforeEntry && !freeActivation });
      }
    }
    const levelLabel = useDisplayLevel ? formatWarplaneLevelLabel : formatTitanRawLevelLabel;
    appendRow({
      rowKey,
      label,
      subsection: `${fieldLabel}: ${option?.label || `#${id}`}`,
      fieldKey,
      optionId: id,
      fromLabel: beforeEntry ? levelLabel(beforeLevel) : lockedLabel,
      toLabel: afterEntry ? levelLabel(afterLevel) : lockedLabel,
      materials,
    });
  });
};

const buildWarplaneChangeRows = (
  baselineDraft = {},
  currentDraft = {},
  {
    levelRows = [],
    xpItems = [],
    skillOptions = [],
    equipOptions = [],
    equipSkillOptions = [],
    skinOptions = [],
    advancementRows = [],
  } = {},
) => {
  const beforeRows = getWarplaneRowsByKey(baselineDraft?.sections?.warplane?.data || {});
  const afterRows = getWarplaneRowsByKey(currentDraft?.sections?.warplane?.data || {});
  const beforeData = normalizeWarplaneData(baselineDraft?.sections?.warplane?.data || {});
  const afterData = normalizeWarplaneData(currentDraft?.sections?.warplane?.data || {});
  const freeStageSkillIds = new Set(getUnlockedWarplaneStageSkillIds(advancementRows, afterData));
  const rows = [];
  const appendRow = (row) => {
    rows.push({
      key: `${row.rowKey}:${row.subsection}:${rows.length}`,
      ...row,
    });
  };

  afterRows.forEach((afterRow, rowKey) => {
    const beforeRow = beforeRows.get(rowKey);
    if (!beforeRow) return;
    const beforeLevel = Math.max(0, Math.floor(Number(beforeRow?.level || 0)));
    const afterLevel = Math.max(0, Math.floor(Number(afterRow?.level || 0)));
    if (beforeLevel !== afterLevel) {
      appendRow({
        rowKey,
        label: String(afterRow?.name || beforeRow?.name || "Warplane"),
        subsection: "Level",
        fromLabel: formatWarplaneLevelLabel(beforeLevel),
        toLabel: formatWarplaneLevelLabel(afterLevel),
        materials: getWarplaneLevelUpgradeCosts(levelRows, xpItems, beforeLevel, afterLevel),
      });
    }
  });

  buildWarplaneOptionMaterialRows({
    beforeRows: beforeData?.skills,
    afterRows: afterData?.skills,
    optionRows: skillOptions,
    fieldKey: WARPLANE_SKILL_GROUP_KEYS.skills,
    fieldLabel: "Skill",
    useDisplayLevel: true,
    freeActivationIds: freeStageSkillIds,
    appendRow,
  });
  buildWarplaneOptionMaterialRows({
    beforeRows: beforeData?.equips,
    afterRows: afterData?.equips,
    optionRows: equipOptions,
    fieldKey: WARPLANE_SKILL_GROUP_KEYS.equips,
    fieldLabel: "Equipment",
    useDisplayLevel: true,
    appendRow,
  });
  buildWarplaneOptionMaterialRows({
    beforeRows: beforeData?.equipSkills,
    afterRows: afterData?.equipSkills,
    optionRows: equipSkillOptions,
    fieldKey: WARPLANE_SKILL_GROUP_KEYS.equipSkills,
    fieldLabel: "Equipment Skill",
    useDisplayLevel: true,
    appendRow,
  });
  buildWarplaneOptionMaterialRows({
    beforeRows: beforeData?.skins,
    afterRows: afterData?.skins,
    optionRows: skinOptions,
    fieldKey: WARPLANE_SKILL_GROUP_KEYS.skins,
    fieldLabel: "Skin",
    useDisplayLevel: true,
    lockedLabel: "Locked",
    costMode: "target",
    appendRow,
  });

  return rows;
};

const getOfficerRowsByIdFromDraft = (draft = {}) => {
  const rows = Array.isArray(draft?.sections?.officers?.data?.rows)
    ? draft.sections.officers.data.rows
    : [];
  return new Map(
    rows
      .map((row) => normalizeOfficerRow(row))
      .map((row) => [Math.max(0, Math.trunc(Number(row?.officerId || 0))), row])
      .filter(([officerId]) => officerId > 0),
  );
};

const formatOfficerStarsLabel = (value = 0, officerId = null) => {
  const normalized = decimalStarToStarStage(Number(value || 0), officerId);
  return `${Number(normalized?.stars || 0).toFixed(1)} stars`;
};

const getOfficerStarUpgradeCosts = (officerId = 0, officerLabel = "", fromStarsRaw = 0, toStarsRaw = 0) => {
  const normalizedOfficerId = Math.max(0, Math.trunc(Number(officerId || 0)));
  if (!normalizedOfficerId) return [];
  const fromStars = Number(decimalStarToStarStage(Number(fromStarsRaw || 0), normalizedOfficerId)?.stars || 0);
  const toStars = Number(decimalStarToStarStage(Number(toStarsRaw || 0), normalizedOfficerId)?.stars || 0);
  if (!Number.isFinite(fromStars) || !Number.isFinite(toStars) || toStars <= fromStars) return [];
  const catalogEntry = getOfficerCatalogEntryById(normalizedOfficerId);
  const starStages = Array.isArray(catalogEntry?.starStages) ? catalogEntry.starStages : [];
  const label = String(officerLabel || catalogEntry?.name || `Officer #${normalizedOfficerId}`);
  const validTargetStates = [
    0,
    ...starStages.flatMap((row) => {
      const star = Math.max(0, Math.floor(Number(row?.star || 0)));
      const maxStage = Math.max(0, Math.floor(Number(row?.maxStage || 0)));
      if (star <= 0) return [];
      return Array.from({ length: maxStage + 1 }, (_, stage) => Math.round((star + (stage / 10)) * 10) / 10);
    }),
  ].filter((value, index, all) => all.indexOf(value) === index)
    .sort((left, right) => left - right);
  const materialMap = new Map();
  validTargetStates
    .filter((targetStars) => targetStars > fromStars + 1e-9 && targetStars <= toStars + 1e-9)
    .forEach((targetStars) => {
      const isWholeStarUnlock = Math.abs(targetStars - 1) < 1e-9;
      const costStar = isWholeStarUnlock
        ? 1
        : Math.max(1, Math.ceil(Number(targetStars || 0) - 1e-9));
      const stageRow = starStages.find((row) => Math.floor(Number(row?.star || 0)) === costStar);
      const count = Math.max(0, Number(stageRow?.upgradeCost || 0));
      if (count <= 0) return;
      const fragmentItemGid = normalizeSkillId(catalogEntry?.fragmentItemGid);
      addMaterialCosts(materialMap, [fragmentItemGid ? {
        resourceType: "item",
        itemGid: fragmentItemGid,
        count,
        label: String(catalogEntry?.fragmentItemLabel || `${label} fragments`),
      } : {
        resourceType: "typed",
        resourceKey: `officer_fragment:${normalizedOfficerId}`,
        count,
        label: `${label} fragments`,
      }], 1);
    });
  return Array.from(materialMap.values()).sort((left, right) => (
    String(left.label || "").localeCompare(String(right.label || ""))
    || String(left.itemGid || left.resourceKey || "").localeCompare(String(right.itemGid || right.resourceKey || ""))
  ));
};

const getOfficerSkinUpgradeCosts = (officerId = 0, officerLabel = "", fromLevelRaw = -1, toLevelRaw = -1) => {
  const normalizedOfficerId = Math.max(0, Math.trunc(Number(officerId || 0)));
  if (!normalizedOfficerId) return [];
  const fromLevel = Number.isFinite(Number(fromLevelRaw)) ? Math.trunc(Number(fromLevelRaw)) : -1;
  const toLevel = Number.isFinite(Number(toLevelRaw)) ? Math.trunc(Number(toLevelRaw)) : -1;
  if (toLevel <= fromLevel) return [];
  const catalogEntry = getOfficerCatalogEntryById(normalizedOfficerId);
  const skinOption = Array.isArray(catalogEntry?.skins) ? catalogEntry.skins[0] : null;
  const skinItemGid = normalizeSkillId(skinOption?.skinItemGid);
  const rankRows = Array.isArray(skinOption?.rankRows) ? skinOption.rankRows : [];
  if (!skinItemGid || !rankRows.length) return [];
  const label = String(officerLabel || catalogEntry?.name || `Officer #${normalizedOfficerId}`);
  const materialMap = new Map();
  rankRows
    .filter((row) => {
      const rank = Math.max(0, Math.floor(Number(row?.rank || 0)));
      return rank > fromLevel && rank <= toLevel;
    })
    .map((row) => {
      const count = Math.max(0, Number(row?.upgradeItemCount || 0));
      if (count <= 0) return null;
      return {
        resourceType: "item",
        itemGid: skinItemGid,
        count,
        label: `${label} skin fragments`,
      };
    })
    .filter(Boolean)
    .forEach((cost) => addMaterialCosts(materialMap, [cost], 1));
  return sortMaterialRows(materialMap);
};

const buildXpItemBreakdown = (totalXp = 0, xpItems = []) => {
  let remaining = Math.max(0, Math.floor(Number(totalXp || 0)));
  if (remaining <= 0) return [];
  const sortedItems = [...(Array.isArray(xpItems) ? xpItems : [])]
    .filter((row) => Number(row?.xp || 0) > 0 && normalizeSkillId(row?.itemGid))
    .sort((left, right) => Number(right?.xp || 0) - Number(left?.xp || 0));
  const out = [];
  sortedItems.forEach((item, index) => {
    if (remaining <= 0) return;
    const xp = Math.max(1, Math.floor(Number(item?.xp || 0)));
    const isLast = index === sortedItems.length - 1;
    const count = isLast ? Math.ceil(remaining / xp) : Math.floor(remaining / xp);
    if (count <= 0) return;
    out.push({
      resourceType: "item",
      itemGid: normalizeSkillId(item?.itemGid),
      level: Math.max(0, Math.floor(Number(item?.level || 0))),
      count,
      label: String(item?.label || `Item #${item?.itemGid || ""}`),
    });
    remaining -= count * xp;
  });
  return out;
};

const getOfficerLevelUpgradeCosts = (officerId = 0, fromLevelRaw = 0, toLevelRaw = 0) => {
  const normalizedOfficerId = Math.max(0, Math.trunc(Number(officerId || 0)));
  const fromLevel = Math.max(0, Math.floor(Number(fromLevelRaw || 0)));
  const toLevel = Math.max(0, Math.floor(Number(toLevelRaw || 0)));
  if (!normalizedOfficerId || toLevel <= fromLevel) return [];
  let totalXp = 0;
  for (let level = fromLevel; level < toLevel; level += 1) {
    totalXp += getOfficerLevelExpToNext(normalizedOfficerId, level);
  }
  if (totalXp <= 0) return [];
  return [
    {
      resourceType: "xp",
      resourceKey: "officer_xp",
      count: totalXp,
      label: "total Officer XP",
    },
    ...buildXpItemBreakdown(totalXp, OFFICER_RESOURCE_CATALOG?.officerXpItems || []),
  ];
};

const getOfficerSkillXpUpgradeCosts = (fromLevelRaw = 0, toLevelRaw = 0) => {
  const fromLevel = Math.max(0, Math.floor(Number(fromLevelRaw || 0)));
  const toLevel = Math.max(0, Math.floor(Number(toLevelRaw || 0)));
  if (toLevel <= fromLevel) return [];
  let totalXp = 0;
  for (let level = fromLevel; level < toLevel; level += 1) {
    totalXp += getOfficerSkillLevelExpToNext(level);
  }
  if (totalXp <= 0) return [];
  return [
    {
      resourceType: "xp",
      resourceKey: "officer_skill_xp",
      count: totalXp,
      label: "total Skill XP",
    },
    ...buildXpItemBreakdown(totalXp, OFFICER_RESOURCE_CATALOG?.officerSkillXpItems || []),
  ];
};

const getOfficerBreakthroughUpgradeCosts = (officerId = 0, officerLabel = "", skillId = 0, fromLevelRaw = -1, toLevelRaw = -1) => {
  const normalizedOfficerId = Math.max(0, Math.trunc(Number(officerId || 0)));
  if (!normalizedOfficerId || !normalizeSkillId(skillId)) return [];
  const fromLevel = Math.max(-1, Math.floor(Number(fromLevelRaw ?? -1)));
  const toLevel = Math.max(-1, Math.floor(Number(toLevelRaw ?? -1)));
  if (toLevel <= fromLevel) return [];
  const catalogEntry = getOfficerCatalogEntryById(normalizedOfficerId);
  const skillOption = getOfficerSkillOptionById(skillId);
  const upgradeCosts = Array.isArray(skillOption?.upgradeCosts) ? skillOption.upgradeCosts : [];
  const totalFragments = Array.from({ length: toLevel - fromLevel }, (_, index) => fromLevel + index + 1)
    .reduce((sum, targetInternalLevel) => sum + Math.max(0, Number(upgradeCosts[targetInternalLevel] || 0)), 0);
  if (totalFragments <= 0) return [];
  const label = String(officerLabel || catalogEntry?.name || `Officer #${normalizedOfficerId}`);
  const fragmentItemGid = normalizeSkillId(catalogEntry?.fragmentItemGid);
  if (fragmentItemGid) {
    return [{
      resourceType: "item",
      itemGid: fragmentItemGid,
      count: totalFragments,
      label: String(catalogEntry?.fragmentItemLabel || `${label} fragments`),
    }];
  }
  return [{
    resourceType: "typed",
    resourceKey: `officer_fragment:${normalizedOfficerId}`,
    count: totalFragments,
    label: `${label} fragments`,
  }];
};

const summarizeCostRowsBetweenLevels = (levelRows = [], fromLevelRaw = 0, toLevelRaw = 0, { fromMissing = false } = {}) => {
  const fromLevel = Math.max(0, Math.floor(Number(fromLevelRaw || 0)));
  const toLevel = Math.max(0, Math.floor(Number(toLevelRaw || 0)));
  if (!fromMissing && toLevel <= fromLevel) return [];
  const materialMap = new Map();
  const firstTargetLevel = fromMissing ? 0 : fromLevel + 1;
  for (let targetLevel = firstTargetLevel; targetLevel <= toLevel; targetLevel += 1) {
    const levelRow = (Array.isArray(levelRows) ? levelRows : [])
      .find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === targetLevel);
    addMaterialCosts(materialMap, levelRow?.costs || [], 1);
  }
  return Array.from(materialMap.values()).sort((left, right) => (
    String(left.label || "").localeCompare(String(right.label || ""))
    || String(left.itemGid || left.resourceKey || "").localeCompare(String(right.itemGid || right.resourceKey || ""))
  ));
};

const buildOfficerEquipmentChangeRows = (officerId = 0, beforeRow = {}, afterRow = {}, appendRow = () => {}) => {
  const equipmentSlots = getOfficerEquipmentSlotsForOfficer(officerId);
  const beforeEquipment = Array.isArray(beforeRow?.equipment) ? beforeRow.equipment : [];
  const afterEquipment = Array.isArray(afterRow?.equipment) ? afterRow.equipment : [];
  equipmentSlots.forEach((slotMeta, slotIndex) => {
    const slotNumber = Number(slotMeta?.slotIndex || slotIndex + 1);
    const beforeSlot = beforeEquipment[slotIndex] || { equipId: slotMeta?.equipId || null, level: 0, masteryLevels: [] };
    const afterSlot = afterEquipment[slotIndex] || { equipId: slotMeta?.equipId || null, level: 0, masteryLevels: [] };
    const beforeLevel = Math.max(0, Math.floor(Number(beforeSlot?.level || 0)));
    const afterLevel = Math.max(0, Math.floor(Number(afterSlot?.level || 0)));
    if (beforeLevel !== afterLevel) {
      appendRow({
        subsection: `Equipment ${slotNumber}`,
        fromLabel: `Level ${beforeLevel}`,
        toLabel: `Level ${afterLevel}`,
        materials: summarizeCostRowsBetweenLevels(slotMeta?.levelRows || [], beforeLevel, afterLevel),
      });
    }

    const beforeMasteryById = new Map(
      (Array.isArray(beforeSlot?.masteryLevels) ? beforeSlot.masteryLevels : [])
        .map((entry) => [Math.max(0, Math.trunc(Number(entry?.id || 0))), Math.max(0, Math.floor(Number(entry?.level || 0)))]),
    );
    const afterMasteryById = new Map(
      (Array.isArray(afterSlot?.masteryLevels) ? afterSlot.masteryLevels : [])
        .map((entry) => [Math.max(0, Math.trunc(Number(entry?.id || 0))), Math.max(0, Math.floor(Number(entry?.level || 0)))]),
    );
    getOfficerEquipmentMasteryTracksInUnlockOrder(slotMeta).forEach((track) => {
      const masteryId = Math.max(0, Math.trunc(Number(track?.masteryId || 0)));
      if (!masteryId) return;
      const beforeMasteryLevel = beforeMasteryById.get(masteryId) || 0;
      const afterMasteryLevel = afterMasteryById.get(masteryId) || 0;
      if (beforeMasteryLevel === afterMasteryLevel) return;
      appendRow({
        subsection: `Equipment ${slotNumber} ${track?.label || `Expertise #${masteryId}`}`,
        fromLabel: `Level ${beforeMasteryLevel}`,
        toLabel: `Level ${afterMasteryLevel}`,
        materials: summarizeCostRowsBetweenLevels(track?.levelRows || [], beforeMasteryLevel, afterMasteryLevel),
      });
    });
  });
};

const stringifyComparableValue = (value) => JSON.stringify(value ?? null);

const buildOfficerChangeRows = (baselineDraft = {}, currentDraft = {}) => {
  const beforeRows = getOfficerRowsByIdFromDraft(baselineDraft);
  const afterRows = getOfficerRowsByIdFromDraft(currentDraft);
  const rows = [];
  afterRows.forEach((afterRow, officerId) => {
    const beforeRow = beforeRows.get(officerId);
    if (!beforeRow) return;
    const catalogEntry = getOfficerCatalogEntryById(officerId);
    const label = String(afterRow?.officerLabel || beforeRow?.officerLabel || catalogEntry?.name || `Officer #${officerId}`);
    const appendRow = ({ subsection, fromLabel, toLabel, materials = [] }) => {
      rows.push({
        key: `${officerId}:${subsection}`,
        officerId,
        label,
        subsection,
        fromLabel,
        toLabel,
        materials,
      });
    };

    const beforeLevel = Math.max(0, Math.floor(Number(beforeRow?.level || 0)));
    const afterLevel = Math.max(0, Math.floor(Number(afterRow?.level || 0)));
    if (beforeLevel !== afterLevel) {
      appendRow({
        subsection: "Level",
        fromLabel: String(toOfficerLevelDisplay(beforeLevel)),
        toLabel: String(toOfficerLevelDisplay(afterLevel)),
        materials: getOfficerLevelUpgradeCosts(officerId, beforeLevel, afterLevel),
      });
    }

    const beforeStars = Number(beforeRow?.stars || 0);
    const afterStars = Number(afterRow?.stars || 0);
    if (Math.abs(beforeStars - afterStars) > 1e-9) {
      appendRow({
        subsection: "Stars",
        fromLabel: formatOfficerStarsLabel(beforeStars, officerId),
        toLabel: formatOfficerStarsLabel(afterStars, officerId),
        materials: getOfficerStarUpgradeCosts(officerId, label, beforeStars, afterStars),
      });
    }

    const beforeSkinLevel = beforeRow?.skinLevel == null || beforeRow.skinLevel === "" ? -1 : Math.trunc(Number(beforeRow.skinLevel));
    const afterSkinLevel = afterRow?.skinLevel == null || afterRow.skinLevel === "" ? -1 : Math.trunc(Number(afterRow.skinLevel));
    if (beforeSkinLevel !== afterSkinLevel) {
      appendRow({
        subsection: "Skin",
        fromLabel: beforeSkinLevel >= 0 ? `Tier ${beforeSkinLevel}` : "Locked",
        toLabel: afterSkinLevel >= 0 ? `Tier ${afterSkinLevel}` : "Locked",
        materials: getOfficerSkinUpgradeCosts(officerId, label, beforeSkinLevel, afterSkinLevel),
      });
    }

    if (stringifyComparableValue(beforeRow?.equipment) !== stringifyComparableValue(afterRow?.equipment)) {
      const rowCountBeforeEquipment = rows.length;
      buildOfficerEquipmentChangeRows(officerId, beforeRow, afterRow, appendRow);
      if (rows.length === rowCountBeforeEquipment) {
        appendRow({ subsection: "Equipment", fromLabel: "Previous equipment", toLabel: "Updated equipment" });
      }
    }
    const hasFixedPassiveSkillSlots = String(catalogEntry?.passiveSkillSelectionMode || "").toLowerCase() === "fixed";
    [0, 1, 2].forEach((slotIndex) => {
      const beforeSlot = Array.isArray(beforeRow?.skills) ? beforeRow.skills[slotIndex] : null;
      const afterSlot = Array.isArray(afterRow?.skills) ? afterRow.skills[slotIndex] : null;
      const fixedSkillId = hasFixedPassiveSkillSlots ? normalizeSkillId(catalogEntry?.fixedPassiveSkillSlotIds?.[slotIndex]) : null;
      const beforeSkillId = fixedSkillId || normalizeSkillId(beforeSlot?.skillId);
      const afterSkillId = fixedSkillId || normalizeSkillId(afterSlot?.skillId);
      const beforeSkillLevel = beforeSkillId ? Math.max(0, Math.floor(Number(beforeSlot?.level || 0))) : 0;
      const afterSkillLevel = afterSkillId ? Math.max(0, Math.floor(Number(afterSlot?.level || 0))) : 0;
      if (beforeSkillId === afterSkillId && beforeSkillLevel === afterSkillLevel) return;
      const sameSkill = beforeSkillId && afterSkillId && beforeSkillId === afterSkillId;
      const fromCostLevel = sameSkill ? beforeSkillLevel : 0;
      const materials = afterSkillId && afterSkillLevel > fromCostLevel
        ? getOfficerSkillXpUpgradeCosts(fromCostLevel, afterSkillLevel)
        : [];
      const beforeSkill = beforeSkillId ? getOfficerSkillOptionById(beforeSkillId) : null;
      const afterSkill = afterSkillId ? getOfficerSkillOptionById(afterSkillId) : null;
      appendRow({
        subsection: `Skill ${slotIndex + 1}`,
        fromLabel: beforeSkillId ? `${beforeSkill?.label || `Skill #${beforeSkillId}`} ${toOfficerSkillLevelDisplay(beforeSkillLevel, true)}` : "None",
        toLabel: afterSkillId ? `${afterSkill?.label || `Skill #${afterSkillId}`} ${toOfficerSkillLevelDisplay(afterSkillLevel, true)}` : "None",
        materials,
      });
    });
    if (Number(beforeRow?.breakthroughLevel ?? -1) !== Number(afterRow?.breakthroughLevel ?? -1)) {
      appendRow({
        subsection: "Breakthrough",
        fromLabel: String(toOfficerBreakthroughLevelDisplay(beforeRow?.breakthroughLevel ?? -1)),
        toLabel: String(toOfficerBreakthroughLevelDisplay(afterRow?.breakthroughLevel ?? -1)),
        materials: getOfficerBreakthroughUpgradeCosts(
          officerId,
          label,
          catalogEntry?.breakthroughSkillId,
          beforeRow?.breakthroughLevel ?? -1,
          afterRow?.breakthroughLevel ?? -1,
        ),
      });
    }
    if (stringifyComparableValue(beforeRow?.unworldly) !== stringifyComparableValue(afterRow?.unworldly)) {
      appendRow({ subsection: "Unworldly", fromLabel: "Previous unworldly skills", toLabel: "Updated unworldly skills" });
    }
  });
  return rows;
};

const sortMaterialRows = (materialMap = new Map()) => (
  Array.from(materialMap.values()).sort((left, right) => (
    String(left.label || "").localeCompare(String(right.label || ""))
    || String(left.itemGid || left.resourceKey || "").localeCompare(String(right.itemGid || right.resourceKey || ""))
  ))
);

const getTitanSideLabel = (sideKey = "") => (
  String(TITAN_SIDE_META?.[sideKey]?.label || sideKey || "Titan")
);

const formatTitanLevelLabel = (levelRaw = 0) => `Level ${toTitanLevelDisplay(levelRaw)}`;

const formatTitanRawLevelLabel = (levelRaw = 0) => `Level ${Math.max(0, Math.floor(Number(levelRaw || 0)))}`;

const formatTitanEvolutionState = (sideData = {}, sideCatalog = null) => {
  const stage = Math.max(0, Math.floor(Number(sideData?.evolutionStage || 0)));
  const step = Math.max(0, Math.floor(Number(sideData?.evolutionLevel || 0)));
  const phaseRow = (Array.isArray(sideCatalog?.phaseRows) ? sideCatalog.phaseRows : [])
    .find((row) => Math.max(0, Math.floor(Number(row?.stage || 0))) === stage);
  return `${phaseRow?.phase || `Tier ${stage + 1}`} Step ${step}`;
};

const summarizeTitanCostRowsForLevelIncrease = (levelRows = [], fromLevelRaw = 0, toLevelRaw = 0, { fromMissing = false } = {}) => {
  const fromLevel = Math.max(0, Math.floor(Number(fromLevelRaw || 0)));
  const toLevel = Math.max(0, Math.floor(Number(toLevelRaw || 0)));
  if (!fromMissing && toLevel <= fromLevel) return [];
  const materialMap = new Map();
  const firstCostLevel = fromMissing ? 0 : fromLevel;
  const lastCostLevel = Math.max(firstCostLevel, toLevel - 1);
  for (let level = firstCostLevel; level <= lastCostLevel; level += 1) {
    const levelRow = (Array.isArray(levelRows) ? levelRows : [])
      .find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === level);
    addMaterialCosts(materialMap, levelRow?.costs || [], 1);
  }
  return sortMaterialRows(materialMap);
};

const getTitanLevelUpgradeCosts = (sideCatalog = null, fromLevelRaw = 0, toLevelRaw = 0) => {
  const fromLevel = Math.max(0, Math.floor(Number(fromLevelRaw || 0)));
  const toLevel = Math.max(0, Math.floor(Number(toLevelRaw || 0)));
  if (toLevel <= fromLevel) return [];
  const levelRows = Array.isArray(sideCatalog?.typeRow?.levelRows) ? sideCatalog.typeRow.levelRows : [];
  let totalXp = 0;
  for (let level = fromLevel; level < toLevel; level += 1) {
    const levelRow = levelRows.find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === level);
    totalXp += Math.max(0, Math.floor(Number(levelRow?.exp || 0)));
  }
  if (totalXp <= 0) return [];
  return [
    {
      resourceType: "xp",
      resourceKey: "titan_xp",
      count: totalXp,
      label: "total Titan XP",
    },
    ...buildXpItemBreakdown(totalXp, sideCatalog?.titanXpItems || []),
  ];
};

const getTitanEvolutionUpgradeCosts = (sideCatalog = null, beforeSide = {}, afterSide = {}) => {
  const phaseRows = Array.isArray(sideCatalog?.phaseRows) ? sideCatalog.phaseRows : [];
  if (!phaseRows.length) return [];
  const states = phaseRows.flatMap((phaseRow) => {
    const stage = Math.max(0, Math.floor(Number(phaseRow?.stage || 0)));
    const levels = Array.isArray(phaseRow?.evolutionLevels) && phaseRow.evolutionLevels.length
      ? phaseRow.evolutionLevels
      : [0];
    return levels
      .map((level) => ({
        stage,
        level: Math.max(0, Math.floor(Number(level || 0))),
      }))
      .sort((left, right) => left.level - right.level);
  });
  const getStateIndex = (side = {}) => {
    const stage = Math.max(0, Math.floor(Number(side?.evolutionStage || 0)));
    const level = Math.max(0, Math.floor(Number(side?.evolutionLevel || 0)));
    const exactIndex = states.findIndex((state) => state.stage === stage && state.level === level);
    if (exactIndex >= 0) return exactIndex;
    const fallbackIndex = states.findIndex((state) => state.stage === stage);
    return fallbackIndex >= 0 ? fallbackIndex : 0;
  };
  const beforeIndex = getStateIndex(beforeSide);
  const afterIndex = getStateIndex(afterSide);
  if (afterIndex <= beforeIndex) return [];
  const materialMap = new Map();
  const stageRows = Array.isArray(sideCatalog?.stageRows) ? sideCatalog.stageRows : [];
  for (let index = beforeIndex + 1; index <= afterIndex; index += 1) {
    const targetState = states[index];
    const stageRow = stageRows.find((row) => (
      Math.max(0, Math.floor(Number(row?.stage || 0))) === Math.max(0, Math.floor(Number(targetState?.stage || 0)))
    ));
    addMaterialCosts(materialMap, stageRow?.costs || [], 1);
  }
  return sortMaterialRows(materialMap);
};

const toIdLevelMap = (rows = []) => new Map(
  normalizeIdLevelEntries(rows).map((row) => [Math.max(0, Math.trunc(Number(row?.id || 0))), row]),
);

const buildTitanOptionChangeRows = ({
  rows,
  sideKey,
  label,
  fieldLabel,
  beforeRows = [],
  afterRows = [],
  optionRows = [],
  optionMap,
  allowMissing = false,
  useDisplayLevel = true,
  lockedLabel = "Inactive",
  freeActivationIds = new Set(),
  appendRow,
}) => {
  const beforeMap = toIdLevelMap(beforeRows);
  const afterMap = toIdLevelMap(afterRows);
  const catalogOptionMap = optionMap instanceof Map
    ? optionMap
    : new Map((Array.isArray(optionRows) ? optionRows : []).map((row) => [normalizeSkillId(row?.id), row]).filter(([id]) => Boolean(id)));
  const ids = new Set([
    ...Array.from(catalogOptionMap.keys()),
    ...Array.from(beforeMap.keys()),
    ...Array.from(afterMap.keys()),
  ]);
  ids.forEach((id) => {
    if (!id) return;
    const beforeEntry = beforeMap.get(id) || null;
    const afterEntry = afterMap.get(id) || null;
    if (!allowMissing && !beforeEntry && !afterEntry) return;
    const beforeLevel = beforeEntry ? Math.max(0, Math.floor(Number(beforeEntry?.level || 0))) : 0;
    const afterLevel = afterEntry ? Math.max(0, Math.floor(Number(afterEntry?.level || 0))) : 0;
    if (Boolean(beforeEntry) === Boolean(afterEntry) && beforeLevel === afterLevel) return;
    const option = catalogOptionMap.get(id) || { id, label: `${fieldLabel} #${id}`, levelRows: [] };
    let materials = [];
    if (afterEntry && (afterLevel > beforeLevel || !beforeEntry)) {
      const freeActivation = !beforeEntry && freeActivationIds instanceof Set && freeActivationIds.has(id);
      if (!freeActivation || afterLevel > 0) {
        materials = summarizeTitanCostRowsForLevelIncrease(
          option?.levelRows || [],
          freeActivation ? 0 : beforeLevel,
          afterLevel,
          { fromMissing: !beforeEntry && !freeActivation },
        );
      }
    }
    const formatLevel = useDisplayLevel ? formatTitanLevelLabel : formatTitanRawLevelLabel;
    appendRow(rows, {
      sideKey,
      label,
      subsection: `${fieldLabel}: ${option?.label || `#${id}`}`,
      fromLabel: beforeEntry ? formatLevel(beforeLevel) : lockedLabel,
      toLabel: afterEntry ? formatLevel(afterLevel) : lockedLabel,
      materials,
    });
  });
};

const buildTitanChangeRows = (baselineDraft = {}, currentDraft = {}, titanCatalogIndex = null) => {
  const beforeData = normalizeTitansDataWithCatalog(baselineDraft?.sections?.titans?.data || {}, titanCatalogIndex);
  const afterData = normalizeTitansDataWithCatalog(currentDraft?.sections?.titans?.data || {}, titanCatalogIndex);
  const rows = [];
  const appendRow = (targetRows, row) => {
    targetRows.push({
      key: `${row.sideKey}:${row.subsection}:${targetRows.length}`,
      ...row,
    });
  };

  TITAN_SIDE_ORDER.forEach((sideKey) => {
    const sideCatalog = titanCatalogIndex?.perType?.[sideKey] || {};
    const beforeSide = beforeData?.[sideKey] || {};
    const afterSide = afterData?.[sideKey] || {};
    const label = getTitanSideLabel(sideKey);
    const freeSpellIds = getUnlockedTitanSpellIdSet(
      sideCatalog,
      afterSide?.evolutionStage,
      afterSide?.evolutionLevel,
    );

    const beforeLevel = Math.max(0, Math.floor(Number(beforeSide?.level || 0)));
    const afterLevel = Math.max(0, Math.floor(Number(afterSide?.level || 0)));
    if (beforeLevel !== afterLevel) {
      appendRow(rows, {
        sideKey,
        label,
        subsection: "Level",
        fromLabel: formatTitanLevelLabel(beforeLevel),
        toLabel: formatTitanLevelLabel(afterLevel),
        materials: getTitanLevelUpgradeCosts(sideCatalog, beforeLevel, afterLevel),
      });
    }

    const beforeStage = Math.max(0, Math.floor(Number(beforeSide?.evolutionStage || 0)));
    const beforeEvolutionLevel = Math.max(0, Math.floor(Number(beforeSide?.evolutionLevel || 0)));
    const afterStage = Math.max(0, Math.floor(Number(afterSide?.evolutionStage || 0)));
    const afterEvolutionLevel = Math.max(0, Math.floor(Number(afterSide?.evolutionLevel || 0)));
    if (beforeStage !== afterStage || beforeEvolutionLevel !== afterEvolutionLevel) {
      appendRow(rows, {
        sideKey,
        label,
        subsection: "Evolution",
        fromLabel: formatTitanEvolutionState(beforeSide, sideCatalog),
        toLabel: formatTitanEvolutionState(afterSide, sideCatalog),
        materials: getTitanEvolutionUpgradeCosts(sideCatalog, beforeSide, afterSide),
      });
    }

    buildTitanOptionChangeRows({
      rows,
      sideKey,
      label,
      fieldLabel: "Talent",
      beforeRows: beforeSide?.spells,
      afterRows: afterSide?.spells,
      optionRows: sideCatalog?.spellRows,
      optionMap: sideCatalog?.spellMap,
      allowMissing: false,
      useDisplayLevel: true,
      freeActivationIds: freeSpellIds,
      appendRow,
    });
    buildTitanOptionChangeRows({
      rows,
      sideKey,
      label,
      fieldLabel: "Equipment",
      beforeRows: beforeSide?.equipPieces,
      afterRows: afterSide?.equipPieces,
      optionRows: sideCatalog?.equipPieceRows,
      optionMap: sideCatalog?.equipPieceMap,
      allowMissing: true,
      useDisplayLevel: false,
      appendRow,
    });
    buildTitanOptionChangeRows({
      rows,
      sideKey,
      label,
      fieldLabel: "Equipment Skill",
      beforeRows: beforeSide?.equipSkills,
      afterRows: afterSide?.equipSkills,
      optionRows: sideCatalog?.equipSkillRows,
      optionMap: sideCatalog?.equipSkillMap,
      allowMissing: true,
      useDisplayLevel: true,
      appendRow,
    });
    buildTitanOptionChangeRows({
      rows,
      sideKey,
      label,
      fieldLabel: "Skin",
      beforeRows: beforeSide?.skins,
      afterRows: afterSide?.skins,
      optionRows: sideCatalog?.skinRows,
      optionMap: sideCatalog?.skinMap,
      allowMissing: true,
      useDisplayLevel: false,
      lockedLabel: "Locked",
      appendRow,
    });
  });

  return rows;
};

const getAntiqueRowsByIdFromDraft = (draft = {}) => new Map(
  normalizeAntiqueCollectionEntries(draft?.sections?.antiques?.data?.collectionOptions || [])
    .map((row) => [Math.max(0, Math.trunc(Number(row?.id || 0))), row])
    .filter(([id]) => id > 0),
);

const formatAntiqueProgressLabel = (entry = null, inactiveLabel = "Inactive") => {
  if (!entry) return inactiveLabel;
  const star = Math.max(0, Math.floor(Number(entry?.star ?? entry?.level ?? 0)));
  const stage = Math.max(0, Math.floor(Number(entry?.stage || 0)));
  return `${(Math.round((star + (stage / 10)) * 10) / 10).toFixed(stage > 0 ? 1 : 0)} stars`;
};

const getAntiqueStarRowCosts = (collectionRow = {}, starRow = {}) => {
  const directCosts = Array.isArray(starRow?.costs) ? starRow.costs : [];
  if (directCosts.length) return directCosts;
  const pieceGid = normalizeSkillId(collectionRow?.pieceGid);
  const count = Math.max(0, Number(starRow?.cost || 0));
  if (!pieceGid || count <= 0) return [];
  return [{
    resourceType: "item",
    itemGid: pieceGid,
    count,
    label: `${collectionRow?.label || "Antique"} fragments`,
  }];
};

const summarizeAntiqueUpgradeCosts = (collectionRow = {}, beforeEntry = null, afterEntry = null) => {
  if (!afterEntry) return [];
  const progressOptions = getAntiqueProgressOptions(collectionRow);
  if (!progressOptions.length) return [];
  const beforeProgress = beforeEntry ? resolveAntiqueProgressOption(collectionRow, beforeEntry) : null;
  const afterProgress = resolveAntiqueProgressOption(collectionRow, afterEntry);
  const beforeIndex = beforeProgress
    ? progressOptions.findIndex((option) => option.star === beforeProgress.star && option.stage === beforeProgress.stage)
    : -1;
  const afterIndex = progressOptions.findIndex((option) => option.star === afterProgress.star && option.stage === afterProgress.stage);
  if (afterIndex < 0 || afterIndex <= beforeIndex) return [];

  const materialMap = new Map();
  for (let index = beforeIndex + 1; index <= afterIndex; index += 1) {
    const option = progressOptions[index];
    const starRow = resolveAntiqueStarStageRow(collectionRow, option);
    addMaterialCosts(materialMap, getAntiqueStarRowCosts(collectionRow, starRow), 1);
  }
  return sortMaterialRows(materialMap);
};

const buildAntiqueChangeRows = (baselineDraft = {}, currentDraft = {}, collectionRows = []) => {
  const beforeMap = getAntiqueRowsByIdFromDraft(baselineDraft);
  const afterMap = getAntiqueRowsByIdFromDraft(currentDraft);
  const collectionMap = new Map(
    (Array.isArray(collectionRows) ? collectionRows : [])
      .map((row) => [normalizeSkillId(row?.id), row])
      .filter(([id]) => Boolean(id)),
  );
  const ids = new Set([
    ...Array.from(beforeMap.keys()),
    ...Array.from(afterMap.keys()),
  ]);
  const rows = [];
  ids.forEach((id) => {
    const beforeEntry = beforeMap.get(id) || null;
    const afterEntry = afterMap.get(id) || null;
    const collectionRow = collectionMap.get(id) || { id, label: `Antique #${id}`, starRows: [] };
    const beforeProgress = beforeEntry ? resolveAntiqueProgressOption(collectionRow, beforeEntry) : null;
    const afterProgress = afterEntry ? resolveAntiqueProgressOption(collectionRow, afterEntry) : null;
    const unchanged = Boolean(beforeEntry) === Boolean(afterEntry)
      && Number(beforeProgress?.star || 0) === Number(afterProgress?.star || 0)
      && Number(beforeProgress?.stage || 0) === Number(afterProgress?.stage || 0);
    if (unchanged) return;
    rows.push({
      key: `antique:${id}:${rows.length}`,
      antiqueId: id,
      label: String(collectionRow?.label || `Antique #${id}`),
      subsection: "Stars",
      fromLabel: formatAntiqueProgressLabel(beforeProgress),
      toLabel: formatAntiqueProgressLabel(afterProgress),
      materials: summarizeAntiqueUpgradeCosts(collectionRow, beforeProgress, afterProgress),
    });
  });
  return rows.sort((left, right) => String(left.label || "").localeCompare(String(right.label || "")));
};

const getBadgeRowsByGidFromDraft = (draft = {}) => new Map(
  normalizeBadgeEquippedEntries(draft?.sections?.badges?.data?.equipped || [])
    .map((row) => [normalizeSkillId(row?.gid), row])
    .filter(([gid]) => Boolean(gid)),
);

const summarizeBadgeUpgradeCosts = (badgeRow = {}, beforeEntry = null, afterEntry = null) => {
  if (!afterEntry) return [];
  const afterLevel = Math.max(0, Math.floor(Number(afterEntry?.level || 0)));
  const beforeLevel = beforeEntry ? Math.max(0, Math.floor(Number(beforeEntry?.level || 0))) : -1;
  if (afterLevel <= beforeLevel) return [];
  const levelRows = Array.isArray(badgeRow?.levelRows) ? badgeRow.levelRows : [];
  const materialMap = new Map();
  for (let targetLevel = beforeLevel + 1; targetLevel <= afterLevel; targetLevel += 1) {
    const levelRow = levelRows.find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === targetLevel);
    addMaterialCosts(materialMap, levelRow?.costs || [], 1);
  }
  return sortMaterialRows(materialMap);
};

const getBadgeLevelOptions = (badgeRow = {}, selectedLevelRaw = 0) => {
  const selectedLevel = Math.max(0, Math.floor(Number(selectedLevelRaw || 0)));
  const levelSet = new Set(
    (Array.isArray(badgeRow?.levelRows) ? badgeRow.levelRows : [])
      .map((row) => Math.max(0, Math.floor(Number(row?.level || 0))))
      .filter((level) => Number.isFinite(level)),
  );
  const maxLevel = Math.max(
    selectedLevel,
    Math.max(0, Math.floor(Number(badgeRow?.maxLevel || 0))),
    ...Array.from(levelSet),
  );
  if (!levelSet.size && maxLevel > 0) {
    buildIntegerRange(0, maxLevel).forEach((level) => levelSet.add(level));
  }
  levelSet.add(selectedLevel);
  levelSet.add(0);
  return Array.from(levelSet).sort((left, right) => left - right);
};

const buildBadgeChangeRows = (baselineDraft = {}, currentDraft = {}, badgeRows = []) => {
  const beforeMap = getBadgeRowsByGidFromDraft(baselineDraft);
  const afterMap = getBadgeRowsByGidFromDraft(currentDraft);
  const badgeMap = new Map(
    (Array.isArray(badgeRows) ? badgeRows : [])
      .map((row) => [normalizeSkillId(row?.gid || row?.id), row])
      .filter(([gid]) => Boolean(gid)),
  );
  const ids = new Set([
    ...Array.from(beforeMap.keys()),
    ...Array.from(afterMap.keys()),
  ]);
  const rows = [];
  ids.forEach((gid) => {
    const beforeEntry = beforeMap.get(gid) || null;
    const afterEntry = afterMap.get(gid) || null;
    const badgeRow = badgeMap.get(gid) || { gid, label: `Badge #${gid}`, levelRows: [] };
    const beforeLevel = beforeEntry ? Math.max(0, Math.floor(Number(beforeEntry?.level || 0))) : -1;
    const afterLevel = afterEntry ? Math.max(0, Math.floor(Number(afterEntry?.level || 0))) : -1;
    if (beforeLevel === afterLevel) return;
    rows.push({
      key: `badge:${gid}:${rows.length}`,
      badgeGid: gid,
      label: String(badgeRow?.label || `Badge #${gid}`),
      subsection: "Level",
      fromLabel: beforeEntry ? `Level ${beforeLevel}` : "Inactive",
      toLabel: afterEntry ? `Level ${afterLevel}` : "Inactive",
      materials: summarizeBadgeUpgradeCosts(badgeRow, beforeEntry, afterEntry),
    });
  });
  return rows.sort((left, right) => (
    Number(badgeMap.get(left.badgeGid)?.position || 0) - Number(badgeMap.get(right.badgeGid)?.position || 0)
    || String(left.label || "").localeCompare(String(right.label || ""))
  ));
};

const getIslandRowsByIdFromDraft = (draft = {}, field = "cityDecorationOptions") => new Map(
  normalizeIdLevelEntries(draft?.sections?.island?.data?.[field] || [])
    .map((row) => [normalizeSkillId(row?.id), row])
    .filter(([id]) => Boolean(id)),
);

const summarizeIslandDecorationUpgradeCosts = (optionRow = {}, beforeEntry = null, afterEntry = null) => {
  if (!afterEntry) return [];
  const afterLevel = Math.max(0, Math.floor(Number(afterEntry?.level || 0)));
  const beforeLevel = beforeEntry ? Math.max(0, Math.floor(Number(beforeEntry?.level || 0))) : -1;
  if (afterLevel <= beforeLevel) return [];
  const materialMap = new Map();
  if (!beforeEntry) {
    addMaterialCosts(materialMap, optionRow?.purchaseCosts || [], 1);
  }
  const levelRows = Array.isArray(optionRow?.levelRows) ? optionRow.levelRows : [];
  for (let targetLevel = beforeLevel + 1; targetLevel <= afterLevel; targetLevel += 1) {
    const levelRow = levelRows.find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === targetLevel);
    addMaterialCosts(materialMap, levelRow?.costs || [], 1);
  }
  return sortMaterialRows(materialMap);
};

const buildIslandDecorationChangeRowsForField = ({
  baselineDraft = {},
  currentDraft = {},
  field = "cityDecorationOptions",
  optionRows = [],
  typeLabel = "Island Decoration",
}) => {
  const beforeMap = getIslandRowsByIdFromDraft(baselineDraft, field);
  const afterMap = getIslandRowsByIdFromDraft(currentDraft, field);
  const optionMap = new Map(
    (Array.isArray(optionRows) ? optionRows : [])
      .map((row) => [normalizeSkillId(row?.id), row])
      .filter(([id]) => Boolean(id)),
  );
  const ids = new Set([...Array.from(beforeMap.keys()), ...Array.from(afterMap.keys())]);
  const rows = [];
  ids.forEach((id) => {
    const beforeEntry = beforeMap.get(id) || null;
    const afterEntry = afterMap.get(id) || null;
    const beforeLevel = beforeEntry ? Math.max(0, Math.floor(Number(beforeEntry?.level || 0))) : -1;
    const afterLevel = afterEntry ? Math.max(0, Math.floor(Number(afterEntry?.level || 0))) : -1;
    if (beforeLevel === afterLevel) return;
    const optionRow = optionMap.get(id) || { id, label: `${typeLabel} #${id}`, levelRows: [] };
    const activateOnly = Boolean(optionRow?.activateOnly);
    const formatIslandChangeLevelLabel = (entry, level) => {
      if (!entry) return "Inactive";
      return activateOnly ? "Activated" : `Level ${toIslandLevelDisplay(level, true)}`;
    };
    rows.push({
      key: `island:${field}:${id}:${rows.length}`,
      typeKey: field,
      islandDecorationId: id,
      label: String(optionRow?.label || `${typeLabel} #${id}`),
      subsection: typeLabel,
      fromLabel: formatIslandChangeLevelLabel(beforeEntry, beforeLevel),
      toLabel: formatIslandChangeLevelLabel(afterEntry, afterLevel),
      materials: summarizeIslandDecorationUpgradeCosts(optionRow, beforeEntry, afterEntry),
    });
  });
  return rows.sort((left, right) => String(left.label || "").localeCompare(String(right.label || "")));
};

const buildIslandDecorationChangeRows = (
  baselineDraft = {},
  currentDraft = {},
  {
    cityDecorationRows = [],
    specialDecorationRows = [],
    dockDecorationRows = [],
  } = {},
) => [
  ...buildIslandDecorationChangeRowsForField({
    baselineDraft,
    currentDraft,
    field: "cityDecorationOptions",
    optionRows: cityDecorationRows,
    typeLabel: "City Decoration",
  }),
  ...buildIslandDecorationChangeRowsForField({
    baselineDraft,
    currentDraft,
    field: "specialDecorationOptions",
    optionRows: specialDecorationRows,
    typeLabel: "Special Decoration",
  }),
  ...buildIslandDecorationChangeRowsForField({
    baselineDraft,
    currentDraft,
    field: "dockDecorationOptions",
    optionRows: dockDecorationRows,
    typeLabel: "Dock Decoration",
  }),
];

const getPermanentCityDecorationRowsByIdFromDraft = (draft = {}) => new Map(
  normalizeIdLevelEntries(draft?.sections?.cityDecorations?.data?.permanent || [])
    .map((row) => [normalizeSkillId(row?.id), row])
    .filter(([id]) => Boolean(id)),
);

const summarizePermanentCityDecorationUpgradeCosts = (optionRow = {}, beforeEntry = null, afterEntry = null) => {
  if (!afterEntry) return [];
  const afterLevel = Math.max(0, Math.floor(Number(afterEntry?.level || 0)));
  const beforeLevel = beforeEntry ? Math.max(0, Math.floor(Number(beforeEntry?.level || 0))) : 0;
  if (afterLevel <= beforeLevel) return [];
  const materialMap = new Map();
  const levelRows = Array.isArray(optionRow?.levels) ? optionRow.levels : [];
  for (let targetLevel = beforeLevel + 1; targetLevel <= afterLevel; targetLevel += 1) {
    const levelRow = levelRows.find((row) => Math.max(0, Math.floor(Number(row?.level || 0))) === targetLevel);
    addMaterialCosts(materialMap, levelRow?.costs || [], 1);
  }
  return sortMaterialRows(materialMap);
};

const buildPermanentCityDecorationChangeRows = (
  baselineDraft = {},
  currentDraft = {},
  optionRows = [],
) => {
  const beforeMap = getPermanentCityDecorationRowsByIdFromDraft(baselineDraft);
  const afterMap = getPermanentCityDecorationRowsByIdFromDraft(currentDraft);
  const optionMap = new Map(
    (Array.isArray(optionRows) ? optionRows : [])
      .map((row) => [normalizeSkillId(row?.id || row?.decorationId || row?.itemGid), row])
      .filter(([id]) => Boolean(id)),
  );
  const ids = new Set([...Array.from(beforeMap.keys()), ...Array.from(afterMap.keys())]);
  const rows = [];
  ids.forEach((id) => {
    const beforeEntry = beforeMap.get(id) || null;
    const afterEntry = afterMap.get(id) || null;
    const beforeLevel = beforeEntry ? Math.max(0, Math.floor(Number(beforeEntry?.level || 0))) : 0;
    const afterLevel = afterEntry ? Math.max(0, Math.floor(Number(afterEntry?.level || 0))) : 0;
    if (beforeLevel === afterLevel) return;
    const optionRow = optionMap.get(id) || { id, name: `City Decoration #${id}`, levels: [] };
    rows.push({
      key: `cityDecorations:permanent:${id}:${rows.length}`,
      cityDecorationId: id,
      label: String(optionRow?.name || optionRow?.label || `City Decoration #${id}`),
      subsection: String(optionRow?.categoryLabel || "City Decoration"),
      fromLabel: beforeLevel > 0 ? `Level ${beforeLevel}` : "Inactive",
      toLabel: afterLevel > 0 ? `Level ${afterLevel}` : "Inactive",
      materials: summarizePermanentCityDecorationUpgradeCosts(optionRow, beforeEntry, afterEntry),
    });
  });
  return rows.sort((left, right) => String(left.label || "").localeCompare(String(right.label || "")));
};

const buildProfileChangeSummary = (
  baselineDraft = {},
  currentDraft = {},
  {
    warplaneAdvancementRows = [],
    warplaneLevelRows = [],
    warplaneXpItems = [],
    warplaneSkillOptions = [],
    warplaneEquipOptions = [],
    warplaneEquipSkillOptions = [],
    warplaneSkinOptions = [],
    titanCatalogIndex = null,
    antiqueCollectionRows = [],
    badgeCatalogRows = [],
    islandDecorationRows = [],
    islandSpecialDecorationRows = [],
    islandDockDecorationRows = [],
    cityDecorationRows = [],
  } = {},
) => {
  const materialMap = new Map();
  const warplaneEvolutionRows = buildWarplaneEvolutionChangeRows(
    baselineDraft,
    currentDraft,
    warplaneAdvancementRows,
  );
  warplaneEvolutionRows.forEach((row) => addMaterialCosts(materialMap, row.materials, 1));
  const warplaneRows = buildWarplaneChangeRows(baselineDraft, currentDraft, {
    advancementRows: warplaneAdvancementRows,
    levelRows: warplaneLevelRows,
    xpItems: warplaneXpItems,
    skillOptions: warplaneSkillOptions,
    equipOptions: warplaneEquipOptions,
    equipSkillOptions: warplaneEquipSkillOptions,
    skinOptions: warplaneSkinOptions,
  });
  warplaneRows.forEach((row) => addMaterialCosts(materialMap, row.materials, 1));
  const titanRows = buildTitanChangeRows(baselineDraft, currentDraft, titanCatalogIndex);
  titanRows.forEach((row) => addMaterialCosts(materialMap, row.materials, 1));
  const officerRows = buildOfficerChangeRows(baselineDraft, currentDraft);
  officerRows.forEach((row) => addMaterialCosts(materialMap, row.materials, 1));
  const antiqueRows = buildAntiqueChangeRows(baselineDraft, currentDraft, antiqueCollectionRows);
  antiqueRows.forEach((row) => addMaterialCosts(materialMap, row.materials, 1));
  const badgeRows = buildBadgeChangeRows(baselineDraft, currentDraft, badgeCatalogRows);
  badgeRows.forEach((row) => addMaterialCosts(materialMap, row.materials, 1));
  const islandRows = buildIslandDecorationChangeRows(baselineDraft, currentDraft, {
    cityDecorationRows: islandDecorationRows,
    specialDecorationRows: islandSpecialDecorationRows,
    dockDecorationRows: islandDockDecorationRows,
  });
  islandRows.forEach((row) => addMaterialCosts(materialMap, row.materials, 1));
  const permanentCityDecorationRows = buildPermanentCityDecorationChangeRows(
    baselineDraft,
    currentDraft,
    cityDecorationRows,
  );
  permanentCityDecorationRows.forEach((row) => addMaterialCosts(materialMap, row.materials, 1));
  const changedSections = getChangedSectionLabels(baselineDraft, currentDraft);
  const identityChanged = (
    String(baselineDraft?.name || "") !== String(currentDraft?.name || "")
    || String(baselineDraft?.linkedCommanderId || "") !== String(currentDraft?.linkedCommanderId || "")
    || String(baselineDraft?.linkedGameCommanderId || "") !== String(currentDraft?.linkedGameCommanderId || "")
    || String(baselineDraft?.linkedCommanderName || "") !== String(currentDraft?.linkedCommanderName || "")
  );
  return {
    changedSections,
    identityChanged,
    warplaneEvolutionRows,
    warplaneRows,
    titanRows,
    officerRows,
    antiqueRows,
    badgeRows,
    islandRows,
    cityDecorationRows: permanentCityDecorationRows,
    materials: sortMaterialRows(materialMap),
  };
};

const BattleSimulationCommanderProfiles = ({
  singleProfileMode = false,
  pageTitle = "Commander Profile Manager",
  showBackToSimulation = true,
  profileApiOverrides = {},
  extraTopContent = null,
  hideProfileName = false,
  commanderLinkMode = "search",
  getCommanderLinkCandidateApi = null,
  linkCommanderByGameIdApi = null,
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthInitializing, isAuthenticated } = useContext(AuthContext);
  const isUserAuthenticated = isAuthenticated();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [selectedSection, setSelectedSection] = useState("powerSummary");
  const [calculatedStatsCombatContext, setCalculatedStatsCombatContext] = useState("crossNation");
  const [calculatedStatsCombatBattle, setCalculatedStatsCombatBattle] = useState("field");
  const [calculatedStatsCombatSide, setCalculatedStatsCombatSide] = useState("attacking");
  const [selectedStatBreakdownKey, setSelectedStatBreakdownKey] = useState("");
  const [isFleetSizeBreakdownOpen, setIsFleetSizeBreakdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [changeConfirmMode, setChangeConfirmMode] = useState("");
  const [pendingNavigationTo, setPendingNavigationTo] = useState("");
  const [pendingPrerequisiteAction, setPendingPrerequisiteAction] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [cartPackCandidatesByItem, setCartPackCandidatesByItem] = useState(() => new Map());
  const [isCartPricingLoading, setIsCartPricingLoading] = useState(false);
  const [cartPricingError, setCartPricingError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT, sections: createEmptySectionsDraft() });
  const [commanderQuery, setCommanderQuery] = useState("");
  const [commanderResults, setCommanderResults] = useState([]);
  const [isCommanderSearching, setIsCommanderSearching] = useState(false);
  const [commanderLinkCandidate, setCommanderLinkCandidate] = useState(null);
  const [isCommanderLinking, setIsCommanderLinking] = useState(false);
  const [linkedCommanderSnapshot, setLinkedCommanderSnapshot] = useState(null);
  const [isLinkedCommanderLoading, setIsLinkedCommanderLoading] = useState(false);
  const [activeOfficerDetailGroup, setActiveOfficerDetailGroup] = useState("skills");
  const [activeTitanDetailGroup, setActiveTitanDetailGroup] = useState("evolution");
  const [activeWarplaneDetailGroup, setActiveWarplaneDetailGroup] = useState("evolution");
  const [selectedWarplaneRowKey, setSelectedWarplaneRowKey] = useState("");
  const [activeAcademyDetailGroup, setActiveAcademyDetailGroup] = useState("resources");
  const [activeCommanderSkillDetailGroup, setActiveCommanderSkillDetailGroup] = useState("developmentSkills");
  const [collapsedOfficerCategories, setCollapsedOfficerCategories] = useState({});
  const [officerQualityFilter, setOfficerQualityFilter] = useState({
    orangeGold: true,
    purple: false,
    blue: false,
  });
  const [officerAppointedOnly, setOfficerAppointedOnly] = useState(false);
  const [antiquesActiveTab, setAntiquesActiveTab] = useState("collections");
  const lastBaselineProfileIdRef = useRef("");
  const listProfilesApi = profileApiOverrides.listProfiles || listCommanderProfiles;
  const createProfileApi = profileApiOverrides.createProfile || createCommanderProfile;
  const updateProfileApi = profileApiOverrides.updateProfile || updateCommanderProfile;
  const deleteProfileApi = profileApiOverrides.deleteProfile || deleteCommanderProfile;
  const useCommanderIdLinking = commanderLinkMode === "gameId";

  const preferredProfileId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get("profileId") || "");
  }, [location.search]);

  const selectedProfile = useMemo(
    () => profiles.find((profile) => String(profile?._id) === String(selectedProfileId)) || null,
    [profiles, selectedProfileId],
  );
  const baselineDraft = useMemo(
    () => createDraftFromProfile(selectedProfile),
    [selectedProfile],
  );
  const baselineComparableDraft = useMemo(
    () => getComparableProfileDraft(baselineDraft),
    [baselineDraft],
  );
  const currentComparableDraft = useMemo(
    () => getComparableProfileDraft(draft),
    [draft],
  );
  const hasUnsavedChanges = Boolean(selectedProfileId) && baselineComparableDraft !== currentComparableDraft;

  const filteredProfiles = useMemo(() => {
    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query) return profiles;
    return profiles.filter((profile) => {
      const name = String(profile?.name || "").toLowerCase();
      const linkedCommanderName = String(profile?.linkedCommanderName || "").toLowerCase();
      return name.includes(query) || linkedCommanderName.includes(query);
    });
  }, [profiles, searchQuery]);

  const selectedSectionMeta = useMemo(
    () => SECTION_ITEMS.find((item) => item.key === selectedSection) || SECTION_ITEMS[0],
    [selectedSection],
  );
  const selectedSectionCatalog = useMemo(
    () => getCommanderSectionCatalog(selectedSection),
    [selectedSection],
  );
  const titansSectionCatalog = useMemo(
    () => getCommanderSectionCatalog("titans"),
    [],
  );
  const warplaneSectionCatalog = useMemo(
    () => getCommanderSectionCatalog("warplane"),
    [],
  );
  const equipmentSectionCatalog = useMemo(
    () => getCommanderSectionCatalog("equipment"),
    [],
  );
  const antiquesSectionCatalog = useMemo(
    () => getCommanderSectionCatalog("antiques"),
    [],
  );
  const badgesSectionCatalog = useMemo(
    () => getCommanderSectionCatalog("badges"),
    [],
  );
  const islandSectionCatalog = useMemo(
    () => getCommanderSectionCatalog("island"),
    [],
  );
  const citySectionCatalog = useMemo(
    () => getCommanderSectionCatalog("city"),
    [],
  );
  const biochemicalSectionCatalog = useMemo(
    () => getCommanderSectionCatalog("biochemicalWorkshop"),
    [],
  );
  const academySectionCatalog = useMemo(
    () => getCommanderSectionCatalog("academy"),
    [],
  );

  const loadProfiles = useCallback(async (nextPreferredProfileId = "", { retryOnUnauthorized = true } = {}) => {
    if (isAuthInitializing || !isUserAuthenticated) return [];
    setIsLoading(true);
    setError("");
    try {
      let loaded = await listProfilesApi({ limit: 200 });
      if (!loaded.length) {
        const created = await createProfileApi({
          name: "Default Commander",
          parityProfile: {
            lvl: 0,
            t_b_r: 1,
          },
          powerSummary: {},
          sections: {},
        });
        loaded = created ? [created] : [];
      }
      setProfiles(loaded);
      const firstId = String(loaded?.[0]?._id || "");
      const explicitCandidateId = String(nextPreferredProfileId || preferredProfileId || "");
      setSelectedProfileId((prevSelectedId) => {
        const fallbackCandidateId = String(prevSelectedId || "");
        const candidateId = explicitCandidateId || fallbackCandidateId;
        const candidateExists = loaded.some((profile) => String(profile?._id) === candidateId);
        return candidateExists ? candidateId : firstId;
      });
      return loaded;
    } catch (err) {
      if (retryOnUnauthorized && Number(err?.response?.status || 0) === 401) {
        await new Promise((resolve) => setTimeout(resolve, 350));
        return loadProfiles(nextPreferredProfileId, { retryOnUnauthorized: false });
      }
      setError(err?.response?.data?.message || err?.message || "Failed to load commander profiles.");
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [
    isAuthInitializing,
    isUserAuthenticated,
    preferredProfileId,
    listProfilesApi,
    createProfileApi,
  ]);

  useEffect(() => {
    if (isAuthInitializing || !isUserAuthenticated) return;
    void loadProfiles(preferredProfileId);
  }, [isAuthInitializing, isUserAuthenticated, loadProfiles, preferredProfileId]);

  useEffect(() => {
    if (isAuthInitializing) return;
    if (isUserAuthenticated) return;
    setProfiles([]);
    setSelectedProfileId("");
    setDraft({ ...EMPTY_DRAFT, sections: createEmptySectionsDraft() });
    setSelectedWarplaneRowKey("");
    setLinkedCommanderSnapshot(null);
    setIsLinkedCommanderLoading(false);
    setIsLoading(false);
  }, [isAuthInitializing, isUserAuthenticated]);

  useEffect(() => {
    const baselineProfileId = String(selectedProfile?._id || selectedProfileId || "");
    const profileChanged = lastBaselineProfileIdRef.current !== baselineProfileId;
    lastBaselineProfileIdRef.current = baselineProfileId;

    setDraft(baselineDraft);
    if (profileChanged) {
      setSuccess("");
      setError("");
      setActiveOfficerDetailGroup("skills");
      setActiveTitanDetailGroup("evolution");
      setActiveWarplaneDetailGroup("evolution");
      setSelectedWarplaneRowKey("");
      setActiveAcademyDetailGroup("resources");
    }
    setPendingPrerequisiteAction(null);
    setShowResetConfirm(false);
  }, [baselineDraft, selectedProfile?._id, selectedProfileId]);

  useEffect(() => {
    if (isAuthInitializing || !isUserAuthenticated) return;
    const linkedCommanderId = String(draft?.linkedCommanderId || "").trim();
    if (!linkedCommanderId) {
      setLinkedCommanderSnapshot(null);
      setIsLinkedCommanderLoading(false);
      return;
    }
    const candidate = commanderResults.find((entry) => String(entry?._id || "") === linkedCommanderId) || null;
    if (candidate) {
      setLinkedCommanderSnapshot(candidate);
    }
    let cancelled = false;
    setIsLinkedCommanderLoading(true);
    (async () => {
      try {
        const commander = await getCommanderById(linkedCommanderId);
        if (cancelled) return;
        if (commander && typeof commander === "object") {
          setLinkedCommanderSnapshot(commander);
        } else {
          setLinkedCommanderSnapshot(candidate);
        }
      } catch (_error) {
        if (cancelled) return;
        setLinkedCommanderSnapshot(candidate);
      } finally {
        if (!cancelled) {
          setIsLinkedCommanderLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthInitializing, isUserAuthenticated, draft?.linkedCommanderId, commanderResults]);

  const updateOfficerRowAt = (rowIndex, updater) => {
    setDraft((prev) => {
      const currentRows = Array.isArray(prev?.sections?.officers?.data?.rows)
        ? prev.sections.officers.data.rows
        : [];
      if (!currentRows[rowIndex]) return prev;
      const nextRows = currentRows.map((row, index) => {
        if (index !== rowIndex) return row;
        const nextRow = typeof updater === "function" ? updater(row) : row;
        return normalizeOfficerRow(nextRow);
      });
      const normalizedRows = applyOfficerAppointmentLimits(nextRows);
      return {
        ...prev,
        sections: {
          ...prev.sections,
          officers: {
            ...prev.sections.officers,
            data: {
              ...prev.sections.officers.data,
              rows: normalizedRows,
            },
          },
        },
      };
    });
  };

  const toggleOfficerQualityFilter = (qualityKey) => {
    if (!OFFICER_QUALITY_FILTER_OPTIONS.some((entry) => entry.key === qualityKey)) return;
    setOfficerQualityFilter((prev) => ({
      ...prev,
      [qualityKey]: !Boolean(prev?.[qualityKey]),
    }));
  };

  const toggleOfficerCategory = (categoryKey) => {
    const key = String(categoryKey || "");
    if (!OFFICER_CATEGORY_ORDER.includes(key)) return;
    setCollapsedOfficerCategories((prev) => ({
      ...prev,
      [key]: !Boolean(prev?.[key]),
    }));
  };

  const updateTitanSideScalar = (sideKey, field, value) => {
    const side = TITAN_SIDE_ORDER.includes(String(sideKey || "")) ? String(sideKey) : "sourceSpirit";
    setDraft((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        titans: {
          ...prev.sections.titans,
          data: (() => {
            const current = normalizeTitansDataWithCatalog(
              prev?.sections?.titans?.data || {},
              titanCatalogIndex,
            );
            const sideCatalog = titanCatalogIndex?.perType?.[side] || null;
            const nextSide = normalizeTitanSideWithCatalog(
              {
                ...current[side],
                [field]: Math.max(0, Math.floor(Number(value || 0))),
              },
              sideCatalog,
            );
            const nextRaw = {
              ...current,
              [side]: applyTitanSideSpellUnlocks(nextSide, sideCatalog),
            };
            return normalizeTitansDataWithCatalog(nextRaw, titanCatalogIndex);
          })(),
        },
      },
    }));
  };

  const updateTitanSideIdLevelCollection = (sideKey, field, id, level) => {
    const side = TITAN_SIDE_ORDER.includes(String(sideKey || "")) ? String(sideKey) : "sourceSpirit";
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    setDraft((prev) => {
      const current = normalizeTitansDataWithCatalog(prev?.sections?.titans?.data || {}, titanCatalogIndex);
      const sideCurrent = current?.[side] || {};
      const existingRows = Array.isArray(sideCurrent?.[field]) ? sideCurrent[field] : [];
      const hasLevelInput = level !== null && level !== undefined && String(level).trim() !== "";
      const parsedLevel = Number(level);
      const hasLevel = hasLevelInput && Number.isFinite(parsedLevel) && parsedLevel >= 0;
      const nextLevel = hasLevel ? Math.max(0, Math.floor(parsedLevel)) : null;
      const without = existingRows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextRows = nextLevel !== null ? [...without, { id: normalizedId, level: nextLevel }] : without;
      const nextRaw = {
        ...current,
        [side]: {
          ...sideCurrent,
          [field]: normalizeIdLevelEntries(nextRows),
        },
      };
      return {
        ...prev,
        sections: {
          ...prev.sections,
          titans: {
            ...prev.sections.titans,
            data: normalizeTitansDataWithCatalog(nextRaw, titanCatalogIndex),
          },
        },
      };
    });
  };

  const updateTitanSideSkinLevel = (sideKey, skinId, nextLevel) => {
    const side = TITAN_SIDE_ORDER.includes(String(sideKey || "")) ? String(sideKey) : "sourceSpirit";
    const normalizedSkinId = normalizeSkillId(skinId);
    if (!normalizedSkinId) return;
    setDraft((prev) => {
      const current = normalizeTitansDataWithCatalog(prev?.sections?.titans?.data || {}, titanCatalogIndex);
      const sideCurrent = current?.[side] || {};
      const existingRows = Array.isArray(sideCurrent?.skins) ? sideCurrent.skins : [];
      const without = existingRows.filter((row) => Number(row?.id || 0) !== normalizedSkinId);
      const hasLevel = Number.isFinite(Number(nextLevel));
      const normalizedLevel = hasLevel ? Math.max(0, Math.floor(Number(nextLevel || 0))) : null;
      const nextRows = normalizedLevel === null
        ? without
        : [...without, { id: normalizedSkinId, level: normalizedLevel }];
      const nextRaw = {
        ...current,
        [side]: {
          ...sideCurrent,
          skins: normalizeIdLevelEntries(nextRows),
          skinGid: normalizedLevel === null
            ? (without.length ? Number(without[0]?.id || null) : null)
            : normalizedSkinId,
        },
      };
      return {
        ...prev,
        sections: {
          ...prev.sections,
          titans: {
            ...prev.sections.titans,
            data: normalizeTitansDataWithCatalog(nextRaw, titanCatalogIndex),
          },
        },
      };
    });
  };

  const updateWarplaneScalar = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        warplane: {
          ...prev.sections.warplane,
          data: updateSelectedWarplaneProgress(
            prev?.sections?.warplane?.data || {},
            { [field]: Math.max(0, Math.floor(Number(value || 0))) },
            selectedWarplaneKey,
            warplaneAdvancementRows,
          ),
        },
      },
    }));
  };

  const updateWarplaneStage = (value) => {
    const nextStage = Math.max(0, Math.floor(Number(value || 0)));
    setDraft((prev) => {
      const current = normalizeWarplaneData(prev?.sections?.warplane?.data || {});
      return {
        ...prev,
        sections: {
          ...prev.sections,
          warplane: {
            ...prev.sections.warplane,
            data: updateSelectedWarplaneProgress(current, {
              advancementStage: nextStage,
              advancementStep: 0,
            }, selectedWarplaneKey, warplaneAdvancementRows),
          },
        },
      };
    });
  };

  const selectWarplaneRow = (rowKey) => {
    setSelectedWarplaneRowKey(String(rowKey || ""));
  };

  const updateWarplaneIdLevelCollection = (field, id, level) => {
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    setDraft((prev) => {
      const current = normalizeWarplaneData(prev?.sections?.warplane?.data || {});
      const existingRows = Array.isArray(current?.[field]) ? current[field] : [];
      const nextLevel = Math.max(-1, Math.floor(Number(level ?? -1)));
      const without = existingRows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextRows = nextLevel >= 0 ? [...without, { id: normalizedId, level: nextLevel }] : without;
      return {
        ...prev,
        sections: {
          ...prev.sections,
          warplane: {
            ...prev.sections.warplane,
            data: {
              ...current,
              [field]: normalizeIdLevelEntries(nextRows),
            },
          },
        },
      };
    });
  };

  const updateBiochemicalPetScalar = (petGid, field, value) => {
    const normalizedPetGid = normalizeSkillId(petGid);
    if (!normalizedPetGid) return;
    const normalizedValue = Math.max(0, Math.floor(Number(value || 0)));
    setDraft((prev) => {
      const current = normalizeBiochemicalWorkshopData(prev?.sections?.biochemicalWorkshop?.data || {});
      const rows = Array.isArray(current?.pets) ? current.pets : [];
      let matched = false;
      const nextRows = rows.map((row) => {
        if (Number(row?.gid || 0) !== normalizedPetGid) return row;
        matched = true;
        const next = { ...row, [field]: normalizedValue };
        if (field === "intimacyLevel") next.incubationLevel = normalizedValue;
        if (field === "healLevel") next.hatcheryLevel = normalizedValue;
        if (field === "intimacyExp") next.incubationExp = normalizedValue;
        if (field === "healExp") next.hatcheryExp = normalizedValue;
        return next;
      });
      if (!matched) {
        const next = {
          gid: normalizedPetGid,
          name: getBiochemicalPetLabel(normalizedPetGid),
          intimacyLevel: 0,
          healLevel: 0,
          intimacyExp: 0,
          healExp: 0,
          incubationLevel: 0,
          hatcheryLevel: 0,
          incubationExp: 0,
          hatcheryExp: 0,
          collectedGids: [],
          growthRewards: [],
          [field]: normalizedValue,
        };
        if (field === "intimacyLevel") next.incubationLevel = normalizedValue;
        if (field === "healLevel") next.hatcheryLevel = normalizedValue;
        if (field === "intimacyExp") next.incubationExp = normalizedValue;
        if (field === "healExp") next.hatcheryExp = normalizedValue;
        nextRows.push(next);
      }
      const nextData = normalizeBiochemicalWorkshopData({ ...current, pets: nextRows });
      return {
        ...prev,
        sections: {
          ...prev.sections,
          biochemicalWorkshop: {
            ...prev.sections.biochemicalWorkshop,
            data: nextData,
          },
        },
      };
    });
  };

  const updateCityBuildingLevel = (sourceIndex, value) => {
    const index = Math.max(0, Math.floor(Number(sourceIndex || 0)));
    const nextLevel = fromCityBuildingLevelDisplay(value);
    setDraft((prev) => {
      const current = normalizeCityData(prev?.sections?.city?.data || {});
      const rows = Array.isArray(current?.buildings) ? current.buildings : [];
      const nextRows = rows.map((row, rowIndex) => (
        rowIndex === index ? { ...row, level: nextLevel } : row
      ));
      return {
        ...prev,
        sections: {
          ...prev.sections,
          city: {
            ...prev.sections.city,
            data: normalizeCityData({ ...current, buildings: nextRows }),
          },
        },
      };
    });
  };

  const updateAcademyTechLevel = (id, level) => {
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    const nextLevel = level === null
      ? null
      : Math.max(0, Math.floor(Number(level || 0)));
    setDraft((prev) => {
      const current = normalizeAcademyData(prev?.sections?.academy?.data || {});
      const rows = Array.isArray(current?.academyTech) ? current.academyTech : [];
      const without = rows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextRows = nextLevel === null ? without : [...without, { id: normalizedId, level: nextLevel }];
      return {
        ...prev,
        sections: {
          ...prev.sections,
          academy: {
            ...prev.sections.academy,
            data: {
              ...current,
              academyTech: normalizeIdLevelEntries(nextRows),
            },
          },
        },
      };
    });
  };

  const updateAcademyTechSelection = (id, rawValue) => {
    if (String(rawValue ?? "") === "") {
      updateAcademyTechLevel(id, null);
      return;
    }
    updateAcademyTechLevel(id, fromResearchLevelDisplay(rawValue));
  };

  const updateAllianceBenefitBuildingCount = (id, count) => {
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    const nextCount = Math.max(0, Math.floor(Number(count || 0)));
    setDraft((prev) => {
      const current = normalizeAllianceData(prev?.sections?.alliance?.data || {});
      const rows = Array.isArray(current?.benefitBuildings) ? current.benefitBuildings : [];
      const without = rows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextRows = nextCount > 0 ? [...without, { id: normalizedId, count: nextCount }] : without;
      return {
        ...prev,
        sections: {
          ...prev.sections,
          alliance: {
            ...(prev.sections.alliance || {}),
            data: {
              ...current,
              benefitBuildings: normalizeAllianceData({ benefitBuildings: nextRows }).benefitBuildings,
            },
          },
        },
      };
    });
  };

  const updateAntiqueActivation = (id, activated) => {
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    setDraft((prev) => {
      const current = normalizeAntiquesData(prev?.sections?.antiques?.data || {});
      const existingRows = Array.isArray(current?.collectionOptions) ? current.collectionOptions : [];
      const without = existingRows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextRows = activated
        ? [...without, { id: normalizedId, level: 0, star: 0, stage: 0 }]
        : without;
      return {
        ...prev,
        sections: {
          ...prev.sections,
          antiques: {
            ...prev.sections.antiques,
            data: {
              ...current,
              collectionOptions: normalizeAntiqueCollectionEntries(nextRows),
            },
          },
        },
      };
    });
  };

  const updateAntiqueProgress = (id, progress) => {
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    const nextStar = Math.max(0, Math.floor(Number(progress?.star || 0)));
    const nextStage = Math.max(0, Math.floor(Number(progress?.stage || 0)));
    setDraft((prev) => {
      const current = normalizeAntiquesData(prev?.sections?.antiques?.data || {});
      const existingRows = Array.isArray(current?.collectionOptions) ? current.collectionOptions : [];
      const without = existingRows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextRows = [...without, {
        id: normalizedId,
        level: Math.round((nextStar + (nextStage / 10)) * 10) / 10,
        star: nextStar,
        stage: nextStage,
      }];
      return {
        ...prev,
        sections: {
          ...prev.sections,
          antiques: {
            ...prev.sections.antiques,
            data: {
              ...current,
              collectionOptions: normalizeAntiqueCollectionEntries(nextRows),
            },
          },
        },
      };
    });
  };

  const updateAntiqueSelection = (id, rawValue, previous = {}, collectionRow = null) => {
    if (String(rawValue ?? "") === "") {
      updateAntiqueActivation(id, false);
      return;
    }
    updateAntiqueProgress(
      id,
      normalizeAntiqueProgressSelectValue(rawValue, previous, collectionRow),
    );
  };

  const updateIslandIdLevelCollection = (field, id, level, selected = true) => {
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    setDraft((prev) => {
      const current = normalizeIslandData(prev?.sections?.island?.data || {});
      const existingRows = Array.isArray(current?.[field]) ? current[field] : [];
      const nextLevel = Math.max(0, Math.floor(Number(level || 0)));
      const without = existingRows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextRows = selected ? [...without, { id: normalizedId, level: nextLevel }] : without;
      return {
        ...prev,
        sections: {
          ...prev.sections,
          island: {
            ...prev.sections.island,
            data: {
              ...current,
              [field]: normalizeIdLevelEntries(nextRows),
            },
          },
        },
      };
    });
  };

  const updateCityDecorationSelection = (id, selected, level = 0) => {
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    const nextLevel = Math.max(0, Math.floor(Number(level || 0)));
    setDraft((prev) => {
      const current = normalizeCityDecorationsData(prev?.sections?.cityDecorations?.data || {});
      const rows = Array.isArray(current?.permanent) ? current.permanent : [];
      const without = rows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextRows = selected ? [...without, { id: normalizedId, level: nextLevel }] : without;
      return {
        ...prev,
        sections: {
          ...prev.sections,
          cityDecorations: {
            ...prev.sections.cityDecorations,
            data: normalizeCityDecorationsData({ ...current, permanent: nextRows }),
          },
        },
      };
    });
  };

  const updateTemporaryBuffSelection = (buff, selected, level = 0) => {
    const itemGid = normalizeSkillId(buff?.itemGid || buff?.id);
    if (!itemGid) return;
    const sourceType = String(buff?.sourceType || "item").trim() || "item";
    const buffId = String(buff?.buffId || `${sourceType}:${itemGid}`).trim();
    const nextLevel = Math.max(0, Math.floor(Number(level || 0)));
    setDraft((prev) => {
      const current = normalizeTemporaryBuffsData(prev?.sections?.temporaryBuffs?.data || {});
      const rows = Array.isArray(current?.active) ? current.active : [];
      const without = rows.filter((row) => String(row?.buffId || "") !== buffId);
      const nextRows = selected
        ? [...without, { buffId, sourceType, id: itemGid, itemGid, level: nextLevel }]
        : without;
      return {
        ...prev,
        sections: {
          ...prev.sections,
          temporaryBuffs: {
            ...prev.sections.temporaryBuffs,
            data: normalizeTemporaryBuffsData({ ...current, active: nextRows }),
          },
        },
      };
    });
  };

  const updateBadgeLevel = (gid, level, position = 0) => {
    const normalizedGid = normalizeSkillId(gid);
    if (!normalizedGid) return;
    const nextLevel = Math.max(0, Math.floor(Number(level || 0)));
    setDraft((prev) => {
      const current = normalizeBadgesData(prev?.sections?.badges?.data || {});
      const rows = Array.isArray(current?.equipped) ? current.equipped : [];
      const existing = rows.find((row) => Number(row?.gid || 0) === normalizedGid) || {};
      const without = rows.filter((row) => Number(row?.gid || 0) !== normalizedGid);
      const nextRows = [...without, {
        id: Math.max(0, Math.floor(Number(existing?.id || 0))),
        gid: normalizedGid,
        level: nextLevel,
        position: Math.max(0, Math.floor(Number(existing?.position || position || 0))),
      }];
      return {
        ...prev,
        sections: {
          ...prev.sections,
          badges: {
            ...prev.sections.badges,
            data: {
              ...current,
              equipped: normalizeBadgeEquippedEntries(nextRows),
            },
          },
        },
      };
    });
  };

  const updateCommanderSkillTreeLevel = (field, id, level) => {
    const normalizedId = normalizeSkillId(id);
    if (!normalizedId) return;
    const nextLevel = level === null
      ? null
      : Math.max(0, Math.floor(Number(level || 0)));
    setDraft((prev) => {
      const currentCommanderSkills = normalizeCommanderSkillsData(prev?.sections?.commanderSkills?.data || {});
      const currentAcademy = normalizeAcademyData(prev?.sections?.academy?.data || {});
      const existingSkillRows = Array.isArray(currentCommanderSkills?.[field]) ? currentCommanderSkills[field] : [];
      const existingUserTechRows = Array.isArray(currentAcademy?.userTech) ? currentAcademy.userTech : [];
      const withoutSkill = existingSkillRows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const withoutUserTech = existingUserTechRows.filter((row) => Number(row?.id || 0) !== normalizedId);
      const nextSkillRows = nextLevel === null ? withoutSkill : [...withoutSkill, { id: normalizedId, level: nextLevel }];
      const nextUserTechRows = nextLevel === null ? withoutUserTech : [...withoutUserTech, { id: normalizedId, level: nextLevel }];
      return {
        ...prev,
        sections: {
          ...prev.sections,
          academy: {
            ...prev.sections.academy,
            data: {
              ...currentAcademy,
              userTech: normalizeIdLevelEntries(nextUserTechRows),
            },
          },
          commanderSkills: {
            ...prev.sections.commanderSkills,
            data: {
              ...currentCommanderSkills,
              [field]: normalizeIdLevelEntries(nextSkillRows),
            },
          },
        },
      };
    });
  };

  const updateCommanderSkillTreeSelection = (field, id, rawValue) => {
    if (String(rawValue ?? "") === "") {
      updateCommanderSkillTreeLevel(field, id, null);
      return;
    }
    updateCommanderSkillTreeLevel(field, id, fromResearchLevelDisplay(rawValue));
  };

  const updateCommanderBoost = (field, nextValue = {}) => {
    const normalizedField = String(field || "");
    if (!["vip", "prosperity", "militaryRank"].includes(normalizedField)) return;
    setDraft((prev) => {
      const current = normalizeCommanderBoostsData(prev?.sections?.commanderBoosts?.data || {});
      const currentValue = current[normalizedField] || {};
      return {
        ...prev,
        sections: {
          ...prev.sections,
          commanderBoosts: {
            ...prev.sections.commanderBoosts,
            data: normalizeCommanderBoostsData({
              ...current,
              [normalizedField]: {
                ...currentValue,
                ...nextValue,
              },
            }),
          },
        },
      };
    });
  };

  const updateImportedSectionData = (sectionKey, nextData) => {
    const key = String(sectionKey || "");
    const normalizers = {
      inventory: normalizeInventoryData,
      city: normalizeCityData,
      biochemicalWorkshop: normalizeBiochemicalWorkshopData,
      academy: normalizeAcademyData,
    };
    const normalize = normalizers[key];
    if (!normalize) return;
    setDraft((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [key]: {
          ...prev.sections[key],
          data: normalize(nextData || {}),
        },
      },
    }));
  };

  const performSave = async () => {
    if (!selectedProfileId) return false;
    setIsSaving(true);
    setError("");
    setSuccess("");
    try {
      const parityProfile = {
        lvl: Math.max(0, Math.floor(Number(draft?.parityProfile?.lvl || 0))),
        t_b_r: Math.max(0.1, Number(draft?.parityProfile?.t_b_r || 1)),
      };
      PARITY_ARRAY_FIELDS.forEach((field) => {
        parityProfile[field] = parseArrayField(draft?.parityProfile?.[field], field);
      });

      const sectionsPayload = SECTION_KEYS.reduce((acc, key) => {
        acc[key] = {
          status: String(draft?.sections?.[key]?.status || "draft"),
          notes: String(draft?.sections?.[key]?.notes || ""),
        };
        return acc;
      }, {});

      const rows = Array.isArray(draft?.sections?.officers?.data?.rows)
        ? draft.sections.officers.data.rows
        : [];
      sectionsPayload.officers.data = {
        rows: applyOfficerAppointmentLimits(rows.map((row) => normalizeOfficerRow(row))),
      };
      sectionsPayload.titans.data = normalizeTitansDataWithCatalog(
        draft?.sections?.titans?.data || {},
        titanCatalogIndex,
      );
      sectionsPayload.warplane.data = normalizeWarplaneData(draft?.sections?.warplane?.data || {});
      sectionsPayload.equipment.data = normalizeEquipmentData(draft?.sections?.equipment?.data || {});
      sectionsPayload.antiques.data = normalizeAntiquesData(draft?.sections?.antiques?.data || {});
      sectionsPayload.badges.data = normalizeBadgesData(draft?.sections?.badges?.data || {});
      sectionsPayload.commanderBoosts.data = normalizeCommanderBoostsData(draft?.sections?.commanderBoosts?.data || {});
      sectionsPayload.island.data = normalizeIslandData(draft?.sections?.island?.data || {});
      sectionsPayload.cityDecorations.data = normalizeCityDecorationsData(draft?.sections?.cityDecorations?.data || {});
      sectionsPayload.temporaryBuffs.data = normalizeTemporaryBuffsData(draft?.sections?.temporaryBuffs?.data || {});
      sectionsPayload.inventory.data = normalizeInventoryData(draft?.sections?.inventory?.data || {});
      sectionsPayload.city.data = normalizeCityData(draft?.sections?.city?.data || {});
      sectionsPayload.biochemicalWorkshop.data = normalizeBiochemicalWorkshopData(draft?.sections?.biochemicalWorkshop?.data || {});
      sectionsPayload.alliance.data = normalizeAllianceData(draft?.sections?.alliance?.data || {});
      sectionsPayload.academy.data = normalizeAcademyData(draft?.sections?.academy?.data || {});
      sectionsPayload.commanderSkills.data = normalizeCommanderSkillsData(draft?.sections?.commanderSkills?.data || {});

      const payload = {
        name: String(draft?.name || "").trim() || "Commander Profile",
        linkedCommanderId: String(draft?.linkedCommanderId || "").trim() || null,
        linkedGameCommanderId: String(draft?.linkedGameCommanderId || "").trim() || null,
        linkedCommanderName: String(draft?.linkedCommanderName || "").trim(),
        powerSummary: documentedPowerSummary,
        parityProfile: applySectionDerivedParityFields(parityProfile, sectionsPayload),
        sections: sectionsPayload,
      };
      await updateProfileApi(selectedProfileId, payload);
      setSuccess("Profile saved.");
      await loadProfiles(selectedProfileId);
      return true;
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save profile.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    if (!selectedProfileId) return;
    setChangeConfirmMode("save");
  };

  const closeChangeConfirm = () => {
    if (isSaving) return;
    setPendingNavigationTo("");
    setChangeConfirmMode("");
  };

  const resetProfileChanges = () => {
    if (isSaving || !hasUnsavedChanges) return;
    setDraft(baselineDraft);
    setSelectedWarplaneRowKey("");
    setPendingNavigationTo("");
    setChangeConfirmMode("");
    setCartPricingError("");
    setShowResetConfirm(false);
    setSuccess("Pending profile changes reset.");
  };

  const requestResetProfileChanges = () => {
    if (isSaving || !hasUnsavedChanges) return;
    setShowResetConfirm(true);
  };

  const closeResetConfirm = () => {
    if (isSaving) return;
    setShowResetConfirm(false);
  };

  const confirmProfileSave = async () => {
    const saved = await performSave();
    if (!saved) return;
    const nextNavigation = pendingNavigationTo;
    setPendingNavigationTo("");
    setChangeConfirmMode("");
    if (changeConfirmMode === "navigate" && nextNavigation) {
      navigate(nextNavigation);
    }
  };

  const discardChangesAndContinueNavigation = () => {
    const nextNavigation = pendingNavigationTo;
    setPendingNavigationTo("");
    setChangeConfirmMode("");
    if (nextNavigation) {
      navigate(nextNavigation);
    }
  };

  const requestNavigation = (to) => {
    const target = String(to || "");
    if (!target) return;
    if (hasUnsavedChanges) {
      setPendingNavigationTo(target);
      setChangeConfirmMode("navigate");
      return;
    }
    navigate(target);
  };

  const handleCreateProfile = async () => {
    setIsSaving(true);
    setError("");
    setSuccess("");
    try {
      const created = await createProfileApi({
        name: `Commander Profile ${new Date().toLocaleDateString()}`,
      });
      if (created?._id) {
        await loadProfiles(created._id);
        setSuccess("Profile created.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to create profile.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!selectedProfileId) return;
    const shouldDelete = window.confirm("Delete this commander profile?");
    if (!shouldDelete) return;
    setIsDeleting(true);
    setError("");
    setSuccess("");
    try {
      await deleteProfileApi(selectedProfileId);
      setSuccess("Profile deleted.");
      await loadProfiles("");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to delete profile.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFindCommander = async () => {
    const q = String(commanderQuery || "").trim();
    if (!q) {
      setCommanderResults([]);
      setCommanderLinkCandidate(null);
      return;
    }
    setIsCommanderSearching(true);
    setError("");
    try {
      if (useCommanderIdLinking && typeof getCommanderLinkCandidateApi === "function") {
        const commander = await getCommanderLinkCandidateApi(q);
        setCommanderLinkCandidate(commander || null);
        setCommanderResults([]);
      } else {
        const results = await searchCommandersByName(q, { page: 1, limit: 12 });
        setCommanderResults(results);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to search commanders.");
      setCommanderLinkCandidate(null);
    } finally {
      setIsCommanderSearching(false);
    }
  };

  const handleLinkCommander = (commander) => {
    const documented = buildDocumentedPowerSummary(commander, draft?.powerSummary || {});
    setDraft((prev) => ({
      ...prev,
      linkedCommanderId: String(commander?._id || ""),
      linkedGameCommanderId: String(commander?.commanderId || commander?.gameCommanderId || ""),
      linkedCommanderName: String(commander?.name || ""),
      powerSummary: documented.summary,
    }));
    setLinkedCommanderSnapshot(commander || null);
  };

  const handleUnlinkCommander = () => {
    setDraft((prev) => ({
      ...prev,
      linkedCommanderId: "",
      linkedGameCommanderId: "",
      linkedCommanderName: "",
      externalContext: {
        ...(prev.externalContext && typeof prev.externalContext === "object" ? prev.externalContext : {}),
        allianceTechnology: null,
      },
    }));
    setLinkedCommanderSnapshot(null);
  };

  const closeCommanderLinkCandidate = () => {
    if (isCommanderLinking) return;
    setCommanderLinkCandidate(null);
  };

  const handleConfirmCommanderIdLink = async () => {
    if (!commanderLinkCandidate || typeof linkCommanderByGameIdApi !== "function") return;
    setIsCommanderLinking(true);
    setError("");
    setSuccess("");
    try {
      const response = await linkCommanderByGameIdApi(commanderLinkCandidate.commanderId);
      const profile = response?.profile || null;
      const commander = response?.commander || commanderLinkCandidate;
      if (profile) {
        setProfiles([profile]);
        setSelectedProfileId(String(profile?._id || selectedProfileId));
        setDraft(createDraftFromProfile(profile));
        setSelectedWarplaneRowKey("");
      } else {
        setDraft((prev) => ({
          ...prev,
          linkedCommanderId: String(commander?.databaseCommanderId || ""),
          linkedGameCommanderId: String(commander?.commanderId || ""),
          linkedCommanderName: String(commander?.name || ""),
        }));
      }
      setLinkedCommanderSnapshot({
        _id: commander?.databaseCommanderId,
        name: commander?.name,
        battlePower: commander?.battlePower,
        cityLevel: commander?.cityLevel,
        nation: commander?.nation,
        alliance: commander?.alliance,
        updatedAt: commander?.updatedAt,
      });
      setCommanderLinkCandidate(null);
      setCommanderQuery("");
      setSuccess("Commander linked.");
      await loadProfiles(profile?._id || selectedProfileId);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to link commander.");
    } finally {
      setIsCommanderLinking(false);
    }
  };

  const officerRows = useMemo(() => {
    const rows = Array.isArray(draft?.sections?.officers?.data?.rows)
      ? draft.sections.officers.data.rows
      : [];
    return applyOfficerAppointmentLimits(rows.map((row) => normalizeOfficerRow(row)));
  }, [draft?.sections?.officers?.data?.rows]);
  const filteredOfficerRows = useMemo(
    () => officerRows
      .map((row, rowIndex) => ({ row, rowIndex }))
      .filter(({ row }) => {
        const catalogEntry = getOfficerCatalogEntryById(row?.officerId);
        const qualityFamily = getOfficerQualityFamily(catalogEntry);
        const matchesQuality = Boolean(officerQualityFilter?.[qualityFamily]);
        if (!matchesQuality) return false;
        if (officerAppointedOnly && !Boolean(row?.appointed)) return false;
        return true;
      }),
    [officerRows, officerQualityFilter, officerAppointedOnly],
  );
  const officerVisibleColumnCount = useMemo(() => {
    if (activeOfficerDetailGroup === "skin") return 4;
    if (activeOfficerDetailGroup === "equipment") return 5;
    if (activeOfficerDetailGroup === "skills") return 7;
    return 8; // unworldly
  }, [activeOfficerDetailGroup]);
  const officerDetailAvailability = useMemo(() => ({
    skin: filteredOfficerRows.some(({ row }) => {
      const bounds = getOfficerSkinTierBounds(row?.officerId);
      const tierOptions = Array.isArray(bounds?.tierOptions) ? bounds.tierOptions : [];
      return Boolean(bounds?.hasSkin && tierOptions.length);
    }),
    equipment: filteredOfficerRows.some(({ row }) => (
      isOfficerEquipmentSlotUnlocked(row?.stars, 0, row?.officerId)
    )),
    skills: true,
    unworldly: filteredOfficerRows.some(({ row }) => {
      const catalogEntry = getOfficerCatalogEntryById(row?.officerId);
      if (!isGoldTierOfficer(catalogEntry)) return false;
      return isOfficerUnworldlySlotUnlocked(row?.stars, 0, row?.officerId);
    }),
  }), [filteredOfficerRows]);
  useEffect(() => {
    const currentKey = String(activeOfficerDetailGroup || "");
    if (!currentKey) {
      setActiveOfficerDetailGroup("skills");
      return;
    }
    if (currentKey === "skills") return;
    if (!Boolean(officerDetailAvailability?.[currentKey])) {
      setActiveOfficerDetailGroup("skills");
    }
  }, [activeOfficerDetailGroup, officerDetailAvailability]);
  const titanCatalogIndex = useMemo(
    () => buildTitanCatalogIndex(titansSectionCatalog),
    [titansSectionCatalog],
  );
  const titanData = normalizeTitansDataWithCatalog(draft?.sections?.titans?.data || {}, titanCatalogIndex);
  const baseWarplaneData = normalizeWarplaneData(draft?.sections?.warplane?.data || {});
  const selectedWarplaneRow = (
    findWarplaneRowByKey(baseWarplaneData?.ownedRows || [], selectedWarplaneRowKey)
    || pickSelectedWarplaneRow(baseWarplaneData)
  );
  const selectedWarplaneKey = selectedWarplaneRow
    ? getWarplaneRowKey(selectedWarplaneRow, (baseWarplaneData?.ownedRows || []).indexOf(selectedWarplaneRow))
    : "";
  const warplaneData = selectedWarplaneRow
    ? applySelectedWarplaneRow(baseWarplaneData, selectedWarplaneRow)
    : baseWarplaneData;
  const equipmentData = normalizeEquipmentData(draft?.sections?.equipment?.data || {});
  const antiquesData = normalizeAntiquesData(draft?.sections?.antiques?.data || {});
  const badgesData = normalizeBadgesData(draft?.sections?.badges?.data || {});
  const commanderBoostsData = normalizeCommanderBoostsData(draft?.sections?.commanderBoosts?.data || {});
  const islandData = normalizeIslandData(draft?.sections?.island?.data || {});
  const cityDecorationsData = normalizeCityDecorationsData(draft?.sections?.cityDecorations?.data || {});
  const temporaryBuffsData = normalizeTemporaryBuffsData(draft?.sections?.temporaryBuffs?.data || {});
  const inventoryData = normalizeInventoryData(draft?.sections?.inventory?.data || {});
  const cityData = normalizeCityData(draft?.sections?.city?.data || {});
  const biochemicalWorkshopData = normalizeBiochemicalWorkshopData(draft?.sections?.biochemicalWorkshop?.data || {});
  const allianceData = normalizeAllianceData(draft?.sections?.alliance?.data || {});
  const academyData = normalizeAcademyData(draft?.sections?.academy?.data || {});
  const commanderSkillsData = normalizeCommanderSkillsData(draft?.sections?.commanderSkills?.data || {});
  const linkedAllianceTechnologyContext = (
    draft?.externalContext?.allianceTechnology
    && typeof draft.externalContext.allianceTechnology === "object"
  )
    ? draft.externalContext.allianceTechnology
    : null;
  const linkedAllianceTechnologyRows = useMemo(
    () => (Array.isArray(linkedAllianceTechnologyContext?.rows)
      ? linkedAllianceTechnologyContext.rows
        .map((row) => {
          const id = normalizeSkillId(row?.id);
          if (!id) return null;
          const displayLevel = Math.max(0, Math.floor(Number(row?.displayLevel || 0)));
          return {
            id,
            level: Math.max(0, Math.floor(Number(row?.level ?? displayLevel - 1 ?? 0))),
            displayLevel,
            category: String(row?.category || ""),
            key: String(row?.key || ""),
            label: String(row?.label || ""),
          };
        })
        .filter(Boolean)
      : []),
    [linkedAllianceTechnologyContext],
  );
  const allianceDataForCalculation = useMemo(() => ({
    ...allianceData,
    allianceTech: linkedAllianceTechnologyRows.length
      ? normalizeIdLevelEntries(linkedAllianceTechnologyRows)
      : allianceData.allianceTech,
  }), [allianceData, linkedAllianceTechnologyRows]);
  const calculatedSectionPayload = useMemo(() => ({
    officers: { data: { rows: officerRows } },
    titans: { data: titanData },
    warplane: { data: warplaneData },
    equipment: { data: equipmentData },
    antiques: { data: antiquesData },
    badges: { data: badgesData },
    commanderBoosts: { data: commanderBoostsData },
    island: { data: islandData },
    cityDecorations: { data: cityDecorationsData },
    temporaryBuffs: { data: temporaryBuffsData },
    inventory: { data: inventoryData },
    city: { data: cityData },
    biochemicalWorkshop: { data: biochemicalWorkshopData },
    alliance: { data: allianceDataForCalculation },
    academy: { data: academyData },
    commanderSkills: { data: commanderSkillsData },
  }), [
    officerRows,
    titanData,
    warplaneData,
    equipmentData,
    antiquesData,
    badgesData,
    commanderBoostsData,
    islandData,
    cityDecorationsData,
    temporaryBuffsData,
    inventoryData,
    cityData,
    biochemicalWorkshopData,
    allianceDataForCalculation,
    academyData,
    commanderSkillsData,
  ]);
  const documentedPowerBundle = useMemo(
    () => buildDocumentedPowerSummary(linkedCommanderSnapshot, draft?.powerSummary || {}),
    [linkedCommanderSnapshot, draft?.powerSummary],
  );
  const documentedPowerSummary = documentedPowerBundle.summary;
  const documentedPowerUpdatedAtLabel = useMemo(() => {
    const timestamp = documentedPowerBundle?.updatedAt;
    if (!timestamp) return "";
    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleString();
  }, [documentedPowerBundle?.updatedAt]);
  const calculatedPowerSummary = useMemo(
    () => calculateCommanderPowerSummary({
      officerRows,
      titansData: titanData,
      titanCatalogIndex,
      warplaneData,
      warplaneSectionCatalog,
      equipmentData,
      equipmentSectionCatalog,
      antiquesData,
      antiquesSectionCatalog,
    }),
    [
      officerRows,
      titanData,
      titanCatalogIndex,
      warplaneData,
      warplaneSectionCatalog,
      equipmentData,
      equipmentSectionCatalog,
      antiquesData,
      antiquesSectionCatalog,
    ],
  );
  const calculatedParityProfile = useMemo(() => {
    return applySectionDerivedParityFields(createBaseCalculatedParityProfile(draft), calculatedSectionPayload, {
      combatContext: calculatedStatsCombatContext,
    });
  }, [
    draft,
    calculatedStatsCombatContext,
    calculatedSectionPayload,
  ]);
  const calculatedExpectedReportStats = useMemo(
    () => buildExpectedReportStatsFromParityProfile(calculatedParityProfile, {
      combatBattle: calculatedStatsCombatBattle,
      combatSide: calculatedStatsCombatSide,
    }),
    [calculatedParityProfile, calculatedStatsCombatBattle, calculatedStatsCombatSide],
  );
  const hasLinkedCommander = Boolean(
    String(draft?.linkedCommanderId || draft?.linkedGameCommanderId || draft?.linkedCommanderName || "").trim(),
  );
  const titanTypeOptions = getGroupRowsByKey(titansSectionCatalog, "titanTypes");
  const officerSkillEffectLabelById = useMemo(
    () => {
      const map = new Map();
      [...OFFICER_PASSIVE_SKILL_OPTIONS, ...OFFICER_ACTIVE_SKILL_OPTIONS].forEach((option) => {
        const fallbackText = String(option?.description || option?.label || "").trim();
        if (!fallbackText) return;
        const effectIds = Array.isArray(option?.effectIds) ? option.effectIds : [];
        effectIds.forEach((rawEffectId) => {
          const effectId = Number(rawEffectId || 0);
          if (!Number.isFinite(effectId) || effectId <= 0) return;
          if (!map.has(effectId)) {
            map.set(effectId, fallbackText);
          }
        });
      });
      return map;
    },
    [],
  );
  const commanderEffectLabelById = useMemo(
    () => {
      const map = new Map(officerSkillEffectLabelById);
      COMMANDER_EFFECT_CATALOG
        .map((row) => [Number(row?.id || 0), String(row?.label || `Effect #${row?.id}`)])
        .filter(([id]) => Number.isFinite(id) && id > 0)
        .forEach(([id, label]) => map.set(id, label));
      return map;
    },
    [officerSkillEffectLabelById],
  );
  const calculatedFleetSizeSummary = useMemo(
    () => buildFleetSizeSummaryFromParityProfile(calculatedParityProfile, {
      sectionPayload: calculatedSectionPayload,
      combatContext: calculatedStatsCombatContext,
      effectLabelById: commanderEffectLabelById,
    }),
    [
      calculatedParityProfile,
      calculatedSectionPayload,
      calculatedStatsCombatContext,
      commanderEffectLabelById,
    ],
  );
  const selectedStatBreakdown = useMemo(() => {
    if (!selectedStatBreakdownKey) return null;
    return buildExpectedReportStatBreakdown({
      statKey: selectedStatBreakdownKey,
      sectionPayload: calculatedSectionPayload,
      draft,
      combatContext: calculatedStatsCombatContext,
      combatBattle: calculatedStatsCombatBattle,
      combatSide: calculatedStatsCombatSide,
      effectLabelById: commanderEffectLabelById,
    });
  }, [
    selectedStatBreakdownKey,
    calculatedSectionPayload,
    draft,
    calculatedStatsCombatContext,
    calculatedStatsCombatBattle,
    calculatedStatsCombatSide,
    commanderEffectLabelById,
  ]);
  const cityBuildingCatalogRows = getGroupRowsByKey(citySectionCatalog, "buildingOptions");
  const cityBuildingRows = useMemo(
    () => buildCityBuildingEditorRows({
      cityData,
      buildingOptions: cityBuildingCatalogRows,
      effectLabelById: commanderEffectLabelById,
    }),
    [cityData, cityBuildingCatalogRows, commanderEffectLabelById],
  );
  const cityBuildingsByCategory = useMemo(() => {
    const grouped = CITY_BUILDING_CATEGORY_ORDER.map((category) => ({
      category,
      label: CITY_BUILDING_CATEGORY_LABELS[category] || category,
      rows: cityBuildingRows.filter((row) => row.category === category),
    })).filter((group) => group.rows.length > 0);
    return grouped.length ? grouped : [{ category: "other", label: "Buildings", rows: cityBuildingRows }];
  }, [cityBuildingRows]);
  const academyTechCatalogRows = useMemo(
    () => getSectionCatalogGroupRows(academySectionCatalog, "academyTechOptions"),
    [academySectionCatalog],
  );
  const allianceTechCatalogRows = useMemo(
    () => getSectionCatalogGroupRows(academySectionCatalog, "allianceTechOptions"),
    [academySectionCatalog],
  );
  const linkedAllianceTechnologyDisplayRows = useMemo(() => {
    const sourceRows = linkedAllianceTechnologyRows.length
      ? linkedAllianceTechnologyRows
      : (Array.isArray(allianceData?.allianceTech) ? allianceData.allianceTech : []);
    if (!sourceRows.length) return [];
    const catalogById = new Map(
      allianceTechCatalogRows
        .map((row) => [normalizeSkillId(row?.id), row])
        .filter(([id]) => id),
    );
    return sourceRows.map((row) => {
      const catalogRow = catalogById.get(row.id) || {};
      const hasDisplayLevel = Number.isFinite(Number(row?.displayLevel));
      const displayLevel = hasDisplayLevel
        ? Math.max(0, Math.floor(Number(row?.displayLevel || 0)))
        : toResearchLevelDisplay(row?.level || 0, true);
      return {
        ...row,
        label: row.label || catalogRow.label || `Alliance Tech #${row.id}`,
        description: catalogRow.description || "",
        displayLevel,
        effectLine: formatAcademyTechEffectLine(catalogRow, displayLevel, commanderEffectLabelById),
      };
    });
  }, [allianceData?.allianceTech, linkedAllianceTechnologyRows, allianceTechCatalogRows, commanderEffectLabelById]);
  const allianceBenefitBuildingRows = useMemo(() => {
    const countById = new Map(
      (Array.isArray(allianceData?.benefitBuildings) ? allianceData.benefitBuildings : [])
        .map((row) => [normalizeSkillId(row?.id), Math.max(0, Math.floor(Number(row?.count || 0)))])
        .filter(([id]) => id),
    );
    return ALLIANCE_BENEFIT_BUILDING_OPTIONS.map((row) => {
      const count = countById.get(normalizeSkillId(row.id)) || 0;
      const effectLines = buildEffectLinesFromRows(
        (Array.isArray(row.effects) ? row.effects : []).map((effect) => ({
          effectId: effect.effectId,
          value: Math.round(Number(effect.value || 0) * count),
        })),
        commanderEffectLabelById,
        row,
      );
      return {
        ...row,
        count,
        baseEffectLines: buildEffectLinesFromRows(row.effects || [], commanderEffectLabelById, row),
        effectLines,
      };
    });
  }, [allianceData?.benefitBuildings, commanderEffectLabelById]);
  const academyRowsByDetailGroup = useMemo(() => {
    const selectedById = new Map(
      (Array.isArray(academyData?.academyTech) ? academyData.academyTech : [])
        .map((row) => [Number(row?.id || 0), Math.max(0, Math.floor(Number(row?.level || 0)))]),
    );
    return ACADEMY_DETAIL_GROUPS.reduce((acc, group) => {
      acc[group.key] = academyTechCatalogRows
        .filter((row) => Number(row?.category || 0) === Number(group.category))
        .map((row) => {
          const id = normalizeSkillId(row?.id);
          const hasSelectedLevel = selectedById.has(id);
          const selectedLevel = Math.max(0, Math.floor(Number(selectedById.get(id) || 0)));
          const displayLevel = toResearchLevelDisplay(selectedLevel, hasSelectedLevel);
          const maxInternalLevel = Math.max(0, Math.floor(Number(row?.maxLevel || 0)));
          const previewDisplayLevel = displayLevel > 0 ? displayLevel : 1;
          const maxDisplayLevel = Math.max(1, displayLevel, maxInternalLevel > 0 ? maxInternalLevel + 1 : displayLevel);
          const effectId = Number(row?.effectId || 0);
          const perLevel = Number(row?.effectValue || 0);
          const effectRangeLines = Number.isFinite(effectId) && effectId > 0 && Number.isFinite(perLevel) && perLevel !== 0
            ? buildEffectRangeLinesFromEntries([
              { effectId, value: perLevel, level: 1, displayLevel: "Level 1" },
              { effectId, value: perLevel * maxDisplayLevel, level: maxDisplayLevel, displayLevel: `Level ${maxDisplayLevel}` },
            ], commanderEffectLabelById, {
              label: row?.label,
              description: row?.description,
            })
            : [];
          return {
            ...row,
            id,
            hasSelectedLevel,
            selectedLevel,
            displayLevel,
            maxDisplayLevel,
            effectLine: formatAcademyTechEffectLine(row, previewDisplayLevel, commanderEffectLabelById),
            effectRangeLines,
          };
        })
        .sort((left, right) => left.id - right.id);
      return acc;
    }, {});
  }, [academyTechCatalogRows, academyData?.academyTech, commanderEffectLabelById]);
  const commanderUserTechCatalogRows = useMemo(
    () => getSectionCatalogGroupRows(academySectionCatalog, "userTechOptions"),
    [academySectionCatalog],
  );
  const commanderSkillRowsByDetailGroup = useMemo(() => {
    const selectedById = new Map(
      (Array.isArray(academyData?.userTech) ? academyData.userTech : [])
        .map((row) => [Number(row?.id || 0), Math.max(0, Math.floor(Number(row?.level || 0)))]),
    );
    COMMANDER_SKILL_DETAIL_GROUPS.forEach((group) => {
      (Array.isArray(commanderSkillsData?.[group.key]) ? commanderSkillsData[group.key] : [])
        .forEach((row) => {
          const id = normalizeSkillId(row?.id);
          if (!id) return;
          selectedById.set(id, Math.max(0, Math.floor(Number(row?.level || 0))));
        });
    });
    return COMMANDER_SKILL_DETAIL_GROUPS.reduce((acc, group) => {
      acc[group.key] = commanderUserTechCatalogRows
        .filter((row) => Number(row?.category || 0) === Number(group.category))
        .map((row) => {
          const id = normalizeSkillId(row?.id);
          const hasSelectedLevel = selectedById.has(id);
          const selectedLevel = Math.max(0, Math.floor(Number(selectedById.get(id) || 0)));
          const displayLevel = toResearchLevelDisplay(selectedLevel, hasSelectedLevel);
          const maxInternalLevel = Math.max(0, Math.floor(Number(row?.maxLevel || 0)));
          const previewDisplayLevel = displayLevel > 0 ? displayLevel : 1;
          const maxDisplayLevel = Math.max(1, displayLevel, maxInternalLevel > 0 ? maxInternalLevel + 1 : displayLevel);
          const effectId = Number(row?.effectId || 0);
          const perLevel = Number(row?.effectValue || 0);
          const effectRangeLines = Number.isFinite(effectId) && effectId > 0 && Number.isFinite(perLevel) && perLevel !== 0
            ? buildEffectRangeLinesFromEntries([
              { effectId, value: perLevel, level: 1, displayLevel: "Level 1" },
              { effectId, value: perLevel * maxDisplayLevel, level: maxDisplayLevel, displayLevel: `Level ${maxDisplayLevel}` },
            ], commanderEffectLabelById, {
              label: row?.label,
              description: row?.description,
            })
            : [];
          return {
            ...row,
            id,
            hasSelectedLevel,
            selectedLevel,
            displayLevel,
            maxDisplayLevel,
            effectLine: formatAcademyTechEffectLine(row, previewDisplayLevel, commanderEffectLabelById),
            effectRangeLines,
          };
        })
        .sort((left, right) => getCommanderSkillDisplaySort(left) - getCommanderSkillDisplaySort(right));
      return acc;
    }, {});
  }, [
    academyData?.userTech,
    commanderSkillsData,
    commanderUserTechCatalogRows,
    commanderEffectLabelById,
  ]);
  const biochemicalGeneCatalogRows = useMemo(
    () => getSectionCatalogGroupRows(biochemicalSectionCatalog, "geneOptions"),
    [biochemicalSectionCatalog],
  );
  const biochemicalPetIntimacyCatalogRows = useMemo(
    () => getSectionCatalogGroupRows(biochemicalSectionCatalog, "petIntimacyLevelOptions"),
    [biochemicalSectionCatalog],
  );
  const biochemicalPetHealCatalogRows = useMemo(
    () => getSectionCatalogGroupRows(biochemicalSectionCatalog, "petHealLevelOptions"),
    [biochemicalSectionCatalog],
  );
  const biochemicalPetRows = useMemo(() => (
    (() => {
      const savedPets = Array.isArray(biochemicalWorkshopData?.pets) ? biochemicalWorkshopData.pets : [];
      const petsByGid = new Map();
      savedPets.forEach((pet) => {
        const gid = normalizeSkillId(pet?.gid);
        if (gid) petsByGid.set(gid, pet);
      });
      [...biochemicalPetIntimacyCatalogRows, ...biochemicalPetHealCatalogRows].forEach((row) => {
        const gid = normalizeSkillId(row?.petId);
        if (!gid || petsByGid.has(gid)) return;
        petsByGid.set(gid, {
          gid,
          name: String(row?.petLabel || "").trim(),
          intimacyLevel: 0,
          healLevel: 0,
          intimacyExp: 0,
          healExp: 0,
          incubationLevel: 0,
          hatcheryLevel: 0,
          incubationExp: 0,
          hatcheryExp: 0,
          collectedGids: [],
          growthRewards: [],
        });
      });
      return Array.from(petsByGid.values());
    })()
      .map((pet) => {
        const selectedIntimacyLevel = Math.max(0, Math.floor(Number(pet?.intimacyLevel ?? pet?.incubationLevel ?? 0)));
        const selectedHealLevel = Math.max(0, Math.floor(Number(pet?.healLevel ?? pet?.hatcheryLevel ?? 0)));
        const selectedIntimacyRow = resolveBiochemicalPetLevelRow(
          biochemicalPetIntimacyCatalogRows,
          pet?.gid,
          selectedIntimacyLevel,
        );
        const selectedHealRow = resolveBiochemicalPetLevelRow(
          biochemicalPetHealCatalogRows,
          pet?.gid,
          selectedHealLevel,
        );
        const petLabel = String(pet?.name || "").trim()
          || selectedIntimacyRow?.petLabel
          || selectedHealRow?.petLabel
          || getBiochemicalPetLabel(pet?.gid);
        const petGid = normalizeSkillId(pet?.gid);
        const maxIntimacyLevel = Math.max(
          selectedIntimacyLevel,
          ...biochemicalPetIntimacyCatalogRows
            .filter((row) => normalizeSkillId(row?.petId) === petGid)
            .map((row) => Math.max(0, Math.floor(Number(row?.level || 0)))),
        );
        const maxHealLevel = Math.max(
          selectedHealLevel,
          ...biochemicalPetHealCatalogRows
            .filter((row) => normalizeSkillId(row?.petId) === petGid)
            .map((row) => Math.max(0, Math.floor(Number(row?.level || 0)))),
        );
        return {
          ...pet,
          petLabel,
          selectedIntimacyLevel,
          selectedHealLevel,
          selectedIntimacyDisplayLevel: toBiochemicalPetLevelDisplay(selectedIntimacyLevel),
          selectedHealDisplayLevel: toBiochemicalPetLevelDisplay(selectedHealLevel),
          maxIntimacyLevel,
          maxHealLevel,
          maxIntimacyDisplayLevel: toBiochemicalPetLevelDisplay(maxIntimacyLevel),
          maxHealDisplayLevel: toBiochemicalPetLevelDisplay(maxHealLevel),
          selectedIntimacyRow,
          selectedHealRow,
          earnedTechPoints: getBiochemicalPetEarnedTechPoints(selectedIntimacyRow, pet),
          intimacyEffectLines: buildEffectLinesFromRows(selectedIntimacyRow?.effects || [], commanderEffectLabelById, selectedIntimacyRow || {}),
          healEffectLines: buildEffectLinesFromRows(selectedHealRow?.effects || [], commanderEffectLabelById, selectedHealRow || {}),
          intimacyEffectRangeLines: buildEffectRangeLinesFromLevelRows(
            biochemicalPetIntimacyCatalogRows.filter((row) => normalizeSkillId(row?.petId) === petGid),
            commanderEffectLabelById,
            selectedIntimacyRow || { label: petLabel },
            { getDisplayLevel: (levelRow) => `Level ${toBiochemicalPetLevelDisplay(levelRow?.level)}` },
          ),
          healEffectRangeLines: buildEffectRangeLinesFromLevelRows(
            biochemicalPetHealCatalogRows.filter((row) => normalizeSkillId(row?.petId) === petGid),
            commanderEffectLabelById,
            selectedHealRow || { label: petLabel },
            { getDisplayLevel: (levelRow) => `Level ${toBiochemicalPetLevelDisplay(levelRow?.level)}` },
          ),
        };
      })
  ), [
    biochemicalPetHealCatalogRows,
    biochemicalPetIntimacyCatalogRows,
    biochemicalWorkshopData?.pets,
    commanderEffectLabelById,
  ]);
  const biochemicalEarnedGenePoints = useMemo(
    () => biochemicalPetRows.reduce((sum, pet) => sum + Math.max(0, Math.floor(Number(pet?.earnedTechPoints || 0))), 0),
    [biochemicalPetRows],
  );
  const biochemicalDerivedGeneTechLevel = useMemo(
    () => resolveBiochemicalGeneTechLevelFromPoints(
      biochemicalGeneCatalogRows,
      biochemicalEarnedGenePoints,
    ),
    [biochemicalEarnedGenePoints, biochemicalGeneCatalogRows],
  );
  const biochemicalGeneRows = useMemo(() => {
    const currentTechLevel = Math.max(0, Math.floor(Number(biochemicalDerivedGeneTechLevel || 0)));
    return (Array.isArray(biochemicalGeneCatalogRows) ? biochemicalGeneCatalogRows : [])
      .map((row) => {
        const id = normalizeSkillId(row?.id);
        const sortIndex = Math.max(0, Math.floor(Number(row?.sortIndex || 0)));
        return {
          ...row,
          id,
          sortIndex,
          unlocked: Boolean(id && id <= currentTechLevel),
          effectLines: buildEffectLinesFromRows(row?.effects || [], commanderEffectLabelById, {
            label: row?.label,
            description: row?.description,
          }),
        };
      })
      .filter((row) => row.id)
      .sort((left, right) => left.sortIndex - right.sortIndex || left.id - right.id);
  }, [biochemicalDerivedGeneTechLevel, biochemicalGeneCatalogRows, commanderEffectLabelById]);
  const biochemicalGeneGroups = useMemo(() => {
    const byGroup = new Map();
    biochemicalGeneRows.forEach((row) => {
      const group = Math.max(0, Math.floor(Number(row?.group || 0)));
      if (!byGroup.has(group)) byGroup.set(group, []);
      byGroup.get(group).push(row);
    });
    return Array.from(byGroup.entries())
      .sort((left, right) => left[0] - right[0])
      .map(([group, rows]) => ({
        group,
        rootGroup: Math.max(0, Math.floor(Number(rows?.[0]?.rootGroup || 0))),
        label: getBiochemicalGeneGroupLabel(rows, group),
        unlockedCount: rows.filter((row) => row.unlocked).length,
        rows,
      }));
  }, [biochemicalGeneRows]);
  const biochemicalGeneProgress = useMemo(() => {
    const currentTechLevel = Math.max(0, Math.floor(Number(biochemicalDerivedGeneTechLevel || 0)));
    const unlockedCount = biochemicalGeneRows.filter((row) => row.unlocked).length;
    const earnedPoints = Math.max(0, Math.floor(Number(biochemicalEarnedGenePoints || 0)));
    const selectedRow = biochemicalGeneRows.find((row) => Number(row?.id || 0) === currentTechLevel) || null;
    const nextRow = biochemicalGeneRows.find((row) => !row.unlocked) || null;
    return {
      currentTechLevel,
      selectedRow,
      nextRow,
      unlockedCount,
      totalCount: biochemicalGeneRows.length,
      earnedPoints,
      spentPoints: Math.max(0, unlockedCount),
    };
  }, [
    biochemicalDerivedGeneTechLevel,
    biochemicalEarnedGenePoints,
    biochemicalGeneRows,
  ]);
  const titanEffectLabelById = useMemo(
    () => {
      const map = new Map(commanderEffectLabelById);
      Object.entries(TITAN_EFFECT_LABEL_OVERRIDES).forEach(([rawId, label]) => {
        const id = Number(rawId);
        if (Number.isFinite(id) && id > 0 && label) {
          map.set(id, String(label));
        }
      });
      return map;
    },
    [commanderEffectLabelById],
  );
  const warplaneEffectLabelById = useMemo(
    () => {
      const map = new Map(commanderEffectLabelById);
      Object.entries(WARPLANE_EFFECT_LABEL_OVERRIDES).forEach(([rawId, label]) => {
        const id = Number(rawId);
        if (Number.isFinite(id) && id > 0 && label) map.set(id, String(label));
      });
      return map;
    },
    [commanderEffectLabelById],
  );
  const warplaneAdvancementRows = getGroupRowsByKey(warplaneSectionCatalog, "advancementRows");
  const warplaneLevelRows = getGroupRowsByKey(warplaneSectionCatalog, "levelRows");
  const warplaneSkillOptions = getGroupRowsByKey(warplaneSectionCatalog, "skillOptions");
  const warplaneEquipOptions = getGroupRowsByKey(warplaneSectionCatalog, "equipOptions");
  const warplaneEquipSkillOptions = getGroupRowsByKey(warplaneSectionCatalog, "equipSkillOptions");
  const warplaneSkinOptions = getGroupRowsByKey(warplaneSectionCatalog, "skinOptions");
  const warplaneXpItems = getGroupRowsByKey(warplaneSectionCatalog, "warplaneXpItems");
  const warplaneEditorModel = useMemo(
    () => buildWarplaneEditorModel({
      warplaneData,
      advancementRows: warplaneAdvancementRows,
      levelRows: warplaneLevelRows,
      skillOptions: warplaneSkillOptions,
      equipOptions: warplaneEquipOptions,
      equipSkillOptions: warplaneEquipSkillOptions,
      skinOptions: warplaneSkinOptions,
      effectLabelById: warplaneEffectLabelById,
    }),
    [
      warplaneData,
      warplaneAdvancementRows,
      warplaneLevelRows,
      warplaneSkillOptions,
      warplaneEquipOptions,
      warplaneEquipSkillOptions,
      warplaneSkinOptions,
      warplaneEffectLabelById,
    ],
  );
  const antiqueCollectionRows = getGroupRowsByKey(antiquesSectionCatalog, "collectionOptions");
  const badgeCatalogRows = getGroupRowsByKey(badgesSectionCatalog, "badgeOptions");
  const islandDecorationRows = getGroupRowsByKey(islandSectionCatalog, "cityDecorationOptions");
  const islandSpecialDecorationRows = getGroupRowsByKey(islandSectionCatalog, "specialDecorationOptions");
  const islandDockDecorationRows = getGroupRowsByKey(islandSectionCatalog, "dockDecorationOptions");
  const profileChangeSummary = useMemo(
    () => buildProfileChangeSummary(baselineDraft, draft, {
      warplaneAdvancementRows,
      warplaneLevelRows,
      warplaneXpItems,
      warplaneSkillOptions,
      warplaneEquipOptions,
      warplaneEquipSkillOptions,
      warplaneSkinOptions,
      titanCatalogIndex,
      antiqueCollectionRows,
      badgeCatalogRows,
      islandDecorationRows,
      islandSpecialDecorationRows,
      islandDockDecorationRows,
      cityDecorationRows: CITY_DECORATION_PERMANENT_OPTIONS,
    }),
    [
      baselineDraft,
      draft,
      warplaneAdvancementRows,
      warplaneLevelRows,
      warplaneXpItems,
      warplaneSkillOptions,
      warplaneEquipOptions,
      warplaneEquipSkillOptions,
      warplaneSkinOptions,
      titanCatalogIndex,
      antiqueCollectionRows,
      badgeCatalogRows,
      islandDecorationRows,
      islandSpecialDecorationRows,
      islandDockDecorationRows,
    ],
  );
  const selectedWarplanePendingEvolution = useMemo(() => (
    (Array.isArray(profileChangeSummary?.warplaneEvolutionRows) ? profileChangeSummary.warplaneEvolutionRows : [])
      .find((row) => String(row?.key || "") === String(selectedWarplaneKey || ""))
      || null
  ), [profileChangeSummary, selectedWarplaneKey]);
  const selectedWarplanePendingRows = useMemo(() => (
    (Array.isArray(profileChangeSummary?.warplaneRows) ? profileChangeSummary.warplaneRows : [])
      .filter((row) => String(row?.rowKey || "global") === "global" || String(row?.rowKey || "") === String(selectedWarplaneKey || ""))
  ), [profileChangeSummary, selectedWarplaneKey]);
  const profileCartChangeCount = useMemo(() => (
    Number(profileChangeSummary.identityChanged ? 1 : 0)
    + Number(profileChangeSummary.changedSections?.length || 0)
    + Number(profileChangeSummary.warplaneEvolutionRows?.length || 0)
    + Number(profileChangeSummary.warplaneRows?.length || 0)
    + Number(profileChangeSummary.titanRows?.length || 0)
    + Number(profileChangeSummary.officerRows?.length || 0)
    + Number(profileChangeSummary.antiqueRows?.length || 0)
    + Number(profileChangeSummary.badgeRows?.length || 0)
    + Number(profileChangeSummary.islandRows?.length || 0)
    + Number(profileChangeSummary.cityDecorationRows?.length || 0)
  ), [profileChangeSummary]);
  const profileCartMaterialCount = Number(profileChangeSummary.materials?.length || 0);
  const cartRequiredItemGids = useMemo(() => (
    (Array.isArray(profileChangeSummary?.materials) ? profileChangeSummary.materials : [])
      .map((row) => (row?.resourceType === "item" ? normalizeSkillId(row?.itemGid) : null))
      .filter((itemGid, index, all) => itemGid && all.indexOf(itemGid) === index)
      .sort((left, right) => left - right)
  ), [profileChangeSummary?.materials]);
  const cartPlanOptions = useMemo(() => (
    buildUpgradeCartPlanOptions(profileChangeSummary?.materials || [], cartPackCandidatesByItem)
  ), [cartPackCandidatesByItem, profileChangeSummary?.materials]);
  useEffect(() => {
    if (!changeConfirmMode || !cartRequiredItemGids.length) {
      setIsCartPricingLoading(false);
      setCartPricingError("");
      return undefined;
    }
    const missingItemGids = cartRequiredItemGids.filter((itemGid) => !cartPackCandidatesByItem.has(itemGid));
    if (!missingItemGids.length) return undefined;
    let cancelled = false;
    setIsCartPricingLoading(true);
    setCartPricingError("");
    Promise.all(missingItemGids.map((itemGid) => (
      axiosInstance.get(`/store-catalog/items/${itemGid}/packs`, { params: { level: "all" } })
        .then((response) => [itemGid, response.data?.packs || []])
    )))
      .then((entries) => {
        if (cancelled) return;
        setCartPackCandidatesByItem((prev) => {
          const next = new Map(prev);
          entries.forEach(([itemGid, packs]) => next.set(itemGid, Array.isArray(packs) ? packs : []));
          return next;
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setCartPricingError(err?.response?.data?.message || err?.message || "Pack pricing options could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsCartPricingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cartPackCandidatesByItem, cartRequiredItemGids, changeConfirmMode]);
  const selectedWarplanePendingRowsByOption = useMemo(() => {
    const map = new Map();
    selectedWarplanePendingRows.forEach((row) => {
      const fieldKey = String(row?.fieldKey || "");
      const optionId = normalizeSkillId(row?.optionId);
      if (!fieldKey || !optionId) return;
      const key = `${fieldKey}:${optionId}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return map;
  }, [selectedWarplanePendingRows]);
  const renderWarplanePendingRows = useCallback((fieldKey, optionId) => {
    const rows = selectedWarplanePendingRowsByOption.get(`${fieldKey}:${normalizeSkillId(optionId)}`) || [];
    if (!rows.length) return null;
    return (
      <div className="battle-sim-warplane-materials battle-sim-warplane-materials--inline">
        <strong>Pending materials</strong>
        {rows.map((changeRow) => (
          <div key={`warplane-inline-pending-${changeRow.key}`}>
            <span>{`${changeRow.fromLabel} -> ${changeRow.toLabel}`}</span>
            <small>
              {changeRow.materials.length > 0
                ? formatMaterialList(changeRow.materials)
                : "No mapped material requirement."}
            </small>
          </div>
        ))}
      </div>
    );
  }, [selectedWarplanePendingRowsByOption]);
  const officerPendingChangeRowsById = useMemo(() => {
    const rows = Array.isArray(profileChangeSummary?.officerRows) ? profileChangeSummary.officerRows : [];
    return rows.reduce((acc, row) => {
      const officerId = Math.max(0, Math.trunc(Number(row?.officerId || 0)));
      if (!officerId) return acc;
      if (!acc.has(officerId)) acc.set(officerId, []);
      acc.get(officerId).push(row);
      return acc;
    }, new Map());
  }, [profileChangeSummary]);
  const titanPendingChangeRowsBySide = useMemo(() => {
    const rows = Array.isArray(profileChangeSummary?.titanRows) ? profileChangeSummary.titanRows : [];
    return rows.reduce((acc, row) => {
      const sideKey = String(row?.sideKey || "");
      if (!sideKey) return acc;
      if (!acc.has(sideKey)) acc.set(sideKey, []);
      acc.get(sideKey).push(row);
      return acc;
    }, new Map());
  }, [profileChangeSummary]);
  const antiquePendingChangeRowsById = useMemo(() => {
    const rows = Array.isArray(profileChangeSummary?.antiqueRows) ? profileChangeSummary.antiqueRows : [];
    return rows.reduce((acc, row) => {
      const antiqueId = Math.max(0, Math.trunc(Number(row?.antiqueId || 0)));
      if (!antiqueId) return acc;
      if (!acc.has(antiqueId)) acc.set(antiqueId, []);
      acc.get(antiqueId).push(row);
      return acc;
    }, new Map());
  }, [profileChangeSummary]);
  const badgePendingChangeRowsByGid = useMemo(() => {
    const rows = Array.isArray(profileChangeSummary?.badgeRows) ? profileChangeSummary.badgeRows : [];
    return rows.reduce((acc, row) => {
      const badgeGid = normalizeSkillId(row?.badgeGid);
      if (!badgeGid) return acc;
      if (!acc.has(badgeGid)) acc.set(badgeGid, []);
      acc.get(badgeGid).push(row);
      return acc;
    }, new Map());
  }, [profileChangeSummary]);
  const islandPendingChangeRowsByTypeAndId = useMemo(() => {
    const rows = Array.isArray(profileChangeSummary?.islandRows) ? profileChangeSummary.islandRows : [];
    return rows.reduce((acc, row) => {
      const typeKey = String(row?.typeKey || "");
      const id = normalizeSkillId(row?.islandDecorationId);
      if (!typeKey || !id) return acc;
      acc.set(`${typeKey}:${id}`, [...(acc.get(`${typeKey}:${id}`) || []), row]);
      return acc;
    }, new Map());
  }, [profileChangeSummary]);
  const cityDecorationPendingChangeRowsById = useMemo(() => {
    const rows = Array.isArray(profileChangeSummary?.cityDecorationRows) ? profileChangeSummary.cityDecorationRows : [];
    return rows.reduce((acc, row) => {
      const id = normalizeSkillId(row?.cityDecorationId);
      if (!id) return acc;
      acc.set(id, [...(acc.get(id) || []), row]);
      return acc;
    }, new Map());
  }, [profileChangeSummary]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handleDocumentClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = event.target?.closest ? event.target.closest("a[href]") : null;
      if (!anchor) return;
      const target = String(anchor.getAttribute("target") || "");
      if (target && target !== "_self") return;
      const href = String(anchor.getAttribute("href") || "");
      if (!href || href.startsWith("#")) return;
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      if (nextPath === currentPath) return;
      event.preventDefault();
      setPendingNavigationTo(nextPath);
      setChangeConfirmMode("navigate");
    };
    document.addEventListener("click", handleDocumentClick, true);
    return () => document.removeEventListener("click", handleDocumentClick, true);
  }, [hasUnsavedChanges, location.hash, location.pathname, location.search]);

  const equipmentOptionRows = getGroupRowsByKey(equipmentSectionCatalog, "equipOptions");
  const equipmentExtensionTypeRows = getGroupRowsByKey(equipmentSectionCatalog, "extensionTypeOptions");
  const equipmentEditorModel = useMemo(
    () => buildEquipmentEditorRows(
      equipmentOptionRows,
      equipmentExtensionTypeRows,
      equipmentData,
      commanderEffectLabelById,
    ),
    [equipmentOptionRows, equipmentExtensionTypeRows, equipmentData, commanderEffectLabelById],
  );
  const equipmentSlotIdSetBySlot = useMemo(
    () => new Map(
      equipmentEditorModel.rows.map((row) => [
        Number(row?.slot || 0),
        new Set(
          (Array.isArray(row?.baseOptions) ? row.baseOptions : [])
            .map((option) => Number(option?.id || 0))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      ]),
    ),
    [equipmentEditorModel.rows],
  );
  const setEquipmentSlotQuality = useCallback((slotRaw, selectedIdRaw, qualityRankRaw) => {
    const slot = Math.max(0, Math.floor(Number(slotRaw || 0)));
    const slotIdSet = equipmentSlotIdSetBySlot.get(slot);
    if (!slot || !slotIdSet || !slotIdSet.size) return;
    const fallbackSelectedId = normalizeSkillId(selectedIdRaw);
    const parsedQuality = Math.floor(Number(qualityRankRaw));
    const nextQualityRank = Number.isFinite(parsedQuality)
      ? parsedQuality
      : EQUIPMENT_QUALITY_NOT_ENABLED;
    setDraft((prev) => {
      const current = normalizeEquipmentData(prev?.sections?.equipment?.data || {});
      const existingRows = Array.isArray(current?.equipOptions) ? current.equipOptions : [];
      const existingSlotRows = existingRows.filter((entry) => slotIdSet.has(Number(entry?.id || 0)));
      const selected = existingSlotRows[0] || (fallbackSelectedId ? { id: fallbackSelectedId } : null);
      const selectedId = Math.max(0, Math.floor(Number(selected?.id || 0)));
      if (!selectedId || !slotIdSet.has(selectedId)) return prev;
      const withoutSlot = existingRows.filter((entry) => !slotIdSet.has(Number(entry?.id || 0)));
      const nextRows = nextQualityRank < 0
        ? [...withoutSlot]
        : [...withoutSlot, { id: selectedId, level: Math.max(0, Math.min(4, nextQualityRank)) }];
      return {
        ...prev,
        sections: {
          ...prev.sections,
          equipment: {
            ...prev.sections.equipment,
            data: {
              ...current,
              equipOptions: normalizeIdLevelEntries(nextRows),
            },
          },
        },
      };
    });
  }, [equipmentSlotIdSetBySlot]);
  const setEquipmentSlotLevel = useCallback((slotRaw, selectedIdRaw) => {
    const slot = Math.max(0, Math.floor(Number(slotRaw || 0)));
    const selectedId = normalizeSkillId(selectedIdRaw);
    const slotIdSet = equipmentSlotIdSetBySlot.get(slot);
    if (!slot || !selectedId || !slotIdSet || !slotIdSet.has(selectedId)) return;
    setDraft((prev) => {
      const current = normalizeEquipmentData(prev?.sections?.equipment?.data || {});
      const existingRows = Array.isArray(current?.equipOptions) ? current.equipOptions : [];
      const existingSlotRows = existingRows.filter((entry) => slotIdSet.has(Number(entry?.id || 0)));
      const previousQualityRank = existingSlotRows.length
        ? Math.max(0, Math.min(4, Math.floor(Number(existingSlotRows[0]?.level || 0))))
        : EQUIPMENT_QUALITY_NOT_ENABLED;
      const withoutSlot = existingRows.filter((entry) => !slotIdSet.has(Number(entry?.id || 0)));
      const nextRows = previousQualityRank < 0
        ? [...withoutSlot]
        : [...withoutSlot, { id: selectedId, level: previousQualityRank }];
      return {
        ...prev,
        sections: {
          ...prev.sections,
          equipment: {
            ...prev.sections.equipment,
            data: {
              ...current,
              equipOptions: normalizeIdLevelEntries(nextRows),
            },
          },
        },
      };
    });
  }, [equipmentSlotIdSetBySlot]);
  const setEquipmentExtensionSlotValue = useCallback((slotRaw, indexRaw, field, valueRaw) => {
    const slot = Math.max(0, Math.floor(Number(slotRaw || 0)));
    const index = Math.max(1, Math.floor(Number(indexRaw || 0)));
    if (!slot || !index || (field !== "quality" && field !== "level")) return;
    setDraft((prev) => {
      const current = normalizeEquipmentData(prev?.sections?.equipment?.data || {});
      const existingRows = Array.isArray(current?.extensionSlots) ? current.extensionSlots : [];
      const map = new Map(
        existingRows.map((entry) => [
          `${Math.max(0, Math.floor(Number(entry?.slot || 0)))}:${Math.max(1, Math.floor(Number(entry?.index || 0)))}`,
          {
            slot: Math.max(0, Math.floor(Number(entry?.slot || 0))),
            index: Math.max(1, Math.floor(Number(entry?.index || 0))),
            quality: Math.max(0, Math.min(4, Math.floor(Number(entry?.quality || 0)))),
            level: Math.max(0, Math.min(5, Math.floor(Number(entry?.level || 0)))),
          },
        ]),
      );
      const key = `${slot}:${index}`;
      const currentEntry = map.get(key) || null;
      if (field === "quality") {
        const parsedQuality = Math.floor(Number(valueRaw));
        const nextQuality = Number.isFinite(parsedQuality) ? parsedQuality : EQUIPMENT_QUALITY_NOT_ENABLED;
        if (nextQuality < 0) {
          map.delete(key);
        } else {
          const nextEntry = {
            ...(currentEntry || { slot, index, level: 0 }),
            slot,
            index,
            quality: Math.max(0, Math.min(4, nextQuality)),
          };
          map.set(key, nextEntry);
        }
      } else {
        if (!currentEntry) return prev;
        const nextEntry = {
          ...currentEntry,
          level: Math.max(0, Math.min(5, Math.floor(Number(valueRaw || 0)))),
        };
        map.set(key, nextEntry);
      }
      const nextRows = Array.from(map.values())
        .filter((entry) => entry.slot > 0 && entry.index > 0)
        .sort((left, right) => left.slot - right.slot || left.index - right.index);
      return {
        ...prev,
        sections: {
          ...prev.sections,
          equipment: {
            ...prev.sections.equipment,
            data: {
              ...current,
              extensionSlots: nextRows,
            },
          },
        },
      };
    });
  }, []);

  const requestPrerequisiteAction = useCallback((action = {}) => {
    if (typeof action?.apply !== "function") return;
    setPendingPrerequisiteAction({
      title: action.title || "Apply prerequisite?",
      message: action.message || "This upgrade requires another upgrade first.",
      targetLabel: action.targetLabel || "",
      requirements: Array.isArray(action.requirements) ? action.requirements.filter(Boolean) : [],
      apply: action.apply,
    });
  }, []);

  const closePrerequisiteAction = useCallback(() => {
    setPendingPrerequisiteAction(null);
  }, []);

  const confirmPrerequisiteAction = useCallback(() => {
    const action = pendingPrerequisiteAction;
    setPendingPrerequisiteAction(null);
    if (typeof action?.apply === "function") {
      action.apply();
    }
  }, [pendingPrerequisiteAction]);

  const updateIslandCityDecorationLevelWithPrerequisite = (row, nextDisplayLevel) => {
    const islandDecorationId = normalizeSkillId(row?.id);
    if (!islandDecorationId) return;
    const requestedDisplayLevel = Math.max(0, Math.floor(Number(nextDisplayLevel || 0)));
    const isSelected = requestedDisplayLevel > 0;
    const internalLevel = Number(row?.maxLevel || 0) > 0
      ? fromIslandLevelDisplay(requestedDisplayLevel)
      : 0;

    if (!isSelected) {
      updateIslandIdLevelCollection("cityDecorationOptions", islandDecorationId, 0, false);
      return;
    }

    const gate = findCityDecorationGateForIslandDecoration(islandDecorationId);
    if (!gate?.decorationId) {
      updateIslandIdLevelCollection("cityDecorationOptions", islandDecorationId, internalLevel, true);
      return;
    }

    const selectedCityDecoration = selectedCityDecorationById.get(gate.decorationId);
    const selectedCityDecorationLevel = selectedCityDecoration
      ? Math.max(0, Math.floor(Number(selectedCityDecoration?.level || 0)))
      : 0;
    const gateMet = Boolean(selectedCityDecoration) && selectedCityDecorationLevel >= gate.level;
    if (gateMet) {
      updateIslandIdLevelCollection("cityDecorationOptions", islandDecorationId, internalLevel, true);
      return;
    }

    requestPrerequisiteAction({
      title: "Island Decoration Prerequisite",
      targetLabel: `${row?.label || "Island decoration"} ${getIslandLevelDisplayLabel(requestedDisplayLevel, row?.maxLevel)}`,
      message: "This island decoration is unlocked by a matching city skin. Apply the required city skin change first?",
      requirements: [`${gate.decorationName} ${gate.levelLabel}`],
      apply: () => {
        updateCityDecorationSelection(gate.decorationId, true, gate.level);
        updateIslandIdLevelCollection("cityDecorationOptions", islandDecorationId, internalLevel, true);
      },
    });
  };

  const updateTitanSpellLevelWithPrerequisite = (sideKey, sideMeta, skillRow, option, nextLevel) => {
    const requestedLevel = nextLevel === null
      ? null
      : Math.max(0, Math.floor(Number(nextLevel ?? 0)));
    if (requestedLevel === null) {
      updateTitanSideIdLevelCollection(sideKey, "spells", option.id, null);
      return;
    }
    if (Boolean(skillRow?.unlocked)) {
      updateTitanSideIdLevelCollection(sideKey, "spells", option.id, requestedLevel);
      return;
    }
    const unlockStage = Math.max(0, Math.floor(Number(skillRow?.unlockStage || 0)));
    const unlockEvolutionLevel = Math.max(0, Math.floor(Number(skillRow?.unlockEvolutionLevel || 0)));
    const sideLabel = String(sideMeta?.label || "Titan");
    requestPrerequisiteAction({
      title: "Titan Prerequisite",
      targetLabel: `${sideLabel} ${option?.label || "skill"}`,
      message: "This titan skill is locked by evolution progress. Apply the required evolution first?",
      requirements: [skillRow?.unlockText || `Evolution tier ${unlockStage}, step ${unlockEvolutionLevel}`],
      apply: () => {
        updateTitanSideScalar(sideKey, "evolutionStage", unlockStage);
        updateTitanSideScalar(sideKey, "evolutionLevel", unlockEvolutionLevel);
        updateTitanSideIdLevelCollection(sideKey, "spells", option.id, requestedLevel);
      },
    });
  };

  const updateWarplaneSkillLevelWithPrerequisite = (row, nextLevel) => {
    const requestedLevel = Math.floor(Number(nextLevel ?? -1));
    if (Boolean(row?.unlocked)) {
      updateWarplaneIdLevelCollection(row.fieldKey, row.id, Math.max(0, requestedLevel));
      return;
    }
    const unlockStage = Math.max(0, Math.floor(Number(row?.stage || 0)));
    const unlockStep = Math.max(0, Math.floor(Number(row?.unlockStep || 0)));
    requestPrerequisiteAction({
      title: "Warplane Prerequisite",
      targetLabel: row?.label || "Warplane skill",
      message: "This warplane skill is locked by advancement progress. Apply the required advancement first?",
      requirements: [row?.unlockText || `Advancement stage ${unlockStage}, step ${unlockStep}`],
      apply: () => {
        updateWarplaneStage(unlockStage);
        updateWarplaneScalar("advancementStep", unlockStep);
        updateWarplaneIdLevelCollection(row.fieldKey, row.id, Math.max(0, requestedLevel));
      },
    });
  };

  const updateWarplaneEquipmentLevelWithPrerequisite = (equipmentRow, nextLevel) => {
    if (!equipmentRow) return;
    const nextInternalLevel = fromWarplaneOptionLevelDisplay(nextLevel);
    if (Math.floor(Number(equipmentRow?.selectedLevel ?? -1)) >= 0) {
      updateWarplaneIdLevelCollection(WARPLANE_SKILL_GROUP_KEYS.equips, equipmentRow.id, nextInternalLevel);
      return;
    }
    requestPrerequisiteAction({
      title: "Warplane Equipment Prerequisite",
      targetLabel: equipmentRow?.label || "Warplane equipment",
      message: "This equipment is not installed. Install it and apply the selected level?",
      requirements: [`Install ${equipmentRow?.label || "warplane equipment"}`],
      apply: () => updateWarplaneIdLevelCollection(WARPLANE_SKILL_GROUP_KEYS.equips, equipmentRow.id, nextInternalLevel),
    });
  };

  const updateWarplaneEquipmentSkillWithPrerequisite = (equipmentRow, skillRow, nextLevel) => {
    const normalizedSkillId = normalizeSkillId(skillRow?.id);
    if (!normalizedSkillId) return;
    const nextInternalLevel = Math.max(-1, Math.floor(Number(nextLevel ?? -1)));
    const equipmentInstalled = !equipmentRow || Math.floor(Number(equipmentRow?.selectedLevel ?? -1)) >= 0;
    if (equipmentInstalled || nextInternalLevel < 0) {
      updateWarplaneIdLevelCollection(WARPLANE_SKILL_GROUP_KEYS.equipSkills, normalizedSkillId, nextInternalLevel);
      return;
    }
    requestPrerequisiteAction({
      title: "Warplane Equipment Prerequisite",
      targetLabel: skillRow?.label || "Warplane equipment skill",
      message: "This equipment skill requires the matching warplane equipment to be installed first. Apply both changes?",
      requirements: [`Install ${equipmentRow?.label || "warplane equipment"}`],
      apply: () => {
        updateWarplaneIdLevelCollection(WARPLANE_SKILL_GROUP_KEYS.equips, equipmentRow.id, 0);
        updateWarplaneIdLevelCollection(WARPLANE_SKILL_GROUP_KEYS.equipSkills, normalizedSkillId, nextInternalLevel);
      },
    });
  };

  const setEquipmentExtensionSlotValueWithPrerequisite = (row, extension, field, value) => {
    if (Boolean(extension?.unlocked)) {
      setEquipmentExtensionSlotValue(row.slot, extension?.index, field, value);
      return;
    }
    const unlockDisplayLevel = Math.max(1, Math.floor(Number(extension?.unlockDisplayLevel || 1)));
    const targetBaseOption = findEquipmentBaseOptionForUnlock(row, unlockDisplayLevel);
    const targetBaseId = Number(targetBaseOption?.id || 0);
    requestPrerequisiteAction({
      title: "Equipment Prerequisite",
      targetLabel: `${row?.label || "Equipment"} Extension ${Number(extension?.index || 0)}`,
      message: "This equipment extension is locked by equipment level. Apply the required equipment level first?",
      requirements: [`${row?.label || "Equipment"} Level ${unlockDisplayLevel}`],
      apply: () => {
        if (targetBaseId) {
          const nextQualityRank = Number(row?.selectedQualityRank || EQUIPMENT_QUALITY_NOT_ENABLED) >= 0
            ? row.selectedQualityRank
            : 0;
          setEquipmentSlotQuality(row.slot, targetBaseId, nextQualityRank);
          setEquipmentSlotLevel(row.slot, targetBaseId);
        }
        setEquipmentExtensionSlotValue(row.slot, extension?.index, field, value);
      },
    });
  };

  const applyOfficerStarPrerequisiteToRow = (rowIndex, requiredStars, updater) => {
    const normalizedRequiredStars = Number(requiredStars);
    updateOfficerRowAt(rowIndex, (prev) => {
      const hasRequiredStars = Number(prev?.stars || 0) + 1e-9 >= normalizedRequiredStars;
      const rowWithStars = hasRequiredStars
        ? prev
        : { ...prev, ...buildOfficerStarsPatchFromInput(normalizedRequiredStars, prev) };
      return typeof updater === "function" ? updater(rowWithStars) : rowWithStars;
    });
  };

  const requestOfficerStarPrerequisiteAction = ({
    rowIndex,
    row,
    requiredStars,
    title,
    targetLabel,
    message,
    apply,
  }) => {
    const normalizedRequiredStars = Number(requiredStars);
    if (!Number.isFinite(normalizedRequiredStars)) return;
    if (Number(row?.stars || 0) + 1e-9 >= normalizedRequiredStars) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    requestPrerequisiteAction({
      title: title || "Officer Star Prerequisite",
      targetLabel,
      message: message || "This officer upgrade is locked by officer stars. Apply the required stars first?",
      requirements: [`${row?.officerLabel || "Officer"} ${formatOfficerStarRequirement(normalizedRequiredStars)}`],
      apply: () => applyOfficerStarPrerequisiteToRow(rowIndex, normalizedRequiredStars, apply),
    });
  };

  const updateOfficerEquipmentLevelWithPrerequisite = (rowIndex, row, slotIndex, slotMeta, nextLevel) => {
    const requiredStars = getOfficerEquipmentSlotUnlockStar(slotIndex);
    const parsedLevel = Number(nextLevel || 0);
    const apply = (prev) => {
      const slots = Array.isArray(prev?.equipment) ? [...prev.equipment] : [];
      const current = slots[slotIndex] && typeof slots[slotIndex] === "object"
        ? slots[slotIndex]
        : { equipId: slotMeta.equipId, level: 0, masteryLevels: [] };
      slots[slotIndex] = {
        ...current,
        equipId: slotMeta.equipId,
        level: parsedLevel,
      };
      return { ...prev, equipment: slots };
    };
    if (parsedLevel <= 0) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    requestOfficerStarPrerequisiteAction({
      rowIndex,
      row,
      requiredStars,
      title: "Officer Equipment Prerequisite",
      targetLabel: `${row?.officerLabel || "Officer"} ${slotMeta?.label || `Equipment ${slotIndex + 1}`}`,
      message: "This officer equipment slot is locked by officer stars. Apply the required stars first?",
      apply,
    });
  };

  const updateOfficerEquipmentMasteryWithPrerequisite = (rowIndex, row, slotIndex, slotMeta, masteryId, nextLevel) => {
    const requiredStars = getOfficerEquipmentSlotUnlockStar(slotIndex);
    const parsedNextLevel = Number(nextLevel || 0);
    const unlockRow = findOfficerEquipmentMasteryUnlockRow(slotMeta, masteryId);
    const requiredParentLevel = parsedNextLevel > 0
      ? Math.max(0, Math.floor(Number(unlockRow?.level || 0)))
      : 0;
    const apply = (prev) => {
      const slots = Array.isArray(prev?.equipment) ? [...prev.equipment] : [];
      const current = slots[slotIndex] && typeof slots[slotIndex] === "object"
        ? slots[slotIndex]
        : { equipId: slotMeta.equipId, level: 0, masteryLevels: [] };
      const masteryLevels = Array.isArray(current?.masteryLevels) ? [...current.masteryLevels] : [];
      const without = masteryLevels.filter((entry) => Number(entry?.id || 0) !== Number(masteryId || 0));
      without.push({ id: Number(masteryId || 0), level: parsedNextLevel });
      slots[slotIndex] = {
        ...current,
        equipId: slotMeta.equipId,
        level: Math.max(Number(current?.level || 0), requiredParentLevel),
        masteryLevels: without,
      };
      return { ...prev, equipment: slots };
    };
    if (parsedNextLevel <= 0) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    const needsStars = Number(row?.stars || 0) + 1e-9 < Number(requiredStars || 0);
    const selectedSlot = Array.isArray(row?.equipment) ? row.equipment[slotIndex] : null;
    const needsParentLevel = Number(selectedSlot?.level || 0) < requiredParentLevel;
    if (!needsStars && !needsParentLevel) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    const requirements = [];
    if (needsStars) requirements.push(`${row?.officerLabel || "Officer"} ${formatOfficerStarRequirement(requiredStars)}`);
    if (needsParentLevel) requirements.push(`${slotMeta?.label || `Equipment ${slotIndex + 1}`} Level ${requiredParentLevel}`);
    requestPrerequisiteAction({
      title: "Officer Equipment Prerequisite",
      targetLabel: `${row?.officerLabel || "Officer"} ${slotMeta?.label || `Equipment ${slotIndex + 1}`} mastery`,
      message: "This officer equipment mastery requires another equipment upgrade first. Apply the prerequisite and mastery together?",
      requirements,
      apply: () => applyOfficerStarPrerequisiteToRow(rowIndex, requiredStars, apply),
    });
  };

  const updateOfficerSkillSelectionWithPrerequisite = (rowIndex, row, skillIndex, selectedSkillId) => {
    const requiredStars = getOfficerSkillSlotUnlockStar(skillIndex);
    const normalizedSelectedSkillId = normalizeSkillId(selectedSkillId);
    const apply = (prev) => {
      const skills = Array.isArray(prev.skills)
        ? [...prev.skills]
        : Array.from({ length: 3 }, () => ({ skillId: null, level: 0 }));
      skills[skillIndex] = {
        skillId: normalizedSelectedSkillId,
        level: 0,
      };
      return { ...prev, skills };
    };
    if (!normalizedSelectedSkillId) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    requestOfficerStarPrerequisiteAction({
      rowIndex,
      row,
      requiredStars,
      title: "Officer Skill Prerequisite",
      targetLabel: `${row?.officerLabel || "Officer"} Skill ${skillIndex + 1}`,
      message: "This officer skill slot is locked by officer stars. Apply the required stars first?",
      apply,
    });
  };

  const updateOfficerSkillLevelWithPrerequisite = (rowIndex, row, skillIndex, skillId, nextDisplayLevel) => {
    const requiredStars = getOfficerSkillSlotUnlockStar(skillIndex);
    const parsedDisplayLevel = Number(nextDisplayLevel || 0);
    const apply = (prev) => {
      const skills = Array.isArray(prev.skills)
        ? [...prev.skills]
        : Array.from({ length: 3 }, () => ({ skillId: null, level: 0 }));
      const current = skills[skillIndex] || { skillId: null, level: 0 };
      skills[skillIndex] = {
        ...current,
        skillId: normalizeSkillId(skillId || current.skillId),
        level: fromOfficerSkillLevelDisplay(parsedDisplayLevel),
      };
      return { ...prev, skills };
    };
    if (parsedDisplayLevel <= 0) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    requestOfficerStarPrerequisiteAction({
      rowIndex,
      row,
      requiredStars,
      title: "Officer Skill Prerequisite",
      targetLabel: `${row?.officerLabel || "Officer"} Skill ${skillIndex + 1}`,
      message: "This officer skill slot is locked by officer stars. Apply the required stars first?",
      apply,
    });
  };

  const updateOfficerBreakthroughWithPrerequisite = (rowIndex, row, nextDisplayLevel) => {
    const parsedDisplayLevel = Number(nextDisplayLevel || 0);
    const apply = (prev) => ({
      ...prev,
      breakthroughLevel: fromOfficerBreakthroughLevelDisplay(parsedDisplayLevel),
    });
    if (parsedDisplayLevel <= 0) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    requestOfficerStarPrerequisiteAction({
      rowIndex,
      row,
      requiredStars: OFFICER_BREAKTHROUGH_UNLOCK_STAR,
      title: "Officer Breakthrough Prerequisite",
      targetLabel: `${row?.officerLabel || "Officer"} Breakthrough`,
      message: "This officer breakthrough skill is locked by officer stars. Apply the required stars first?",
      apply,
    });
  };

  const updateOfficerUnworldlySkillWithPrerequisite = (rowIndex, row, slotIndex, selectedSkillId, unworldlyOptions) => {
    const requiredStars = getOfficerUnworldlySlotUnlockStar(slotIndex);
    const normalizedSkillId = normalizeSkillId(selectedSkillId);
    const apply = (prev) => {
      const unworldly = Array.isArray(prev.unworldly)
        ? [...prev.unworldly]
        : Array.from({ length: 5 }, () => ({ groupId: null, skillId: null, tierId: null, tierRank: null, level: 0 }));
      const option = unworldlyOptions.find((entry) => Number(entry?.skillId || 0) === Number(normalizedSkillId || 0));
      const tierRows = Array.isArray(option?.tiers) ? option.tiers : [];
      const firstTier = tierRows.find((entry) => !Boolean(entry?.isLegacyTier)) || tierRows[0] || null;
      unworldly[slotIndex] = {
        groupId: Number(option?.groupId || null),
        skillId: normalizedSkillId,
        tierId: Number(firstTier?.tierId || null),
        tierRank: Number(firstTier?.tierRank || null),
        level: normalizedSkillId ? 1 : 0,
      };
      return { ...prev, unworldly };
    };
    if (!normalizedSkillId) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    requestOfficerStarPrerequisiteAction({
      rowIndex,
      row,
      requiredStars,
      title: "Officer Unworldly Prerequisite",
      targetLabel: `${row?.officerLabel || "Officer"} Unworldly ${slotIndex + 1}`,
      message: "This unworldly slot is locked by officer stars. Apply the required stars first?",
      apply,
    });
  };

  const updateOfficerUnworldlyTierWithPrerequisite = (rowIndex, row, slotIndex, tierId) => {
    const requiredStars = getOfficerUnworldlySlotUnlockStar(slotIndex);
    requestOfficerStarPrerequisiteAction({
      rowIndex,
      row,
      requiredStars,
      title: "Officer Unworldly Prerequisite",
      targetLabel: `${row?.officerLabel || "Officer"} Unworldly ${slotIndex + 1}`,
      message: "This unworldly slot is locked by officer stars. Apply the required stars first?",
      apply: (prev) => {
        const unworldly = Array.isArray(prev.unworldly)
          ? [...prev.unworldly]
          : Array.from({ length: 5 }, () => ({ groupId: null, skillId: null, tierId: null, tierRank: null, level: 0 }));
        const current = unworldly[slotIndex] || { groupId: null, skillId: null, tierId: null, tierRank: null, level: 0 };
        const resolved = resolveUnworldlyTierBySkill(row.officerId, current.skillId, normalizeSkillId(tierId));
        unworldly[slotIndex] = {
          ...current,
          tierId: Number(resolved?.tier?.tierId || null),
          tierRank: Number(resolved?.tier?.tierRank || null),
        };
        return { ...prev, unworldly };
      },
    });
  };

  const updateOfficerUnworldlyLevelWithPrerequisite = (rowIndex, row, slotIndex, nextLevel) => {
    const requiredStars = getOfficerUnworldlySlotUnlockStar(slotIndex);
    const parsedLevel = Number(nextLevel || 0);
    const apply = (prev) => {
      const unworldly = Array.isArray(prev.unworldly)
        ? [...prev.unworldly]
        : Array.from({ length: 5 }, () => ({ groupId: null, skillId: null, tierId: null, tierRank: null, level: 0 }));
      const current = unworldly[slotIndex] || { groupId: null, skillId: null, tierId: null, tierRank: null, level: 0 };
      unworldly[slotIndex] = {
        ...current,
        level: parsedLevel,
      };
      return { ...prev, unworldly };
    };
    if (parsedLevel <= 0) {
      updateOfficerRowAt(rowIndex, apply);
      return;
    }
    requestOfficerStarPrerequisiteAction({
      rowIndex,
      row,
      requiredStars,
      title: "Officer Unworldly Prerequisite",
      targetLabel: `${row?.officerLabel || "Officer"} Unworldly ${slotIndex + 1}`,
      message: "This unworldly slot is locked by officer stars. Apply the required stars first?",
      apply,
    });
  };
  const antiqueSuitRows = getGroupRowsByKey(antiquesSectionCatalog, "collectionSuitOptions");

  const sectionEffectLabelById = useMemo(() => (
    new Map(
      (Array.isArray(COMMANDER_EFFECT_CATALOG) ? COMMANDER_EFFECT_CATALOG : [])
        .map((entry) => [Number(entry?.id || 0), String(entry?.label || "").trim()])
        .filter(([id, label]) => Number.isFinite(id) && id > 0 && label),
    )
  ), []);

  const antiqueGroups = useMemo(
    () => buildAntiqueCollectionEditorGroups({
      collectionRows: antiqueCollectionRows,
      selectedRows: antiquesData?.collectionOptions,
      effectLabelById: sectionEffectLabelById,
    }),
    [antiqueCollectionRows, antiquesData?.collectionOptions, sectionEffectLabelById],
  );

  const antiqueSuitEditorRows = useMemo(
    () => buildAntiqueSuitRows({
      suitRows: antiqueSuitRows,
      selectedRows: antiquesData?.collectionOptions,
      collectionRows: antiqueCollectionRows,
      effectLabelById: sectionEffectLabelById,
    }),
    [antiqueSuitRows, antiquesData?.collectionOptions, antiqueCollectionRows, sectionEffectLabelById],
  );
  const antiqueCollectionRowById = useMemo(() => (
    new Map(
      (Array.isArray(antiqueCollectionRows) ? antiqueCollectionRows : [])
        .map((row) => [Number(row?.id || 0), row])
      .filter(([id]) => Number.isFinite(id) && id > 0),
    )
  ), [antiqueCollectionRows]);

  const badgeEditorRows = useMemo(() => {
    const equippedByGid = new Map(
      (Array.isArray(badgesData?.equipped) ? badgesData.equipped : [])
        .map((row) => [Number(row?.gid || 0), row])
        .filter(([gid]) => Number.isFinite(gid) && gid > 0),
    );
    const itemCountsByGid = new Map();
    (Array.isArray(badgesData?.items) ? badgesData.items : []).forEach((row) => {
      const gid = Number(row?.gid || 0);
      if (!Number.isFinite(gid) || gid <= 0) return;
      itemCountsByGid.set(gid, (itemCountsByGid.get(gid) || 0) + Math.max(0, Math.floor(Number(row?.count || 0))));
    });
    return (Array.isArray(badgeCatalogRows) ? badgeCatalogRows : []).map((row) => {
      const gid = normalizeSkillId(row?.gid || row?.id);
      const equipped = equippedByGid.get(gid) || {};
      const selectedLevel = Math.max(0, Math.floor(Number(equipped?.level || 0)));
      const levelRows = Array.isArray(row?.levelRows) ? row.levelRows : [];
      const selectedLevelRow = levelRows.find((entry) => Number(entry?.level || 0) === selectedLevel)
        || levelRows.find((entry) => Number(entry?.level || 0) === 0)
        || null;
      const levelOptions = getBadgeLevelOptions(row, selectedLevel);
      return {
        ...row,
        gid,
        selectedLevel,
        levelOptions,
        selectedLevelRow,
        ownedCount: itemCountsByGid.get(gid) || 0,
        effectLines: buildEffectLinesFromRows(selectedLevelRow?.effects || [], commanderEffectLabelById, {
          label: row?.label,
          description: row?.description,
        }),
        effectRangeLines: buildEffectRangeLinesFromLevelRows(levelRows, commanderEffectLabelById, {
          label: row?.label,
          description: row?.description,
        }),
      };
    }).sort((left, right) => (
      Number(left?.position || 0) - Number(right?.position || 0)
      || Number(left?.gid || 0) - Number(right?.gid || 0)
    ));
  }, [badgeCatalogRows, badgesData?.equipped, badgesData?.items, commanderEffectLabelById]);

  const islandDecorationGroups = useMemo(
    () => buildIslandDecorationEditorGroups({
      decorationRows: islandDecorationRows,
      selectedRows: islandData?.cityDecorationOptions,
      effectLabelById: sectionEffectLabelById,
    }),
    [islandDecorationRows, islandData?.cityDecorationOptions, sectionEffectLabelById],
  );
  const islandDecorationSetRows = useMemo(
    () => buildIslandDecorationSetRows({
      decorationRows: islandDecorationRows,
      selectedRows: islandData?.cityDecorationOptions,
      effectLabelById: sectionEffectLabelById,
    }),
    [islandDecorationRows, islandData?.cityDecorationOptions, sectionEffectLabelById],
  );
  const islandSpecialDecorationGroups = useMemo(
    () => buildIslandDecorationEditorGroups({
      decorationRows: islandSpecialDecorationRows,
      selectedRows: islandData?.specialDecorationOptions,
      effectLabelById: sectionEffectLabelById,
    }),
    [islandSpecialDecorationRows, islandData?.specialDecorationOptions, sectionEffectLabelById],
  );
  const islandSpecialSelectedCount = Array.isArray(islandData?.specialDecorationOptions)
    ? islandData.specialDecorationOptions.length
    : 0;
  const islandDockDecorationGroups = useMemo(
    () => buildIslandDecorationEditorGroups({
      decorationRows: islandDockDecorationRows,
      selectedRows: islandData?.dockDecorationOptions,
      effectLabelById: sectionEffectLabelById,
    }),
    [islandDockDecorationRows, islandData?.dockDecorationOptions, sectionEffectLabelById],
  );
  const islandDockSelectedCount = Array.isArray(islandData?.dockDecorationOptions)
    ? islandData.dockDecorationOptions.length
    : 0;
  const commanderBoostsModel = useMemo(() => {
    const vip = commanderBoostsData?.vip && typeof commanderBoostsData.vip === "object"
      ? commanderBoostsData.vip
      : { level: 0, active: false };
    const vipLevel = Math.max(0, Math.min(VIP_MAX_LEVEL, Math.floor(Number(vip?.level || 0))));
    const vipRow = getVipLevelOption(vipLevel);
    const prosperity = commanderBoostsData?.prosperity && typeof commanderBoostsData.prosperity === "object"
      ? commanderBoostsData.prosperity
      : { level: 0, active: false, count: 0 };
    const level = Math.max(0, Math.min(PROSPERITY_MAX_LEVEL, Math.floor(Number(prosperity?.level || 0))));
    const prosperityRow = getProsperityLevelOption(level);
    const militaryRank = commanderBoostsData?.militaryRank && typeof commanderBoostsData.militaryRank === "object"
      ? commanderBoostsData.militaryRank
      : { level: 0 };
    const militaryRankLevel = Math.max(0, Math.min(MILITARY_RANK_MAX_LEVEL, Math.floor(Number(militaryRank?.level || 0))));
    const militaryRankRow = getMilitaryRankOption(militaryRankLevel);
    return {
      vip: {
        level: vipLevel,
        active: normalizeBoolean(vip?.active, false),
        effectLines: buildEffectLinesFromRows(vipRow?.effects || [], commanderEffectLabelById, {
          label: "VIP Privileges",
          description: "VIP stat bonuses",
        }),
        effectRangeLines: buildEffectRangeLinesFromLevelRows(VIP_LEVEL_OPTIONS, commanderEffectLabelById, {
          label: "VIP Privileges",
          description: "VIP stat bonuses",
        }, {
          getLevel: (row) => Number(row?.displayLevel || row?.level || 0),
          getDisplayLevel: (row) => `Level ${Number(row?.displayLevel || 0).toLocaleString()}`,
        }),
      },
      prosperity: {
        level,
        count: Math.max(0, Math.floor(Number(prosperity?.count || 0))),
        active: normalizeBoolean(prosperity?.active, false),
        requiredCount: Math.max(0, Math.floor(Number(prosperityRow?.count || 0))),
        power: Math.max(0, Math.floor(Number(prosperityRow?.power || 0))),
        effectLines: buildEffectLinesFromRows(prosperityRow?.effects || [], commanderEffectLabelById, {
          label: "Prosperity",
          description: "Prosperity stat bonuses",
        }),
        effectRangeLines: buildEffectRangeLinesFromLevelRows(PROSPERITY_LEVEL_OPTIONS, commanderEffectLabelById, {
          label: "Prosperity",
          description: "Prosperity stat bonuses",
        }),
      },
      militaryRank: {
        level: militaryRankLevel,
        label: militaryRankRow?.label || "None",
        requiredExp: Math.max(0, Math.floor(Number(militaryRankRow?.requiredExp || 0))),
        effectLines: buildEffectLinesFromRows(militaryRankRow?.effects || [], commanderEffectLabelById, {
          label: militaryRankRow?.label || "Military Rank",
          description: "Military rank stat bonuses",
        }),
        effectRangeLines: buildEffectRangeLinesFromLevelRows(MILITARY_RANK_OPTIONS, commanderEffectLabelById, {
          label: "Military Rank",
          description: "Military rank stat bonuses",
        }),
      },
    };
  }, [commanderBoostsData?.militaryRank, commanderBoostsData?.prosperity, commanderBoostsData?.vip, commanderEffectLabelById]);
  const selectedCityDecorationById = useMemo(
    () => new Map(
      (Array.isArray(cityDecorationsData?.permanent) ? cityDecorationsData.permanent : [])
        .map((row) => [Number(row?.id || 0), row])
        .filter(([id]) => Number.isFinite(id) && id > 0),
    ),
    [cityDecorationsData?.permanent],
  );
  const cityDecorationGroups = useMemo(
    () => buildCityDecorationDisplayGroups(CITY_DECORATION_PERMANENT_OPTIONS),
    [],
  );
  const selectedTemporaryBuffById = useMemo(
    () => new Map(
      (Array.isArray(temporaryBuffsData?.active) ? temporaryBuffsData.active : [])
        .map((row) => [String(row?.buffId || ""), row])
        .filter(([buffId]) => Boolean(buffId)),
    ),
    [temporaryBuffsData?.active],
  );
  const temporaryBuffGroups = useMemo(
    () => groupRowsByCategory(TEMPORARY_BUFF_CATALOG),
    [],
  );

  const renderOfficerMobileField = (label, content, extraClassName = "", key = label) => (
    <div key={key} className={`battle-sim-officer-mobile-field ${extraClassName}`.trim()}>
      <div className="battle-sim-officer-mobile-field-label">{label}</div>
      <div className="battle-sim-officer-mobile-field-control">{content}</div>
    </div>
  );

  const renderOfficerMobileCard = ({ row, rowIndex, categoryKey }) => {
    const catalogEntry = getOfficerCatalogEntryById(row.officerId);
    const iconPaths = getOfficerIconPathsById(row.officerId);
    const hasSkinIcon = Boolean(iconPaths?.skin);
    const skinTierBounds = getOfficerSkinTierBounds(row.officerId);
    const skinTierOptions = Array.isArray(skinTierBounds?.tierOptions)
      ? skinTierBounds.tierOptions
      : [];
    const hasSkinTierControl = Boolean(skinTierBounds?.hasSkin && skinTierOptions.length);
    const displayIconPath = getOfficerDisplayIconPath(row.officerId, false);
    const skillOptions = getOfficerPassiveSkillPoolForOfficer(row.officerId);
    const breakthroughSkill = getOfficerSkillOptionById(catalogEntry?.breakthroughSkillId);
    const maxBreakthroughDisplayLevel = Math.max(1, Number(breakthroughSkill?.maxLevel || 20));
    const breakthroughUnlocked = Number(row?.star || 0) >= OFFICER_BREAKTHROUGH_UNLOCK_STAR;
    const maxSkillLevelByStar = Math.max(0, getOfficerSkillMaxLevelForStar(row?.star));
    const unworldlyOptions = getOfficerUnworldlyOptionsByOfficer(row.officerId);
    const supportsAdvancedOfficerSkills = isGoldTierOfficer(catalogEntry);
    const equipmentSlots = getOfficerEquipmentSlotsForOfficer(row.officerId);
    const equipmentSelection = Array.isArray(row?.equipment) ? row.equipment : [];
    const equipmentEffects = getOfficerEquipmentEffectContributions(
      row.officerId,
      row?.equipment,
      Number(row?.stars || 0),
    );
    const previewRows = buildOfficerPreviewRows(row, equipmentEffects, commanderEffectLabelById);
    const officerBattlePower = calculateOfficerRowBattlePower(row);
    const pendingOfficerRows = officerPendingChangeRowsById.get(Math.max(0, Math.trunc(Number(row?.officerId || 0)))) || [];

    const skinContent = hasSkinTierControl ? (
      <select
        value={String(row?.skinLevel == null || row.skinLevel === "" || !Number.isFinite(Number(row.skinLevel)) ? -1 : Number(row.skinLevel))}
        onChange={(event) => updateOfficerRowAt(rowIndex, (prev) => ({
          ...prev,
          skinLevel: Math.trunc(Number(event.target.value || -1)),
        }))}
      >
        <option value="-1">Locked</option>
        {skinTierOptions.map((tierValue) => (
          <option key={`mobile-skin-tier-${row.officerId}-${tierValue}`} value={tierValue}>
            {tierValue}
          </option>
        ))}
      </select>
    ) : (
      <span className="battle-sim-officer-skin-na">N/A</span>
    );

    const renderEquipmentMobileContent = (slotIndex) => {
      const slotMeta = equipmentSlots[slotIndex];
      const selectedSlot = equipmentSelection[slotIndex] || { equipId: null, level: 0, masteryLevels: [] };
      if (!slotMeta) return <span className="battle-sim-officer-skin-na">N/A</span>;
      const selectedLevel = Math.max(0, Math.floor(Number(selectedSlot?.level || 0)));
      const slotUnlockStar = getOfficerEquipmentSlotUnlockStar(slotIndex);
      const slotUnlocked = isOfficerEquipmentSlotUnlocked(row?.stars, slotIndex, row?.officerId);
      const levelRows = Array.isArray(slotMeta?.levelRows) ? slotMeta.levelRows : [];
      const selectedLevelRow = selectedLevel > 0
        ? (levelRows.find((entry) => Number(entry?.level || 0) === selectedLevel)
          || [...levelRows].reverse().find((entry) => Number(entry?.level || 0) <= selectedLevel)
          || null)
        : null;
      const unlockedMasteryIds = new Set(
        Array.isArray(selectedLevelRow?.masteryIds)
          ? selectedLevelRow.masteryIds.map((value) => Number(value || 0)).filter((value) => Number.isFinite(value)).map((value) => Math.trunc(value))
          : [],
      );
      const maxMasteryByParent = Math.max(0, Number(selectedLevelRow?.masteryLevelMax || 0));
      const masterySelectionById = new Map(
        (Array.isArray(selectedSlot?.masteryLevels) ? selectedSlot.masteryLevels : [])
          .map((entry) => [Number(entry?.id || 0), Math.max(0, Math.floor(Number(entry?.level || 0)))]),
      );

      return (
        <div className={`battle-sim-unworldly-cell ${slotUnlocked ? "" : "battle-sim-unworldly-cell--locked"}`.trim()}>
          <div className="battle-sim-officer-skill-readonly battle-sim-officer-equipment-label" title={`Equipment #${slotMeta.equipId}`}>
            {slotMeta?.iconPath ? (
              <img
                src={slotMeta.iconPath}
                alt={slotMeta.label || "Equipment"}
                className="battle-sim-officer-equipment-icon"
                loading="lazy"
              />
            ) : null}
            <span className="battle-sim-officer-equipment-text">{slotMeta.label}</span>
          </div>
          <input
            type="number"
            min={0}
            max={Math.max(0, Number(slotMeta?.maxLevel || 0))}
            value={selectedLevel}
            onFocus={(event) => event.target.select()}
            onChange={(event) => updateOfficerEquipmentLevelWithPrerequisite(
              rowIndex,
              row,
              slotIndex,
              slotMeta,
              event.target.value,
            )}
          />
          {getOfficerEquipmentMasteryTracksInUnlockOrder(slotMeta).map((track) => {
            const masteryId = Number(track?.masteryId || 0);
            const rawMax = Math.max(0, Number(track?.maxLevel || 0));
            const unlocked = unlockedMasteryIds.has(masteryId);
            const maxAllowed = unlocked ? Math.max(0, Math.min(rawMax, maxMasteryByParent)) : 0;
            const unlockRow = findOfficerEquipmentMasteryUnlockRow(slotMeta, masteryId);
            const maxAllowedForControl = unlocked
              ? maxAllowed
              : Math.max(0, Math.min(rawMax, Number(unlockRow?.masteryLevelMax || rawMax)));
            const selectedMasteryLevel = Math.max(0, Number(masterySelectionById.get(masteryId) || 0));
            return (
              <label key={`mobile-equip-${slotIndex}-mastery-${masteryId}`} className="battle-sim-officer-equipment-mastery-row">
                <span>{track?.label || `Mastery #${masteryId}`}</span>
                <input
                  type="number"
                  min={0}
                  max={maxAllowedForControl}
                  disabled={!unlockRow || maxAllowedForControl <= 0}
                  value={Math.min(selectedMasteryLevel, maxAllowedForControl)}
                  onFocus={(event) => event.target.select()}
                  onChange={(event) => updateOfficerEquipmentMasteryWithPrerequisite(
                    rowIndex,
                    row,
                    slotIndex,
                    slotMeta,
                    masteryId,
                    event.target.value,
                  )}
                />
              </label>
            );
          })}
          <div className="battle-sim-officer-calc-values">
            {(() => {
              const calcRows = buildOfficerEquipmentPreviewRows({
                slotMeta,
                selectedSlot,
                selectedLevel,
                effectLabelById: commanderEffectLabelById,
              });
              if (!slotUnlocked) {
                return (
                  <>
                    <div className="battle-sim-officer-calc-empty">{`Unlocks at ${slotUnlockStar.toFixed(1)} stars`}</div>
                    {renderEffectLineList(calcRows, `mobile-equip-locked-range-${slotIndex}`)}
                  </>
                );
              }
              if (!calcRows.length) return <div className="battle-sim-officer-calc-empty">No active bonus.</div>;
              return calcRows.map((line, calcIdx) => (
                <div key={`mobile-equip-calc-${slotIndex}-${calcIdx}`}>{line}</div>
              ));
            })()}
          </div>
        </div>
      );
    };

    const renderSkillMobileContent = (skillIndex) => {
      const slot = row?.skills?.[skillIndex] || { skillId: null, level: 0 };
      const locked = !isOfficerSkillSlotUnlocked(row?.star, skillIndex);
      const hasFixedPassiveSkillSlots = String(catalogEntry?.passiveSkillSelectionMode || "").toLowerCase() === "fixed";
      const fixedSkillId = hasFixedPassiveSkillSlots
        ? normalizeSkillId(catalogEntry?.fixedPassiveSkillSlotIds?.[skillIndex])
        : null;
      const fixedSkillOption = fixedSkillId ? getOfficerSkillOptionById(fixedSkillId) : null;
      const previewSkillId = hasFixedPassiveSkillSlots ? fixedSkillId : slot?.skillId;
      const activeSkillId = locked ? previewSkillId : previewSkillId;
      const hasActiveSkill = Boolean(activeSkillId) && (!locked || Number(slot?.level || 0) >= 0);
      const skillSlotUnlockStar = getOfficerSkillSlotUnlockStar(skillIndex);
      const maxSkillLevelForControl = Math.max(
        maxSkillLevelByStar,
        getOfficerSkillMaxLevelForStar(Math.max(Number(row?.star || 0), skillSlotUnlockStar)),
      );
      const skillCalcRows = locked
        ? buildOfficerSkillRangeRows({
          officerId: row.officerId,
          skillId: activeSkillId,
          officerLevel: Number(row?.level || 0),
          decimalStars: Math.max(Number(row?.stars || 0), skillSlotUnlockStar),
          effectLabelById: commanderEffectLabelById,
        })
        : buildOfficerSkillCalculatedRows({
          officerId: row.officerId,
          skillId: activeSkillId,
          skillLevel: toOfficerSkillLevelDisplay(slot?.level || 0, hasActiveSkill),
          officerLevel: Number(row?.level || 0),
          decimalStars: Number(row?.stars || 0),
          effectLabelById: commanderEffectLabelById,
          includeNextDelta: true,
        });

      return (
        <div className="battle-sim-unworldly-cell">
          {hasFixedPassiveSkillSlots ? (
            <div className="battle-sim-officer-skill-readonly">
              {locked ? "Locked by stars" : (fixedSkillOption?.label || (fixedSkillId ? `Skill #${fixedSkillId}` : "N/A"))}
            </div>
          ) : (
            <select
              value={String(slot?.skillId || "")}
              onChange={(event) => updateOfficerSkillSelectionWithPrerequisite(
                rowIndex,
                row,
                skillIndex,
                event.target.value,
              )}
            >
              <option value="">{locked ? "Locked by stars" : "None"}</option>
              {skillOptions.map((option) => (
                <option key={`mobile-${option.skillId}-${skillIndex}`} value={option.skillId}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
          <select
            value={String(toOfficerSkillLevelDisplay(slot?.level || 0, hasActiveSkill))}
            disabled={!(hasFixedPassiveSkillSlots ? fixedSkillId : slot?.skillId)}
            onChange={(event) => updateOfficerSkillLevelWithPrerequisite(
              rowIndex,
              row,
              skillIndex,
              hasFixedPassiveSkillSlots ? fixedSkillId : slot?.skillId,
              event.target.value,
            )}
          >
            <option value="0">Inactive</option>
            {buildIntegerRange(1, toOfficerSkillLevelDisplay(maxSkillLevelForControl, true)).map((displayLevel) => (
              <option key={`mobile-officer-skill-level-${skillIndex}-${displayLevel}`} value={displayLevel}>
                {displayLevel}
              </option>
            ))}
          </select>
          <div className="battle-sim-officer-calc-values">
            {skillCalcRows.length ? skillCalcRows.map((line, calcIdx) => (
              <div key={`mobile-skill-calc-${skillIndex}-${calcIdx}`}>{line}</div>
            )) : <div className="battle-sim-officer-calc-empty">No active bonus.</div>}
          </div>
        </div>
      );
    };

    const breakthroughContent = supportsAdvancedOfficerSkills ? (
      <div className="battle-sim-unworldly-cell">
        <div className="battle-sim-officer-skill-readonly">
          {!breakthroughUnlocked ? "Locked by stars" : (breakthroughSkill?.label || "No breakthrough skill")}
        </div>
        <select
          value={String(toOfficerBreakthroughLevelDisplay(row.breakthroughLevel ?? -1))}
          disabled={!breakthroughSkill}
          onChange={(event) => updateOfficerBreakthroughWithPrerequisite(rowIndex, row, event.target.value)}
        >
          <option value="0">Unactivated</option>
          {Array.from({ length: maxBreakthroughDisplayLevel }, (_, levelIndex) => levelIndex + 1).map((displayLevel) => (
            <option key={`mobile-breakthrough-${row.officerId}-${displayLevel}`} value={displayLevel}>
              {displayLevel}
            </option>
          ))}
        </select>
        <div className="battle-sim-officer-calc-values">
          {(() => {
            const displayLevel = toOfficerBreakthroughLevelDisplay(row.breakthroughLevel ?? -1);
            const calcRows = !breakthroughUnlocked || displayLevel <= 0
              ? buildOfficerSkillRangeRows({
                officerId: row.officerId,
                skillId: breakthroughSkill?.skillId,
                officerLevel: Number(row?.level || 0),
                decimalStars: Math.max(Number(row?.stars || 0), OFFICER_BREAKTHROUGH_UNLOCK_STAR),
                effectLabelById: commanderEffectLabelById,
                applyMastery: false,
              })
              : buildOfficerSkillCalculatedRows({
                officerId: row.officerId,
                skillId: breakthroughSkill?.skillId,
                skillLevel: displayLevel,
                officerLevel: Number(row?.level || 0),
                decimalStars: Number(row?.stars || 0),
                effectLabelById: commanderEffectLabelById,
                applyMastery: false,
                includeNextDelta: true,
              });
            if (!calcRows.length) return <div className="battle-sim-officer-calc-empty">No active bonus.</div>;
            return calcRows.map((line, calcIdx) => (
              <div key={`mobile-breakthrough-calc-${rowIndex}-${calcIdx}`}>{line}</div>
            ));
          })()}
        </div>
      </div>
    ) : (
      <span className="battle-sim-officer-skin-na">N/A</span>
    );

    const renderUnworldlyMobileContent = (slotIndex) => {
      if (!supportsAdvancedOfficerSkills) return <span className="battle-sim-officer-skin-na">N/A</span>;
      const slot = row?.unworldly?.[slotIndex] || { skillId: null, tierId: null, level: 0 };
      const slotUnlockStar = getOfficerUnworldlySlotUnlockStar(slotIndex);
      const slotUnlocked = isOfficerUnworldlySlotUnlocked(row?.stars, slotIndex, row?.officerId);
      const selectedOption = unworldlyOptions.find((option) => Number(option?.skillId || 0) === Number(slot?.skillId || 0));
      const tierOptionsAll = Array.isArray(selectedOption?.tiers) ? selectedOption.tiers : [];
      const selectedTier = tierOptionsAll.find((tier) => Number(tier?.tierId || 0) === Number(slot?.tierId || 0))
        || tierOptionsAll.find((tier) => !Boolean(tier?.isLegacyTier))
        || tierOptionsAll[0];
      const tierOptionsVisible = tierOptionsAll.filter((tier) => !Boolean(tier?.isLegacyTier));
      const tierOptions = (
        selectedTier && Boolean(selectedTier?.isLegacyTier)
          ? [selectedTier, ...tierOptionsVisible]
          : tierOptionsVisible
      );
      const maxUnworldlyLevel = Math.max(0, Number(selectedTier?.maxLevel || 0));
      const unworldlyCalcRows = buildOfficerUnworldlyCalculatedRows({
        option: selectedOption,
        tier: selectedTier,
        level: Number(slot?.level || (slotUnlocked ? 0 : 1)),
      });
      const unworldlyRangeRows = buildOfficerUnworldlyRangeRows({
        option: selectedOption,
        tier: selectedTier,
      });

      return (
        <div className={`battle-sim-unworldly-cell ${slotUnlocked ? "" : "battle-sim-unworldly-cell--locked"}`.trim()}>
          <select
            value={String(slot?.skillId || "")}
            onChange={(event) => updateOfficerUnworldlySkillWithPrerequisite(
              rowIndex,
              row,
              slotIndex,
              event.target.value,
              unworldlyOptions,
            )}
          >
            <option value="">{slotUnlocked ? "None" : `Locked (${slotUnlockStar.toFixed(1)} stars)`}</option>
            {unworldlyOptions.map((option) => (
              <option key={`mobile-u-${slotIndex}-${option.skillId}`} value={option.skillId}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={String(slot?.tierId || "")}
            disabled={!slot?.skillId}
            onChange={(event) => updateOfficerUnworldlyTierWithPrerequisite(rowIndex, row, slotIndex, event.target.value)}
          >
            <option value="">{slot?.skillId ? "Select tier" : "No skill selected"}</option>
            {tierOptions.map((tier) => (
              <option key={`mobile-u-tier-${slotIndex}-${tier.tierId}`} value={tier.tierId}>
                {`${tier.tierDisplayLabel || tier.tierLabel || `Tier ${tier.tierRank}`}`}
              </option>
            ))}
          </select>
          <select
            value={String(Number(slot?.level || 0))}
            disabled={!slot?.skillId}
            onChange={(event) => updateOfficerUnworldlyLevelWithPrerequisite(rowIndex, row, slotIndex, event.target.value)}
          >
            {buildIntegerRange(0, Math.max(0, maxUnworldlyLevel)).map((level) => (
              <option key={`mobile-unworldly-level-${slotIndex}-${level}`} value={level}>
                {level}
              </option>
            ))}
          </select>
          <div className="battle-sim-officer-calc-values">
            {!slotUnlocked ? (
              <>
                <div className="battle-sim-officer-calc-empty">{`Unlocks at ${slotUnlockStar.toFixed(1)} stars`}</div>
                {renderEffectLineList(unworldlyRangeRows, `mobile-unworldly-range-${slotIndex}`)}
              </>
            ) : unworldlyCalcRows.length ? unworldlyCalcRows.map((line, calcIdx) => (
              <div key={`mobile-unworldly-calc-${slotIndex}-${calcIdx}`}>{line}</div>
            )) : <div className="battle-sim-officer-calc-empty">No active bonus.</div>}
          </div>
        </div>
      );
    };

    const detailFields = (() => {
      if (activeOfficerDetailGroup === "skin") {
        return [renderOfficerMobileField("Skin", skinContent, "battle-sim-officer-mobile-field--skin", "skin")];
      }
      if (activeOfficerDetailGroup === "equipment") {
        return [0, 1].map((slotIndex) => renderOfficerMobileField(
          `Equipment ${slotIndex + 1}`,
          renderEquipmentMobileContent(slotIndex),
          "battle-sim-officer-mobile-field--equipment",
          `equipment-${slotIndex}`,
        ));
      }
      if (activeOfficerDetailGroup === "unworldly") {
        return [0, 1, 2, 3, 4].map((slotIndex) => renderOfficerMobileField(
          `Unworldly ${slotIndex + 1}`,
          renderUnworldlyMobileContent(slotIndex),
          "battle-sim-officer-mobile-field--unworldly",
          `unworldly-${slotIndex}`,
        ));
      }
      return [
        ...[0, 1, 2].map((skillIndex) => renderOfficerMobileField(
          `Skill ${skillIndex + 1}`,
          renderSkillMobileContent(skillIndex),
          "battle-sim-officer-mobile-field--skill",
          `skill-${skillIndex}`,
        )),
        renderOfficerMobileField("Breakthrough", breakthroughContent, "battle-sim-officer-mobile-field--breakthrough", "breakthrough"),
      ];
    })();

    return (
      <div key={`mobile-${row.officerId || rowIndex}-${categoryKey}`} className="battle-sim-officer-mobile-card">
        <div className="battle-sim-officer-mobile-card-head">
          <div className="battle-sim-officer-mobile-icon">
            {displayIconPath ? (
              <img
                src={displayIconPath}
                alt={row.officerLabel || "Officer"}
                className="battle-sim-officer-icon-img"
                loading="lazy"
              />
            ) : (
              <div className="battle-sim-officer-icon-placeholder">No Icon</div>
            )}
            {hasSkinIcon ? <span className="battle-sim-officer-icon-has-skin">Skin</span> : null}
          </div>
          <div className="battle-sim-officer-mobile-title">
            <strong>{row.officerLabel}</strong>
            <small>{`Battle Power ${formatPowerNumber(officerBattlePower)}`}</small>
            <small>{`Mastery ${Number(getOfficerMasteryValue(row.officerId, row.level, row.stars) || 0).toLocaleString()}`}</small>
            {pendingOfficerRows.length > 0 ? (
              <div className="battle-sim-officer-materials">
                <strong>Pending materials</strong>
                {pendingOfficerRows.map((changeRow) => (
                  <div key={`mobile-pending-officer-${changeRow.key}`}>
                    <span>{`${changeRow.subsection}: ${changeRow.fromLabel} -> ${changeRow.toLabel}`}</span>
                    <small>
                      {changeRow.materials.length > 0
                        ? formatMaterialList(changeRow.materials)
                        : "No mapped material requirement."}
                    </small>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="battle-sim-officer-mobile-core">
          <label>
            <span>Appointed</span>
            <input
              type="checkbox"
              checked={Boolean(row.appointed)}
              onChange={(event) => updateOfficerRowAt(rowIndex, (prev) => ({
                ...prev,
                appointed: event.target.checked,
              }))}
            />
          </label>
          <label>
            <span>Level</span>
            <select
              value={String(toOfficerLevelDisplay(row.level || 0))}
              onChange={(event) => updateOfficerRowAt(rowIndex, (prev) => ({
                ...prev,
                level: fromOfficerLevelDisplay(event.target.value),
              }))}
            >
              {buildIntegerRange(1, Math.max(1, Number(catalogEntry?.maxLevel || 55))).map((displayLevel) => (
                <option key={`mobile-officer-level-${row.officerId}-${displayLevel}`} value={displayLevel}>
                  {displayLevel}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Stars</span>
            <select
              value={String(Number(row.stars || 0))}
              aria-label={`${row.officerLabel || "Officer"} stars`}
              onChange={(event) => updateOfficerRowAt(rowIndex, (prev) => ({
                ...prev,
                ...buildOfficerStarsPatchFromInput(event.target.value, prev),
              }))}
            >
              {getOfficerStarSelectOptions(row?.officerId || null).map((starValue) => (
                <option key={`mobile-officer-stars-${row.officerId}-${starValue}`} value={starValue}>
                  {formatOfficerStarsLabel(starValue, row?.officerId || null)}
                </option>
              ))}
            </select>
          </label>
        </div>
        {previewRows.length > 0 ? (
          <div className="battle-sim-officer-mobile-preview">
            {previewRows.slice(0, 4).map((line, previewIndex) => (
              <div key={`mobile-preview-${rowIndex}-${previewIndex}`}>{line}</div>
            ))}
          </div>
        ) : null}
        <div className="battle-sim-officer-mobile-detail">
          {detailFields}
        </div>
      </div>
    );
  };

  const renderOfficerMobileList = () => (
    <div className={`battle-sim-officer-mobile-list battle-sim-officer-mobile-list--detail-${activeOfficerDetailGroup || "skills"}`}>
      {OFFICER_CATEGORY_ORDER.map((categoryKey) => {
        const categoryRows = filteredOfficerRows
          .filter((entry) => String(entry.row?.category || "") === categoryKey);
        const isCategoryCollapsed = Boolean(collapsedOfficerCategories?.[categoryKey]);
        return (
          <div key={`mobile-category-${categoryKey}`} className="battle-sim-officer-mobile-category">
            <button
              type="button"
              className="battle-sim-officer-category-toggle battle-sim-officer-mobile-category-toggle"
              onClick={() => toggleOfficerCategory(categoryKey)}
              aria-expanded={!isCategoryCollapsed}
            >
              <span>{isCategoryCollapsed ? "+" : "-"}</span>
              <strong>{getOfficerCategoryLabel(categoryKey)}</strong>
              <small>{`${categoryRows.length.toLocaleString()} shown`}</small>
            </button>
            {isCategoryCollapsed ? null : categoryRows.length === 0 ? (
              <div className="battle-sim-officer-mobile-empty">No officers match current filters in this category.</div>
            ) : (
              <div className="battle-sim-officer-mobile-cards">
                {categoryRows.map((entry) => renderOfficerMobileCard({ ...entry, categoryKey }))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderProfileChangeCategory = (
    title,
    rows = [],
    renderRowLabel = () => "",
    emptyMaterialText = "No item requirements are mapped for this category.",
  ) => {
    const changeRows = Array.isArray(rows) ? rows : [];
    if (!changeRows.length) return null;
    const materials = summarizeChangeRowMaterials(changeRows);
    return (
      <details className="battle-sim-profile-change-row battle-sim-profile-change-category">
        <summary>
          <div>
            <strong>{title}</strong>
            <small>{`${changeRows.length.toLocaleString()} change${changeRows.length === 1 ? "" : "s"}`}</small>
          </div>
          <span>{materials.length > 0 ? formatMaterialList(materials) : emptyMaterialText}</span>
        </summary>
        <div className="battle-sim-profile-change-list">
          {changeRows.map((row) => (
            <div key={`${title}-${row.key}`}>
              <span>{renderRowLabel(row)}</span>
              <small>
                {row.materials.length > 0
                  ? formatMaterialList(row.materials)
                  : emptyMaterialText}
              </small>
            </div>
          ))}
        </div>
      </details>
    );
  };

  const renderUpgradeCartPlanComparison = () => (
    <div className="battle-sim-upgrade-cart-plans">
      <div className="battle-sim-upgrade-cart-plans-head">
        <div>
          <strong>Cost / Timeline Options</strong>
          <small>Estimated from matching store packs and inferred reset cadence.</small>
        </div>
        {isCartPricingLoading ? <span>Loading pack options...</span> : null}
      </div>
      {cartPricingError ? <div className="battle-sim-profile-error">{cartPricingError}</div> : null}
      <div className="battle-sim-upgrade-cart-plan-grid">
        <div className="battle-sim-upgrade-cart-plan-grid-head">Plan</div>
        <div className="battle-sim-upgrade-cart-plan-grid-head">Completes</div>
        <div className="battle-sim-upgrade-cart-plan-grid-head">Cost</div>
        <div className="battle-sim-upgrade-cart-plan-grid-head">Packs</div>
        {cartPlanOptions.map((plan) => (
          <details key={`cart-plan-${plan.key}`} className="battle-sim-upgrade-cart-plan-row">
            <summary>
              <div>
                <strong>{plan.label}</strong>
                <small>{plan.description}</small>
                <span className="battle-sim-upgrade-cart-plan-toggle">View details</span>
              </div>
              <span>{formatCartTimeline(plan.completionDays)}</span>
              <span>{plan.totalUsd === null ? "Partial" : formatCartUsd(plan.totalUsd)}</span>
              <span>{plan.packCount.toLocaleString()}</span>
            </summary>
            <div className="battle-sim-upgrade-cart-plan-details">
              <small>
                {plan.missingRows.length > 0
                  ? `${plan.missingRows.length.toLocaleString()} required material${plan.missingRows.length === 1 ? "" : "s"} do not currently have matching pack data.`
                  : `${plan.pricedMaterialCount.toLocaleString()} of ${plan.totalMaterialCount.toLocaleString()} material${plan.totalMaterialCount === 1 ? "" : "s"} priced.`}
              </small>
              <div>
                {plan.detailRows.map((detail) => {
                  const material = detail.material || {};
                  const label = material.label || (material.itemGid ? `Item #${material.itemGid}` : "Material");
                  if (detail.status !== "priced") {
                    return (
                      <div key={`cart-plan-${plan.key}-${material.itemGid || label}`} className="battle-sim-upgrade-cart-plan-detail-row">
                        <span>{label}</span>
                        <small>{`${Number(detail.requiredCount || 0).toLocaleString()} required | no matching pack found`}</small>
                      </div>
                    );
                  }
                  const purchaseGroups = Array.isArray(detail.purchaseGroups) ? detail.purchaseGroups : [];
                  return (
                    <div key={`cart-plan-${plan.key}-${material.itemGid || label}`} className="battle-sim-upgrade-cart-plan-detail-row">
                      <span>{label}</span>
                      <small>{`${Number(detail.requiredCount || 0).toLocaleString()} required | ${Number(detail.totalRewardCount || 0).toLocaleString()} covered | ${formatCartUsd(detail.subtotalUsd)}`}</small>
                      {purchaseGroups.map((group) => (
                        <small key={`cart-plan-${plan.key}-${material.itemGid || label}-${getCartPackIdentity(group.pack) || group.title}-${group.firstDay}-${group.lastDay}`}>
                          {`${Number(group.quantity || 0).toLocaleString()} x ${group.title || "Pack"}${group.weekday ? ` (${group.weekday})` : ""} (${Number(group.rewardEach || 0).toLocaleString()} each, ${Number(group.rewardCountTotal || 0).toLocaleString()} total, ${formatCartPurchaseWindow(group.firstDay, group.lastDay)}, resets every ${Number(group.resetDays || 0).toLocaleString()} day${Number(group.resetDays || 0) === 1 ? "" : "s"}) | ${formatCartUsd(group.subtotalUsd)}`}
                        </small>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </details>
        ))}
      </div>
      <small className="battle-sim-upgrade-cart-plan-note">
        Each pack is treated as one purchase per reset window. Weekday daily picks are only scheduled on their matching weekday, and event/special packs are excluded from these estimates. Current estimator prices each material independently; shared pack overlap and owned inventory offsets can be added as the next refinement.
      </small>
    </div>
  );

  return (
    <PageShell maxWidth className="battle-sim-profile-page">
      {pageTitle || showBackToSimulation ? (
      <div className="battle-sim-profile-top">
        {pageTitle ? <h2>{pageTitle}</h2> : <span />}
        {showBackToSimulation ? (
          <button type="button" onClick={() => requestNavigation("/battle-simulation")}>Back to Simulation</button>
        ) : null}
      </div>
      ) : null}

      {error ? <div className="battle-sim-profile-error">{error}</div> : null}
      {success ? <div className="battle-sim-profile-success">{success}</div> : null}
      {extraTopContent}

      <div className={`battle-sim-profile-layout ${singleProfileMode ? "battle-sim-profile-layout--my-commander" : "battle-sim-profile-layout--two-panel"}`}>
        {!singleProfileMode ? (
          <SectionCard className="battle-sim-profile-list-card">
            <div className="battle-sim-profile-list-head">
              <strong>Profiles</strong>
              <button type="button" onClick={handleCreateProfile} disabled={isSaving || isLoading}>New Profile</button>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search profile..."
            />
            <div className="battle-sim-profile-list">
              {isLoading ? <div className="battle-sim-profile-empty">Loading profiles...</div> : null}
              {!isLoading && filteredProfiles.length === 0 ? <div className="battle-sim-profile-empty">No profiles found.</div> : null}
              {!isLoading && filteredProfiles.map((profile) => (
                <button
                  type="button"
                  key={profile._id}
                  className={`battle-sim-profile-row ${String(profile?._id) === String(selectedProfileId) ? "selected" : ""}`}
                  onClick={() => setSelectedProfileId(String(profile?._id))}
                >
                  <span>{profile?.name || "Unnamed"}</span>
                  <small>{profile?.linkedCommanderName || "No linked commander"}</small>
                </button>
              ))}
            </div>
          </SectionCard>
        ) : null}

        <SectionCard className="battle-sim-profile-editor-card">
          {!selectedProfileId ? (
            <div className="battle-sim-profile-empty">Select a profile to edit.</div>
          ) : (
            <>
              <div className="battle-sim-profile-editor-head">
                {!hideProfileName ? (
                  <label>
                    <span>Profile Name</span>
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                    />
                  </label>
                ) : null}
                <div className={`battle-sim-profile-head-actions ${singleProfileMode ? "battle-sim-profile-head-actions--floating" : ""}`}>
                  {!singleProfileMode ? (
                    <button type="button" onClick={handleDeleteProfile} disabled={isSaving || isDeleting}>
                      {isDeleting ? "Deleting..." : "Delete Profile"}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="battle-sim-profile-control-row">
                <div className="battle-sim-profile-section-toolbar">
                  <label className="battle-sim-profile-section-select">
                    <span>Section</span>
                    <select
                      value={selectedSection}
                      onChange={(event) => setSelectedSection(event.target.value)}
                    >
                      {SECTION_ITEMS.map((item) => (
                        <option key={item.key} value={item.key}>
                          {`${item.label} - ${String(draft?.sections?.[item.key]?.status || "draft")}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <small>{selectedSectionMeta.description}</small>
                </div>

                <div className="battle-sim-profile-linked">
                  <div className="battle-sim-profile-linked-head">
                    <strong>Linked Commander</strong>
                    {hasLinkedCommander ? (
                      <button type="button" onClick={handleUnlinkCommander}>Unlink</button>
                    ) : null}
                  </div>
                  <div className="battle-sim-profile-linked-current">
                    {draft.linkedCommanderName || draft.linkedGameCommanderId || "No linked commander selected"}
                    {isLinkedCommanderLoading ? (
                      <small>Refreshing linked commander snapshot...</small>
                    ) : null}
                  </div>
                  {!hasLinkedCommander ? (
                    <div className="battle-sim-profile-linked-search">
                      <input
                        type={useCommanderIdLinking ? "number" : "text"}
                        inputMode={useCommanderIdLinking ? "numeric" : undefined}
                        min={useCommanderIdLinking ? "0" : undefined}
                        value={commanderQuery}
                        onChange={(event) => setCommanderQuery(event.target.value)}
                        placeholder={useCommanderIdLinking ? "Enter commander ID..." : "Search existing commanders..."}
                      />
                      <button type="button" onClick={handleFindCommander} disabled={isCommanderSearching}>
                        {isCommanderSearching ? "Checking..." : (useCommanderIdLinking ? "Check ID" : "Search")}
                      </button>
                    </div>
                  ) : null}
                  {!useCommanderIdLinking && !hasLinkedCommander && commanderResults.length > 0 ? (
                    <div className="battle-sim-profile-linked-results">
                      {commanderResults.map((row) => (
                        <button type="button" key={row?._id} onClick={() => handleLinkCommander(row)}>
                          {row?.name || row?._id}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              {changeConfirmMode ? (
                <div className="battle-sim-profile-link-modal" role="dialog" aria-modal="true" aria-labelledby="profile-change-confirm-title">
                  <div className="battle-sim-profile-link-modal-card battle-sim-profile-change-modal-card">
                    <h3 id="profile-change-confirm-title">
                      {changeConfirmMode === "navigate" ? "Unsaved Commander Changes" : "Upgrade Cart"}
                    </h3>
                    <p>
                      {changeConfirmMode === "navigate"
                        ? "Review the pending profile changes before leaving this page."
                        : "Review the pending upgrades, required materials, and profile changes before saving."}
                    </p>

                    <div className="battle-sim-profile-change-summary">
                      {profileChangeSummary.identityChanged ? (
                        <div className="battle-sim-profile-change-row">
                          <strong>Profile identity</strong>
                          <small>Name or linked commander changed.</small>
                        </div>
                      ) : null}
                      {profileChangeSummary.changedSections.length > 0 ? (
                        <div className="battle-sim-profile-change-row">
                          <strong>Changed sections</strong>
                          <small>{profileChangeSummary.changedSections.join(", ")}</small>
                        </div>
                      ) : null}
                      {profileChangeSummary.materials.length > 0 ? renderUpgradeCartPlanComparison() : null}
                      {renderProfileChangeCategory(
                        "Warplane evolution",
                        profileChangeSummary.warplaneEvolutionRows,
                        (row) => `${row.label}: ${row.fromLabel} -> ${row.toLabel}`,
                        "No additional materials required or mapped for this direction.",
                      )}
                      {renderProfileChangeCategory(
                        "Warplane changes",
                        profileChangeSummary.warplaneRows,
                        (row) => `${row.label}: ${row.subsection} ${row.fromLabel} -> ${row.toLabel}`,
                        "No item requirements are mapped for this warplane change or direction.",
                      )}
                      {renderProfileChangeCategory(
                        "Titan changes",
                        profileChangeSummary.titanRows,
                        (row) => `${row.label}: ${row.subsection} ${row.fromLabel} -> ${row.toLabel}`,
                        "No item requirements are mapped for this titan change or direction.",
                      )}
                      {renderProfileChangeCategory(
                        "Antique changes",
                        profileChangeSummary.antiqueRows,
                        (row) => `${row.label}: ${row.subsection} ${row.fromLabel} -> ${row.toLabel}`,
                        "No item requirements are mapped for this antique change or direction.",
                      )}
                      {renderProfileChangeCategory(
                        "Badge changes",
                        profileChangeSummary.badgeRows,
                        (row) => `${row.label}: ${row.subsection} ${row.fromLabel} -> ${row.toLabel}`,
                        "No item requirements are mapped for this badge change or direction.",
                      )}
                      {renderProfileChangeCategory(
                        "Island decoration changes",
                        profileChangeSummary.islandRows,
                        (row) => `${row.label}: ${row.subsection} ${row.fromLabel} -> ${row.toLabel}`,
                        "No item requirements are mapped for this island decoration change or direction.",
                      )}
                      {renderProfileChangeCategory(
                        "City decoration changes",
                        profileChangeSummary.cityDecorationRows,
                        (row) => `${row.label}: ${row.subsection} ${row.fromLabel} -> ${row.toLabel}`,
                        "No item requirements are mapped for this city decoration change or direction.",
                      )}
                      {renderProfileChangeCategory(
                        "Officer changes",
                        profileChangeSummary.officerRows,
                        (row) => `${row.label}: ${row.subsection} ${row.fromLabel} -> ${row.toLabel}`,
                        "No item requirements are mapped for this officer subsection yet.",
                      )}
                      <details className="battle-sim-profile-change-row battle-sim-profile-change-category" open>
                        <summary>
                          <div>
                            <strong>Total required materials</strong>
                            <small>{`${profileChangeSummary.materials.length.toLocaleString()} material${profileChangeSummary.materials.length === 1 ? "" : "s"}`}</small>
                          </div>
                          <span>
                            {profileChangeSummary.materials.length > 0
                              ? formatMaterialList(profileChangeSummary.materials)
                              : "No item requirements are mapped for these changes yet."}
                          </span>
                        </summary>
                        {profileChangeSummary.materials.length > 0 ? (
                          <div className="battle-sim-profile-change-list">
                            {profileChangeSummary.materials.map((row) => (
                              <div key={`total-material-${row.resourceType || "item"}-${row.itemGid || row.resourceKey || row.label}-${row.level || 0}`}>
                                <span>{row.label || (row.itemGid ? `Item #${row.itemGid}` : row.resourceKey || "Material")}</span>
                                <small>{Number(row.count || 0).toLocaleString()}</small>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </details>
                    </div>

                    <div className="battle-sim-profile-link-modal-actions">
                      <button type="button" className="battle-sim-profile-modal-button battle-sim-profile-modal-button--secondary" onClick={closeChangeConfirm} disabled={isSaving}>
                        {changeConfirmMode === "navigate" ? "Stay Here" : "Cancel"}
                      </button>
                      {changeConfirmMode === "navigate" ? (
                        <button type="button" className="battle-sim-profile-modal-button battle-sim-profile-modal-button--danger" onClick={discardChangesAndContinueNavigation} disabled={isSaving}>
                          Discard And Leave
                        </button>
                      ) : null}
                      <button type="button" className="battle-sim-profile-modal-button battle-sim-profile-modal-button--danger" onClick={requestResetProfileChanges} disabled={isSaving || !hasUnsavedChanges}>
                        Reset Changes
                      </button>
                      <button type="button" className="battle-sim-profile-modal-button battle-sim-profile-modal-button--primary" onClick={confirmProfileSave} disabled={isSaving}>
                        {isSaving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {pendingPrerequisiteAction ? (
                <div className="battle-sim-profile-link-modal" role="dialog" aria-modal="true" aria-labelledby="profile-prerequisite-title">
                  <div className="battle-sim-profile-link-modal-card">
                    <h3 id="profile-prerequisite-title">{pendingPrerequisiteAction.title}</h3>
                    <p>{pendingPrerequisiteAction.message}</p>
                    {pendingPrerequisiteAction.targetLabel ? (
                      <div className="battle-sim-profile-prerequisite-target">
                        <span>Requested upgrade</span>
                        <strong>{pendingPrerequisiteAction.targetLabel}</strong>
                      </div>
                    ) : null}
                    {pendingPrerequisiteAction.requirements.length > 0 ? (
                      <div className="battle-sim-profile-prerequisite-list">
                        <strong>Required first</strong>
                        {pendingPrerequisiteAction.requirements.map((requirement, requirementIndex) => (
                          <small key={`prerequisite-${requirementIndex}`}>{requirement}</small>
                        ))}
                      </div>
                    ) : null}
                    <div className="battle-sim-profile-link-modal-actions">
                      <button
                        type="button"
                        className="battle-sim-profile-modal-button battle-sim-profile-modal-button--secondary"
                        onClick={closePrerequisiteAction}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="battle-sim-profile-modal-button battle-sim-profile-modal-button--primary"
                        onClick={confirmPrerequisiteAction}
                      >
                        Apply Prerequisite
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {showResetConfirm ? (
                <div className="battle-sim-profile-link-modal" role="dialog" aria-modal="true" aria-labelledby="profile-reset-confirm-title">
                  <div className="battle-sim-profile-link-modal-card">
                    <h3 id="profile-reset-confirm-title">Reset Pending Changes?</h3>
                    <p>
                      This will discard every unsaved commander profile change and restore the last saved version.
                    </p>
                    <div className="battle-sim-profile-prerequisite-target">
                      <span>Pending changes</span>
                      <strong>
                        {`${profileCartChangeCount.toLocaleString()} change${profileCartChangeCount === 1 ? "" : "s"} will be discarded`}
                      </strong>
                    </div>
                    <div className="battle-sim-profile-link-modal-actions">
                      <button type="button" className="battle-sim-profile-modal-button battle-sim-profile-modal-button--secondary" onClick={closeResetConfirm} disabled={isSaving}>
                        Cancel
                      </button>
                      <button type="button" className="battle-sim-profile-modal-button battle-sim-profile-modal-button--danger" onClick={resetProfileChanges} disabled={isSaving || !hasUnsavedChanges}>
                        Reset Changes
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {commanderLinkCandidate ? (
                <div className="battle-sim-profile-link-modal" role="dialog" aria-modal="true" aria-labelledby="commander-link-title">
                  <div className="battle-sim-profile-link-modal-card">
                    <h3 id="commander-link-title">Confirm Commander Link</h3>
                    <div className="battle-sim-profile-link-confirm-grid">
                      <span>Commander ID</span>
                      <strong>{commanderLinkCandidate.commanderId}</strong>
                      <span>Name</span>
                      <strong>{commanderLinkCandidate.name || "Unknown"}</strong>
                      <span>Nation</span>
                      <strong>
                        {commanderLinkCandidate.nation?.number
                          ? `Nation ${commanderLinkCandidate.nation.number}`
                          : (commanderLinkCandidate.nation?.name || "Unknown")}
                      </strong>
                      <span>Alliance</span>
                      <strong>
                        {commanderLinkCandidate.alliance?.acronym
                          || commanderLinkCandidate.alliance?.name
                          || "None"}
                      </strong>
                      <span>City Level</span>
                      <strong>{commanderLinkCandidate.cityLevel || "Unknown"}</strong>
                      <span>Battle Power</span>
                      <strong>{formatPowerNumber(commanderLinkCandidate.battlePower)}</strong>
                      <span>Captured Truth</span>
                      <strong>
                        {commanderLinkCandidate.importAvailable
                          ? `${Number(commanderLinkCandidate.importCounts?.officers || 0).toLocaleString()} officers, ${Number(commanderLinkCandidate.importCounts?.inventoryItems || 0).toLocaleString()} items`
                          : "No capture found"}
                      </strong>
                    </div>
                    <div className="battle-sim-profile-link-modal-actions">
                      <button type="button" onClick={closeCommanderLinkCandidate} disabled={isCommanderLinking}>
                        Cancel
                      </button>
                      <button type="button" onClick={handleConfirmCommanderIdLink} disabled={isCommanderLinking}>
                        {isCommanderLinking ? "Linking..." : "Confirm Link"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedStatBreakdown ? (
                <div
                  className="battle-sim-profile-stat-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="stat-breakdown-title"
                  onClick={() => setSelectedStatBreakdownKey("")}
                >
                  <div className="battle-sim-profile-stat-modal-card" onClick={(event) => event.stopPropagation()}>
                    <div className="battle-sim-profile-stat-modal-head">
                      <div>
                        <h3 id="stat-breakdown-title">{selectedStatBreakdown.label}</h3>
                        <small>
                          {COMBAT_CONTEXT_OPTIONS.find((option) => option.key === calculatedStatsCombatContext)?.label || "Internal"}
                          {" | "}
                          {COMBAT_BATTLE_OPTIONS.find((option) => option.key === calculatedStatsCombatBattle)?.label || "Field / Other"}
                          {" | "}
                          {COMBAT_SIDE_OPTIONS.find((option) => option.key === calculatedStatsCombatSide)?.label || "Attacking"}
                        </small>
                      </div>
                      <button type="button" onClick={() => setSelectedStatBreakdownKey("")}>Close</button>
                    </div>
                    <div className="battle-sim-profile-stat-total">
                      <span>Calculated Total</span>
                      <strong>{formatExpectedReportStatValue(selectedStatBreakdown.totalRow || {}, { maximumFractionDigits: 3 })}</strong>
                    </div>
                    <div className="battle-sim-profile-stat-breakdown-section">
                      <h4>Current Contributions</h4>
                      {selectedStatBreakdown.activeRows.length > 0 ? (
                        <div className="battle-sim-profile-stat-source-list">
                          {selectedStatBreakdown.activeRows.map((row) => (
                            <div key={`active-stat-source-${row.key}`} className="battle-sim-profile-stat-source">
                              <div>
                                <strong>{row.label}</strong>
                                {row.effectLabels.length > 0 ? (
                                  <small>{row.effectLabels.slice(0, 3).join(", ")}</small>
                                ) : (
                                  <small>Calculated from active profile entries in this section.</small>
                                )}
                              </div>
                              <span>{row.displayValue}</span>
                              {Array.isArray(row.detailRows) && row.detailRows.length > 0 ? (
                                <details className="battle-sim-profile-stat-detail-list">
                                  <summary>{`${row.detailRows.length.toLocaleString()} detailed contribution${row.detailRows.length === 1 ? "" : "s"}`}</summary>
                                  <div>
                                    {row.detailRows.map((detailRow) => (
                                      <div key={`stat-detail-${detailRow.key}`} className="battle-sim-profile-stat-detail-row">
                                        <div>
                                          <strong>{detailRow.label}</strong>
                                          <small>{detailRow.meta}</small>
                                        </div>
                                        <span>{detailRow.displayValue}</span>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p>No active profile sections currently add to this stat.</p>
                      )}
                    </div>
                    <div className="battle-sim-profile-stat-breakdown-section">
                      <h4>Potential Sources</h4>
                      {selectedStatBreakdown.potentialRows.length > 0 ? (
                        <div className="battle-sim-profile-stat-source-list">
                          {selectedStatBreakdown.potentialRows.map((row) => (
                            <div
                              key={`potential-stat-source-${row.key}`}
                              className={`battle-sim-profile-stat-source battle-sim-profile-stat-source--inactive ${row.isActiveSection ? "battle-sim-profile-stat-source--partly-active" : ""}`}
                            >
                              <div>
                                <strong>{row.label}</strong>
                                <small>{row.meta || `${row.sectionLabel} | ${row.groupLabel}`}</small>
                                {row.description ? <small>{row.description}</small> : null}
                              </div>
                              <span>{row.statusLabel || (row.isActiveSection ? "Review" : "Inactive")}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p>No additional catalog sources were found for this stat yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {isFleetSizeBreakdownOpen ? (
                <div
                  className="battle-sim-profile-stat-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="fleet-size-breakdown-title"
                  onClick={() => setIsFleetSizeBreakdownOpen(false)}
                >
                  <div className="battle-sim-profile-stat-modal-card" onClick={(event) => event.stopPropagation()}>
                    <div className="battle-sim-profile-stat-modal-head">
                      <div>
                        <h3 id="fleet-size-breakdown-title">Fleet Size</h3>
                        <small>
                          {COMBAT_CONTEXT_OPTIONS.find((option) => option.key === calculatedStatsCombatContext)?.label || "Internal"}
                          {" | Normal troop fleet limit"}
                        </small>
                      </div>
                      <button type="button" onClick={() => setIsFleetSizeBreakdownOpen(false)}>Close</button>
                    </div>
                    <div className="battle-sim-profile-stat-total">
                      <span>Calculated Fleet Size</span>
                      <strong>{`${formatFleetSizeUnits(calculatedFleetSizeSummary.totalUnits)} units`}</strong>
                    </div>
                    <div className="battle-sim-profile-stat-breakdown-section">
                      <h4>Calculation</h4>
                      <div className="battle-sim-profile-stat-source-list">
                        <div className="battle-sim-profile-stat-source">
                          <div>
                            <strong>Boostable Unit Pool</strong>
                            <small>Base troops plus boostable sources such as Garage, Academy Leadership, and Reactor-style equipment.</small>
                          </div>
                          <span>{formatFleetSizeUnits(calculatedFleetSizeSummary.boostableUnitLimit)}</span>
                        </div>
                        <div className="battle-sim-profile-stat-source">
                          <div>
                            <strong>Fleet Size Boost</strong>
                            <small>Applied only to the boostable unit pool.</small>
                          </div>
                          <span>{`+${formatFleetSizePercent(calculatedFleetSizeSummary.percentBoost)}`}</span>
                        </div>
                        <div className="battle-sim-profile-stat-source">
                          <div>
                            <strong>Boost Units</strong>
                            <small>Units added by the percentage boost.</small>
                          </div>
                          <span>{formatSignedFleetSizeUnits(calculatedFleetSizeSummary.percentBonusUnits)}</span>
                        </div>
                        <div className="battle-sim-profile-stat-source">
                          <div>
                            <strong>Additional Unit Limit</strong>
                            <small>Flat sources that do not receive the percentage boost, including officers, badges, island, military rank, guards, and cross-nation expansion.</small>
                          </div>
                          <span>{formatSignedFleetSizeUnits(calculatedFleetSizeSummary.additionalUnitLimit)}</span>
                        </div>
                        {calculatedFleetSizeSummary.biochemicalZombieLimit > 0 ? (
                          <div className="battle-sim-profile-stat-source">
                            <div>
                              <strong>Biochemical Zombie Army Limit</strong>
                              <small>Separate cap, not added to normal fleet size.</small>
                            </div>
                            <span>{`${formatFleetSizeUnits(calculatedFleetSizeSummary.biochemicalZombieLimit)} units`}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="battle-sim-profile-stat-breakdown-section">
                      <h4>Fleet Size Contributions</h4>
                      {calculatedFleetSizeSummary.normalContributionRows.length > 0 ? (
                        <div className="battle-sim-profile-stat-source-list">
                          {calculatedFleetSizeSummary.normalContributionRows.map((row) => (
                            <div key={`fleet-modal-normal-${row.key}`} className="battle-sim-profile-stat-source">
                              <div>
                                <strong>{row.label}</strong>
                                <small>{[row.groupLabel, row.meta].filter(Boolean).join(" | ") || "Active profile entry"}</small>
                                {row.normalLines.map((line, lineIndex) => (
                                  <small key={`fleet-modal-normal-line-${row.key}-${line.key}-${lineIndex}`}>{line.text}</small>
                                ))}
                              </div>
                              <span>{formatFleetSizeRowDisplayValue({ ...row.summary, zombieUnits: 0 })}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p>No active profile sections currently add to normal fleet size.</p>
                      )}
                    </div>
                    <div className="battle-sim-profile-stat-breakdown-section">
                      <h4>Zombie Limit Contributions</h4>
                      {calculatedFleetSizeSummary.zombieContributionRows.length > 0 ? (
                        <div className="battle-sim-profile-stat-source-list">
                          {calculatedFleetSizeSummary.zombieContributionRows.map((row) => (
                            <div key={`fleet-modal-zombie-${row.key}`} className="battle-sim-profile-stat-source">
                              <div>
                                <strong>{row.label}</strong>
                                <small>{[row.groupLabel, row.meta].filter(Boolean).join(" | ") || "Active profile entry"}</small>
                                {row.zombieLines.map((line, lineIndex) => (
                                  <small key={`fleet-modal-zombie-line-${row.key}-${line.key}-${lineIndex}`}>{line.text}</small>
                                ))}
                              </div>
                              <span>{formatFleetSizeRowDisplayValue({
                                boostableCoreUnits: 0,
                                additionalUnits: 0,
                                percentRaw: 0,
                                zombieUnits: row.summary?.zombieUnits,
                              })}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p>No active profile sections currently add to the biochemical zombie army limit.</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className={`battle-sim-profile-section-detail ${selectedSection === "officers" ? "battle-sim-profile-section-detail--officers" : ""}`.trim()}>
                <div className="battle-sim-profile-section-detail-head">
                  <strong>{selectedSectionMeta.label}</strong>
                  <small>{selectedSectionMeta.description}</small>
                </div>

                {selectedSection === "powerSummary" ? (
                  <div className="battle-sim-profile-power battle-sim-profile-power--section">
                    <div className="battle-sim-profile-power-meta">
                      <small>
                        Documented source: {documentedPowerBundle.source === "linkedCommander"
                          ? "Linked commander record"
                          : "Profile snapshot"}
                        {documentedPowerUpdatedAtLabel ? ` | Updated ${documentedPowerUpdatedAtLabel}` : ""}
                      </small>
                    </div>
                    <div className="battle-sim-profile-power-compare-grid">
                      <div className="battle-sim-profile-power-column">
                        <div className="battle-sim-profile-power-column-head">Calculated</div>
                        {POWER_SUMMARY_FIELDS.map(([field, label]) => (
                          <div key={`calculated-${field}`} className="battle-sim-profile-power-row">
                            <span>{label}</span>
                            <strong>{formatPowerNumber(calculatedPowerSummary?.[field])}</strong>
                          </div>
                        ))}
                      </div>
                      <div className="battle-sim-profile-power-column">
                        <div className="battle-sim-profile-power-column-head">Documented</div>
                        {POWER_SUMMARY_FIELDS.map(([field, label]) => (
                          <div key={`documented-${field}`} className="battle-sim-profile-power-row">
                            <span>{label}</span>
                            <strong>{formatPowerNumber(documentedPowerSummary?.[field])}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="battle-sim-profile-power-column battle-sim-profile-power-column--full">
                      <div className="battle-sim-profile-power-column-head battle-sim-profile-power-column-head--with-control">
                        <span>Calculated Battle Report Stats</span>
                        <label className="battle-sim-profile-combat-context">
                          <span>Combat</span>
                          <select
                            value={calculatedStatsCombatContext}
                            onChange={(event) => setCalculatedStatsCombatContext(event.target.value)}
                          >
                            {COMBAT_CONTEXT_OPTIONS.map((option) => (
                              <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className="battle-sim-profile-combat-context">
                          <span>Battle</span>
                          <select
                            value={calculatedStatsCombatBattle}
                            onChange={(event) => setCalculatedStatsCombatBattle(event.target.value)}
                          >
                            {COMBAT_BATTLE_OPTIONS.map((option) => (
                              <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className="battle-sim-profile-combat-context">
                          <span>Side</span>
                          <select
                            value={calculatedStatsCombatSide}
                            onChange={(event) => setCalculatedStatsCombatSide(event.target.value)}
                            disabled={!isCitySiegeCombat(calculatedStatsCombatBattle)}
                          >
                            {COMBAT_SIDE_OPTIONS.map((option) => (
                              <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <button
                        type="button"
                        className="battle-sim-profile-fleet-size-panel battle-sim-profile-fleet-size-panel--button"
                        aria-label="View contributing factors for fleet size"
                        title="View contributing factors for fleet size"
                        onClick={() => setIsFleetSizeBreakdownOpen(true)}
                      >
                        <div className="battle-sim-profile-fleet-size-head">
                          <div className="battle-sim-profile-fleet-size-primary">
                            <strong>{`${formatFleetSizeUnits(calculatedFleetSizeSummary.totalUnits)} units`}</strong>
                            <span>Fleet Size</span>
                            <small>
                              {COMBAT_CONTEXT_OPTIONS.find((option) => option.key === calculatedStatsCombatContext)?.label || "Internal"}
                              {" normal troop limit"}
                            </small>
                          </div>
                          <div className="battle-sim-profile-fleet-size-grid">
                            <div>
                              <span>Boostable pool</span>
                              <strong>{formatFleetSizeUnits(calculatedFleetSizeSummary.boostableUnitLimit)}</strong>
                            </div>
                            <div>
                              <span>Boost</span>
                              <strong>{`+${formatFleetSizePercent(calculatedFleetSizeSummary.percentBoost)}`}</strong>
                            </div>
                            <div>
                              <span>Boost units</span>
                              <strong>{formatSignedFleetSizeUnits(calculatedFleetSizeSummary.percentBonusUnits)}</strong>
                            </div>
                            <div>
                              <span>Additional units</span>
                              <strong>{formatSignedFleetSizeUnits(calculatedFleetSizeSummary.additionalUnitLimit)}</strong>
                            </div>
                            {calculatedFleetSizeSummary.biochemicalZombieLimit > 0 ? (
                              <div>
                                <span>Zombie limit</span>
                                <strong>{formatFleetSizeUnits(calculatedFleetSizeSummary.biochemicalZombieLimit)}</strong>
                              </div>
                            ) : null}
                          </div>
                          <span className="battle-sim-profile-stat-action-hint">
                            <InfoOutlinedIcon fontSize="inherit" aria-hidden="true" />
                            <span>Factors</span>
                          </span>
                        </div>
                      </button>
                      <div className="battle-sim-profile-expected-stats-grid">
                        {calculatedExpectedReportStats.length > 0 ? calculatedExpectedReportStats.map((row) => (
                          <button
                            key={`expected-stat-${row.key}`}
                            type="button"
                            className="battle-sim-profile-power-row battle-sim-profile-power-row--button"
                            aria-label={`View contributing factors for ${row.label}`}
                            title={`View contributing factors for ${row.label}`}
                            onClick={() => setSelectedStatBreakdownKey(row.key)}
                          >
                            <span className="battle-sim-profile-stat-label">{row.label}</span>
                            <span className="battle-sim-profile-stat-value-action">
                              <strong>{formatExpectedReportStatValue(row)}</strong>
                              <span className="battle-sim-profile-stat-action-hint">
                                <InfoOutlinedIcon fontSize="inherit" aria-hidden="true" />
                                <span>Factors</span>
                              </span>
                            </span>
                          </button>
                        )) : (
                          <div className="battle-sim-profile-power-row">
                            <span>No calculated battle-report stats are available yet.</span>
                            <strong>--</strong>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="battle-sim-profile-power-notes">
                      {POWER_CALCULATION_NOTES.map((note) => (
                        <small key={note}>{note}</small>
                      ))}
                    </div>
                  </div>
                ) : selectedSection === "officers" ? (
                  <>
                    <div className="battle-sim-officer-filters">
                      <div className="battle-sim-officer-filters-group">
                        <strong>Quality</strong>
                        <div className="battle-sim-officer-filters-options">
                          {OFFICER_QUALITY_FILTER_OPTIONS.map((option) => (
                            <label key={`officer-quality-${option.key}`} className="battle-sim-officer-filter-option">
                              <input
                                type="checkbox"
                                checked={Boolean(officerQualityFilter?.[option.key])}
                                onChange={() => toggleOfficerQualityFilter(option.key)}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <label className="battle-sim-officer-filter-select">
                        <span>Detail View</span>
                        <select
                          value={String(activeOfficerDetailGroup || "skills")}
                          onChange={(event) => {
                            const nextValue = String(event.target.value || "");
                            if (!OFFICER_DETAIL_GROUPS.some((entry) => entry.key === nextValue)) return;
                            if (!Boolean(officerDetailAvailability?.[nextValue])) return;
                            setActiveOfficerDetailGroup(nextValue);
                          }}
                        >
                          {OFFICER_DETAIL_GROUPS.map((entry) => (
                            <option
                              key={`detail-view-${entry.key}`}
                              value={entry.key}
                              disabled={!Boolean(officerDetailAvailability?.[entry.key])}
                            >
                              {entry.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="battle-sim-officer-filter-option battle-sim-officer-filter-option--appointed">
                        <input
                          type="checkbox"
                          checked={Boolean(officerAppointedOnly)}
                          onChange={(event) => setOfficerAppointedOnly(Boolean(event.target.checked))}
                        />
                        <span>Appointed Only</span>
                      </label>
                    </div>
                    <div
                      className={`battle-sim-officer-table-wrap battle-sim-officer-table-wrap--detail-${activeOfficerDetailGroup || "skills"}`}
                    >
                    {renderOfficerMobileList()}
                    <table className="battle-sim-officer-table">
                      <thead>
                        <tr>
                          <th className="battle-sim-col-icon battle-sim-officer-col-sticky battle-sim-col-sticky-1">Icon</th>
                          <th className="battle-sim-col-officer battle-sim-officer-col-sticky battle-sim-col-sticky-2">Officer</th>
                          <th className="battle-sim-col-basics battle-sim-officer-col-sticky battle-sim-col-sticky-3">Basics</th>
                          <th className="battle-sim-col-skin battle-sim-detail-group-head battle-sim-detail-group-head--skin">Skin</th>
                          <th className="battle-sim-col-equipment battle-sim-detail-group-head battle-sim-detail-group-head--equipment">Equip 1</th>
                          <th className="battle-sim-col-equipment battle-sim-detail-group-head battle-sim-detail-group-head--equipment">Equip 2</th>
                          <th className="battle-sim-col-skill battle-sim-detail-group-head battle-sim-detail-group-head--skills">Skill 1</th>
                          <th className="battle-sim-col-skill battle-sim-detail-group-head battle-sim-detail-group-head--skills">Skill 2</th>
                          <th className="battle-sim-col-skill battle-sim-detail-group-head battle-sim-detail-group-head--skills">Skill 3</th>
                          <th className="battle-sim-col-breakthrough battle-sim-detail-group-head battle-sim-detail-group-head--skills">Breakthrough</th>
                          <th className="battle-sim-col-unworldly battle-sim-detail-group-head battle-sim-detail-group-head--unworldly">Unworldly 1</th>
                          <th className="battle-sim-col-unworldly battle-sim-detail-group-head battle-sim-detail-group-head--unworldly">Unworldly 2</th>
                          <th className="battle-sim-col-unworldly battle-sim-detail-group-head battle-sim-detail-group-head--unworldly">Unworldly 3</th>
                          <th className="battle-sim-col-unworldly battle-sim-detail-group-head battle-sim-detail-group-head--unworldly">Unworldly 4</th>
                          <th className="battle-sim-col-unworldly battle-sim-detail-group-head battle-sim-detail-group-head--unworldly">Unworldly 5</th>
                        </tr>
                      </thead>
                      <tbody>
                        {OFFICER_CATEGORY_ORDER.map((categoryKey) => {
                          const categoryRows = filteredOfficerRows
                            .filter((entry) => String(entry.row?.category || "") === categoryKey);
                          const isCategoryCollapsed = Boolean(collapsedOfficerCategories?.[categoryKey]);
                          return (
                            <React.Fragment key={categoryKey}>
                              <tr className="battle-sim-officer-category-row">
                                <td colSpan={officerVisibleColumnCount}>
                                  <button
                                    type="button"
                                    className="battle-sim-officer-category-toggle"
                                    onClick={() => toggleOfficerCategory(categoryKey)}
                                    aria-expanded={!isCategoryCollapsed}
                                  >
                                    <span>{isCategoryCollapsed ? "+" : "-"}</span>
                                    <strong>{getOfficerCategoryLabel(categoryKey)}</strong>
                                    <small>{`${categoryRows.length.toLocaleString()} shown`}</small>
                                  </button>
                                </td>
                              </tr>
                              {isCategoryCollapsed ? null : categoryRows.length === 0 ? (
                                <tr className="battle-sim-officer-empty-row">
                                  <td colSpan={officerVisibleColumnCount}>No officers match current filters in this category.</td>
                                </tr>
                              ) : categoryRows.map(({ row, rowIndex }) => {
                                const catalogEntry = getOfficerCatalogEntryById(row.officerId);
                                const iconPaths = getOfficerIconPathsById(row.officerId);
                                const hasSkinIcon = Boolean(iconPaths?.skin);
                                const skinTierBounds = getOfficerSkinTierBounds(row.officerId);
                                const skinTierOptions = Array.isArray(skinTierBounds?.tierOptions)
                                  ? skinTierBounds.tierOptions
                                  : [];
                                const hasSkinTierControl = Boolean(skinTierBounds?.hasSkin && skinTierOptions.length);
                                const displayIconPath = getOfficerDisplayIconPath(row.officerId, false);
                                const skillOptions = getOfficerPassiveSkillPoolForOfficer(row.officerId);
                                const breakthroughSkill = getOfficerSkillOptionById(catalogEntry?.breakthroughSkillId);
                                const maxBreakthroughDisplayLevel = Math.max(1, Number(breakthroughSkill?.maxLevel || 20));
                                const breakthroughUnlocked = Number(row?.star || 0) >= OFFICER_BREAKTHROUGH_UNLOCK_STAR;
                                const maxSkillLevelByStar = Math.max(0, getOfficerSkillMaxLevelForStar(row?.star));
                                const unworldlyOptions = getOfficerUnworldlyOptionsByOfficer(row.officerId);
                                const supportsAdvancedOfficerSkills = isGoldTierOfficer(catalogEntry);
                                const equipmentSlots = getOfficerEquipmentSlotsForOfficer(row.officerId);
                                const equipmentSelection = Array.isArray(row?.equipment) ? row.equipment : [];
                                const equipmentEffects = getOfficerEquipmentEffectContributions(
                                  row.officerId,
                                  row?.equipment,
                                  Number(row?.stars || 0),
                                );
                                const previewRows = buildOfficerPreviewRows(row, equipmentEffects, commanderEffectLabelById);
                                const officerBattlePower = calculateOfficerRowBattlePower(row);
                                const pendingOfficerRows = officerPendingChangeRowsById.get(Math.max(0, Math.trunc(Number(row?.officerId || 0)))) || [];
                                return (
                                  <tr key={`${row.officerId || rowIndex}-${categoryKey}`} className="battle-sim-officer-data-row">
                                    <td
                                      className="battle-sim-officer-icon-cell battle-sim-col-icon battle-sim-officer-col-sticky battle-sim-col-sticky-1"
                                      data-label="Icon"
                                    >
                                      {displayIconPath ? (
                                        <img
                                          src={displayIconPath}
                                          alt={row.officerLabel || "Officer"}
                                          className="battle-sim-officer-icon-img"
                                          loading="lazy"
                                        />
                                      ) : (
                                        <div className="battle-sim-officer-icon-placeholder">No Icon</div>
                                      )}
                                      {hasSkinIcon ? (
                                        <span className="battle-sim-officer-icon-has-skin" title="Skin icon available in game files">Skin</span>
                                      ) : null}
                                    </td>
                                    <td className="battle-sim-col-officer battle-sim-officer-col-sticky battle-sim-col-sticky-2" data-label="Officer">
                                      <div className="battle-sim-officer-name-cell">
                                        <strong>{row.officerLabel}</strong>
                                        <small>{`Battle Power ${formatPowerNumber(officerBattlePower)}`}</small>
                                        <small>{`Mastery ${Number(getOfficerMasteryValue(row.officerId, row.level, row.stars) || 0).toLocaleString()}`}</small>
                                        {pendingOfficerRows.length > 0 ? (
                                          <div className="battle-sim-officer-materials">
                                            <strong>Pending materials</strong>
                                            {pendingOfficerRows.map((changeRow) => (
                                              <div key={`pending-officer-${changeRow.key}`}>
                                                <span>{`${changeRow.subsection}: ${changeRow.fromLabel} -> ${changeRow.toLabel}`}</span>
                                                <small>
                                                  {changeRow.materials.length > 0
                                                    ? formatMaterialList(changeRow.materials)
                                                    : "No mapped material requirement."}
                                                </small>
                                              </div>
                                            ))}
                                          </div>
                                        ) : null}
                                        {previewRows.length > 0 ? (
                                          <div className="battle-sim-officer-preview-list battle-sim-officer-preview-list--inline">
                                            {previewRows.map((line, previewIndex) => (
                                              <div key={`preview-${rowIndex}-${previewIndex}`}>{line}</div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="battle-sim-officer-preview-empty">No active effects selected.</div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="battle-sim-col-basics battle-sim-officer-col-sticky battle-sim-col-sticky-3" data-label="Basics">
                                      <div className="battle-sim-officer-basics-cell">
                                        <label className="battle-sim-officer-basic-row battle-sim-officer-basic-row--appointed">
                                          <span>Appointed</span>
                                          <input
                                            type="checkbox"
                                            checked={Boolean(row.appointed)}
                                            onChange={(event) => updateOfficerRowAt(rowIndex, (prev) => ({
                                              ...prev,
                                              appointed: event.target.checked,
                                            }))}
                                          />
                                        </label>
                                        <label className="battle-sim-officer-basic-row">
                                          <span>Level</span>
                                          <select
                                            value={String(toOfficerLevelDisplay(row.level || 0))}
                                            onChange={(event) => updateOfficerRowAt(rowIndex, (prev) => ({
                                              ...prev,
                                              level: fromOfficerLevelDisplay(event.target.value),
                                            }))}
                                          >
                                            {buildIntegerRange(1, Math.max(1, Number(catalogEntry?.maxLevel || 55))).map((displayLevel) => (
                                              <option key={`officer-level-${row.officerId}-${displayLevel}`} value={displayLevel}>
                                                {displayLevel}
                                              </option>
                                            ))}
                                          </select>
                                        </label>
                                        <label className="battle-sim-officer-basic-row">
                                          <span>Stars</span>
                                          <select
                                            value={String(Number(row.stars || 0))}
                                            aria-label={`${row.officerLabel || "Officer"} stars`}
                                            onChange={(event) => updateOfficerRowAt(rowIndex, (prev) => ({
                                              ...prev,
                                              ...buildOfficerStarsPatchFromInput(event.target.value, prev),
                                            }))}
                                          >
                                            {getOfficerStarSelectOptions(row?.officerId || null).map((starValue) => (
                                              <option key={`officer-stars-${row.officerId}-${starValue}`} value={starValue}>
                                                {formatOfficerStarsLabel(starValue, row?.officerId || null)}
                                              </option>
                                            ))}
                                          </select>
                                        </label>
                                      </div>
                                    </td>
                                    <td
                                      className="battle-sim-col-skin battle-sim-detail-group-cell battle-sim-detail-group-cell--skin"
                                      data-label="Skin"
                                    >
                                      {hasSkinTierControl ? (
                                        <select
                                          value={String(row?.skinLevel == null || row.skinLevel === "" || !Number.isFinite(Number(row.skinLevel)) ? -1 : Number(row.skinLevel))}
                                          onChange={(event) => updateOfficerRowAt(rowIndex, (prev) => ({
                                            ...prev,
                                            skinLevel: Math.trunc(Number(event.target.value || -1)),
                                          }))}
                                        >
                                          <option value="-1">Locked</option>
                                          {skinTierOptions.map((tierValue) => (
                                            <option key={`skin-tier-${row.officerId}-${tierValue}`} value={tierValue}>
                                              {tierValue}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <span className="battle-sim-officer-skin-na">N/A</span>
                                      )}
                                    </td>
                                    {[0, 1].map((slotIndex) => {
                                      const slotMeta = equipmentSlots[slotIndex];
                                      const selectedSlot = equipmentSelection[slotIndex] || { equipId: null, level: 0, masteryLevels: [] };
                                      if (!slotMeta) {
                                        return (
                                          <td
                                            key={`equip-slot-${slotIndex}`}
                                            className="battle-sim-col-equipment battle-sim-detail-group-cell battle-sim-detail-group-cell--equipment"
                                            data-label={`Equipment ${slotIndex + 1}`}
                                          >
                                            <span className="battle-sim-officer-skin-na">N/A</span>
                                          </td>
                                        );
                                      }
                                      const selectedLevel = Math.max(0, Math.floor(Number(selectedSlot?.level || 0)));
                                      const slotUnlockStar = getOfficerEquipmentSlotUnlockStar(slotIndex);
                                      const slotUnlocked = isOfficerEquipmentSlotUnlocked(row?.stars, slotIndex, row?.officerId);
                                      const levelRows = Array.isArray(slotMeta?.levelRows) ? slotMeta.levelRows : [];
                                      const selectedLevelRow = selectedLevel > 0
                                        ? (levelRows.find((entry) => Number(entry?.level || 0) === selectedLevel)
                                          || [...levelRows].reverse().find((entry) => Number(entry?.level || 0) <= selectedLevel)
                                          || null)
                                        : null;
                                      const unlockedMasteryIds = new Set(
                                        Array.isArray(selectedLevelRow?.masteryIds)
                                          ? selectedLevelRow.masteryIds.map((value) => Number(value || 0)).filter((value) => Number.isFinite(value)).map((value) => Math.trunc(value))
                                          : [],
                                      );
                                      const maxMasteryByParent = Math.max(0, Number(selectedLevelRow?.masteryLevelMax || 0));
                                      const masterySelectionById = new Map(
                                        (Array.isArray(selectedSlot?.masteryLevels) ? selectedSlot.masteryLevels : [])
                                          .map((entry) => [Number(entry?.id || 0), Math.max(0, Math.floor(Number(entry?.level || 0)))]),
                                      );
                                      return (
                                        <td
                                          key={`equip-slot-${slotIndex}`}
                                          className="battle-sim-col-equipment battle-sim-detail-group-cell battle-sim-detail-group-cell--equipment"
                                          data-label={`Equipment ${slotIndex + 1}`}
                                        >
                                          <div className={`battle-sim-unworldly-cell ${slotUnlocked ? "" : "battle-sim-unworldly-cell--locked"}`.trim()}>
                                            <div className="battle-sim-officer-skill-readonly battle-sim-officer-equipment-label" title={`Equipment #${slotMeta.equipId}`}>
                                              {slotMeta?.iconPath ? (
                                                <img
                                                  src={slotMeta.iconPath}
                                                  alt={slotMeta.label || "Equipment"}
                                                  className="battle-sim-officer-equipment-icon"
                                                  loading="lazy"
                                                />
                                              ) : null}
                                              <span className="battle-sim-officer-equipment-text">{slotMeta.label}</span>
                                            </div>
                                            <select
                                              value={String(selectedLevel)}
                                              onChange={(event) => updateOfficerEquipmentLevelWithPrerequisite(
                                                rowIndex,
                                                row,
                                                slotIndex,
                                                slotMeta,
                                                event.target.value,
                                              )}
                                            >
                                              {buildIntegerRange(0, Math.max(0, Number(slotMeta?.maxLevel || 0))).map((level) => (
                                                <option key={`officer-equipment-level-${row.officerId}-${slotIndex}-${level}`} value={level}>
                                                  {level}
                                                </option>
                                              ))}
                                            </select>
                                            {getOfficerEquipmentMasteryTracksInUnlockOrder(slotMeta).map((track) => {
                                              const masteryId = Number(track?.masteryId || 0);
                                              const rawMax = Math.max(0, Number(track?.maxLevel || 0));
                                              const unlocked = unlockedMasteryIds.has(masteryId);
                                              const maxAllowed = unlocked ? Math.max(0, Math.min(rawMax, maxMasteryByParent)) : 0;
                                              const unlockRow = findOfficerEquipmentMasteryUnlockRow(slotMeta, masteryId);
                                              const maxAllowedForControl = unlocked
                                                ? maxAllowed
                                                : Math.max(0, Math.min(rawMax, Number(unlockRow?.masteryLevelMax || rawMax)));
                                              const selectedMasteryLevel = Math.max(0, Number(masterySelectionById.get(masteryId) || 0));
                                              return (
                                                <label key={`equip-${slotIndex}-mastery-${masteryId}`} className="battle-sim-officer-equipment-mastery-row">
                                                  <span>{track?.label || `Mastery #${masteryId}`}</span>
                                                  <select
                                                    disabled={!unlockRow || maxAllowedForControl <= 0}
                                                    value={String(Math.min(selectedMasteryLevel, maxAllowedForControl))}
                                                    onChange={(event) => updateOfficerEquipmentMasteryWithPrerequisite(
                                                      rowIndex,
                                                      row,
                                                      slotIndex,
                                                      slotMeta,
                                                      masteryId,
                                                      event.target.value,
                                                    )}
                                                  >
                                                    {buildIntegerRange(0, maxAllowedForControl).map((level) => (
                                                      <option key={`officer-equipment-mastery-${row.officerId}-${slotIndex}-${masteryId}-${level}`} value={level}>
                                                        {level}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </label>
                                              );
                                            })}
                                            <div className="battle-sim-officer-calc-values">
                                              {(() => {
                                                if (!slotUnlocked) {
                                                  const lockedCalcRows = buildOfficerEquipmentPreviewRows({
                                                    slotMeta,
                                                    selectedSlot,
                                                    selectedLevel,
                                                    effectLabelById: commanderEffectLabelById,
                                                  });
                                                  return (
                                                    <>
                                                      <div className="battle-sim-officer-calc-empty">{`Unlocks at ${slotUnlockStar.toFixed(1)} stars`}</div>
                                                      {lockedCalcRows.map((line, calcIdx) => (
                                                        <div key={`equip-locked-range-${slotIndex}-${calcIdx}`}>{line}</div>
                                                      ))}
                                                    </>
                                                  );
                                                }
                                                if (!slotUnlocked) {
                                                  return <div className="battle-sim-officer-calc-empty">{`Unlocks at ${slotUnlockStar.toFixed(1)}â˜…`}</div>;
                                                }
                                                const calcRows = buildOfficerEquipmentPreviewRows({
                                                  slotMeta,
                                                  selectedSlot,
                                                  selectedLevel,
                                                  effectLabelById: commanderEffectLabelById,
                                                });
                                                if (!calcRows.length) {
                                                  return <div className="battle-sim-officer-calc-empty">No active bonus.</div>;
                                                }
                                                return calcRows.map((line, calcIdx) => (
                                                  <div key={`equip-calc-${slotIndex}-${calcIdx}`}>{line}</div>
                                                ));
                                              })()}
                                            </div>
                                          </div>
                                        </td>
                                      );
                                    })}
                                    {[0, 1, 2].map((skillIndex) => {
                                      const slot = row?.skills?.[skillIndex] || { skillId: null, level: 0 };
                                      const locked = !isOfficerSkillSlotUnlocked(row?.star, skillIndex);
                                      const hasFixedPassiveSkillSlots = String(catalogEntry?.passiveSkillSelectionMode || "").toLowerCase() === "fixed";
                                      const fixedSkillId = hasFixedPassiveSkillSlots
                                        ? normalizeSkillId(catalogEntry?.fixedPassiveSkillSlotIds?.[skillIndex])
                                        : null;
                                      const fixedSkillOption = fixedSkillId ? getOfficerSkillOptionById(fixedSkillId) : null;
                                      const previewSkillId = hasFixedPassiveSkillSlots ? fixedSkillId : slot?.skillId;
                                      const activeSkillId = previewSkillId;
                                      const hasActiveSkill = Boolean(activeSkillId) && (!locked || Number(slot?.level || 0) >= 0);
                                      const skillSlotUnlockStar = getOfficerSkillSlotUnlockStar(skillIndex);
                                      const maxSkillLevelForControl = Math.max(
                                        maxSkillLevelByStar,
                                        getOfficerSkillMaxLevelForStar(Math.max(Number(row?.star || 0), skillSlotUnlockStar)),
                                      );
                                      const skillCalcRows = buildOfficerSkillCalculatedRows({
                                        officerId: row.officerId,
                                        skillId: activeSkillId,
                                        skillLevel: toOfficerSkillLevelDisplay(slot?.level || 0, hasActiveSkill),
                                        officerLevel: Number(row?.level || 0),
                                        decimalStars: Math.max(Number(row?.stars || 0), locked ? skillSlotUnlockStar : 0),
                                        effectLabelById: commanderEffectLabelById,
                                        includeNextDelta: !locked,
                                        previewWhenInactive: locked,
                                      });
                                      return (
                                        <td
                                          key={`skill-${skillIndex}`}
                                          className="battle-sim-col-skill battle-sim-detail-group-cell battle-sim-detail-group-cell--skills"
                                          data-label={`Skill ${skillIndex + 1}`}
                                        >
                                          <div className="battle-sim-unworldly-cell">
                                            {hasFixedPassiveSkillSlots ? (
                                              <div className="battle-sim-officer-skill-readonly">
                                                {locked ? "Locked by stars" : (fixedSkillOption?.label || (fixedSkillId ? `Skill #${fixedSkillId}` : "N/A"))}
                                              </div>
                                            ) : (
                                              <select
                                                value={String(slot?.skillId || "")}
                                                onChange={(event) => updateOfficerSkillSelectionWithPrerequisite(
                                                  rowIndex,
                                                  row,
                                                  skillIndex,
                                                  event.target.value,
                                                )}
                                              >
                                                <option value="">{locked ? "Locked by stars" : "None"}</option>
                                                {skillOptions.map((option) => (
                                                  <option key={`${option.skillId}-${skillIndex}`} value={option.skillId}>
                                                    {option.label}
                                                  </option>
                                                ))}
                                              </select>
                                            )}
                                            <select
                                              value={String(toOfficerSkillLevelDisplay(slot?.level || 0, hasActiveSkill))}
                                              disabled={!(hasFixedPassiveSkillSlots ? fixedSkillId : slot?.skillId)}
                                              onChange={(event) => updateOfficerSkillLevelWithPrerequisite(
                                                rowIndex,
                                                row,
                                                skillIndex,
                                                hasFixedPassiveSkillSlots ? fixedSkillId : slot?.skillId,
                                                event.target.value,
                                              )}
                                            >
                                              <option value="0">Inactive</option>
                                              {buildIntegerRange(1, toOfficerSkillLevelDisplay(maxSkillLevelForControl, true)).map((displayLevel) => (
                                                <option key={`officer-skill-level-${row.officerId}-${skillIndex}-${displayLevel}`} value={displayLevel}>
                                                  {displayLevel}
                                                </option>
                                              ))}
                                            </select>
                                            <div className="battle-sim-officer-calc-values">
                                              {skillCalcRows.length ? skillCalcRows.map((line, calcIdx) => (
                                                <div key={`skill-calc-${skillIndex}-${calcIdx}`}>{line}</div>
                                              )) : locked ? (
                                                buildOfficerSkillRangeRows({
                                                  skillId: activeSkillId,
                                                  officerId: row.officerId,
                                                  officerLevel: Number(row?.level || 0),
                                                  decimalStars: Math.max(Number(row?.stars || 0), skillSlotUnlockStar),
                                                  effectLabelById: commanderEffectLabelById,
                                                }).map((line, calcIdx) => (
                                                  <div key={`skill-range-${skillIndex}-${calcIdx}`}>{line}</div>
                                                ))
                                              ) : <div className="battle-sim-officer-calc-empty">No active bonus.</div>}
                                            </div>
                                          </div>
                                        </td>
                                      );
                                    })}
                                    <td className="battle-sim-col-breakthrough battle-sim-detail-group-cell battle-sim-detail-group-cell--skills" data-label="Breakthrough">
                                      {supportsAdvancedOfficerSkills ? (
                                        <div className="battle-sim-unworldly-cell">
                                          <div className="battle-sim-officer-skill-readonly">
                                            {!breakthroughUnlocked ? "Locked by stars" : (breakthroughSkill?.label || "No breakthrough skill")}
                                          </div>
                                          <select
                                            value={String(toOfficerBreakthroughLevelDisplay(row.breakthroughLevel ?? -1))}
                                            disabled={!breakthroughSkill}
                                            onChange={(event) => updateOfficerBreakthroughWithPrerequisite(rowIndex, row, event.target.value)}
                                          >
                                            <option value="0">Unactivated</option>
                                            {Array.from({ length: maxBreakthroughDisplayLevel }, (_, levelIndex) => levelIndex + 1).map((displayLevel) => (
                                              <option key={`breakthrough-${row.officerId}-${displayLevel}`} value={displayLevel}>
                                                {displayLevel}
                                              </option>
                                            ))}
                                          </select>
                                          <div className="battle-sim-officer-calc-values">
                                            {(() => {
                                              const displayLevel = toOfficerBreakthroughLevelDisplay(row.breakthroughLevel ?? -1);
                                              const calcRows = buildOfficerSkillCalculatedRows({
                                                officerId: row.officerId,
                                                skillId: breakthroughSkill?.skillId,
                                                skillLevel: displayLevel,
                                                officerLevel: Number(row?.level || 0),
                                                decimalStars: Math.max(Number(row?.stars || 0), breakthroughUnlocked ? 0 : OFFICER_BREAKTHROUGH_UNLOCK_STAR),
                                                effectLabelById: commanderEffectLabelById,
                                                applyMastery: false,
                                                includeNextDelta: breakthroughUnlocked,
                                                previewWhenInactive: !breakthroughUnlocked || displayLevel <= 0,
                                              });
                                              if (!calcRows.length) {
                                                const rangeRows = buildOfficerSkillRangeRows({
                                                  skillId: breakthroughSkill?.skillId,
                                                  officerId: row.officerId,
                                                  officerLevel: Number(row?.level || 0),
                                                  decimalStars: Math.max(Number(row?.stars || 0), OFFICER_BREAKTHROUGH_UNLOCK_STAR),
                                                  effectLabelById: commanderEffectLabelById,
                                                });
                                                if (rangeRows.length) {
                                                  return rangeRows.map((line, calcIdx) => (
                                                    <div key={`breakthrough-range-${rowIndex}-${calcIdx}`}>{line}</div>
                                                  ));
                                                }
                                                return <div className="battle-sim-officer-calc-empty">No active bonus.</div>;
                                              }
                                              return calcRows.map((line, calcIdx) => (
                                                <div key={`breakthrough-calc-${rowIndex}-${calcIdx}`}>{line}</div>
                                              ));
                                            })()}
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="battle-sim-officer-skin-na">N/A</span>
                                      )}
                                    </td>
                                    {[0, 1, 2, 3, 4].map((slotIndex) => {
                                      if (!supportsAdvancedOfficerSkills) {
                                        return (
                                          <td
                                            key={`unworldly-${slotIndex}`}
                                            className="battle-sim-col-unworldly battle-sim-detail-group-cell battle-sim-detail-group-cell--unworldly"
                                            data-label={`Unworldly ${slotIndex + 1}`}
                                          >
                                            <span className="battle-sim-officer-skin-na">N/A</span>
                                          </td>
                                        );
                                      }
                                      const slot = row?.unworldly?.[slotIndex] || { skillId: null, tierId: null, level: 0 };
                                      const slotUnlockStar = getOfficerUnworldlySlotUnlockStar(slotIndex);
                                      const slotUnlocked = isOfficerUnworldlySlotUnlocked(row?.stars, slotIndex, row?.officerId);
                                      const selectedOption = unworldlyOptions.find((option) => Number(option?.skillId || 0) === Number(slot?.skillId || 0));
                                      const tierOptionsAll = Array.isArray(selectedOption?.tiers) ? selectedOption.tiers : [];
                                      const selectedTier = tierOptionsAll.find((tier) => Number(tier?.tierId || 0) === Number(slot?.tierId || 0))
                                        || tierOptionsAll.find((tier) => !Boolean(tier?.isLegacyTier))
                                        || tierOptionsAll[0];
                                      const tierOptionsVisible = tierOptionsAll.filter((tier) => !Boolean(tier?.isLegacyTier));
                                      const tierOptions = (
                                        selectedTier && Boolean(selectedTier?.isLegacyTier)
                                          ? [selectedTier, ...tierOptionsVisible]
                                          : tierOptionsVisible
                                      );
                                      const maxUnworldlyLevel = Math.max(0, Number(selectedTier?.maxLevel || 0));
                                      const unworldlyCalcRows = buildOfficerUnworldlyCalculatedRows({
                                        option: selectedOption,
                                        tier: selectedTier,
                                        level: Number(slot?.level || (slotUnlocked ? 0 : 1)),
                                      });
                                      return (
                                        <td
                                          key={`unworldly-${slotIndex}`}
                                          className="battle-sim-col-unworldly battle-sim-detail-group-cell battle-sim-detail-group-cell--unworldly"
                                          data-label={`Unworldly ${slotIndex + 1}`}
                                        >
                                          <div className={`battle-sim-unworldly-cell ${slotUnlocked ? "" : "battle-sim-unworldly-cell--locked"}`.trim()}>
                                            <select
                                              value={String(slot?.skillId || "")}
                                              onChange={(event) => updateOfficerUnworldlySkillWithPrerequisite(
                                                rowIndex,
                                                row,
                                                slotIndex,
                                                event.target.value,
                                                unworldlyOptions,
                                              )}
                                            >
                                              <option value="">{slotUnlocked ? "None" : `Locked (${slotUnlockStar.toFixed(1)}â˜…)`}</option>
                                              {unworldlyOptions.map((option) => (
                                                <option key={`u-${slotIndex}-${option.skillId}`} value={option.skillId}>
                                                  {option.label}
                                                </option>
                                              ))}
                                            </select>
                                            <select
                                              value={String(slot?.tierId || "")}
                                              disabled={!slot?.skillId}
                                              onChange={(event) => updateOfficerUnworldlyTierWithPrerequisite(rowIndex, row, slotIndex, event.target.value)}
                                            >
                                              <option value="">{slot?.skillId ? "Select tier" : "No skill selected"}</option>
                                              {tierOptions.map((tier) => (
                                                <option key={`u-tier-${slotIndex}-${tier.tierId}`} value={tier.tierId}>
                                                  {`${tier.tierDisplayLabel || tier.tierLabel || `Tier ${tier.tierRank}`}`}
                                                </option>
                                              ))}
                                            </select>
                                            <select
                                              value={String(Number(slot?.level || 0))}
                                              disabled={!slot?.skillId}
                                              onChange={(event) => updateOfficerUnworldlyLevelWithPrerequisite(rowIndex, row, slotIndex, event.target.value)}
                                            >
                                              {buildIntegerRange(0, Math.max(0, maxUnworldlyLevel)).map((level) => (
                                                <option key={`officer-unworldly-level-${row.officerId}-${slotIndex}-${level}`} value={level}>
                                                  {level}
                                                </option>
                                              ))}
                                            </select>
                                            <div className="battle-sim-officer-calc-values">
                                              {!slotUnlocked ? (
                                                <>
                                                  <div className="battle-sim-officer-calc-empty">{`Unlocks at ${slotUnlockStar.toFixed(1)} stars`}</div>
                                                  {buildOfficerUnworldlyRangeRows({
                                                    option: selectedOption,
                                                    tier: selectedTier,
                                                  }).map((line, calcIdx) => (
                                                    <div key={`unworldly-range-${slotIndex}-${calcIdx}`}>{line}</div>
                                                  ))}
                                                </>
                                              ) : !slotUnlocked ? (
                                                <div className="battle-sim-officer-calc-empty">{`Unlocks at ${slotUnlockStar.toFixed(1)}â˜…`}</div>
                                              ) : unworldlyCalcRows.length ? unworldlyCalcRows.map((line, calcIdx) => (
                                                <div key={`unworldly-calc-${slotIndex}-${calcIdx}`}>{line}</div>
                                              )) : <div className="battle-sim-officer-calc-empty">No active bonus.</div>}
                                            </div>
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                ) : selectedSection === "titans" ? (
                  <div className="battle-sim-titan-editor">
                    <div className="battle-sim-officer-detail-toolbar battle-sim-titan-detail-toolbar">
                      <label>
                        <span>Titan Section</span>
                        <select
                          value={activeTitanDetailGroup}
                          onChange={(event) => setActiveTitanDetailGroup(event.target.value)}
                        >
                          {TITAN_DETAIL_GROUPS.map((group) => (
                            <option key={`titan-detail-${group.key}`} value={group.key}>
                              {group.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="battle-sim-titan-columns">
                    {TITAN_SIDE_ORDER.map((sideKey) => {
                      const sideMeta = TITAN_SIDE_META[sideKey];
                      const sideCatalog = titanCatalogIndex?.perType?.[sideKey] || {};
                      const sideData = titanData?.[sideKey] || {
                        level: 0,
                        evolutionStage: 0,
                        evolutionLevel: 0,
                        skinGid: null,
                        skinStarLevel: 0,
                        skins: [],
                        spells: [],
                        equipPieces: [],
                        equipSkills: [],
                      };
                      const pendingTitanRows = titanPendingChangeRowsBySide.get(sideKey) || [];
                      const skinOptions = Array.isArray(sideCatalog?.skinRows) ? sideCatalog.skinRows : [];
                      const skinStarMilestones = Array.isArray(sideCatalog?.skinStarRow?.milestones)
                        ? sideCatalog.skinStarRow.milestones
                        : [];
                      const activeSkinStarMilestones = skinStarMilestones.filter(
                        (row) => Number(row?.starLevel || 0) <= Number(sideData?.skinStarLevel || 0),
                      );
                      const equipGroups = (() => {
                        const pieces = Array.isArray(sideCatalog?.equipPieceRows) ? sideCatalog.equipPieceRows : [];
                        const grouped = new Map();
                        pieces.forEach((row) => {
                          const equipId = Number(row?.equipId || 0);
                          if (!grouped.has(equipId)) grouped.set(equipId, []);
                          grouped.get(equipId).push(row);
                        });
                        return Array.from(grouped.entries()).sort((left, right) => left[0] - right[0]);
                      })();
                      const equipSkillRows = Array.isArray(sideCatalog?.equipSkillRows) ? sideCatalog.equipSkillRows : [];
                      const titanTypeRow = titanTypeOptions.find((row) => Number(row?.id || 0) === Number(sideMeta?.typeId || 0)) || null;
                      const maxLevel = Math.max(0, Number(sideCatalog?.maxLevel || titanTypeRow?.maxLevel || 45));
                      const phaseRows = Array.isArray(sideCatalog?.phaseRows) ? sideCatalog.phaseRows : [];
                      const selectedPhaseRow = phaseRows.find((row) => Number(row?.stage || 0) === Number(sideData?.evolutionStage || 0))
                        || phaseRows[0]
                        || null;
                      const sidePower = calculateTitanSidePower(sideData, sideCatalog);
                      const titanLevelDisplayOptions = buildIntegerRange(1, Math.max(1, Number(maxLevel || 0) + 1));
                      const evolutionLevelOptions = Array.isArray(selectedPhaseRow?.evolutionLevels)
                        ? selectedPhaseRow.evolutionLevels
                        : [0];
                      const skillRowsForEvolution = buildTitanSkillRowsForEvolution(sideCatalog, sideData);
                      return (
                        <div key={sideKey} className="battle-sim-titan-column">
                          <div
                            className="battle-sim-titan-column-head"
                            style={selectedPhaseRow?.phaseColor ? {
                              borderColor: selectedPhaseRow.phaseColor,
                              backgroundColor: `${selectedPhaseRow.phaseColor}1a`,
                            } : null}
                          >
                            <div className="battle-sim-titan-column-icon">
                              {renderTitanIconCell(titanTypeRow, sideMeta.label, sideMeta.label.charAt(0))}
                            </div>
                            <div className="battle-sim-titan-column-title">
                              <strong>{sideMeta.label}</strong>
                              <small>{sideMeta.subtitle}</small>
                              <small>{`Power ${formatPowerNumber(sidePower)}`}</small>
                            </div>
                          </div>
                          {pendingTitanRows.length > 0 ? (
                            <div className="battle-sim-warplane-materials">
                              <strong>Pending materials</strong>
                              {pendingTitanRows.map((changeRow) => (
                                <div key={`pending-titan-${changeRow.key}`}>
                                  <span>{`${changeRow.subsection}: ${changeRow.fromLabel} -> ${changeRow.toLabel}`}</span>
                                  <small>
                                    {changeRow.materials.length > 0
                                      ? formatMaterialList(changeRow.materials)
                                      : "No mapped material requirement."}
                                  </small>
                                </div>
                              ))}
                            </div>
                          ) : null}

                          {activeTitanDetailGroup === "evolution" ? (
                          <div className="battle-sim-titan-panel">
                            <div className="battle-sim-titan-panel-head">
                              <strong>Evolution + Level</strong>
                            </div>
                            <div className="battle-sim-titan-evolution-row">
                              <label>
                                <span>Level</span>
                                <select
                                  value={toTitanLevelDisplay(sideData?.level || 0)}
                                  onChange={(event) => updateTitanSideScalar(sideKey, "level", fromTitanLevelDisplay(event.target.value))}
                                >
                                  {titanLevelDisplayOptions.map((displayLevel) => (
                                    <option key={`titan-level-${sideKey}-${displayLevel}`} value={displayLevel}>
                                      {`Level ${displayLevel}`}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                <span>Evolution Tier</span>
                                <select
                                  value={String(Number(sideData?.evolutionStage || 0))}
                                  onChange={(event) => updateTitanSideScalar(sideKey, "evolutionStage", event.target.value)}
                                >
                                  {phaseRows.map((phaseRow) => (
                                    <option key={`phase-${sideKey}-${phaseRow.stage}`} value={phaseRow.stage}>
                                      {phaseRow.phase}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                <span>Evolution Step</span>
                                <select
                                  value={String(Number(sideData?.evolutionLevel || 0))}
                                  onChange={(event) => updateTitanSideScalar(sideKey, "evolutionLevel", event.target.value)}
                                >
                                  {evolutionLevelOptions.map((step) => (
                                    <option key={`phase-step-${sideKey}-${step}`} value={step}>
                                      {`Step ${step}`}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>
                          </div>
                          ) : null}

                          {activeTitanDetailGroup === "talents" ? (
                          <div className="battle-sim-titan-panel">
                            <div className="battle-sim-titan-panel-head">
                              <strong>Skills</strong>
                            </div>
                            <div className="battle-sim-titan-row-list">
                              {skillRowsForEvolution.map((skillRow) => {
                                const option = skillRow?.option || null;
                                if (!option) return null;
                                const selectedEntry = getSelectedEntryById(sideData?.spells, option.id);
                                const unlocked = Boolean(skillRow?.unlocked);
                                const selectedLevel = unlocked && selectedEntry
                                  ? Math.max(0, Math.floor(Number(selectedEntry?.level || 0)))
                                  : 0;
                                const resolved = resolveTitanLevelRow(option, selectedLevel);
                                const resolvedSkillDescription = renderTitanSkillDescription(option, resolved);
                                const skillDisplayLevelOptions = getLevelOptionsFromRows(
                                  option?.levelRows || [],
                                  Math.max(0, Number(option?.maxLevel || 20)),
                                ).map((level) => toTitanLevelDisplay(level));
                                const selectedDisplayLevel = toTitanLevelDisplay(selectedLevel);
                                return (
                                  <div
                                    key={`spell-${sideKey}-${option.id}`}
                                    className={`battle-sim-titan-row${unlocked ? "" : " battle-sim-titan-row--locked"}`}
                                  >
                                    <div className="battle-sim-titan-row-icon">
                                      {renderTitanIconCell(option, option.label, String(option?.label || "?").charAt(0))}
                                    </div>
                                    <div className="battle-sim-titan-row-body">
                                      <div className="battle-sim-titan-row-head">
                                        <strong>{option.label}</strong>
                                        <select
                                          value={unlocked ? selectedDisplayLevel : ""}
                                          onChange={(event) => updateTitanSpellLevelWithPrerequisite(
                                            sideKey,
                                            sideMeta,
                                            skillRow,
                                            option,
                                            event.target.value === "" ? null : fromTitanLevelDisplay(event.target.value),
                                          )}
                                        >
                                          {!unlocked ? (
                                            <option value="">Unactivated</option>
                                          ) : null}
                                          {skillDisplayLevelOptions.map((displayLevel) => (
                                            <option key={`titan-spell-level-${sideKey}-${option.id}-${displayLevel}`} value={displayLevel}>
                                              {`Level ${displayLevel}`}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      {!unlocked ? (
                                        <small>{skillRow?.unlockText || "Locked"}</small>
                                      ) : null}
                                      <small>{resolvedSkillDescription || renderTitanEffectsSummary(resolved?.effects || [], titanEffectLabelById, {
                                        label: option?.label,
                                        description: option?.description,
                                      })}</small>
                                      {!unlocked ? renderEffectLineList(
                                        buildEffectRangeLinesForOption(option, titanEffectLabelById, {
                                          label: option?.label,
                                          description: option?.description,
                                        }),
                                        `titan-spell-range-${sideKey}-${option.id}`,
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          ) : null}

                          {activeTitanDetailGroup === "skins" ? (
                          <div className="battle-sim-titan-panel">
                            <div className="battle-sim-titan-panel-head">
                              <strong>Skins</strong>
                            </div>
                            <div className="battle-sim-titan-skin-shared-row">
                              <label>
                                <span>Shared Skin Star Level (Derived)</span>
                                <input
                                  type="number"
                                  min={0}
                                  value={Number(sideData?.skinStarLevel || 0)}
                                  readOnly
                                  disabled
                                />
                              </label>
                            </div>
                            <div className="battle-sim-titan-row-list">
                              {skinOptions.map((option) => {
                                const selectedEntry = getSelectedEntryById(sideData?.skins, option.id);
                                const unlocked = Boolean(selectedEntry);
                                const selectedSkinLevel = unlocked ? Math.max(0, Number(selectedEntry?.level || 0)) : 0;
                                const resolved = unlocked ? resolveTitanLevelRow(option, selectedSkinLevel) : null;
                                const maxSkinLevel = Math.max(0, Number(option?.maxStarLevel || 0));
                                const skinLevelOptions = buildIntegerRange(0, maxSkinLevel);
                                return (
                                  <div
                                    key={`skin-${sideKey}-${option.id}`}
                                    className={`battle-sim-titan-row${unlocked ? "" : " battle-sim-titan-row--locked"}`}
                                  >
                                    <div className="battle-sim-titan-row-icon">
                                      {renderTitanIconCell(option, option.label, String(option?.label || "?").charAt(0))}
                                    </div>
                                    <div className="battle-sim-titan-row-body">
                                      <div className="battle-sim-titan-row-head">
                                        <strong>{option.label}</strong>
                                        <select
                                          value={unlocked ? String(selectedSkinLevel) : ""}
                                          onChange={(event) => updateTitanSideSkinLevel(
                                            sideKey,
                                            option.id,
                                            event.target.value === "" ? null : event.target.value,
                                          )}
                                        >
                                          <option value="">Locked</option>
                                          {skinLevelOptions.map((level) => (
                                            <option key={`titan-skin-level-${sideKey}-${option.id}-${level}`} value={level}>
                                              {`Level ${level}`}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      {unlocked ? (
                                        <small>{renderTitanEffectsSummary(resolved?.effects || [], titanEffectLabelById, {
                                          label: option?.label,
                                          description: option?.description,
                                        })}</small>
                                      ) : (
                                        <>
                                          <small>Locked. Unlock to activate this skin&apos;s base bonus.</small>
                                          {renderEffectLineList(
                                            buildEffectRangeLinesForOption(option, titanEffectLabelById, {
                                              label: option?.label,
                                              description: option?.description,
                                            }),
                                            `titan-skin-range-${sideKey}-${option.id}`,
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {activeSkinStarMilestones.length > 0 ? (
                              <div className="battle-sim-titan-effect-block">
                                <div className="battle-sim-titan-effect-list">
                                  {activeSkinStarMilestones.map((milestone) => (
                                    <div key={`skin-ms-${sideKey}-${milestone.starLevel}`} className="battle-sim-titan-effect-line">
                                      <strong>{`Shared Star ${milestone.starLevel}`}</strong>
                                      <span>{renderTitanSkinStarEffectsSummary(
                                        milestone?.effects || [],
                                        sideCatalog,
                                        titanEffectLabelById,
                                      )}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="battle-sim-titan-effect-empty">No shared star milestone effects active.</div>
                            )}
                          </div>
                          ) : null}

                          {activeTitanDetailGroup === "equipment" ? (
                          <div className="battle-sim-titan-panel">
                            <div className="battle-sim-titan-panel-head">
                              <strong>Equipment</strong>
                            </div>
                            <div className="battle-sim-titan-row-list">
                              {equipGroups.map(([equipId, rows], groupIndex) => (
                                <div key={`equip-group-${sideKey}-${equipId}-${groupIndex}`} className="battle-sim-titan-equip-group">
                                  <div className="battle-sim-titan-equip-group-title">
                                    {getTitanEquipmentGroupLabel(rows, `Set ${groupIndex + 1}`)}
                                  </div>
                                  {rows.map((option) => {
                                    const pieceLabel = getTitanEquipmentPieceLabel(option, titanEffectLabelById);
                                    const selectedEntry = getSelectedEntryById(sideData?.equipPieces, option.id);
                                    const activated = Boolean(selectedEntry);
                                    const selectedLevel = activated
                                      ? Math.max(0, Math.floor(Number(selectedEntry?.level || 0)))
                                      : null;
                                    const resolved = activated ? resolveTitanLevelRow(option, selectedLevel) : null;
                                    const equipmentLevelOptions = getLevelOptionsFromRows(
                                      option?.levelRows || [],
                                      Math.max(0, Number(option?.maxLevel || 20)),
                                    );
                                    return (
                                      <div
                                        key={`equip-piece-${sideKey}-${option.id}`}
                                        className={`battle-sim-titan-row${activated ? "" : " battle-sim-titan-row--locked"}`}
                                      >
                                        <div className="battle-sim-titan-row-icon">
                                          {renderTitanIconCell(option, pieceLabel, String(pieceLabel || "?").charAt(0))}
                                        </div>
                                        <div className="battle-sim-titan-row-body">
                                          <div className="battle-sim-titan-row-head">
                                            <strong>{pieceLabel}</strong>
                                            <select
                                              value={activated ? String(selectedLevel) : ""}
                                              onChange={(event) => updateTitanSideIdLevelCollection(
                                                sideKey,
                                                "equipPieces",
                                                option.id,
                                                event.target.value === "" ? null : event.target.value,
                                              )}
                                            >
                                              <option value="">Locked</option>
                                              {equipmentLevelOptions.map((level) => (
                                                <option key={`equip-piece-level-${sideKey}-${option.id}-${level}`} value={level}>
                                                  {`Level ${level}`}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          {activated ? (
                                            <small>{renderTitanEffectsSummary(resolved?.effects || [], titanEffectLabelById, {
                                              label: pieceLabel,
                                              description: option?.description,
                                            })}</small>
                                          ) : (
                                            <>
                                              <small>Locked.</small>
                                              {renderEffectLineList(
                                                buildEffectRangeLinesForOption(option, titanEffectLabelById, {
                                                  label: pieceLabel,
                                                  description: option?.description,
                                                }),
                                                `titan-equip-range-${sideKey}-${option.id}`,
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {equipSkillRows
                                    .filter((row) => Number(row?.equipId || 0) === Number(equipId || 0))
                                    .map((option) => {
                                      const selectedEntry = getSelectedEntryById(sideData?.equipSkills, option.id);
                                      const activated = Boolean(selectedEntry);
                                      const selectedLevel = activated
                                        ? Math.max(0, Math.floor(Number(selectedEntry?.level || 0)))
                                        : null;
                                      const resolved = activated ? resolveTitanLevelRow(option, selectedLevel) : null;
                                      const maxDisplayLevel = Math.max(1, Number(option?.maxLevel || 20) + 1);
                                      return (
                                        <div key={`equip-skill-${sideKey}-${option.id}`} className="battle-sim-titan-row battle-sim-titan-row--skill">
                                          <div className="battle-sim-titan-row-icon">
                                            {renderTitanIconCell(option, option.label, String(option?.label || "?").charAt(0))}
                                          </div>
                                          <div className="battle-sim-titan-row-body">
                                            <div className="battle-sim-titan-row-head">
                                              <strong>{option.label}</strong>
                                              <select
                                                value={activated ? String(toTitanLevelDisplay(selectedLevel)) : ""}
                                                onChange={(event) => updateTitanSideIdLevelCollection(
                                                  sideKey,
                                                  "equipSkills",
                                                  option.id,
                                                  event.target.value === "" ? null : fromTitanLevelDisplay(event.target.value),
                                                )}
                                              >
                                                <option value="">Unactivated</option>
                                                {Array.from({ length: maxDisplayLevel }, (_entry, index) => index + 1).map((displayLevel) => (
                                                  <option key={`equip-skill-level-${sideKey}-${option.id}-${displayLevel}`} value={displayLevel}>
                                                    {`Level ${displayLevel}`}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                            {activated ? (
                                              <small>{renderTitanEffectsSummary(resolved?.effects || [], titanEffectLabelById, {
                                                label: option?.label,
                                                description: option?.description,
                                              })}</small>
                                            ) : (
                                              <>
                                                <small>Unactivated.</small>
                                                {renderEffectLineList(
                                                  buildEffectRangeLinesForOption(option, titanEffectLabelById, {
                                                    label: option?.label,
                                                    description: option?.description,
                                                  }),
                                                  `titan-equip-skill-range-${sideKey}-${option.id}`,
                                                )}
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              ))}
                            </div>
                          </div>
                          ) : null}
                        </div>
                      );
                    })}
                    </div>
                  </div>
                ) : selectedSection === "warplane" ? (
                  <div className="battle-sim-warplane-editor">
                    <div className="battle-sim-officer-detail-toolbar battle-sim-warplane-detail-toolbar">
                      <label>
                        <span>Warplane Section</span>
                        <select
                          value={activeWarplaneDetailGroup}
                          onChange={(event) => setActiveWarplaneDetailGroup(event.target.value)}
                        >
                          {WARPLANE_DETAIL_GROUPS.map((group) => (
                            <option key={`warplane-detail-${group.key}`} value={group.key}>
                              {group.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    {activeWarplaneDetailGroup === "evolution" ? (
                    <div className="battle-sim-warplane-columns">
                      <div className="battle-sim-warplane-panel">
                        <div className="battle-sim-warplane-panel-head">
                          <strong>Owned Warplanes</strong>
                          <small>
                            {`${Number(warplaneData?.ownedRows?.length || 0).toLocaleString()} stored | ${formatPowerNumber(calculatedPowerSummary?.warplanePower)} power`}
                          </small>
                        </div>
                        {Array.isArray(warplaneData?.ownedRows) && warplaneData.ownedRows.length > 0 ? (
                          <div className="battle-sim-warplane-owned-list">
                            {warplaneData.ownedRows.map((row, index) => {
                              const rowKey = getWarplaneRowKey(row, index);
                              const slot = Number(row?.slot || 0);
                              const label = String(row?.name || "").trim() || `Warplane ${slot || index + 1}`;
                              const selected = rowKey === selectedWarplaneKey;
                              return (
                                <button
                                  type="button"
                                  key={rowKey}
                                  className={`battle-sim-warplane-owned-row${selected ? " selected" : ""}`}
                                  onClick={() => selectWarplaneRow(rowKey)}
                                >
                                  <strong>{`${slot ? `#${slot} ` : ""}${label}`}</strong>
                                  <span>{`Level ${toWarplaneLevelDisplay(row?.level || 0)} | ${String(getWarplaneStageMeta(row?.advancementStage || 0).stageLabel)}`}</span>
                                  <small>{`${Number(row?.power || 0).toLocaleString()} power`}</small>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="battle-sim-warplane-effect-empty">No warplanes imported yet.</div>
                        )}
                      </div>

                      <div className="battle-sim-warplane-panel">
                        <div className="battle-sim-warplane-panel-head">
                          <strong>Selected Warplane Evolution</strong>
                        </div>
                        <div className="battle-sim-warplane-base-grid">
                          <label>
                            <span>Warplane Level</span>
                            <select
                              value={String(toWarplaneLevelDisplay(warplaneEditorModel?.selectedLevel || 0))}
                              onChange={(event) => updateWarplaneScalar("level", fromWarplaneLevelDisplay(event.target.value))}
                            >
                              {buildIntegerRange(1, Math.max(1, toWarplaneLevelDisplay(warplaneEditorModel?.maxLevel || 0))).map((displayLevel) => (
                                <option key={`warplane-level-${displayLevel}`} value={displayLevel}>
                                  {displayLevel}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>Evolution Tier</span>
                            <select
                              value={String(Number(warplaneEditorModel?.selectedStage || 0))}
                              onChange={(event) => updateWarplaneStage(event.target.value)}
                            >
                              {(Array.isArray(warplaneEditorModel?.stageRows) ? warplaneEditorModel.stageRows : []).map((row) => {
                                const stage = Math.max(0, Math.floor(Number(row?.stage || 0)));
                                return (
                                  <option key={`warplane-stage-${stage}`} value={stage}>
                                    {String(row?.stageLabel || `Tier ${stage + 1}`)}
                                  </option>
                                );
                              })}
                            </select>
                          </label>
                          <label>
                            <span>Evolution Step</span>
                            <select
                              value={String(Number(warplaneEditorModel?.selectedProgressStep || 0))}
                              onChange={(event) => updateWarplaneScalar("advancementStep", event.target.value)}
                            >
                              {Array.from({
                                length: Math.max(1, Number(warplaneEditorModel?.maxProgressStep || 0) + 1),
                              }).map((_, step) => (
                                <option key={`warplane-step-${step}`} value={step}>
                                  {`Step ${step}`}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="battle-sim-warplane-summary">
                          <small>{`Selected evolution step: ${Number(warplaneEditorModel?.selectedProgressStep || 0)} / ${Number(warplaneEditorModel?.maxProgressStep || 0)}`}</small>
                          <small>{`Max skill level at current warplane level: ${Number(warplaneEditorModel?.maxSkillDisplayLevel || 0).toLocaleString()}`}</small>
                        </div>
                        <div className="battle-sim-warplane-materials">
                          <strong>Pending materials</strong>
                          {selectedWarplanePendingEvolution?.materials?.length > 0 || selectedWarplanePendingRows.length > 0 ? (
                            <>
                              {selectedWarplanePendingEvolution?.materials?.length > 0 ? (
                                <div>
                                  <span>{`${selectedWarplanePendingEvolution.stepCount.toLocaleString()} evolution step${selectedWarplanePendingEvolution.stepCount === 1 ? "" : "s"}`}</span>
                                  <small>{formatMaterialList(selectedWarplanePendingEvolution.materials)}</small>
                                </div>
                              ) : null}
                              {selectedWarplanePendingRows.map((changeRow) => (
                                <div key={`selected-warplane-pending-${changeRow.key}`}>
                                  <span>{`${changeRow.subsection}: ${changeRow.fromLabel} -> ${changeRow.toLabel}`}</span>
                                  <small>
                                    {changeRow.materials.length > 0
                                      ? formatMaterialList(changeRow.materials)
                                      : "No mapped material requirement."}
                                  </small>
                                </div>
                              ))}
                            </>
                          ) : (
                            <small>No pending warplane material change.</small>
                          )}
                        </div>

                        <div className="battle-sim-warplane-effect-block">
                          {buildEffectLinesFromRows(warplaneEditorModel?.selectedLevelRow?.baseEffects || [], warplaneEffectLabelById, {
                            label: warplaneEditorModel?.selectedWarplane?.label || "Warplane",
                            description: warplaneEditorModel?.selectedLevelRow?.description,
                          }).length > 0 ? (
                            buildEffectLinesFromRows(warplaneEditorModel?.selectedLevelRow?.baseEffects || [], warplaneEffectLabelById, {
                              label: warplaneEditorModel?.selectedWarplane?.label || "Warplane",
                              description: warplaneEditorModel?.selectedLevelRow?.description,
                            }).map((line, index) => (
                              <div key={`warplane-base-effect-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                            ))
                          ) : (
                            <div className="battle-sim-warplane-effect-empty">No base effects found for current level row.</div>
                          )}
                        </div>
                      </div>
                    </div>
                    ) : null}

                    {activeWarplaneDetailGroup === "labSkills" ? (
                    <div className="battle-sim-warplane-panel">
                      <div className="battle-sim-warplane-panel-head">
                        <strong>Lab Skills</strong>
                        <small>{`${Number(warplaneEditorModel?.stageSkillRows?.length || 0).toLocaleString()} mapped skills`}</small>
                      </div>
                      <div className="battle-sim-warplane-row-list">
                        {(Array.isArray(warplaneEditorModel?.stageSkillRows) ? warplaneEditorModel.stageSkillRows : []).map((row) => {
                          const isLocked = !Boolean(row?.unlocked);
                          const canLevel = Number(row?.maxLevel || 0) > 1;
                          const selectedLevel = Math.floor(Number(row?.selectedLevel ?? -1));
                          const selectedDisplayLevel = toWarplaneOptionLevelDisplay(selectedLevel);
                          const controlDisplayLevel = isLocked ? selectedDisplayLevel : Math.max(1, selectedDisplayLevel);
                          const maxDisplayLevel = toWarplaneOptionLevelDisplay(row?.maxLevel ?? -1);
                          return (
                            <div
                              key={`warplane-skill-${row.fieldKey}-${row.id}`}
                              className={`battle-sim-warplane-row${isLocked ? " battle-sim-warplane-row--locked" : ""}`}
                            >
                              <div className="battle-sim-warplane-row-head">
                                <strong>{row.label}</strong>
                                <span
                                  className="battle-sim-warplane-row-stage"
                                  style={row?.stageColor ? {
                                    borderColor: row.stageColor,
                                    backgroundColor: `${row.stageColor}1a`,
                                    color: row.stageColor,
                                  } : null}
                                >
                                  {`${row.stageLabel}${row.slotType === "passive" ? " Passive" : row.slotType === "active" ? " Active" : ""}`}
                                </span>
                                {canLevel ? (
                                  <select
                                    value={String(controlDisplayLevel)}
                                    onChange={(event) => updateWarplaneSkillLevelWithPrerequisite(
                                      row,
                                      fromWarplaneOptionLevelDisplay(event.target.value),
                                    )}
                                  >
                                    {isLocked ? <option value="0">Unactivated</option> : null}
                                    {Array.from({ length: maxDisplayLevel }, (_, levelIndex) => levelIndex + 1).map((displayLevel) => (
                                      <option key={`warplane-skill-level-${row.id}-${displayLevel}`} value={displayLevel}>
                                        {displayLevel}
                                      </option>
                                    ))}
                                  </select>
                                ) : null}
                              </div>
                              {isLocked ? (
                                <small>{row?.unlockText || "Locked by current evolution tier."}</small>
                              ) : null}
                              {row?.description ? (
                                <small>{renderGameColorTaggedText(row.description, `warplane-stage-desc-${row.id}`)}</small>
                              ) : null}
                              {Array.isArray(row?.effectLines) && row.effectLines.length > 0 ? (
                                <div className="battle-sim-warplane-effect-block">
                                  {row.effectLines.map((line, index) => (
                                    <div key={`warplane-skill-effect-${row.id}-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                                  ))}
                                </div>
                              ) : isLocked && Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0 ? (
                                <div className="battle-sim-warplane-effect-block">
                                  {row.effectRangeLines.map((line, index) => (
                                    <div key={`warplane-skill-range-${row.id}-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                                  ))}
                                </div>
                              ) : (
                                <div className="battle-sim-warplane-effect-empty">No active effect.</div>
                              )}
                              {renderWarplanePendingRows(row.fieldKey, row.id)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    ) : null}

                    {activeWarplaneDetailGroup === "equipmentSkills" ? (
                        <div className="battle-sim-warplane-panel">
                          <div className="battle-sim-warplane-panel-head">
                            <strong>{WARPLANE_SKILL_GROUP_LABELS[WARPLANE_SKILL_GROUP_KEYS.equipSkills]}</strong>
                            <small>
                              {`${Number(warplaneEditorModel?.equipmentSkillGroups?.length || 0).toLocaleString()} groups, ${Number(warplaneEditorModel?.equipSkillRows?.length || 0).toLocaleString()} skills`}
                            </small>
                          </div>
                          <div className="battle-sim-warplane-equipment-group-list">
                            {(Array.isArray(warplaneEditorModel?.equipmentSkillGroups) ? warplaneEditorModel.equipmentSkillGroups : []).map((group) => {
                              const equipmentRow = group?.equipmentRow || null;
                              const equipmentSelectedLevel = Math.floor(Number(equipmentRow?.selectedLevel ?? -1));
                              const equipmentEnabled = equipmentRow ? equipmentSelectedLevel >= 0 : true;
                              const equipmentToggleLevel = Math.max(0, equipmentSelectedLevel);
                              const equipmentCanLevel = equipmentRow ? Number(equipmentRow?.maxLevel || 0) > 1 : false;
                              const equipmentDisplayLevel = toWarplaneOptionLevelDisplay(equipmentSelectedLevel);
                              const equipmentMaxDisplayLevel = toWarplaneOptionLevelDisplay(equipmentRow?.maxLevel ?? -1);
                              return (
                                <div key={`warplane-equipment-group-${group.id}`} className="battle-sim-warplane-equipment-group">
                                  <div className="battle-sim-warplane-equipment-group-head">
                                    <div>
                                      <strong>{group.label}</strong>
                                      <small>{`${Number(group?.skillRows?.length || 0).toLocaleString()} skills`}</small>
                                    </div>
                                    {equipmentRow ? (
                                      <div className="battle-sim-warplane-equipment-controls">
                                        <label className="battle-sim-warplane-row-toggle">
                                          <input
                                            type="checkbox"
                                            checked={equipmentEnabled}
                                            onChange={(event) => updateWarplaneIdLevelCollection(
                                              WARPLANE_SKILL_GROUP_KEYS.equips,
                                              equipmentRow.id,
                                              event.target.checked ? equipmentToggleLevel : -1,
                                            )}
                                          />
                                          <span>Installed</span>
                                        </label>
                                        {equipmentCanLevel ? (
                                          <select
                                            value={String(equipmentDisplayLevel > 0 ? equipmentDisplayLevel : 1)}
                                            onChange={(event) => updateWarplaneEquipmentLevelWithPrerequisite(equipmentRow, event.target.value)}
                                          >
                                            {Array.from({ length: Math.max(1, equipmentMaxDisplayLevel) }, (_, levelIndex) => levelIndex + 1).map((displayLevel) => (
                                              <option key={`warplane-equipment-level-${equipmentRow.id}-${displayLevel}`} value={displayLevel}>
                                                {displayLevel}
                                              </option>
                                            ))}
                                          </select>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                  {equipmentRow?.description ? (
                                    <small>{renderGameColorTaggedText(equipmentRow.description, `warplane-equipment-desc-${equipmentRow.id}`)}</small>
                                  ) : null}
                                  {equipmentEnabled && Array.isArray(equipmentRow?.effectLines) && equipmentRow.effectLines.length > 0 ? (
                                    <div className="battle-sim-warplane-effect-block">
                                      {equipmentRow.effectLines.map((line, index) => (
                                        <div key={`warplane-equipment-effect-${equipmentRow.id}-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                                      ))}
                                    </div>
                                  ) : !equipmentEnabled && Array.isArray(equipmentRow?.effectRangeLines) && equipmentRow.effectRangeLines.length > 0 ? (
                                    <div className="battle-sim-warplane-effect-block">
                                      {equipmentRow.effectRangeLines.map((line, index) => (
                                        <div key={`warplane-equipment-range-${equipmentRow.id}-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                                      ))}
                                    </div>
                                  ) : null}
                                  {equipmentRow ? renderWarplanePendingRows(WARPLANE_SKILL_GROUP_KEYS.equips, equipmentRow.id) : null}
                                  <div className="battle-sim-warplane-equipment-skill-list">
                                    {(Array.isArray(group?.skillRows) ? group.skillRows : []).map((row) => {
                                      const selectedLevel = Math.floor(Number(row?.selectedLevel ?? -1));
                                      const enabled = selectedLevel >= 0;
                                      const selectedDisplayLevel = toWarplaneOptionLevelDisplay(selectedLevel);
                                      const maxDisplayLevel = toWarplaneOptionLevelDisplay(row?.maxLevel ?? -1);
                                      return (
                                        <div
                                          key={`warplane-group-row-${WARPLANE_SKILL_GROUP_KEYS.equipSkills}-${row.id}`}
                                          className={`battle-sim-warplane-row battle-sim-warplane-row--equipment-skill${enabled ? "" : " battle-sim-warplane-row--locked"}`}
                                        >
                                          <div className="battle-sim-warplane-row-head">
                                            <strong>{row.label}</strong>
                                            <select
                                              value={enabled ? String(selectedDisplayLevel > 0 ? selectedDisplayLevel : 1) : ""}
                                              onChange={(event) => updateWarplaneEquipmentSkillWithPrerequisite(
                                                equipmentRow,
                                                row,
                                                event.target.value === ""
                                                  ? -1
                                                  : fromWarplaneOptionLevelDisplay(event.target.value),
                                              )}
                                            >
                                              <option value="">Locked</option>
                                              {Array.from({ length: Math.max(1, maxDisplayLevel) }, (_entry, levelIndex) => levelIndex + 1).map((displayLevel) => (
                                                <option key={`warplane-equipment-skill-level-${row.id}-${displayLevel}`} value={displayLevel}>
                                                  {`Level ${displayLevel}`}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          {row?.description ? (
                                            <small>{renderGameColorTaggedText(row.description, `warplane-group-desc-${WARPLANE_SKILL_GROUP_KEYS.equipSkills}-${row.id}`)}</small>
                                          ) : null}
                                          {Array.isArray(row?.effectLines) && row.effectLines.length > 0 ? (
                                            <div className="battle-sim-warplane-effect-block">
                                              {row.effectLines.map((line, index) => (
                                                <div key={`warplane-group-effect-${WARPLANE_SKILL_GROUP_KEYS.equipSkills}-${row.id}-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                                              ))}
                                            </div>
                                          ) : !enabled && Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0 ? (
                                            <div className="battle-sim-warplane-effect-block">
                                              {row.effectRangeLines.map((line, index) => (
                                                <div key={`warplane-group-range-${WARPLANE_SKILL_GROUP_KEYS.equipSkills}-${row.id}-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="battle-sim-warplane-effect-empty">No active effect.</div>
                                          )}
                                          {renderWarplanePendingRows(WARPLANE_SKILL_GROUP_KEYS.equipSkills, row.id)}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                    ) : null}

                    {activeWarplaneDetailGroup === "skins" ? (
                        <div className="battle-sim-warplane-panel">
                          <div className="battle-sim-warplane-panel-head">
                            <strong>{WARPLANE_SKILL_GROUP_LABELS[WARPLANE_SKILL_GROUP_KEYS.skins]}</strong>
                            <small>{`${Number(warplaneEditorModel?.skinRows?.length || 0).toLocaleString()} options`}</small>
                          </div>
                          <div className="battle-sim-warplane-row-list">
                            {(Array.isArray(warplaneEditorModel?.skinRows) ? warplaneEditorModel.skinRows : []).map((row) => {
                              const selectedLevel = Math.floor(Number(row?.selectedLevel ?? -1));
                              const enabled = selectedLevel >= 0;
                              const maxLevel = Math.max(0, Number(row?.maxLevel || 0));
                              const levelOptions = getWarplaneOptionLevelOptions(row?.option, maxLevel);
                              return (
                                <div key={`warplane-skin-row-${row.id}`} className={`battle-sim-warplane-row${enabled ? "" : " battle-sim-warplane-row--locked"}`}>
                                  <div className="battle-sim-warplane-row-head">
                                    <strong>{row.label}</strong>
                                    <select
                                      value={String(enabled ? selectedLevel : -1)}
                                      aria-label={`${row.label || "Warplane skin"} level`}
                                      onChange={(event) => updateWarplaneIdLevelCollection(
                                        WARPLANE_SKILL_GROUP_KEYS.skins,
                                        row.id,
                                        event.target.value,
                                      )}
                                    >
                                      <option value="-1">Locked</option>
                                      {levelOptions.map((level) => (
                                        <option key={`warplane-skin-level-${row.id}-${level}`} value={level}>
                                          {`Level ${toWarplaneOptionLevelDisplay(level)}`}
                                        </option>
                                      ))}
                                    </select>
                                    {enabled && levelOptions.length <= 1 ? (
                                      <span className="battle-sim-warplane-row-stage">Unlocked</span>
                                    ) : null}
                                  </div>
                                  {enabled ? (
                                    <small>
                                      {`Selected Level ${toWarplaneOptionLevelDisplay(selectedLevel)}`}
                                    </small>
                                  ) : null}
                                  {row?.description ? (
                                    <small>{renderGameColorTaggedText(row.description, `warplane-skin-desc-${row.id}`)}</small>
                                  ) : enabled ? null : (
                                    <small>Locked. Unlock to activate this skin&apos;s bonus.</small>
                                  )}
                                  {Array.isArray(row?.effectLines) && row.effectLines.length > 0 ? (
                                    <div className="battle-sim-warplane-effect-block">
                                      {row.effectLines.map((line, index) => (
                                        <div key={`warplane-skin-effect-${row.id}-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                                      ))}
                                    </div>
                                  ) : !enabled && Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0 ? (
                                    <div className="battle-sim-warplane-effect-block">
                                      {row.effectRangeLines.map((line, index) => (
                                        <div key={`warplane-skin-range-${row.id}-${index}`} className="battle-sim-warplane-effect-line">{line}</div>
                                      ))}
                                    </div>
                                  ) : enabled ? (
                                    <div className="battle-sim-warplane-effect-empty">No active skin effect.</div>
                                  ) : null}
                                  {renderWarplanePendingRows(WARPLANE_SKILL_GROUP_KEYS.skins, row.id)}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                    ) : null}
                  </div>
                ) : selectedSection === "equipment" ? (
                  <div className="battle-sim-equipment-editor">
                    <div className="battle-sim-equipment-editor-head">
                      <strong>Commander Equipment</strong>
                      <small>{`${Number(equipmentEditorModel.rows.length || 0).toLocaleString()} equipment types`}</small>
                    </div>
                    <div className="battle-sim-equipment-table-wrap">
                      <div className="battle-sim-equipment-card-list">
                        {equipmentEditorModel.rows.map((row) => {
                          const selectedBaseId = Number(row?.selectedBaseId || 0) || null;
                          const selectedQualityRankRaw = Math.floor(Number(row?.selectedQualityRank));
                          const selectedQualityRank = Number.isFinite(selectedQualityRankRaw)
                            ? selectedQualityRankRaw
                            : EQUIPMENT_QUALITY_NOT_ENABLED;
                          const levelOptions = Array.isArray(row?.baseOptions) ? row.baseOptions : [];
                          return (
                            <div key={`equipment-mobile-${row.slot}`} className="battle-sim-equipment-card">
                              <div className="battle-sim-equipment-card-head">
                                <div>
                                  <strong>{row.label}</strong>
                                  <small>{`Slot ${Number(row.slot || 0)}`}</small>
                                </div>
                                <div className="battle-sim-equipment-card-buffs">
                                  {Array.isArray(row?.activeBuffLines) && row.activeBuffLines.length ? (
                                    row.activeBuffLines.map((line, lineIdx) => (
                                      <div key={`equip-mobile-active-buff-${row.slot}-${lineIdx}`}>{line}</div>
                                    ))
                                  ) : (
                                    <div className="battle-sim-equipment-buff-preview-empty">No active combined bonus.</div>
                                  )}
                                </div>
                              </div>
                              <div className="battle-sim-equipment-card-controls">
                                <label>
                                  <span>Quality</span>
                                  <select
                                    value={String(selectedQualityRank)}
                                    disabled={!selectedBaseId}
                                    onChange={(event) => setEquipmentSlotQuality(row.slot, selectedBaseId, event.target.value)}
                                  >
                                    {(Array.isArray(row?.qualityOptions) ? row.qualityOptions : []).map((option) => (
                                      <option key={`equip-mobile-quality-${row.slot}-${option.rank}`} value={option.rank}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  <span>Level</span>
                                  <select
                                    value={selectedBaseId ? String(selectedBaseId) : ""}
                                    disabled={!selectedBaseId}
                                    onChange={(event) => setEquipmentSlotLevel(row.slot, event.target.value)}
                                  >
                                    {levelOptions.map((option) => (
                                      <option key={`equip-mobile-level-${row.slot}-${option.id}`} value={option.id}>
                                        {`Level ${Math.max(1, Math.floor(Number(option?.displayLevel || 1)))}`}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                              {Array.isArray(row?.baseBuffLines) && row.baseBuffLines.length ? (
                                <div className="battle-sim-equipment-buff-preview">
                                  {row.baseBuffLines.map((line, lineIdx) => (
                                    <div key={`equip-mobile-base-buff-${row.slot}-${lineIdx}`}>{line}</div>
                                  ))}
                                </div>
                              ) : Array.isArray(row?.baseEffectRangeLines) && row.baseEffectRangeLines.length ? (
                                <div className="battle-sim-equipment-buff-preview">
                                  {row.baseEffectRangeLines.map((line, lineIdx) => (
                                    <div key={`equip-mobile-base-range-${row.slot}-${lineIdx}`}>{line}</div>
                                  ))}
                                </div>
                              ) : (
                                <div className="battle-sim-equipment-buff-preview battle-sim-equipment-buff-preview-empty">No active base bonus.</div>
                              )}
                              <div className="battle-sim-equipment-card-extensions">
                                {Array.from({ length: 3 }).map((_, extensionIdx) => {
                                  const extension = Array.isArray(row?.extensionSlots)
                                    ? (row.extensionSlots[extensionIdx] || null)
                                    : null;
                                  const unlocked = Boolean(extension?.unlocked);
                                  const qualityOptions = Array.isArray(extension?.qualityOptions) && extension.qualityOptions.length
                                    ? extension.qualityOptions
                                    : [EQUIPMENT_QUALITY_NOT_ENABLED, 0, 1, 2, 3, 4];
                                  const selectedExtensionQualityRaw = Number.isFinite(Number(extension?.quality))
                                    ? Math.floor(Number(extension?.quality))
                                    : EQUIPMENT_QUALITY_NOT_ENABLED;
                                  const selectedExtensionQuality = qualityOptions.includes(selectedExtensionQualityRaw)
                                    ? selectedExtensionQualityRaw
                                    : EQUIPMENT_QUALITY_NOT_ENABLED;
                                  const extensionEnabled = selectedExtensionQuality >= 0;
                                  const selectedExtensionLevelDisplay = Math.max(
                                    1,
                                    Math.floor(Number(extension?.level || 0)) + 1,
                                  );
                                  const maxExtensionLevelDisplay = Math.max(
                                    1,
                                    Math.floor(Number(extension?.maxLevel || 5)) + 1,
                                  );
                                  return (
                                    <div key={`equipment-mobile-${row.slot}-ext-${extensionIdx}`} className="battle-sim-equipment-extension-card">
                                      <div className="battle-sim-equipment-extension-title">
                                        <strong>{`Extension ${extensionIdx + 1}`}</strong>
                                        {!unlocked ? (
                                          <span>{`Unlocks at Level ${Math.max(1, Number(extension?.unlockDisplayLevel || 1))}`}</span>
                                        ) : (
                                          <span>{extensionEnabled ? String(extension?.selectedRankLabel || extension?.typeLabel || "") : "Not enabled"}</span>
                                        )}
                                      </div>
                                      <>
                                          <div className="battle-sim-equipment-extension-controls">
                                            <label>
                                              <span>Quality</span>
                                              <select
                                                value={String(selectedExtensionQuality)}
                                                onChange={(event) => setEquipmentExtensionSlotValueWithPrerequisite(
                                                  row,
                                                  extension,
                                                  "quality",
                                                  event.target.value,
                                                )}
                                              >
                                                {qualityOptions.map((rank) => (
                                                  <option key={`equipment-mobile-ext-quality-${row.slot}-${extension?.index}-${rank}`} value={rank}>
                                                    {formatEquipmentQualityLabel(rank)}
                                                  </option>
                                                ))}
                                              </select>
                                            </label>
                                            <label>
                                              <span>Level</span>
                                              <select
                                                disabled={!extensionEnabled}
                                                value={selectedExtensionLevelDisplay}
                                                onChange={(event) => {
                                                  const displayLevel = Math.max(1, Math.floor(Number(event.target.value || 1)));
                                                  setEquipmentExtensionSlotValueWithPrerequisite(
                                                    row,
                                                    extension,
                                                    "level",
                                                    Math.max(0, displayLevel - 1),
                                                  );
                                                }}
                                              >
                                                {Array.from({ length: maxExtensionLevelDisplay }).map((__, levelIdx) => {
                                                  const displayLevel = levelIdx + 1;
                                                  return (
                                                    <option key={`equipment-mobile-ext-level-${row.slot}-${extension?.index}-${displayLevel}`} value={displayLevel}>
                                                      {`Level ${displayLevel}`}
                                                    </option>
                                                  );
                                                })}
                                              </select>
                                            </label>
                                          </div>
                                          {extensionEnabled && Array.isArray(extension?.buffLines) && extension.buffLines.length ? (
                                            <div className="battle-sim-equipment-buff-preview">
                                              {extension.buffLines.map((line, lineIdx) => (
                                                <div key={`equip-mobile-ext-buff-${row.slot}-${extension?.index}-${lineIdx}`}>{line}</div>
                                              ))}
                                            </div>
                                          ) : extensionEnabled ? (
                                            <div className="battle-sim-equipment-buff-preview battle-sim-equipment-buff-preview-empty">No active extension bonus.</div>
                                          ) : Array.isArray(extension?.effectRangeLines) && extension.effectRangeLines.length ? (
                                            <div className="battle-sim-equipment-buff-preview">
                                              {extension.effectRangeLines.map((line, lineIdx) => (
                                                <div key={`equip-mobile-ext-range-${row.slot}-${extension?.index}-${lineIdx}`}>{line}</div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="battle-sim-equipment-buff-preview battle-sim-equipment-buff-preview-empty">Not enabled.</div>
                                          )}
                                      </>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <table className="battle-sim-equipment-table">
                        <thead>
                          <tr>
                            <th className="battle-sim-equipment-col-type">Equipment</th>
                            <th className="battle-sim-equipment-col-quality">Quality</th>
                            <th className="battle-sim-equipment-col-level">Level</th>
                            {Array.from({ length: 3 }).map((_, extensionIdx) => (
                              <th key={`equip-ext-group-${extensionIdx}`} colSpan={2} className="battle-sim-equipment-col-ext-head">
                                {`Extension ${extensionIdx + 1}`}
                              </th>
                            ))}
                          </tr>
                          <tr className="battle-sim-equipment-subhead-row">
                            <th />
                            <th />
                            <th />
                            {Array.from({ length: 3 }).map((_, extensionIdx) => (
                              <React.Fragment key={`equip-ext-subhead-${extensionIdx}`}>
                                <th className="battle-sim-equipment-col-quality">Quality</th>
                                <th className="battle-sim-equipment-col-level">Level</th>
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {equipmentEditorModel.rows.map((row) => {
                            const selectedBaseId = Number(row?.selectedBaseId || 0) || null;
                            const selectedQualityRankRaw = Math.floor(Number(row?.selectedQualityRank));
                            const selectedQualityRank = Number.isFinite(selectedQualityRankRaw)
                              ? selectedQualityRankRaw
                              : EQUIPMENT_QUALITY_NOT_ENABLED;
                            const levelOptions = Array.isArray(row?.baseOptions) ? row.baseOptions : [];
                            return (
                              <tr key={`equipment-slot-${row.slot}`}>
                                <td className="battle-sim-equipment-col-type" data-label="Equipment">
                                  <strong>{row.label}</strong>
                                  <small>{`Slot ${Number(row.slot || 0)}`}</small>
                                  <div className="battle-sim-equipment-buff-list">
                                    {Array.isArray(row?.activeBuffLines) && row.activeBuffLines.length ? (
                                      row.activeBuffLines.map((line, lineIdx) => (
                                        <div key={`equip-active-buff-${row.slot}-${lineIdx}`}>{line}</div>
                                      ))
                                    ) : (
                                      <div className="battle-sim-equipment-buff-preview-empty">No active combined bonus.</div>
                                    )}
                                  </div>
                                </td>
                                <td className="battle-sim-equipment-col-quality" data-label="Quality">
                                  <select
                                    value={String(selectedQualityRank)}
                                    disabled={!selectedBaseId}
                                    onChange={(event) => setEquipmentSlotQuality(row.slot, selectedBaseId, event.target.value)}
                                  >
                                    {(Array.isArray(row?.qualityOptions) ? row.qualityOptions : []).map((option) => (
                                      <option key={`equip-quality-${row.slot}-${option.rank}`} value={option.rank}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="battle-sim-equipment-col-level" data-label="Level">
                                  <select
                                    value={selectedBaseId ? String(selectedBaseId) : ""}
                                    disabled={!selectedBaseId}
                                    onChange={(event) => setEquipmentSlotLevel(
                                      row.slot,
                                      event.target.value,
                                    )}
                                  >
                                    {levelOptions.map((option) => (
                                      <option key={`equip-level-${row.slot}-${option.id}`} value={option.id}>
                                        {`Level ${Math.max(1, Math.floor(Number(option?.displayLevel || 1)))}`}
                                      </option>
                                    ))}
                                  </select>
                                  {Array.isArray(row?.baseBuffLines) && row.baseBuffLines.length ? (
                                    <div className="battle-sim-equipment-buff-preview">
                                      {row.baseBuffLines.map((line, lineIdx) => (
                                        <div key={`equip-base-buff-${row.slot}-${lineIdx}`}>{line}</div>
                                      ))}
                                    </div>
                                  ) : Array.isArray(row?.baseEffectRangeLines) && row.baseEffectRangeLines.length ? (
                                    <div className="battle-sim-equipment-buff-preview">
                                      {row.baseEffectRangeLines.map((line, lineIdx) => (
                                        <div key={`equip-base-range-${row.slot}-${lineIdx}`}>{line}</div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="battle-sim-equipment-buff-preview battle-sim-equipment-buff-preview-empty">No active base bonus.</div>
                                  )}
                                </td>
                                {Array.from({ length: 3 }).map((_, extensionIdx) => {
                                  const extension = Array.isArray(row?.extensionSlots)
                                    ? (row.extensionSlots[extensionIdx] || null)
                                    : null;
                                  const unlocked = Boolean(extension?.unlocked);
                                  const qualityOptions = Array.isArray(extension?.qualityOptions) && extension.qualityOptions.length
                                    ? extension.qualityOptions
                                    : [EQUIPMENT_QUALITY_NOT_ENABLED, 0, 1, 2, 3, 4];
                                  const selectedExtensionQualityRaw = Number.isFinite(Number(extension?.quality))
                                    ? Math.floor(Number(extension?.quality))
                                    : EQUIPMENT_QUALITY_NOT_ENABLED;
                                  const selectedExtensionQuality = qualityOptions.includes(selectedExtensionQualityRaw)
                                    ? selectedExtensionQualityRaw
                                    : EQUIPMENT_QUALITY_NOT_ENABLED;
                                  const extensionEnabled = selectedExtensionQuality >= 0;
                                  const selectedExtensionLevelDisplay = Math.max(
                                    1,
                                    Math.floor(Number(extension?.level || 0)) + 1,
                                  );
                                  const maxExtensionLevelDisplay = Math.max(
                                    1,
                                    Math.floor(Number(extension?.maxLevel || 5)) + 1,
                                  );
                                  return (
                                    <React.Fragment key={`equipment-slot-${row.slot}-ext-${extensionIdx}`}>
                                      <td className="battle-sim-equipment-col-quality" data-label={`Extension ${extensionIdx + 1} Quality`}>
                                        <div className="battle-sim-equipment-extension-quality">
                                          <select
                                            value={String(selectedExtensionQuality)}
                                            onChange={(event) => setEquipmentExtensionSlotValueWithPrerequisite(
                                              row,
                                              extension,
                                              "quality",
                                              event.target.value,
                                            )}
                                          >
                                            {qualityOptions.map((rank) => (
                                              <option key={`equipment-ext-quality-${row.slot}-${extension?.index}-${rank}`} value={rank}>
                                                {formatEquipmentQualityLabel(rank)}
                                              </option>
                                            ))}
                                          </select>
                                          <small>
                                            {unlocked
                                              ? (extensionEnabled ? String(extension?.selectedRankLabel || extension?.typeLabel || "") : "Not enabled")
                                              : `Unlocks at Level ${Math.max(1, Number(extension?.unlockDisplayLevel || 1))}`}
                                          </small>
                                        </div>
                                      </td>
                                      <td className="battle-sim-equipment-col-level" data-label={`Extension ${extensionIdx + 1} Level`}>
                                        <>
                                          <select
                                            disabled={!extensionEnabled}
                                            value={selectedExtensionLevelDisplay}
                                            onChange={(event) => {
                                              const displayLevel = Math.max(1, Math.floor(Number(event.target.value || 1)));
                                              setEquipmentExtensionSlotValueWithPrerequisite(
                                                row,
                                                extension,
                                                "level",
                                                Math.max(0, displayLevel - 1),
                                              );
                                            }}
                                          >
                                            {Array.from({ length: maxExtensionLevelDisplay }).map((__, levelIdx) => {
                                              const displayLevel = levelIdx + 1;
                                              return (
                                                <option key={`equipment-ext-level-${row.slot}-${extension?.index}-${displayLevel}`} value={displayLevel}>
                                                  {`Level ${displayLevel}`}
                                                </option>
                                              );
                                            })}
                                          </select>
                                          {extensionEnabled && Array.isArray(extension?.buffLines) && extension.buffLines.length ? (
                                            <div className="battle-sim-equipment-buff-preview">
                                              {extension.buffLines.map((line, lineIdx) => (
                                                <div key={`equip-ext-buff-${row.slot}-${extension?.index}-${lineIdx}`}>{line}</div>
                                              ))}
                                            </div>
                                          ) : extensionEnabled ? (
                                            <div className="battle-sim-equipment-buff-preview battle-sim-equipment-buff-preview-empty">No active extension bonus.</div>
                                          ) : Array.isArray(extension?.effectRangeLines) && extension.effectRangeLines.length ? (
                                            <div className="battle-sim-equipment-buff-preview">
                                              {extension.effectRangeLines.map((line, lineIdx) => (
                                                <div key={`equip-ext-range-${row.slot}-${extension?.index}-${lineIdx}`}>{line}</div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="battle-sim-equipment-buff-preview battle-sim-equipment-buff-preview-empty">Not enabled.</div>
                                          )}
                                        </>
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : selectedSection === "antiques" ? (
                  <div className="battle-sim-section-form-grid">
                    <div className="battle-sim-section-form-card battle-sim-antiques-editor-card">
                      <strong>Antique Collections</strong>
                      <div className="battle-sim-antiques-tab-controls">
                        {ANTIQUES_EDITOR_TABS.map((tab) => (
                          <button
                            key={`antiques-tab-${tab.key}`}
                            type="button"
                            className={`battle-sim-antiques-tab-button ${antiquesActiveTab === tab.key ? "active" : ""}`}
                            onClick={() => setAntiquesActiveTab(tab.key)}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                      {antiquesActiveTab === "collections" ? (
                        <div className="battle-sim-antiques-groups">
                          {antiqueGroups.length > 0 ? antiqueGroups.map((group) => (
                            <div key={`antique-quality-${group.quality}`} className="battle-sim-antiques-quality-card">
                              <div className="battle-sim-antiques-quality-head">
                                <strong>{group.label}</strong>
                                <small>{`${Number(group.rows.length || 0).toLocaleString()} antiques`}</small>
                              </div>
                              <div className="battle-sim-antiques-table-wrap">
                                <div className="battle-sim-antiques-card-list">
                                  {group.rows.map((row) => (
                                    <div key={`antique-mobile-${row.id}`} className="battle-sim-antiques-card">
                                      <div className="battle-sim-antiques-card-head">
                                        <SectionIconCell
                                          iconPath={row.iconPath}
                                          iconSprite={row.iconSprite}
                                          alt={row.label}
                                          candidatePrefixes={ANTIQUE_ICON_PREFIXES}
                                          fallbackClassName="battle-sim-missing-icon"
                                        />
                                        <div>
                                          <strong>{row.label}</strong>
                                          <small>{`${row.qualityLabel} - Max ${Math.max(0, Number(row.maxStar || 0))}*`}</small>
                                          {row.description ? <small>{row.description}</small> : null}
                                        </div>
                                      </div>
                                      <div className="battle-sim-antiques-card-controls">
                                        <label>
                                          <span>Progress</span>
                                          <select
                                            value={row.activated ? getAntiqueProgressOptionKey(row) : ""}
                                            onChange={(event) => updateAntiqueSelection(
                                              row.id,
                                              event.target.value,
                                              row,
                                              row,
                                            )}
                                          >
                                            <option value="">Inactive</option>
                                            {getAntiqueProgressSelectOptions(row, row).map((option) => (
                                              <option key={`antique-mobile-progress-${row.id}-${getAntiqueProgressOptionKey(option)}`} value={getAntiqueProgressOptionKey(option)}>
                                                {formatAntiqueProgressOptionLabel(option)}
                                              </option>
                                            ))}
                                          </select>
                                          <small>{row.activated ? `Star ${Number(row.progressValue || 0).toFixed(Number(row.stage || 0) > 0 ? 1 : 0)} / Max ${Math.max(0, Number(row.maxStar || 0))}` : "Inactive"}</small>
                                        </label>
                                      </div>
                                      {renderPendingMaterialRows(
                                        antiquePendingChangeRowsById.get(row.id) || [],
                                        `pending-antique-mobile-${row.id}`,
                                      )}
                                      <div className="battle-sim-antiques-card-buffs">
                                        {row.activated && row.buffLines.length > 0 ? (
                                          <>
                                            <small>{`Active at ${Number(row.star || 0)}*${Number(row.activeStage || 0) > 0 ? ` +${Number(row.activeStage || 0)}` : ""}`}</small>
                                            <small>{`Power ${Number(row.activePower || 0).toLocaleString()}`}</small>
                                            {row.buffLines.map((line, lineIndex) => (
                                              <div key={`antique-mobile-buff-${row.id}-${lineIndex}`}>{line}</div>
                                            ))}
                                          </>
                                        ) : row.activated ? (
                                          <small>No active buff at this star.</small>
                                        ) : Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length ? (
                                          <>
                                            <small>Activate to apply buffs (starts at 0*).</small>
                                            {renderEffectLineList(row.effectRangeLines, `antique-mobile-range-${row.id}`)}
                                          </>
                                        ) : (
                                          <small>Activate to apply buffs (starts at 0*).</small>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <table className="battle-sim-antiques-table">
                                  <thead>
                                    <tr>
                                      <th>Icon</th>
                                      <th>Antique</th>
                                      <th>Progress</th>
                                      <th>Buff</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.rows.map((row) => (
                                      <tr key={`antique-row-${row.id}`}>
                                        <td className="battle-sim-antiques-col-icon" data-label="Icon">
                                          <SectionIconCell
                                            iconPath={row.iconPath}
                                            iconSprite={row.iconSprite}
                                            alt={row.label}
                                            candidatePrefixes={ANTIQUE_ICON_PREFIXES}
                                            fallbackClassName="battle-sim-missing-icon"
                                          />
                                        </td>
                                        <td className="battle-sim-antiques-col-name" data-label="Antique">
                                          <strong>{row.label}</strong>
                                          <small>{`${row.qualityLabel} - Max ${Math.max(0, Number(row.maxStar || 0))}*`}</small>
                                          {row.description ? <small>{row.description}</small> : null}
                                          {renderPendingMaterialRows(
                                            antiquePendingChangeRowsById.get(row.id) || [],
                                            `pending-antique-row-${row.id}`,
                                          )}
                                        </td>
                                        <td className="battle-sim-antiques-col-star" data-label="Progress">
                                          <select
                                            value={row.activated ? getAntiqueProgressOptionKey(row) : ""}
                                            onChange={(event) => updateAntiqueSelection(
                                              row.id,
                                              event.target.value,
                                              row,
                                              row,
                                            )}
                                          >
                                            <option value="">Inactive</option>
                                            {getAntiqueProgressSelectOptions(row, row).map((option) => (
                                              <option key={`antique-progress-${row.id}-${getAntiqueProgressOptionKey(option)}`} value={getAntiqueProgressOptionKey(option)}>
                                                {formatAntiqueProgressOptionLabel(option)}
                                              </option>
                                            ))}
                                          </select>
                                          <small>{row.activated ? `Star ${Number(row.progressValue || 0).toFixed(Number(row.stage || 0) > 0 ? 1 : 0)} / Max ${Math.max(0, Number(row.maxStar || 0))}` : "Inactive"}</small>
                                        </td>
                                        <td className="battle-sim-antiques-col-buff" data-label="Buff">
                                          {row.activated && row.buffLines.length > 0 ? (
                                            <>
                                              <small>{`Active at ${Number(row.star || 0)}*${Number(row.activeStage || 0) > 0 ? ` +${Number(row.activeStage || 0)}` : ""}`}</small>
                                              <small>{`Power ${Number(row.activePower || 0).toLocaleString()}`}</small>
                                              {row.buffLines.map((line, lineIndex) => (
                                                <div key={`antique-buff-${row.id}-${lineIndex}`}>{line}</div>
                                              ))}
                                            </>
                                          ) : row.activated ? (
                                            <small>No active buff at this star.</small>
                                          ) : Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length ? (
                                            <>
                                              <small>Activate to apply buffs (starts at 0*).</small>
                                              {renderEffectLineList(row.effectRangeLines, `antique-range-${row.id}`)}
                                            </>
                                          ) : (
                                            <small>Activate to apply buffs (starts at 0*).</small>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )) : (
                            <div className="battle-sim-id-level-editor-empty">No antique collections were found in the active game bundle.</div>
                          )}
                        </div>
                      ) : (
                        <>
                          <small>A set effect activates only when every antique in the set reaches the required star.</small>
                          <div className="battle-sim-antiques-suit-list">
                            {antiqueSuitEditorRows.length > 0 ? antiqueSuitEditorRows.map((suit) => (
                              <div key={`antique-suit-${suit.id}`} className="battle-sim-antiques-suit-card">
                                <div className="battle-sim-antiques-suit-head">
                                  <strong>{suit.label}</strong>
                                  <small>{`Matched ${Number(suit.matchedStar || 0)}*`}</small>
                                </div>
                                {suit.description ? <small className="battle-sim-antiques-suit-desc">{suit.description}</small> : null}
                                <div className="battle-sim-antiques-suit-thresholds">
                                  {Array.isArray(suit.levelRows) ? suit.levelRows.map((levelRow) => {
                                    const requiredStar = Number(levelRow?.star || 0);
                                    const active = Number(suit.matchedStar || 0) >= requiredStar && requiredStar > 0;
                                    return (
                                      <span
                                        key={`antique-suit-${suit.id}-star-${requiredStar}`}
                                        className={`battle-sim-antiques-threshold-pill ${active ? "active" : ""}`}
                                      >
                                        {`${requiredStar}*`}
                                      </span>
                                    );
                                  }) : null}
                                </div>
                                <div className="battle-sim-antiques-suit-reqs">
                                  {Array.isArray(suit.requirements) ? suit.requirements.map((requirement) => {
                                    const collection = antiqueCollectionRowById.get(Number(requirement.id || 0));
                                    const maxStar = Math.max(0, Number(requirement.maxStar || collection?.maxStar || 0));
                                    const isActivated = Boolean(requirement.activated);
                                    return (
                                      <div key={`antique-suit-${suit.id}-req-${requirement.id}`} className="battle-sim-antiques-suit-req-row">
                                        <div className="battle-sim-antiques-suit-req-label">
                                          <SectionIconCell
                                            iconPath={requirement.iconPath}
                                            iconSprite={requirement.iconSprite}
                                            alt={requirement.label}
                                            candidatePrefixes={ANTIQUE_ICON_PREFIXES}
                                            fallbackClassName="battle-sim-missing-icon battle-sim-missing-icon--small"
                                          />
                                          <span>{requirement.label}</span>
                                          {renderPendingMaterialRows(
                                            antiquePendingChangeRowsById.get(requirement.id) || [],
                                            `pending-antique-suit-${suit.id}-${requirement.id}`,
                                          )}
                                        </div>
                                        <div className="battle-sim-antiques-suit-req-controls">
                                          <select
                                            value={isActivated ? getAntiqueProgressOptionKey(requirement) : ""}
                                            onChange={(event) => updateAntiqueSelection(
                                              requirement.id,
                                              event.target.value,
                                              requirement,
                                              requirement,
                                            )}
                                          >
                                            <option value="">Inactive</option>
                                            {getAntiqueProgressSelectOptions(requirement, requirement).map((option) => (
                                              <option key={`antique-suit-progress-${suit.id}-${requirement.id}-${getAntiqueProgressOptionKey(option)}`} value={getAntiqueProgressOptionKey(option)}>
                                                {formatAntiqueProgressOptionLabel(option)}
                                              </option>
                                            ))}
                                          </select>
                                          <small>{isActivated ? `Star ${Number(requirement.progressValue || 0).toFixed(Number(requirement.stage || 0) > 0 ? 1 : 0)} / Max ${maxStar}` : "Inactive"}</small>
                                        </div>
                                      </div>
                                    );
                                  }) : null}
                                </div>
                                <div className="battle-sim-antiques-suit-buffs">
                                  {Array.isArray(suit.activeBuffLines) && suit.activeBuffLines.length > 0 ? suit.activeBuffLines.map((line, lineIndex) => (
                                    <div key={`antique-suit-${suit.id}-buff-${lineIndex}`}>{line}</div>
                                  )) : Array.isArray(suit?.effectRangeLines) && suit.effectRangeLines.length > 0 ? suit.effectRangeLines.map((line, lineIndex) => (
                                    <div key={`antique-suit-${suit.id}-range-${lineIndex}`}>{line}</div>
                                  )) : (
                                    <small>No set effect active.</small>
                                  )}
                                </div>
                              </div>
                            )) : (
                              <div className="battle-sim-id-level-editor-empty">No antique suit definitions were found in the active game bundle.</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : selectedSection === "badges" ? (
                  <div className="battle-sim-badges-editor">
                    <div className="battle-sim-badges-panel">
                      <div className="battle-sim-badges-panel-head">
                        <strong>Badges</strong>
                        <small>{`${Number(badgeEditorRows.length || 0).toLocaleString()} badges`}</small>
                      </div>
                      {badgeEditorRows.length > 0 ? (
                        <div className="battle-sim-badges-list">
                          {badgeEditorRows.map((row) => (
                            <div key={`badge-${row.gid}`} className="battle-sim-badges-row">
                              <div className="battle-sim-badges-main">
                                <strong>{row.label || `Badge #${row.gid}`}</strong>
                                <small>{`Position ${Number(row.position || 0).toLocaleString()} | Owned pieces ${Number(row.ownedCount || 0).toLocaleString()}`}</small>
                                {row.description ? <small>{row.description}</small> : null}
                                {renderPendingMaterialRows(
                                  badgePendingChangeRowsByGid.get(row.gid) || [],
                                  `pending-badge-${row.gid}`,
                                )}
                              </div>
                              <div className="battle-sim-badges-controls">
                                <label>
                                  <span>Level</span>
                                  <select
                                    value={Number(row?.selectedLevel || 0)}
                                    onChange={(event) => updateBadgeLevel(row.gid, event.target.value, row.position)}
                                  >
                                    {(Array.isArray(row?.levelOptions) ? row.levelOptions : [0]).map((level) => (
                                      <option key={`badge-level-${row.gid}-${level}`} value={level}>
                                        {level > 0 ? `Level ${level}` : "Inactive"}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                              <div className="battle-sim-badges-effects">
                                <small>{`Power ${Number(row?.selectedLevelRow?.power || 0).toLocaleString()}`}</small>
                                {Array.isArray(row.effectLines) && row.effectLines.length > 0 ? row.effectLines.map((line, lineIndex) => (
                                  <div key={`badge-${row.gid}-effect-${lineIndex}`}>{line}</div>
                                )) : Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0 ? row.effectRangeLines.map((line, lineIndex) => (
                                  <div key={`badge-${row.gid}-range-${lineIndex}`}>{line}</div>
                                )) : <span>No mapped effect at this level.</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="battle-sim-badges-empty">No badge config rows were found in the active game bundle.</div>
                      )}
                    </div>
                  </div>
                ) : selectedSection === "commanderBoosts" ? (
                  <div className="battle-sim-section-form-grid">
                    <div className="battle-sim-section-form-card">
                      <strong>VIP</strong>
                      <small>VIP bonuses only apply while VIP is active.</small>
                      <div className="battle-sim-commander-vip-editor">
                        <label>
                          <span>Level</span>
                          <select
                            value={Number(commanderBoostsModel.vip.level || 0)}
                            onChange={(event) => updateCommanderBoost("vip", { level: event.target.value })}
                          >
                            <option value={0}>Inactive</option>
                            {VIP_LEVEL_OPTIONS.map((row) => (
                              <option key={`vip-level-${row.displayLevel}`} value={row.displayLevel}>
                                {`Level ${row.displayLevel}`}
                              </option>
                            ))}
                          </select>
                          <small>{`Max ${VIP_MAX_LEVEL}`}</small>
                        </label>
                        <label className="battle-sim-commander-vip-active">
                          <input
                            type="checkbox"
                            checked={Boolean(commanderBoostsModel.vip.active)}
                            onChange={(event) => updateCommanderBoost("vip", { active: event.target.checked })}
                          />
                          <span>Active</span>
                        </label>
                      </div>
                      <div className="battle-sim-academy-tech-effect">
                        {commanderBoostsModel.vip.effectLines.length > 0 ? commanderBoostsModel.vip.effectLines.map((line, lineIndex) => (
                          <div key={`commander-boost-vip-${lineIndex}`}>{line}</div>
                        )) : commanderBoostsModel.vip.effectRangeLines.length > 0 ? commanderBoostsModel.vip.effectRangeLines.map((line, lineIndex) => (
                          <div key={`commander-boost-vip-range-${lineIndex}`}>{line}</div>
                        )) : <span>No active buff at this level.</span>}
                      </div>
                    </div>
                    <div className="battle-sim-section-form-card">
                      <strong>Prosperity</strong>
                      <small>Source: login <code>refugee</code> value matched against ServerConfig <code>pros</code>.</small>
                      <div className="battle-sim-commander-vip-editor">
                        <label>
                          <span>Level</span>
                          <select
                            value={Number(commanderBoostsModel.prosperity.level || 0)}
                            onChange={(event) => updateCommanderBoost("prosperity", { level: event.target.value })}
                          >
                            {PROSPERITY_LEVEL_OPTIONS.map((row) => (
                              <option key={`prosperity-level-${row.level}`} value={row.level}>
                                {row.level > 0 ? `Level ${row.level}` : "Inactive"}
                              </option>
                            ))}
                          </select>
                          <small>{`Max ${PROSPERITY_MAX_LEVEL}`}</small>
                        </label>
                        <label className="battle-sim-commander-vip-active">
                          <input
                            type="checkbox"
                            checked={Boolean(commanderBoostsModel.prosperity.active)}
                            onChange={(event) => updateCommanderBoost("prosperity", { active: event.target.checked })}
                          />
                          <span>Active</span>
                        </label>
                      </div>
                      <div className="battle-sim-academy-tech-effect">
                        <small>{`Captured count ${Number(commanderBoostsModel.prosperity.count || 0).toLocaleString()} | Level threshold ${Number(commanderBoostsModel.prosperity.requiredCount || 0).toLocaleString()} | Power ${Number(commanderBoostsModel.prosperity.power || 0).toLocaleString()}`}</small>
                        {commanderBoostsModel.prosperity.effectLines.length > 0 ? commanderBoostsModel.prosperity.effectLines.map((line, lineIndex) => (
                          <div key={`commander-boost-prosperity-${lineIndex}`}>{line}</div>
                        )) : commanderBoostsModel.prosperity.effectRangeLines.length > 0 ? commanderBoostsModel.prosperity.effectRangeLines.map((line, lineIndex) => (
                          <div key={`commander-boost-prosperity-range-${lineIndex}`}>{line}</div>
                        )) : <span>No active buff at this level.</span>}
                      </div>
                    </div>
                    <div className="battle-sim-section-form-card">
                      <strong>Military Rank</strong>
                      <small>Source: login <code>mpos</code> value matched against ServerConfig <code>mrcs</code>.</small>
                      <div className="battle-sim-commander-vip-editor">
                        <label>
                          <span>Rank</span>
                          <select
                            value={Number(commanderBoostsModel.militaryRank.level || 0)}
                            onChange={(event) => updateCommanderBoost("militaryRank", { level: event.target.value })}
                          >
                            {MILITARY_RANK_OPTIONS.map((row) => (
                              <option key={`military-rank-${row.level}`} value={row.level}>
                                {row.level > 0 ? `${row.level} - ${row.label}` : row.label}
                              </option>
                            ))}
                          </select>
                          <small>{`Max ${MILITARY_RANK_MAX_LEVEL}`}</small>
                        </label>
                      </div>
                      <div className="battle-sim-academy-tech-effect">
                        <small>{`Selected ${commanderBoostsModel.militaryRank.label} | Required merit ${Number(commanderBoostsModel.militaryRank.requiredExp || 0).toLocaleString()}`}</small>
                        {commanderBoostsModel.militaryRank.effectLines.length > 0 ? commanderBoostsModel.militaryRank.effectLines.map((line, lineIndex) => (
                          <div key={`commander-boost-military-rank-${lineIndex}`}>{line}</div>
                        )) : commanderBoostsModel.militaryRank.effectRangeLines.length > 0 ? commanderBoostsModel.militaryRank.effectRangeLines.map((line, lineIndex) => (
                          <div key={`commander-boost-military-rank-range-${lineIndex}`}>{line}</div>
                        )) : <span>No active buff at this rank.</span>}
                      </div>
                    </div>
                  </div>
                ) : selectedSection === "island" ? (
                  <div className="battle-sim-section-form-grid">
                    <div className="battle-sim-section-form-card">
                      <strong>City Island Decorations</strong>
                      <small>Source table: <code>deco</code> from live game files.</small>
                      <div className="battle-sim-island-decoration-groups">
                        {islandDecorationGroups.length > 0 ? islandDecorationGroups.map((group) => (
                          <div key={`island-quality-${group.quality}`} className="battle-sim-island-quality-card">
                            <div className="battle-sim-island-quality-head">
                              <strong>{group.label}</strong>
                              <small>{`${Number(group.rows.length || 0).toLocaleString()} decorations`}</small>
                            </div>
                            <div className="battle-sim-island-decoration-table-wrap">
                              <div className="battle-sim-island-decoration-card-list">
                                {group.rows.map((row) => (
                                  <div key={`island-decoration-mobile-${row.id}`} className="battle-sim-island-decoration-card">
                                    <div className="battle-sim-island-decoration-card-head">
                                      <SectionIconCell
                                        iconPath={row.iconPath}
                                        iconSprite={row.iconSprite}
                                        alt={row.label}
                                        candidatePrefixes={ISLAND_ICON_PREFIXES}
                                        fallbackClassName="battle-sim-missing-icon"
                                      />
                                      <div>
                                        <strong>{row.label}</strong>
                                        {row.description ? <small>{row.description}</small> : null}
                                        {renderPendingMaterialRows(
                                          islandPendingChangeRowsByTypeAndId.get(`cityDecorationOptions:${row.id}`) || [],
                                          `pending-island-city-mobile-${row.id}`,
                                        )}
                                      </div>
                                    </div>
                                    <div className="battle-sim-island-decoration-card-controls">
                                      <label>
                                        <span>Level</span>
                                        <select
                                          value={Number(row.displayLevel || 0)}
                                          disabled={Number(row.maxDisplayLevel || 0) <= 0}
                                          aria-label={`${row.label || "Island decoration"} level`}
                                          onChange={(event) => {
                                            const nextValue = Math.max(0, Math.floor(Number(event.target.value || 0)));
                                            updateIslandCityDecorationLevelWithPrerequisite(row, nextValue);
                                          }}
                                        >
                                          {(Array.isArray(row.displayLevelOptions) ? row.displayLevelOptions : [0]).map((level) => (
                                            <option key={`island-city-mobile-level-${row.id}-${level}`} value={level}>
                                              {getIslandLevelDisplayLabel(level, row.maxLevel)}
                                            </option>
                                          ))}
                                        </select>
                                        <small>{`Max ${Math.max(0, Number(row.maxDisplayLevel || 0))}`}</small>
                                      </label>
                                    </div>
                                    <div className="battle-sim-island-decoration-card-buffs">
                                      {row.buffLines.length > 0 ? (
                                        <>
                                          {row.activeLevelLabel ? <small>{row.activeLevelLabel}</small> : null}
                                          {row.buffLines.map((line, lineIndex) => (
                                            <div key={`island-decoration-mobile-buff-${row.id}-${lineIndex}`}>{line}</div>
                                          ))}
                                        </>
                                      ) : Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0 ? (
                                        <>
                                          <small>Inactive.</small>
                                          {renderEffectLineList(row.effectRangeLines, `island-decoration-mobile-range-${row.id}`)}
                                        </>
                                      ) : (
                                        <small>No active buff.</small>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <table className="battle-sim-island-decoration-table">
                                <thead>
                                  <tr>
                                    <th>Icon</th>
                                    <th>Decoration</th>
                                    <th>Level</th>
                                    <th>Buff</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.rows.map((row) => (
                                    <tr key={`island-decoration-${row.id}`}>
                                      <td className="battle-sim-island-decoration-col-icon" data-label="Icon">
                                        <SectionIconCell
                                          iconPath={row.iconPath}
                                          iconSprite={row.iconSprite}
                                          alt={row.label}
                                          candidatePrefixes={ISLAND_ICON_PREFIXES}
                                          fallbackClassName="battle-sim-missing-icon"
                                        />
                                      </td>
                                      <td className="battle-sim-island-decoration-col-name" data-label="Decoration">
                                        <strong>{row.label}</strong>
                                        {row.description ? <small>{row.description}</small> : null}
                                        {renderPendingMaterialRows(
                                          islandPendingChangeRowsByTypeAndId.get(`cityDecorationOptions:${row.id}`) || [],
                                          `pending-island-city-row-${row.id}`,
                                        )}
                                      </td>
                                      <td className="battle-sim-island-decoration-col-level" data-label="Level">
                                        <select
                                          value={Number(row.displayLevel || 0)}
                                          disabled={Number(row.maxDisplayLevel || 0) <= 0}
                                          aria-label={`${row.label || "Island decoration"} level`}
                                          onChange={(event) => {
                                            const nextValue = Math.max(0, Math.floor(Number(event.target.value || 0)));
                                            updateIslandCityDecorationLevelWithPrerequisite(row, nextValue);
                                          }}
                                        >
                                          {(Array.isArray(row.displayLevelOptions) ? row.displayLevelOptions : [0]).map((level) => (
                                            <option key={`island-city-row-level-${row.id}-${level}`} value={level}>
                                              {getIslandLevelDisplayLabel(level, row.maxLevel)}
                                            </option>
                                          ))}
                                        </select>
                                        <small>{`Max ${Math.max(0, Number(row.maxDisplayLevel || 0))}`}</small>
                                      </td>
                                      <td className="battle-sim-island-decoration-col-buff" data-label="Buff">
                                        {row.buffLines.length > 0 ? (
                                          <>
                                            {row.activeLevelLabel ? <small>{row.activeLevelLabel}</small> : null}
                                            {row.buffLines.map((line, lineIndex) => (
                                              <div key={`island-decoration-buff-${row.id}-${lineIndex}`}>{line}</div>
                                            ))}
                                          </>
                                        ) : Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0 ? (
                                          <>
                                            <small>Inactive.</small>
                                            {renderEffectLineList(row.effectRangeLines, `island-decoration-range-${row.id}`)}
                                          </>
                                        ) : (
                                          <small>No active buff.</small>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )) : (
                          <div className="battle-sim-id-level-editor-empty">No city decorations were found in the active game bundle.</div>
                        )}
                      </div>
                      {islandDecorationSetRows.length > 0 ? (
                        <div className="battle-sim-island-decoration-groups">
                          <div className="battle-sim-island-quality-card">
                            <div className="battle-sim-island-quality-head">
                              <strong>Decoration Sets</strong>
                              <small>{`${Number(islandDecorationSetRows.length || 0).toLocaleString()} sets`}</small>
                            </div>
                            <small className="battle-sim-antiques-suit-desc">Set effects activate when the main decoration and all attached decorations are selected.</small>
                            <div className="battle-sim-antiques-suit-list">
                              {islandDecorationSetRows.map((setRow) => (
                                <div key={`island-decoration-set-${setRow.id}`} className="battle-sim-antiques-suit-card">
                                  <div className="battle-sim-antiques-suit-head">
                                    <strong>{setRow.label}</strong>
                                    <small>{`${Number(setRow.activeCount || 0)}/${Number(setRow.totalCount || 0)} selected`}</small>
                                  </div>
                                  {setRow.description ? <small className="battle-sim-antiques-suit-desc">{setRow.description}</small> : null}
                                  <div className="battle-sim-antiques-suit-reqs">
                                    {setRow.requirements.map((row) => (
                                      <div key={`island-decoration-set-req-${setRow.id}-${row.id}`} className="battle-sim-antiques-suit-req-row">
                                        <div className="battle-sim-antiques-suit-req-label">
                                          <SectionIconCell
                                            iconPath={row.iconPath}
                                            iconSprite={row.iconSprite}
                                            alt={row.label}
                                            candidatePrefixes={ISLAND_ICON_PREFIXES}
                                            fallbackClassName="battle-sim-missing-icon"
                                          />
                                          <span title={row.label}>{row.label}</span>
                                          {renderPendingMaterialRows(
                                            islandPendingChangeRowsByTypeAndId.get(`cityDecorationOptions:${row.id}`) || [],
                                            `pending-island-set-${setRow.id}-${row.id}`,
                                          )}
                                        </div>
                                        <div className="battle-sim-antiques-suit-req-controls">
                                          <small>{row.roleLabel}</small>
                                          <label>
                                            <span>Level</span>
                                            <select
                                              value={Number(row.displayLevel || 0)}
                                              disabled={Number(row.maxDisplayLevel || 0) <= 0}
                                              aria-label={`${row.label || "Island decoration"} set level`}
                                              onChange={(event) => {
                                                const nextValue = Math.max(0, Math.floor(Number(event.target.value || 0)));
                                                updateIslandCityDecorationLevelWithPrerequisite(row, nextValue);
                                              }}
                                            >
                                              {(Array.isArray(row.displayLevelOptions) ? row.displayLevelOptions : [0]).map((level) => (
                                                <option key={`island-set-level-${setRow.id}-${row.id}-${level}`} value={level}>
                                                  {getIslandLevelDisplayLabel(level, row.maxLevel)}
                                                </option>
                                              ))}
                                            </select>
                                          </label>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="battle-sim-antiques-suit-buffs">
                                    {setRow.setBuffLines.length > 0 ? (
                                      <>
                                        <small>{setRow.setLevelLabel || "Set effect"}</small>
                                        {setRow.setBuffLines.map((line, lineIndex) => (
                                          <div key={`island-decoration-set-buff-${setRow.id}-${lineIndex}`}>{line}</div>
                                        ))}
                                      </>
                                    ) : Array.isArray(setRow?.setEffectRangeLines) && setRow.setEffectRangeLines.length > 0 ? (
                                      <>
                                        <small>{setRow.setActive ? "No set effect at the selected level." : "Set effect inactive."}</small>
                                        {renderEffectLineList(setRow.setEffectRangeLines, `island-decoration-set-range-${setRow.id}`)}
                                      </>
                                    ) : (
                                      <small>{setRow.setActive ? "No set effect at the selected level." : "Set effect inactive."}</small>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {islandSpecialDecorationGroups.length > 0 ? (
                        <div className="battle-sim-island-decoration-groups">
                          {islandSpecialDecorationGroups.map((group) => (
                            <div key={`island-special-${group.quality}`} className="battle-sim-island-quality-card">
                              <div className="battle-sim-island-quality-head">
                                <strong>{`${group.label} Special Decorations`}</strong>
                                <small>{`${Number(group.rows.length || 0).toLocaleString()} rows | ${Number(islandSpecialSelectedCount || 0).toLocaleString()} selected`}</small>
                              </div>
                              <div className="battle-sim-academy-tech-list">
                                {group.rows.map((row) => (
                                  <div key={`island-special-decoration-${row.id}`} className="battle-sim-academy-tech-row">
                                    <div className="battle-sim-academy-tech-main">
                                      <div className="battle-sim-antiques-suit-req-label">
                                        <SectionIconCell
                                          iconPath={row.iconPath}
                                          iconSprite={row.iconSprite}
                                          alt={row.label}
                                          candidatePrefixes={ISLAND_ICON_PREFIXES}
                                          fallbackClassName="battle-sim-missing-icon"
                                        />
                                        <span title={row.label}>{row.label}</span>
                                      </div>
                                      {row.description ? <small>{row.description}</small> : null}
                                      {renderPendingMaterialRows(
                                        islandPendingChangeRowsByTypeAndId.get(`specialDecorationOptions:${row.id}`) || [],
                                        `pending-island-special-${row.id}`,
                                      )}
                                    </div>
                                    <div className="battle-sim-academy-tech-controls">
                                      <label>
                                        <span>{row.activateOnly ? "Status" : "Level"}</span>
                                        <select
                                          value={Number(row.displayLevel || 0)}
                                          disabled={Number(row.maxDisplayLevel || 0) <= 0}
                                          aria-label={`${row.label || "Special decoration"} ${row.activateOnly ? "status" : "level"}`}
                                          onChange={(event) => {
                                            const nextValue = Math.max(0, Math.floor(Number(event.target.value || 0)));
                                            const nextLevel = row.activateOnly ? 0 : fromIslandLevelDisplay(nextValue);
                                            updateIslandIdLevelCollection("specialDecorationOptions", row.id, nextLevel, nextValue > 0);
                                          }}
                                        >
                                          {(Array.isArray(row.displayLevelOptions) ? row.displayLevelOptions : [0]).map((level) => (
                                            <option key={`island-special-level-${row.id}-${level}`} value={level}>
                                              {row.activateOnly ? (level > 0 ? "Activated" : "Inactive") : getIslandLevelDisplayLabel(level, row.maxLevel)}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                    </div>
                                    <div className="battle-sim-academy-tech-effect">
                                      {row.buffLines.length > 0 ? row.buffLines.map((line, lineIndex) => (
                                        <div key={`island-special-buff-${row.id}-${lineIndex}`}>{line}</div>
                                      )) : Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0 ? row.effectRangeLines.map((line, lineIndex) => (
                                        <div key={`island-special-range-${row.id}-${lineIndex}`}>{line}</div>
                                      )) : <span>{row.selected ? "Activated." : "No active buff."}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      <div className="battle-sim-island-decoration-groups">
                        {islandDockDecorationGroups.length > 0 ? islandDockDecorationGroups.map((group) => (
                          <div key={`island-dock-${group.quality}`} className="battle-sim-island-quality-card">
                            <div className="battle-sim-island-quality-head">
                              <strong>Dock Decorations</strong>
                              <small>{`${Number(group.rows.length || 0).toLocaleString()} dock rows | ${Number(islandDockSelectedCount || 0).toLocaleString()} selected`}</small>
                            </div>
                            {islandDockSelectedCount <= 0 ? (
                              <small className="battle-sim-antiques-suit-desc">No dock decorations are selected or imported, so the Island summary has no dock buff to add.</small>
                            ) : null}
                            <div className="battle-sim-academy-tech-list">
                              {group.rows.map((row) => (
                                <div key={`island-dock-decoration-${row.id}`} className="battle-sim-academy-tech-row">
                                  <div className="battle-sim-academy-tech-main">
                                    <strong>{row.label}</strong>
                                    {row.description ? <small>{row.description}</small> : null}
                                    {renderPendingMaterialRows(
                                      islandPendingChangeRowsByTypeAndId.get(`dockDecorationOptions:${row.id}`) || [],
                                      `pending-island-dock-${row.id}`,
                                    )}
                                  </div>
                                  <div className="battle-sim-academy-tech-controls">
                                    <label>
                                      <span>Level</span>
                                      <select
                                        value={Number(row.displayLevel || 0)}
                                        disabled={Number(row.maxLevel || 0) <= 0}
                                        aria-label={`${row.label || "Dock decoration"} level`}
                                        onChange={(event) => {
                                          const nextValue = Math.max(0, Math.floor(Number(event.target.value || 0)));
                                          updateIslandIdLevelCollection("dockDecorationOptions", row.id, fromIslandLevelDisplay(nextValue), nextValue > 0);
                                        }}
                                      >
                                        {(Array.isArray(row.displayLevelOptions) ? row.displayLevelOptions : [0]).map((level) => (
                                          <option key={`island-dock-level-${row.id}-${level}`} value={level}>
                                            {level > 0 ? `Level ${level}` : "Inactive"}
                                          </option>
                                        ))}
                                      </select>
                                      <small>{`Max ${Math.max(0, Number(row.maxDisplayLevel || 0))}`}</small>
                                    </label>
                                  </div>
                                  <div className="battle-sim-academy-tech-effect">
                                    {row.buffLines.length > 0 ? row.buffLines.map((line, lineIndex) => (
                                      <div key={`island-dock-buff-${row.id}-${lineIndex}`}>{line}</div>
                                    )) : Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0 ? row.effectRangeLines.map((line, lineIndex) => (
                                      <div key={`island-dock-range-${row.id}-${lineIndex}`}>{line}</div>
                                    )) : <span>No active buff.</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )) : (
                          <div className="battle-sim-id-level-editor-empty">No dock decorations were found in the active profile. Dock buffs are included in Island summary calculations when dock rows are present.</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : selectedSection === "cityDecorations" ? (
                  <div className="battle-sim-city-editor">
                    <div className="battle-sim-city-building-groups">
                      {cityDecorationGroups.length > 0 ? cityDecorationGroups.map((group) => (
                        <details
                          key={`city-decoration-group-${group.category}`}
                          className="battle-sim-city-building-group"
                          open
                        >
                          <summary>
                            <strong>{group.label}</strong>
                            <span>{`${Number(group.rows.length || 0).toLocaleString()} permanent options`}</span>
                          </summary>
                          <div className="battle-sim-city-building-list">
                            {group.rows.map((row) => {
                              const decorationId = Number(row.id || row.decorationId || row.itemGid || 0);
                              const selected = selectedCityDecorationById.get(decorationId);
                              const levelOptions = Array.isArray(row.levels) ? row.levels : [];
                              const childRows = Array.isArray(row.childRows) ? row.childRows : [];
                              const selectedLevel = Math.max(0, Math.floor(Number(selected?.level || 0)));
                              const defaultLevel = Math.max(0, Math.floor(Number(levelOptions[0]?.level || 0)));
                              const controlLevel = selected ? (selectedLevel || defaultLevel) : 0;
                              const selectedLevelRow = selected
                                ? levelOptions.find((option) => Number(option?.level || 0) === controlLevel) || levelOptions[0] || null
                                : null;
                              const effectLines = selectedLevelRow
                                ? formatBuffEffectLines(selectedLevelRow?.effects || [], commanderEffectLabelById, row)
                                : [];
                              const effectRangeLines = buildEffectRangeLinesFromLevelRows(levelOptions, commanderEffectLabelById, row, {
                                getDisplayLevel: (levelRow) => levelRow?.levelLabel || `Level ${Number(levelRow?.level || 0).toLocaleString()}`,
                              });
                              return (
                                <div key={`city-decoration-${decorationId}`} className="battle-sim-city-building-row">
                                  <div className="battle-sim-city-building-main">
                                    <strong>{row.name}</strong>
                                    <small>{row.description || "No description in current catalog."}</small>
                                    <span>
                                      {[
                                        row.categoryLabel,
                                        childRows.length ? `${childRows.length} ${row.category === "guard" ? "variants" : "components"}` : "",
                                      ].filter(Boolean).join(" | ")}
                                    </span>
                                    {renderPendingMaterialRows(
                                      cityDecorationPendingChangeRowsById.get(decorationId) || [],
                                      `pending-city-decoration-${decorationId}`,
                                    )}
                                  </div>
                                  <div className="battle-sim-city-building-controls">
                                    <label>
                                      <span>Level</span>
                                      <select
                                        value={controlLevel}
                                        onChange={(event) => {
                                          const nextLevel = Math.max(0, Math.floor(Number(event.target.value || 0)));
                                          updateCityDecorationSelection(decorationId, nextLevel > 0, nextLevel);
                                        }}
                                      >
                                        <option value={0}>Inactive</option>
                                        {levelOptions.map((option) => (
                                          <option key={`city-decoration-${decorationId}-level-${option.level}`} value={option.level}>
                                            {option.levelLabel || `Level ${option.level}`}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                  </div>
                                  <div className="battle-sim-city-building-effects">
                                    {effectLines.length > 0 ? effectLines.map((line, index) => (
                                      <div key={`city-decoration-effect-${decorationId}-${index}`}>{line}</div>
                                    )) : !selected && effectRangeLines.length > 0 ? effectRangeLines.map((line, index) => (
                                      <div key={`city-decoration-range-${decorationId}-${index}`}>{line}</div>
                                    )) : <span>{selected ? "No mapped stat effect." : "Inactive."}</span>}
                                  </div>
                                  {childRows.length > 0 ? (
                                    <details className="battle-sim-city-building-group battle-sim-city-building-group--nested">
                                      <summary>
                                        <strong>{row.category === "guard" ? "Variants" : "Components"}</strong>
                                        <span>{`${childRows.length.toLocaleString()} stackable options`}</span>
                                      </summary>
                                      <div className="battle-sim-city-building-list">
                                        {childRows.map((child) => {
                                          const childId = Number(child.id || child.decorationId || child.itemGid || 0);
                                          const childSelected = selectedCityDecorationById.get(childId);
                                          const childLevelOptions = Array.isArray(child.levels) ? child.levels : [];
                                          const childSelectedLevel = Math.max(0, Math.floor(Number(childSelected?.level || 0)));
                                          const childDefaultLevel = Math.max(0, Math.floor(Number(childLevelOptions[0]?.level || 0)));
                                          const childControlLevel = childSelected ? (childSelectedLevel || childDefaultLevel) : 0;
                                          const childLevelRow = childSelected
                                            ? childLevelOptions.find((option) => Number(option?.level || 0) === childControlLevel) || childLevelOptions[0] || null
                                            : null;
                                          const childEffectLines = childLevelRow
                                            ? formatBuffEffectLines(childLevelRow?.effects || [], commanderEffectLabelById, child)
                                            : [];
                                          const childEffectRangeLines = buildEffectRangeLinesFromLevelRows(childLevelOptions, commanderEffectLabelById, child, {
                                            getDisplayLevel: (levelRow) => levelRow?.levelLabel || `Level ${Number(levelRow?.level || 0).toLocaleString()}`,
                                          });
                                          return (
                                            <div key={`city-decoration-child-${childId}`} className="battle-sim-city-building-row">
                                              <div className="battle-sim-city-building-main">
                                                <strong>{child.name}</strong>
                                                <small>{child.description || `Part of ${row.name}`}</small>
                                                <span>
                                                  {[
                                                    child.categoryLabel,
                                                    child.rank ? `Rank ${child.rank}` : "",
                                                    child.guardCode ? `Variant ${child.guardCode}` : "",
                                                  ].filter(Boolean).join(" | ")}
                                                </span>
                                                {renderPendingMaterialRows(
                                                  cityDecorationPendingChangeRowsById.get(childId) || [],
                                                  `pending-city-decoration-child-${childId}`,
                                                )}
                                              </div>
                                              <div className="battle-sim-city-building-controls">
                                                <label>
                                                  <span>Level</span>
                                                  <select
                                                    value={childControlLevel}
                                                    onChange={(event) => {
                                                      const nextLevel = Math.max(0, Math.floor(Number(event.target.value || 0)));
                                                      updateCityDecorationSelection(childId, nextLevel > 0, nextLevel);
                                                    }}
                                                  >
                                                    <option value={0}>Inactive</option>
                                                    {childLevelOptions.map((option) => (
                                                      <option key={`city-decoration-child-${childId}-level-${option.level}`} value={option.level}>
                                                        {option.levelLabel || `Level ${option.level}`}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </label>
                                              </div>
                                              <div className="battle-sim-city-building-effects">
                                                {childEffectLines.length > 0 ? childEffectLines.map((line, index) => (
                                                  <div key={`city-decoration-child-effect-${childId}-${index}`}>{line}</div>
                                                )) : !childSelected && childEffectRangeLines.length > 0 ? childEffectRangeLines.map((line, index) => (
                                                  <div key={`city-decoration-child-range-${childId}-${index}`}>{line}</div>
                                                )) : <span>{childSelected ? "No mapped stat effect." : "Inactive."}</span>}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </details>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )) : (
                        <div className="battle-sim-id-level-editor-empty">No permanent city decoration options were found in the active game bundle.</div>
                      )}
                    </div>
                  </div>
                ) : selectedSection === "temporaryBuffs" ? (
                  <div className="battle-sim-city-editor">
                    <div className="battle-sim-city-building-groups">
                      {temporaryBuffGroups.length > 0 ? temporaryBuffGroups.map((group) => (
                        <details
                          key={`temporary-buff-group-${group.category}`}
                          className="battle-sim-city-building-group"
                          open={group.category === "itemBuff" || group.category === "alliance_hall_skin" || group.category === "designation" || group.category === "nation" || group.category === "commanderAndBreakthroughSkills"}
                        >
                          <summary>
                            <strong>{group.label}</strong>
                            <span>{`${Number(group.rows.length || 0).toLocaleString()} buff options`}</span>
                          </summary>
                          <div className="battle-sim-city-building-list">
                            {group.rows.map((row) => {
                              const buffId = String(row.buffId || `${row.sourceType}:${row.itemGid}`);
                              const selected = selectedTemporaryBuffById.get(buffId);
                              const durationOptions = Array.isArray(row.durationOptions) ? row.durationOptions : [];
                              const selectedLevel = Math.max(0, Math.floor(Number(selected?.level || 0)));
                              const selectedOption = durationOptions.find((option) => Number(option?.level || 0) === selectedLevel) || durationOptions[0] || null;
                              const effectLines = formatBuffEffectLines(
                                selectedOption?.effects?.length ? selectedOption.effects : row.effects,
                                commanderEffectLabelById,
                                row,
                              );
                              return (
                                <div key={`temporary-buff-${buffId}`} className="battle-sim-city-building-row">
                                  <div className="battle-sim-city-building-main">
                                    <strong>{row.name}</strong>
                                    <small>{row.description || "Temporary stat buff."}</small>
                                    <span>{`${row.categoryLabel}${formatDurationOptionsText(durationOptions) ? ` | ${formatDurationOptionsText(durationOptions)}` : ""}`}</span>
                                  </div>
                                  <div className="battle-sim-city-building-controls">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={Boolean(selected)}
                                        onChange={(event) => updateTemporaryBuffSelection(row, event.target.checked, selectedLevel)}
                                      />
                                      <span>{selected ? "Active" : "Inactive"}</span>
                                    </label>
                                    {durationOptions.length > 1 ? (
                                      <label>
                                        <span>{row.sourceType === "commanderSkill" ? "Level" : "Duration"}</span>
                                        <select
                                          value={selectedLevel}
                                          disabled={!selected}
                                          onChange={(event) => updateTemporaryBuffSelection(row, true, event.target.value)}
                                        >
                                          {durationOptions.map((option) => (
                                            <option key={`temporary-buff-${buffId}-level-${option.level}`} value={option.level}>
                                              {option.durationLabel || `Level ${option.level}`}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                    ) : null}
                                  </div>
                                  <div className="battle-sim-city-building-effects">
                                    {effectLines.length > 0 ? effectLines.map((line, index) => (
                                      <div key={`temporary-buff-effect-${buffId}-${index}`}>{line}</div>
                                    )) : <span>No mapped stat effect.</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )) : (
                        <div className="battle-sim-id-level-editor-empty">No temporary buff options were found in the active game bundle.</div>
                      )}
                    </div>
                  </div>
                ) : selectedSection === "inventory" ? (
                  <ImportedProfileSectionEditor
                    sectionKey="inventory"
                    title="Inventory"
                    description="Point-in-time currencies, items, materials, and owned equipment from a capture import draft."
                    data={inventoryData}
                    onApply={(nextData) => updateImportedSectionData("inventory", nextData)}
                  />
                ) : selectedSection === "city" ? (
                  <div className="battle-sim-city-editor">
                    <div className="battle-sim-city-summary-grid">
                      <div className="battle-sim-city-summary-card">
                        <span>Commander</span>
                        <strong>{cityData?.commander?.name || draft?.linkedCommanderName || "Unlinked"}</strong>
                      </div>
                      <div className="battle-sim-city-summary-card">
                        <span>City Level</span>
                        <strong>{Number(toCityBuildingLevelDisplay(cityData?.commander?.cityLevel || 0)).toLocaleString()}</strong>
                      </div>
                      <div className="battle-sim-city-summary-card">
                        <span>Buildings</span>
                        <strong>{Number(cityBuildingRows.length || 0).toLocaleString()}</strong>
                      </div>
                      <div className="battle-sim-city-summary-card">
                        <span>Building Power</span>
                        <strong>{Number(cityBuildingRows.reduce((sum, row) => sum + Number(row?.power || 0), 0)).toLocaleString()}</strong>
                      </div>
                    </div>

                    {cityBuildingRows.length > 0 ? (
                      <div className="battle-sim-city-building-groups">
                        {cityBuildingsByCategory.map((group) => (
                          <details
                            key={`city-building-group-${group.category}`}
                            className="battle-sim-city-building-group"
                            open={group.category === "core" || group.category === "military"}
                          >
                            <summary>
                              <strong>{group.label}</strong>
                              <span>{`${Number(group.rows.length || 0).toLocaleString()} buildings`}</span>
                            </summary>
                            <div className="battle-sim-city-building-list">
                              {group.rows.map((row) => (
                                <div key={`city-building-${row.rowKey}`} className="battle-sim-city-building-row">
                                  <div className="battle-sim-city-building-main">
                                    <strong>{row.label}</strong>
                                    <small>{row.description || "No description in current catalog."}</small>
                                    <span>{`${getCityBuildingStatusLabel(row.status)}${row.position ? ` | Position ${row.position}` : ""}`}</span>
                                  </div>
                                  <div className="battle-sim-city-building-controls">
                                    <label>
                                      <span>Level</span>
                                      <input
                                        type="number"
                                        min={1}
                                        max={Number(row?.maxDisplayLevel || row?.displayLevel || 1)}
                                        value={Number(row?.displayLevel || 0)}
                                        onFocus={selectInputText}
                                        onClick={selectInputText}
                                        onChange={(event) => updateCityBuildingLevel(row.sourceIndex, event.target.value)}
                                      />
                                    </label>
                                    <div>
                                      <span>Power</span>
                                      <strong>{Number(row?.power || 0).toLocaleString()}</strong>
                                    </div>
                                  </div>
                                  <div className="battle-sim-city-building-effects">
                                    {row.effectLines.length > 0 ? row.effectLines.map((line, index) => (
                                      <div key={`city-building-effect-${row.rowKey}-${index}`}>{line}</div>
                                    )) : <span>No mapped effect for this level.</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        ))}
                      </div>
                    ) : (
                      <div className="battle-sim-id-level-editor-empty">No city buildings were imported yet.</div>
                    )}
                  </div>
                ) : selectedSection === "biochemicalWorkshop" ? (
                  <div className="battle-sim-biochemical-editor">
                    <div className="battle-sim-biochemical-panel">
                      <div className="battle-sim-biochemical-panel-head">
                        <strong>Biochemical Workshop</strong>
                        <small>{`${Number(biochemicalPetRows.length || 0).toLocaleString()} pets | ${Number(biochemicalGeneProgress.unlockedCount || 0).toLocaleString()} gene nodes unlocked`}</small>
                      </div>
                      {biochemicalPetRows.length > 0 ? (
                        <div className="battle-sim-biochemical-pet-grid">
                          {biochemicalPetRows.map((pet) => (
                            <div key={`biochemical-pet-${pet.gid}`} className="battle-sim-biochemical-pet-card">
                              <div className="battle-sim-biochemical-pet-head">
                                <strong>{pet.petLabel}</strong>
                                <small>{`${Number(pet.earnedTechPoints || 0).toLocaleString()} research points`}</small>
                              </div>
                              <div className="battle-sim-biochemical-form-grid">
                                <label>
                                  <span>Heal Level</span>
                                  <select
                                    value={Number(pet.selectedHealDisplayLevel || 1)}
                                    onChange={(event) => updateBiochemicalPetScalar(
                                      pet.gid,
                                      "healLevel",
                                      fromBiochemicalPetLevelDisplay(event.target.value),
                                    )}
                                  >
                                    {buildIntegerRange(1, Number(pet.maxHealDisplayLevel || pet.selectedHealDisplayLevel || 1)).map((displayLevel) => (
                                      <option key={`biochemical-heal-level-${pet.gid}-${displayLevel}`} value={displayLevel}>
                                        {`Level ${displayLevel}`}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  <span>Intimacy Level</span>
                                  <select
                                    value={Number(pet.selectedIntimacyDisplayLevel || 1)}
                                    onChange={(event) => updateBiochemicalPetScalar(
                                      pet.gid,
                                      "intimacyLevel",
                                      fromBiochemicalPetLevelDisplay(event.target.value),
                                    )}
                                  >
                                    {buildIntegerRange(1, Number(pet.maxIntimacyDisplayLevel || pet.selectedIntimacyDisplayLevel || 1)).map((displayLevel) => (
                                      <option key={`biochemical-intimacy-level-${pet.gid}-${displayLevel}`} value={displayLevel}>
                                        {`Level ${displayLevel}`}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                              <div className="battle-sim-biochemical-pet-effects">
                                <div>
                                  <strong>Heal Buffs</strong>
                                  {pet.healEffectLines.length > 0 ? pet.healEffectLines.map((line, index) => (
                                    <span key={`biochemical-heal-effect-${pet.gid}-${index}`}>{line}</span>
                                  )) : pet.healEffectRangeLines.length > 0 ? pet.healEffectRangeLines.map((line, index) => (
                                    <span key={`biochemical-heal-range-${pet.gid}-${index}`}>{line}</span>
                                  )) : <span>No mapped heal buffs.</span>}
                                </div>
                                <div>
                                  <strong>Intimacy Buffs</strong>
                                  {pet.intimacyEffectLines.length > 0 ? pet.intimacyEffectLines.map((line, index) => (
                                    <span key={`biochemical-intimacy-effect-${pet.gid}-${index}`}>{line}</span>
                                  )) : pet.intimacyEffectRangeLines.length > 0 ? pet.intimacyEffectRangeLines.map((line, index) => (
                                    <span key={`biochemical-intimacy-range-${pet.gid}-${index}`}>{line}</span>
                                  )) : <span>No mapped intimacy buffs.</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="battle-sim-biochemical-empty">No treatment room pets were imported yet.</div>
                      )}

                      <div className="battle-sim-biochemical-gene-summary">
                        <div className="battle-sim-biochemical-gene-metric">
                          <strong>{Number(biochemicalGeneProgress.earnedPoints || 0).toLocaleString()}</strong>
                          <span>research points from intimacy</span>
                        </div>
                        <div className="battle-sim-biochemical-gene-metric">
                          <strong>{Number(biochemicalGeneProgress.spentPoints || 0).toLocaleString()}</strong>
                          <span>gene nodes assumed used</span>
                        </div>
                        <div className="battle-sim-biochemical-gene-metric">
                          <strong>{biochemicalGeneProgress.selectedRow ? (biochemicalGeneProgress.selectedRow.skill?.label || biochemicalGeneProgress.selectedRow.label) : "Not started"}</strong>
                          <span>latest active gene</span>
                        </div>
                        <div className="battle-sim-biochemical-gene-metric">
                          <strong>{biochemicalGeneProgress.nextRow ? (biochemicalGeneProgress.nextRow.skill?.label || biochemicalGeneProgress.nextRow.label) : "Complete"}</strong>
                          <span>next gene</span>
                        </div>
                      </div>

                      {biochemicalGeneGroups.length > 0 ? (
                        <div className="battle-sim-biochemical-gene-groups">
                          {biochemicalGeneGroups.map((group) => (
                            <details
                              key={`biochemical-gene-group-${group.group}`}
                              className="battle-sim-biochemical-gene-group"
                              open={group.unlockedCount > 0 && group.unlockedCount < group.rows.length}
                            >
                              <summary>
                                <strong>{group.label}</strong>
                                <span>{`${Number(group.unlockedCount || 0).toLocaleString()} / ${Number(group.rows.length || 0).toLocaleString()}`}</span>
                              </summary>
                              <div className="battle-sim-biochemical-gene-node-list">
                                {group.rows.map((row) => (
                                  <div
                                    key={`biochemical-gene-node-${row.id}`}
                                    className={`battle-sim-biochemical-gene-node${row.unlocked ? " is-unlocked" : ""}`}
                                  >
                                    <span>{Number(row.sortIndex || 0).toLocaleString()}</span>
                                    <strong>{row.skill?.label || row.label}</strong>
                                    <small>
                                      {row.skill
                                        ? `Skill level ${Number(row.skill.displayLevel || 0).toLocaleString()}`
                                        : (row.effectLines[0] || "Gene effect")}
                                    </small>
                                  </div>
                                ))}
                              </div>
                            </details>
                          ))}
                        </div>
                      ) : (
                        <div className="battle-sim-biochemical-empty">No biochemical gene tree catalog is available yet.</div>
                      )}
                    </div>
                  </div>
                ) : selectedSection === "alliance" ? (
                  <div className="battle-sim-academy-editor">
                    <div className="battle-sim-academy-panel battle-sim-linked-alliance-tech-panel">
                      <div className="battle-sim-academy-panel-head">
                        <strong>Alliance Technology</strong>
                        <small>
                          {linkedAllianceTechnologyContext ? [
                            linkedAllianceTechnologyContext?.alliance?.acronym || "",
                            linkedAllianceTechnologyContext?.alliance?.name || "",
                          ].filter(Boolean).join(" | ") || "Linked alliance" : "Stored profile rows"}
                          {` | ${Number(linkedAllianceTechnologyDisplayRows.length || 0).toLocaleString()} active technologies`}
                        </small>
                      </div>
                      {linkedAllianceTechnologyContext ? (
                        linkedAllianceTechnologyDisplayRows.length > 0 ? (
                          <div className="battle-sim-linked-alliance-tech-grid">
                            {linkedAllianceTechnologyDisplayRows.map((row) => (
                              <div key={`linked-alliance-tech-${row.id}`} className="battle-sim-linked-alliance-tech-row">
                                <div>
                                  <strong>{row.label}</strong>
                                  <small>{row.description || "Alliance technology from the linked commander's alliance."}</small>
                                </div>
                                <span>{`Level ${Number(row.displayLevel || 0).toLocaleString()}`}</span>
                                <small>{row.effectLine || "No mapped effect."}</small>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="battle-sim-academy-empty">
                            This commander is linked to an alliance, but no active alliance technology levels are stored for it yet.
                          </div>
                        )
                      ) : (
                        <div className="battle-sim-academy-empty">
                          Link this profile to a commander/alliance to display read-only alliance technology here.
                        </div>
                      )}
                    </div>
                    <div className="battle-sim-academy-panel">
                      <div className="battle-sim-academy-panel-head">
                        <strong>Benefit Buildings</strong>
                        <small>Source table: <code>ServerConfig.mapbuilding</code>; capture payload not found in login.</small>
                      </div>
                      <div className="battle-sim-academy-tech-list">
                        {allianceBenefitBuildingRows.map((row) => (
                          <div key={`alliance-benefit-building-${row.id}`} className="battle-sim-academy-tech-row">
                            <div className="battle-sim-academy-tech-main">
                              <strong>{row.label}</strong>
                              <small>{row.baseEffectLines.join(" | ") || `Building #${row.id}`}</small>
                            </div>
                            <div className="battle-sim-academy-tech-controls">
                              <label>
                                <span>Captured</span>
                                <input
                                  type="number"
                                  min={0}
                                  value={Number(row.count || 0)}
                                  onFocus={selectInputText}
                                  onClick={selectInputText}
                                  onChange={(event) => updateAllianceBenefitBuildingCount(row.id, event.target.value)}
                                />
                              </label>
                            </div>
                            <div className="battle-sim-academy-tech-effect">
                              {row.count > 0 && row.effectLines.length > 0 ? row.effectLines.join(" | ") : "No captured buildings selected."}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : selectedSection === "academy" ? (
                  <div className="battle-sim-academy-editor">
                    <div className="battle-sim-officer-detail-toolbar battle-sim-academy-detail-toolbar">
                      <label>
                        <span>Academy Section</span>
                        <select
                          value={activeAcademyDetailGroup}
                          onChange={(event) => setActiveAcademyDetailGroup(event.target.value)}
                        >
                          {ACADEMY_DETAIL_GROUPS.map((group) => (
                            <option key={`academy-detail-${group.key}`} value={group.key}>
                              {group.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="battle-sim-academy-panel">
                      <div className="battle-sim-academy-panel-head">
                        <strong>
                          {(ACADEMY_DETAIL_GROUPS.find((group) => group.key === activeAcademyDetailGroup) || ACADEMY_DETAIL_GROUPS[0]).label}
                        </strong>
                        <small>{`${Number((academyRowsByDetailGroup?.[activeAcademyDetailGroup] || []).length).toLocaleString()} technologies`}</small>
                      </div>
                      {(academyRowsByDetailGroup?.[activeAcademyDetailGroup] || []).length > 0 ? (
                        <div className="battle-sim-academy-tech-list">
                          {(academyRowsByDetailGroup?.[activeAcademyDetailGroup] || []).map((row) => {
                            const maxDisplayLevel = Math.max(1, Number(row?.maxDisplayLevel || row?.displayLevel || 1));
                            const displayLevelOptions = buildIntegerRange(1, maxDisplayLevel);
                            return (
                              <div key={`academy-tech-${row.id}`} className="battle-sim-academy-tech-row">
                                <div className="battle-sim-academy-tech-main">
                                  <strong>{row.label || `Technology #${row.id}`}</strong>
                                  <small>{row.description || "No description found in catalog."}</small>
                                </div>
                                <div className="battle-sim-academy-tech-controls">
                                  <label>
                                    <span>Level</span>
                                    <select
                                      value={row?.hasSelectedLevel ? Number(row?.displayLevel || 1) : ""}
                                      onChange={(event) => updateAcademyTechSelection(row.id, event.target.value)}
                                    >
                                      <option value="">Locked</option>
                                      {displayLevelOptions.map((displayLevel) => (
                                        <option key={`academy-tech-level-${row.id}-${displayLevel}`} value={displayLevel}>
                                          {`Level ${displayLevel}`}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                </div>
                                <div className="battle-sim-academy-tech-effect">
                                  {!row?.hasSelectedLevel && Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0
                                    ? renderEffectLineList(row.effectRangeLines, `academy-tech-range-${row.id}`)
                                    : (row.effectLine
                                      ? `${row?.hasSelectedLevel ? "" : "Locked: "}${row.effectLine}`
                                      : "No mapped effect.")}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="battle-sim-academy-empty">No academy technologies found for this section.</div>
                      )}
                    </div>
                  </div>
                ) : selectedSection === "commanderSkills" ? (
                  <div className="battle-sim-commander-skills-editor">
                    <div className="battle-sim-profile-section-toolbar battle-sim-commander-skills-detail-toolbar">
                      <label className="battle-sim-profile-section-select">
                        <span>Commander Skill Section</span>
                        <select
                          value={activeCommanderSkillDetailGroup}
                          onChange={(event) => setActiveCommanderSkillDetailGroup(event.target.value)}
                        >
                          {COMMANDER_SKILL_DETAIL_GROUPS.map((group) => (
                            <option key={group.key} value={group.key}>{group.label}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="battle-sim-commander-skills-panel">
                      <div className="battle-sim-commander-skills-panel-head">
                        <strong>
                          {(COMMANDER_SKILL_DETAIL_GROUPS.find((group) => group.key === activeCommanderSkillDetailGroup) || COMMANDER_SKILL_DETAIL_GROUPS[0]).label}
                        </strong>
                        <small>{`${Number((commanderSkillRowsByDetailGroup?.[activeCommanderSkillDetailGroup] || []).length).toLocaleString()} skills`}</small>
                      </div>
                      {(commanderSkillRowsByDetailGroup?.[activeCommanderSkillDetailGroup] || []).length > 0 ? (
                        <div className="battle-sim-commander-skills-list">
                          {(commanderSkillRowsByDetailGroup?.[activeCommanderSkillDetailGroup] || []).map((row) => {
                            const maxDisplayLevel = Math.max(1, Number(row?.maxDisplayLevel || row?.displayLevel || 1));
                            const displayLevelOptions = buildIntegerRange(1, maxDisplayLevel);
                            return (
                              <div key={`commander-skill-${row.id}`} className="battle-sim-commander-skills-row">
                                <div className="battle-sim-commander-skills-main">
                                  <strong>{row.label || `Skill #${row.id}`}</strong>
                                  <small>{row.description || "No description found in catalog."}</small>
                                </div>
                                <div className="battle-sim-commander-skills-controls">
                                  <label>
                                    <span>Level</span>
                                    <select
                                      value={row?.hasSelectedLevel ? Number(row?.displayLevel || 1) : ""}
                                      onChange={(event) => updateCommanderSkillTreeSelection(activeCommanderSkillDetailGroup, row.id, event.target.value)}
                                    >
                                      <option value="">Locked</option>
                                      {displayLevelOptions.map((displayLevel) => (
                                        <option key={`commander-skill-level-${row.id}-${displayLevel}`} value={displayLevel}>
                                          {`Level ${displayLevel}`}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                </div>
                                <div className="battle-sim-commander-skills-effect">
                                  {Array.isArray(row?.effectLines) && row.effectLines.length > 0
                                    ? row.effectLines.map((line, index) => (
                                      <div key={`commander-skill-effect-${row.id}-${index}`}>{line}</div>
                                    ))
                                    : (!row?.hasSelectedLevel && Array.isArray(row?.effectRangeLines) && row.effectRangeLines.length > 0
                                      ? renderEffectLineList(row.effectRangeLines, `commander-skill-range-${row.id}`)
                                      : (row.effectLine
                                        ? `${row?.hasSelectedLevel ? "" : "Locked: "}${row.effectLine}`
                                        : "No mapped effect."))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="battle-sim-commander-skills-empty">No commander skills found for this section.</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="battle-sim-profile-section-placeholder">
                    <strong>{selectedSectionMeta.label}</strong>
                    <p>{selectedSectionCatalog?.description || selectedSectionMeta.description}</p>
                    {Array.isArray(selectedSectionCatalog?.sources) && selectedSectionCatalog.sources.length > 0 ? (
                      <div className="battle-sim-section-sources">
                        <span>Game sources:</span>
                        <code>{selectedSectionCatalog.sources.join(", ")}</code>
                      </div>
                    ) : null}
                    {Array.isArray(selectedSectionCatalog?.groups) && selectedSectionCatalog.groups.length > 0 ? (
                      <div className="battle-sim-section-groups">
                        {selectedSectionCatalog.groups.map((group) => (
                          <div key={group.key} className="battle-sim-section-group-card">
                            <div className="battle-sim-section-group-head">
                              <strong>{group.label}</strong>
                              <small>{Number(group.count || 0).toLocaleString()}</small>
                            </div>
                            {Array.isArray(group.rows) && group.rows.length > 0 ? (
                              <div className="battle-sim-section-group-preview">
                                {group.rows.slice(0, 8).map((row, index) => (
                                  <span key={`${group.key}-${row?.id || index}`}>{formatSectionRowLabel(row)}</span>
                                ))}
                              </div>
                            ) : (
                              <div className="battle-sim-section-group-empty">No rows available.</div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <p>This section is catalog-backed and read-only in this pass. Officers, titans, warplane, equipment, antiques, island, and commander skills are editable.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </SectionCard>
      </div>

      {selectedProfileId ? (
        <div className="battle-sim-upgrade-cart-actions">
          <button
            type="button"
            className="battle-sim-upgrade-cart-reset-button"
            onClick={requestResetProfileChanges}
            disabled={isSaving || isDeleting || !hasUnsavedChanges}
          >
            Reset
          </button>
          <button
            type="button"
            className="battle-sim-upgrade-cart-button"
            onClick={handleSave}
            disabled={isSaving || isDeleting || !hasUnsavedChanges}
            aria-label="Open upgrade cart"
          >
            <span className="battle-sim-upgrade-cart-icon" aria-hidden="true">
              <FaShoppingCart />
            </span>
            <span className="battle-sim-upgrade-cart-copy">
              <strong>{isSaving ? "Saving..." : "Upgrade Cart"}</strong>
              <small>
                {hasUnsavedChanges
                  ? `${profileCartChangeCount.toLocaleString()} change${profileCartChangeCount === 1 ? "" : "s"} | ${profileCartMaterialCount.toLocaleString()} material${profileCartMaterialCount === 1 ? "" : "s"}`
                  : "No pending upgrades"}
              </small>
            </span>
            {profileCartChangeCount > 0 ? (
              <span className="battle-sim-upgrade-cart-badge">{profileCartChangeCount.toLocaleString()}</span>
            ) : null}
          </button>
        </div>
      ) : null}
    </PageShell>
  );
};

export default BattleSimulationCommanderProfiles;

